import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { getCategoryPreset } from "@/lib/categoryConfig";
import HostIntakeFieldsTab from "@/components/host/HostIntakeFieldsTab";
import HostPaymentSettingsTab from "@/components/host/HostPaymentSettingsTab";
import { HostManagementPipelineTab, HostManagementListTab } from "@/components/host/HostManagementTabs";
import PreferenceFieldToggles from "@/components/host/PreferenceFieldToggles";
import ExperiencePreferenceConfig from "@/components/host/ExperiencePreferenceConfig";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import PhygitalIcon from "@/components/PhygitalIcon";
import GooglePlacesAutocomplete from "@/components/GooglePlacesAutocomplete";
import ImageUpload from "@/components/ui/image-upload";
import { TrialBadge, UpgradeModal, useTierInfo } from "@/components/billing/TierGating";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { useEvent, useEventRsvps, revalidateEventData, revalidateAllUserData, triggerGlobalSync, useSyncListener } from "@/hooks/useUserData";
import { useUiSurface, slotText } from "@/hooks/useUiSurface";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Users,
  Utensils,
  Accessibility,
  Download,
  Plus,
  ExternalLink,
  Link2,
  Trash2,
  CheckCircle2,
  Clock,
  BarChart3,
  Calendar,
  RefreshCw,
  Globe,
  Settings,
  Copy,
  Eye,
  EyeOff,
  Check,
  Crown,
  X,
  Building2,
  UserPlus,
  Shield,
  Mail,
  User,
  Edit3,
  Upload,
  Image as ImageIcon,
  Music,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Search,
  Filter,
  MoreHorizontal,
  Save,
  Palette,
  FileText,
  Zap,
  Home,
  ListChecks,
  Tag,
  UserCog,
  AlertCircle,
  Phone,
  MapPin,
  Briefcase,
  Heart,
  Sparkles,
  Wallet,
  XCircle,
  MessageSquare,
  Send,
  Share,
  CreditCard,
  Info,
  Bell,
  Car,
  Mic,
  Star,
  Lock,
  GraduationCap,
  ShoppingBag,
  Grid3X3,
  CalendarDays,
  Loader2,
  HelpCircle,
  Link as LinkIcon,
  Package,
  DollarSign
} from "lucide-react";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CreateListModal,
  AddListPersonModal,
  AddListOrgModal,
  ToolModal,
  DeleteConfirmModal
} from "@/components/host-dashboard/HostDashboardModals";
import HostInsightsDashboard from "@/components/host/HostInsightsDashboard";
import { PeopleTabGrid } from "@/components/host/PeopleTabGrid";
import ServicesCRUD from "@/components/host/ServicesCRUD";
import AcuityServiceMatchModal from "@/components/host/AcuityServiceMatchModal";
import AcuityClientSyncModal from "@/components/host/AcuityClientSyncModal";
import PrivateInspoPanel from "@/components/host/PrivateInspoPanel";
import { DefaultAvatar, DefaultOrgIcon } from "@/components/DefaultAvatars";
import LocationReleaseSettings from "@/components/host/LocationReleaseSettings";
import ProductDirectory from "@/components/host/ProductDirectory";
import PreAppointmentResources from "@/components/host/PreAppointmentResources";
import AcuityConnect from "@/components/host/AcuityConnect";
import ClientKanban from "@/components/host/ClientKanban";
import AppointmentCalendar from "@/components/host/AppointmentCalendar";
import AcuityAppointmentsPanel from "@/components/host/AcuityAppointmentsPanel";
import AcuitySyncHub from "@/components/host/AcuitySyncHub";
import AcuityServicesPanel from "@/components/host/AcuityServicesPanel";
import ClientExperiencePanel from "@/components/host/ClientExperiencePanel";
import PaymentsUpsellsPanel from "@/components/host/PaymentsUpsellsPanel";
import HostInvitesPanel from "@/components/host/HostInvitesPanel";
import HostHelpUpdatesPanel from "@/components/host/HostHelpUpdatesPanel";
import RsvpPrivateInspoViewer from "@/components/host/RsvpPrivateInspoViewer";
import MediaLibraryPanel from "@/components/host/MediaLibraryPanel";
import HostNotesEditor from "@/components/host/HostNotesEditor";
import SubscriptionManager from "@/components/host/SubscriptionManager";
import MediaLibraryPopup from "@/components/MediaLibraryPopup";
import InviteClientModal from "@/components/host/InviteClientModal";
import FindOrImportClientModal from "@/components/host/FindOrImportClientModal";
import EditClientProfileModal from "@/components/host/EditClientProfileModal";
import { UpgradeProvider, GatedFeature, useUpgrade } from "@/components/FeatureGating";
import WebSocketAnalyticsDashboard from "@/components/analytics/WebSocketAnalyticsDashboard";
import HostRollupSummary from "@/components/host/HostRollupSummary";
import HostFinancialsTab from "@/components/host/HostFinancialsTab";
import UnifiedTopBar from "@/components/UnifiedTopBar";
import { UpgradeBanner } from "@/components/UpgradePrompt";
import { 
  PILOT_CATEGORIES, 
  DASHBOARD_TYPES,
  getPilotCategory, 
  isEventDashboard, 
  hasQuickActions,
  getDefaultTapInState 
} from "@/config/pilotCategories";
import { TAP_IN_LOGO } from "@/lib/integrationConfig";
import { useAdminCopy } from "@/contexts/AdminCopyContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Iter 9.40 Slice C⁷ - colors now sync from db.categories via the static
// override cache pushed by useLiveCategoryMeta(). Static map kept as a
// safety fallback when the override hasn't loaded yet.
const HOST_CATEGORY_COLORS_FALLBACK = {
  hair: { iconColor: "#e83e8c", bgColor: "#ffeef4" },
  makeup: { iconColor: "#9c57f5", bgColor: "#f2e9ff" },
  nails: { iconColor: "#f15a70", bgColor: "#ffe8ec" },
  lashes: { iconColor: "#774cda", bgColor: "#ede7f7" },
  events: { iconColor: "#f9c434", bgColor: "#fff5e6" },
  instructors: { iconColor: "#4a90d9", bgColor: "#e0eeff" },
  culinary: { iconColor: "#f28c28", bgColor: "#fff3e6" },
  wellness: { iconColor: "#75d6d6", bgColor: "#e7f5f5" },
  travel: { iconColor: "#73b7ff", bgColor: "#e7f3ff" },
  dispensary: { iconColor: "#60d680", bgColor: "#e8fef0" },
  retail: { iconColor: "#9950fa", bgColor: "#f0e8ff" },
};

const getHostCatColor = (tags) => {
  if (!tags?.length) return null;
  for (const t of tags) {
    const live = getCategoryPreset(t);
    if (live?.bgColor || live?.iconColor) {
      return { bgColor: live.bgColor, iconColor: live.iconColor };
    }
    if (HOST_CATEGORY_COLORS_FALLBACK[t]) return HOST_CATEGORY_COLORS_FALLBACK[t];
  }
  return null;
};

const HostPortalUpdates = () => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUpdate, setSelectedUpdate] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/updates/feed`, {
          headers: token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : {}
        });
        if (res.ok) {
          const data = await res.json();
          const filtered = (Array.isArray(data) ? data : []).filter(u =>
            (u.is_published !== false) &&
            ((u.assigned_portals || u.target_portals || []).includes("host") || (u.target_portals || []).length === 0)
          ).map(u => ({
            ...u,
            cover_image: u.image_url || u.cover_image || (u.media?.[0]?.url),
            tags: u.target_categories || u.tags || [],
          }));
          setUpdates(filtered);
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <div className="flex justify-center py-6"><div className="w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" /></div>;

  if (updates.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <Bell className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm font-medium">No updates from Phygital yet</p>
        <p className="text-xs mt-1">Updates will appear here when published</p>
      </div>
    );
  }

  const gridCols = updates.length === 1 ? 'grid-cols-1' : updates.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';

  return (
    <>
      <div className={`grid ${gridCols} gap-3`}>
        {updates.map((update) => {
          const catColor = getHostCatColor(update.tags);
          const ctaList = update.cta_buttons || update.buttons || [];
          const singleCta = !ctaList.length && update.cta_url ? [{ label: update.cta_text || "Learn More", url: update.cta_url }] : ctaList;
          return (
            <div key={update.update_id}
              className="rounded-xl overflow-hidden border hover:shadow-md transition-shadow cursor-pointer"
              style={{ borderColor: catColor ? catColor.iconColor + '25' : '#e5e7eb' }}
              data-testid={`host-update-${update.update_id}`}
              onClick={() => setSelectedUpdate(update)}
            >
              {update.cover_image && (
                <div className="w-full" style={{ aspectRatio: '16/9' }}>
                  <img src={update.cover_image.startsWith('http') ? update.cover_image : `${API}${update.cover_image}`} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-3" style={{ backgroundColor: catColor ? catColor.bgColor : undefined }}>
                <h4 className="font-semibold text-gray-900 text-sm leading-snug">{update.title}</h4>
                <p className="text-xs text-gray-600 mt-1 line-clamp-2">{update.content}</p>
                {singleCta.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {singleCta.map((btn, i) => (
                      <button key={i}
                        className="text-[10px] font-medium px-2.5 py-1 rounded-lg flex items-center gap-1 text-white"
                        style={{ backgroundColor: catColor ? catColor.iconColor : '#6b7280' }}
                        onClick={e => { e.stopPropagation(); window.open(btn.url || btn.cta_url, '_blank'); }}
                      >{btn.label || btn.cta_text} <ExternalLink className="w-2.5 h-2.5" /></button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Update Detail Modal */}
      {selectedUpdate && (
        <Dialog open={!!selectedUpdate} onOpenChange={() => setSelectedUpdate(null)}>
          <DialogContent className="max-w-2xl p-0 overflow-hidden bg-white" style={{ maxHeight: '85vh' }}>
            <DialogTitle className="sr-only">Update Details</DialogTitle>
            <div className="overflow-y-auto" style={{ maxHeight: '85vh' }}>
              {selectedUpdate.cover_image && (
                <div className="w-full" style={{ aspectRatio: '16/9' }}>
                  <img src={selectedUpdate.cover_image.startsWith('http') ? selectedUpdate.cover_image : `${API}${selectedUpdate.cover_image}`} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-6 space-y-4">
                <h2 className="text-xl font-bold text-gray-900">{selectedUpdate.title}</h2>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedUpdate.content}</p>
                {(selectedUpdate.gallery_images || []).length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {selectedUpdate.gallery_images.map((img, i) => (
                      <div key={i} className="aspect-video rounded-lg overflow-hidden bg-gray-100">
                        <img src={typeof img === "string" ? img : (img.url || img.image_url)} alt="" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};


// Labels for team members and RSVPs (Event dashboards)
const EVENT_LABELS = [
  { value: "host", label: "Host", color: "bg-purple-100 text-purple-700" },
  { value: "instructor", label: "Instructor", color: "bg-blue-100 text-blue-700 border-blue-300" },
  { value: "team", label: "Team", color: "bg-blue-100 text-blue-700" },
  { value: "speaker", label: "Speaker/Panelist", color: "bg-pink-100 text-pink-700" },
  { value: "sponsor", label: "Sponsor/Partner", color: "bg-green-100 text-green-700" },
  { value: "vip", label: "VIP", color: "bg-amber-100 text-amber-700" },
  { value: "vendor", label: "Vendor", color: "bg-orange-100 text-orange-700" },
  { value: "partner", label: "Partner", color: "bg-teal-100 text-teal-700" },
  { value: "pre_invite", label: "Pre-Invite", color: "bg-violet-100 text-violet-700" },
  { value: "attendee", label: "Participant", color: "bg-gray-100 text-gray-700" }
];

// Labels for provider dashboards (Hair, Makeup, Culinary, etc.)
const PROVIDER_LABELS = [
  { value: "client", label: "Client", color: "bg-gray-100 text-gray-700" },
  { value: "vip", label: "VIP", color: "bg-amber-100 text-amber-700" },
  { value: "host", label: "Host", color: "bg-purple-100 text-purple-700" },
  { value: "team", label: "Team", color: "bg-blue-100 text-blue-700" },
  { value: "partner", label: "Partner", color: "bg-teal-100 text-teal-700" },
  { value: "pre_invite", label: "Prospect", color: "bg-violet-100 text-violet-700" },
  { value: "attendee", label: "Client", color: "bg-gray-100 text-gray-700" },
  { value: "rsvp", label: "Client", color: "bg-gray-100 text-gray-700" },
];

// Team roles for permissions
const TEAM_ROLES = [
  { value: "owner", label: "Co-Host", description: "Full dashboard access" },
  { value: "editor", label: "Team Admin", description: "Manage people, limited settings" },
  { value: "viewer", label: "Team Member", description: "View only, check-in access" }
];

// Helper to format time
const formatTime12Hour = (time) => {
  if (!time) return "";
  // Already in 12-hour format with AM/PM
  if (time.match(/\d+:\d+\s*(AM|PM)/i)) return time;
  if (time.includes(':')) {
    const [hours, minutes] = time.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return time;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const h = hours % 12 || 12;
    return `${h}:${String(minutes).padStart(2, '0')} ${ampm}`;
  }
  return time;
};

// Format relative time for timestamps
const formatRelativeTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Get image URL helper
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('data:')) return path; // Base64 images
  // Path includes /api/ prefix, use BACKEND_URL
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  return `${baseUrl}${path}`;
};

// Helper function to ensure URLs are absolute (have protocol)
const ensureAbsoluteUrl = (url) => {
  if (!url) return null;
  // If it already has a protocol, return as is
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('mailto:') || url.startsWith('tel:')) {
    return url;
  }
  // Otherwise, add https://
  return `https://${url}`;
};

// Helper function to blur/mask email for privacy
const blurEmail = (email) => {
  if (!email) return "";
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const maskedLocal = local.length > 3 
    ? local.slice(0, 2) + '•••' + local.slice(-1) 
    : local[0] + '•••';
  return `${maskedLocal}@${domain}`;
};


// Instructor Insight Visibility Controls - host controls what charts show on the insights share page
const InsightVisibilityControls = ({ eventId }) => {
  const [visibility, setVisibility] = useState(null);
  
  const insightCategories = [
    { key: "interests", label: "Interests" },
    { key: "audience_type", label: "Audience Type" },
    { key: "city", label: "Location" },
    { key: "stage", label: "Stage" },
    { key: "learning_preferences", label: "Learning Preferences" },
    { key: "vibe", label: "Vibe" },
    { key: "accessibility_needs", label: "Accessibility Needs" },
    { key: "dietary_restrictions", label: "Dietary Restrictions" },
    { key: "builder_role", label: "Builder Role" },
    { key: "builder_journey", label: "Builder Journey" },
    { key: "builder_current_project", label: "Current Project" },
  ];

  useEffect(() => {
    const token = localStorage.getItem("session_token");
    fetch(`${API}/events/${eventId}/instructor-insights-visibility`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then(r => r.json())
      .then(d => setVisibility(d.instructor_insights_visibility || {}))
      .catch(() => setVisibility({}));
  }, [eventId]);

  const toggleField = async (field, enabled) => {
    const updated = { ...visibility, [field]: enabled };
    setVisibility(updated);
    const token = localStorage.getItem("session_token");
    try {
      await fetch(`${API}/events/${eventId}/instructor-insights-visibility`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(updated)
      });
    } catch {}
  };

  if (visibility === null) return null;

  return (
    <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200" data-testid="insight-visibility-controls">
      <div className="flex items-center gap-1.5 mb-2.5">
        <Eye className="w-3.5 h-3.5 text-gray-500" />
        <span className="text-xs font-medium text-gray-700">Instructor can see:</span>
      </div>
      <div className="grid grid-cols-2 gap-1">
        {insightCategories.map(cat => (
          <label key={cat.key} className="flex items-center gap-2 text-xs p-1.5 rounded hover:bg-white cursor-pointer transition-colors" data-testid={`insight-toggle-${cat.key}`}>
            <input
              type="checkbox"
              checked={visibility[cat.key] !== false}
              onChange={e => toggleField(cat.key, e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className={visibility[cat.key] === false ? 'text-gray-400 line-through' : 'text-gray-700'}>{cat.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};


// Instructor Submission Panel - shows full instructor intake data + links + visibility toggles
const InstructorSubmissionPanel = ({ event, eventId, API, instructorProfile, setInstructorProfile, instructorProfileLoading, setInstructorProfileLoading }) => {
  useEffect(() => {
    const fetchProfile = async () => {
      setInstructorProfileLoading(true);
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/events/${eventId}/instructor-profile`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (res.ok) {
          const data = await res.json();
          setInstructorProfile(data.profile);
        }
      } catch (err) { console.error(err); }
      setInstructorProfileLoading(false);
    };
    fetchProfile();
  }, [eventId]);

  const prof = instructorProfile;
  const session = prof?.session || {};
  const personal = prof?.personal || {};
  const resources = prof?.resources || [];

  return (
    <Card data-testid="instructor-subtab">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-blue-600" />
              Instructor
            </CardTitle>
            <CardDescription>Full instructor intake data, session details, and shareable links</CardDescription>
          </div>
          {prof && <Badge className="bg-green-100 text-green-700 border-green-200">Submitted</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {instructorProfileLoading ? (
          <div className="text-center py-8 text-gray-400"><RefreshCw className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : !prof ? (
          <div className="text-center py-8">
            <GraduationCap className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No instructor submission yet. Share the onboarding link below.</p>
          </div>
        ) : (
          <>
            {/* Instructor Profile Card */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 font-bold text-xl flex-shrink-0">
                  {personal.headshot_url ? (
                    <img src={personal.headshot_url} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (personal.first_name || "I")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-base">{personal.first_name} {personal.last_name}</h3>
                  <p className="text-sm text-gray-600">{personal.title} {personal.company ? `at ${personal.company}` : ''}</p>
                  <p className="text-xs text-gray-500 mt-1">{personal.email}</p>
                  {personal.bio && <p className="text-xs text-gray-600 mt-2 line-clamp-2">{personal.bio}</p>}
                  <div className="flex gap-3 mt-2">
                    {personal.linkedin && <a href={`https://linkedin.com/in/${personal.linkedin}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">LinkedIn</a>}
                    {personal.instagram && <span className="text-xs text-pink-500">@{personal.instagram}</span>}
                    {personal.twitter && <span className="text-xs text-gray-500">@{personal.twitter}</span>}
                    {personal.website && <a href={personal.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">Website</a>}
                  </div>
                </div>
              </div>
            </div>

            {/* Session Content */}
            <div className="p-4 border rounded-xl space-y-3">
              <h4 className="font-medium text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600" /> Session Content</h4>
              <div className="space-y-2">
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Title</p>
                  <p className="text-sm font-semibold text-gray-900">{session.title || "-"}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Description</p>
                  <p className="text-xs text-gray-700 whitespace-pre-wrap">{session.description || "-"}</p>
                </div>
                <div className="flex gap-3">
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Format</p>
                    <Badge variant="outline" className="mt-0.5">{session.format || "-"}</Badge>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Length</p>
                    <Badge variant="outline" className="mt-0.5">{session.length || "-"}</Badge>
                  </div>
                </div>
                {session.topics?.length > 0 && (
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Topics / Proposals</p>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {session.topics.map((t, i) => (
                        <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200">{t}</span>
                      ))}
                    </div>
                  </div>
                )}
                {session.key_learnings && (
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Key Takeaways</p>
                    <p className="text-xs text-gray-700">{session.key_learnings}</p>
                  </div>
                )}
                {session.questions_for_audience && (
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide">Questions for Audience</p>
                    <p className="text-xs text-gray-700">{session.questions_for_audience}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Resources */}
            {resources.length > 0 && (
              <div className="p-4 border rounded-xl">
                <h4 className="font-medium text-sm mb-2">Resources ({resources.length})</h4>
                <div className="space-y-2">
                  {resources.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <Link2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate">{r.title || 'Resource'}</p>
                        {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 hover:underline truncate block">{r.url}</a>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Instructor Onboarding Link - ONLY for instructor pilot events */}
        {event?.pilot_category === 'instructor' && (
        <div className="p-4 border-2 border-blue-200 bg-blue-50/50 rounded-xl">
          <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-blue-600" />
            Instructor Onboarding Link
          </h4>
          <p className="text-xs text-gray-500 mb-3">Send this link to the instructor to fill out their session intake form.</p>
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={`${window.location.origin}/instructor-intake/${eventId}?code=${event?.instructor_invite_code || ''}`}
              className="text-xs font-mono bg-white"
              data-testid="instructor-onboarding-link"
            />
            <Button variant="outline" size="sm" className="bg-blue-100 border-blue-200 text-blue-700 hover:bg-blue-200"
              onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/instructor-intake/${eventId}?code=${event?.instructor_invite_code || ''}`); toast.success("Onboarding link copied!"); }}
              data-testid="copy-onboarding-link-btn">Copy</Button>
          </div>
        </div>
        )}

        {/* Shareable Insights Link - ONLY for instructor pilot events */}
        {event?.pilot_category === 'instructor' && (
        <div className="p-4 border rounded-xl">
          <h4 className="font-medium text-sm mb-2">Shareable Insights Link</h4>
          <p className="text-xs text-gray-500 mb-3">Share this read-only link with the instructor so they can see audience insights (no names/emails).</p>
          <div className="flex items-center gap-2">
            <Input readOnly value={`${window.location.origin}/events/${eventId}/share/instructor`} className="text-xs font-mono bg-gray-50" data-testid="instructor-insights-link" />
            <Button variant="outline" size="sm"
              onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/events/${eventId}/share/instructor`); toast.success("Insights link copied!"); }}
              data-testid="copy-insights-link-btn">Copy</Button>
            <Button variant="outline" size="sm"
              onClick={() => window.open(`/events/${eventId}/share/instructor`, '_blank')}
              data-testid="open-insights-link-btn">Open</Button>
          </div>
        </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function HostDashboardV2() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const t = useAdminCopy();
  
  // SWR hooks for real-time data
  const { event, isLoading: eventLoading, mutateEvent } = useEvent(eventId);
  const { rsvps, isLoading: rsvpsLoading, mutateRsvps } = useEventRsvps(eventId);
  // Acuity-imported / host-invite-imported clients live in provider_clients,
  // not rsvps. Merge them in so the People tab shows them alongside RSVPs.
  const [importedClients, setImportedClients] = useState([]);
  const refetchImportedClients = useCallback(async () => {
    if (!eventId) return;
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("session_token");
      const r = await fetch(`${API}/provider/clients?event_id=${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const data = await r.json();
      setImportedClients(Array.isArray(data) ? data : []);
    } catch (_e) { /* silent */ }
  }, [eventId]);
  useEffect(() => { refetchImportedClients(); }, [refetchImportedClients]);
  
  // Auth headers helper for API calls
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("session_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Pass 2 - Draft route guard
  // Hosts cannot access their dashboard while it is in admin-draft mode.
  // Admin-impersonated sessions and super_admin bypass this guard.
  // Reference: /app/memory/PRD.md §"Pass 2".
  useEffect(() => {
    if (!event) return;
    if (event.dashboard_status !== "draft") return;
    const isAdmin = user?.role === "super_admin" || user?.role === "admin" || user?.is_super_admin;
    const isImpersonating = !!localStorage.getItem("original_admin_token");
    if (isAdmin || isImpersonating) return;
    // Host hitting their own draft dashboard → redirect to /me with a flag
    try { localStorage.setItem("draft_dashboard_notice", JSON.stringify({ event_id: event.event_id, name: event.name, ts: Date.now() })); } catch (_e) { /* silent */ }
    navigate("/me?draft=true", { replace: true });
  }, [event, user, navigate]);
  
  // Listen for global sync events
  useSyncListener();
  
  const isInstructorEvent = (event?.pilot_category || '').toLowerCase() === 'instructor' || event?.event_type === 'instructor_pilot';

  // Determine dashboard type. Source of truth, in priority order:
  //   1. event.dashboard_kind ('provider' | 'instructor' | 'events')
  //   2. event.dashboard_type (legacy)
  //   3. event.pilot_category / event_type
  // Quick Actions (share links) policy:
  //   PROVIDER   -> none
  //   INSTRUCTOR -> Audience Insights only
  //   EVENT      -> full Chef/Venue/DJ tiles
  const dashboardType = useMemo(() => {
    const kind = (event?.dashboard_kind || '').toLowerCase();
    if (kind === 'instructor') return DASHBOARD_TYPES.INSTRUCTOR;
    if (kind === 'provider') return DASHBOARD_TYPES.PROVIDER;
    if (kind === 'events' || kind === 'event') return DASHBOARD_TYPES.EVENT;

    if (event?.dashboard_type === 'provider') return DASHBOARD_TYPES.PROVIDER;
    if (event?.dashboard_type === 'instructor') return DASHBOARD_TYPES.INSTRUCTOR;
    if (event?.dashboard_type === 'event') return DASHBOARD_TYPES.EVENT;

    const pilotCategory = (event?.pilot_category || '').toLowerCase();
    const eventType = (event?.event_type || '').toLowerCase().replace('_pilot', '');

    if (pilotCategory === 'instructor' || eventType === 'instructor') {
      return DASHBOARD_TYPES.INSTRUCTOR;
    }
    if (pilotCategory === 'event' || pilotCategory === 'events' || eventType === 'event' || eventType === 'events') {
      return DASHBOARD_TYPES.EVENT;
    }

    const providerDashboardTypes = ['provider_dashboard', 'provider', 'service_provider', 'makeup', 'culinary', 'hair', 'nails', 'nail', 'lash', 'wellness', 'travel', 'dispensary', 'style', 'style', 'beauty'];
    if (providerDashboardTypes.includes(pilotCategory) || providerDashboardTypes.includes(eventType)) {
      return DASHBOARD_TYPES.PROVIDER;
    }

    return DASHBOARD_TYPES.EVENT;
  }, [event?.event_type, event?.pilot_category, event?.dashboard_type, event?.dashboard_kind]);

  // Quick Actions visibility: only Event dashboards show the full grid; instructor dashboards
  // surface their single Audience Insights tile through their own block, not Quick Actions.
  const showQuickActions = dashboardType === DASHBOARD_TYPES.EVENT;
  
  // Category preset colors for this dashboard
  const catPreset = useMemo(() => getCategoryPreset(event?.pilot_category || event?.event_type), [event?.pilot_category, event?.event_type]);
  const catIconColor = catPreset?.iconColor || '#9c57f5';
  const catBgColor = catPreset?.bgColor || '#f8f4ff';
  const catColors = catPreset;
  const CatIcon = catPreset?.icon;
  
  // Check if Tap In should be enabled by default
  const tapInDefaultEnabled = useMemo(() => {
    return dashboardType === DASHBOARD_TYPES.EVENT;
  }, [dashboardType]);
  
  // UI State
  const [activeTab, setActiveTab] = useState("overview");
  const [settingsTab, setSettingsTab] = useState("welcome");
  // Iter 9.49+ - Settings - Services sub-tab toggle (Host Services | Acuity).
  const [servicesSubTab, setServicesSubTab] = useState("host");

  // Iter 9.47.r - Default tab for host_management dashboards is "pipeline"
  useEffect(() => {
    if (event?.subtype === "host_management" && activeTab === "overview") {
      setActiveTab("pipeline");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.subtype]);
  const [settingsAdvancedOpen, setSettingsAdvancedOpen] = useState(false);
  const [providerView, setProviderView] = useState("kanban"); // kanban or calendar
  const [myDashboards, setMyDashboards] = useState([]); // All dashboards this host owns
  // Iter 9.47.j - Tab labels + status banner copy live in Airtable `surface_host_dashboard`.
  const { slotMap: hd } = useUiSurface("surface_host_dashboard");
  // Phase 9 - instructor add-ons attached to this host dashboard.
  // When non-empty, the parent dashboard renders an "Instructor" sub-section
  // inside Settings → Operations so the host can switch into the lessons view.
  const [instructorAddons, setInstructorAddons] = useState([]);

  // Iter 9.49 - Airtable-driven dashboard template (tab labels + visibility).
  // Falls back to hardcoded tab structure if the API returns no template
  // (e.g. category not yet seeded in Airtable). Reads from
  // Phygital Core -> Host Dashboard Templates + Host Dashboard Tabs.
  const [airtableTemplate, setAirtableTemplate] = useState(null);
  useEffect(() => {
    const cat = event?.category_id || event?.category;
    if (!cat) return;
    const mode = event?.instructor_mode || "none";
    fetch(`${API}/host/dashboard-template?category=${encodeURIComponent(cat)}&instructor_mode=${encodeURIComponent(mode)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.template) setAirtableTemplate(d); })
      .catch(() => {});
  }, [event?.category_id, event?.category, event?.instructor_mode]);

  // Build a quick lookup from canonical tab_id -> Airtable label so we can
  // override hardcoded labels in the tab bar without rebuilding the tab tree.
  // Canonical mapping between local tab values and Airtable Tab IDs.
  const tabIdAlias = useMemo(() => ({
    overview: "tab_overview",
    people: "tab_clients",
    services: "tab_services",
    inspo: "tab_inspo",
    products: "tab_products",
    // Iter 9.49+ R5 - Mia + all makeup hosts get "Insights" tab. Brand Portal
    // showcase is shelved until the host_brand standalone dashboard is built.
    brand_portal: "tab_insights",
    lessons: "tab_lessons",
    advocates: "tab_advocates",
    questions: "tab_clients",     // intake is a child of clients
    analytics: "tab_insights", // insights/analytics
    settings: "tab_settings",
    integrations: "tab_integrations",
  }), []);
  const airtableLabelFor = useMemo(() => {
    const map = {};
    (airtableTemplate?.tabs || []).forEach((t) => { map[t.tab_id] = t.label; });
    return map;
  }, [airtableTemplate]);

  useEffect(() => {
    if (!eventId) return;
    fetch(`${API}/instructor/hosts/${eventId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setInstructorAddons(d?.addons || []))
      .catch(() => {});
  }, [eventId]);

  // Fetch question bank when questions tab is activated
  useEffect(() => {
    if (activeTab === "questions" && eventId) {
      fetchQuestionBank();
    }
  }, [activeTab, eventId]);

  // Fetch all dashboards this host manages (for dashboard switcher)
  useEffect(() => {
    const fetchMyDashboards = async () => {
      try {
        const token = localStorage.getItem("session_token");
        if (!token) return;
        const res = await fetch(`${API}/host/my-dashboards`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMyDashboards(data.dashboards || []);
        }
      } catch (e) { /* ignore */ }
    };
    fetchMyDashboards();
  }, [eventId]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncingTeam, setSyncingTeam] = useState(false); // For team sync button
  const [lastSynced, setLastSynced] = useState(null); // For sync indicator
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Dirty state tracking
  const [showUnsavedModal, setShowUnsavedModal] = useState(false); // Unsaved changes popup
  const [pendingTabChange, setPendingTabChange] = useState(null); // Tab user wants to navigate to
  const [partnerNotes, setPartnerNotes] = useState(""); // Partner portal notes
  const [templateDefaults, setTemplateDefaults] = useState(null); // Template defaults from admin
  const [categoryTemplate, setCategoryTemplate] = useState(null); // Category template for inheritance tracking
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
  // Iter 9.37 - Tier gating (trial badge + upgrade modal)
  const tierInfo = useTierInfo();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  
  // People tab state
  const [peopleFilter, setPeopleFilter] = useState("all");
  const [peopleSearch, setPeopleSearch] = useState("");
  const [peopleSubTab, setPeopleSubTab] = useState("attendees");
  const [instructorProfile, setInstructorProfile] = useState(null);
  const [instructorProfileLoading, setInstructorProfileLoading] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showPreferenceCard, setShowPreferenceCard] = useState(false);
  const [showExpandedDetails, setShowExpandedDetails] = useState(false);
  const [showEditPersonModal, setShowEditPersonModal] = useState(false);
  const [editPersonData, setEditPersonData] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteEventConfirm, setShowDeleteEventConfirm] = useState(false);
  const [personToDelete, setPersonToDelete] = useState(null);
  const [updatingPerson, setUpdatingPerson] = useState(false);
  const [showTeamNotes, setShowTeamNotes] = useState(false);
  const [teamNotesContent, setTeamNotesContent] = useState("");
  
  // Internal Notes Modal state
  const [showInternalNotesModal, setShowInternalNotesModal] = useState(false);
  const [showAcuityServiceMatch, setShowAcuityServiceMatch] = useState(false);
  const [showAcuityClientSync, setShowAcuityClientSync] = useState(false);
  const [selectedPersonForNotes, setSelectedPersonForNotes] = useState(null);
  const [newInternalNote, setNewInternalNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  
  // Invite Client Modal state
  const [showInviteClientModal, setShowInviteClientModal] = useState(false);
  const [showFindImportModal, setShowFindImportModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  
  // Host Inspo state
  const [showHostInspoPopup, setShowHostInspoPopup] = useState(false);
  const [hostInspoItems, setHostInspoItems] = useState([]);
  const [hostInspoLoading, setHostInspoLoading] = useState(false);
  
  // Lists state
  const [lists, setLists] = useState([]);
  const [showCreateListModal, setShowCreateListModal] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newListHostName, setNewListHostName] = useState("");
  const [newListHostEmail, setNewListHostEmail] = useState("");
  const [newListInviteLimit, setNewListInviteLimit] = useState(10); // Default 10 VIP invites
  const [newListPlusLimit, setNewListPlusLimit] = useState(2); // Default +2 per guest
  const [selectedListHostId, setSelectedListHostId] = useState("");
  const [editingListId, setEditingListId] = useState(null);
  const [editListData, setEditListData] = useState({});
  const [expandedListId, setExpandedListId] = useState(null);
  const [showAddMemberToListModal, setShowAddMemberToListModal] = useState(false);
  const [addMemberToListId, setAddMemberToListId] = useState(null);
  
  // Bulk selection state for VIP list members
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Extra invite requests state
  const [extraInviteRequests, setExtraInviteRequests] = useState([]);
  
  // VIP List Host View Modal
  const [showVipHostViewModal, setShowVipHostViewModal] = useState(false);
  const [selectedVipListForView, setSelectedVipListForView] = useState(null);
  
  // Modal states for custom popups (NO SYSTEM PROMPTS)
  const [showAddPersonModal, setShowAddPersonModal] = useState(false);
  const [showAddOrgModal, setShowAddOrgModal] = useState(false);
  const [showAddToolModal, setShowAddToolModal] = useState(false);
  const [showEditToolModal, setShowEditToolModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [currentListId, setCurrentListId] = useState(null);
  const [newPersonData, setNewPersonData] = useState({ name: '', email: '', org_name: '' });
  const [newOrgData, setNewOrgData] = useState({ name: '', contact_name: '', contact_email: '' });
  const [newToolData, setNewToolData] = useState({ name: '', link: '', description: '' });
  const [editingTool, setEditingTool] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState({ type: '', name: '', onConfirm: null });
  
  // Speakers state
  const [speakers, setSpeakers] = useState([]);
  const [speakersLoading, setSpeakersLoading] = useState(false);
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  
  // Question Bank state (Phase 1C: Instructor Pilot)
  const [showQuestionBankModal, setShowQuestionBankModal] = useState(false);
  const [questionBankData, setQuestionBankData] = useState({ 
    questions: [], 
    total_questions: 0, 
    selectedQuestions: [],
    filterRecipient: 'all'  // 'all', 'instructor', 'emergent', 'both'
  });
  const [questionBankLoading, setQuestionBankLoading] = useState(false);
  
  // User organizations for event org selection
  const [userOrganizations, setUserOrganizations] = useState([]);
  const [showOrgPicker, setShowOrgPicker] = useState(false);
  
  // Fetch user's organizations for org picker
  useEffect(() => {
    const fetchUserOrgs = async () => {
      try {
        const token = localStorage.getItem("session_token");
        if (!token) return;
        const res = await fetch(`${API}/organizations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const orgs = await res.json();
          setUserOrganizations(Array.isArray(orgs) ? orgs : []);
        }
      } catch (err) {
        console.error("Failed to fetch user orgs:", err);
      }
    };
    fetchUserOrgs();
  }, []);
  
  // Resolve the event's org from user's organizations
  const eventOrg = useMemo(() => {
    if (!event?.org_id || !userOrganizations.length) return null;
    return userOrganizations.find(o => o.org_id === event.org_id) || null;
  }, [event?.org_id, userOrganizations]);
  
  // The display org: prefer event's org, fallback to user's primary
  const displayOrg = useMemo(() => {
    if (eventOrg) return { name: eventOrg.name, logo: eventOrg.logo || eventOrg.logo_url, org_id: eventOrg.org_id };
    if (user?.org_name) return { name: user.org_name, logo: user.org_logo, org_id: user.org_id };
    return null;
  }, [eventOrg, user?.org_name, user?.org_logo, user?.org_id]);
  
  // Change event's org
  const changeEventOrg = async (org) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/settings`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ org_id: org.org_id, host_organization: org.name })
      });
      if (res.ok) {
        mutateEvent();
        toast.success(`Event org changed to ${org.name}`);
        setShowOrgPicker(false);
      }
    } catch (err) {
      toast.error("Failed to change organization");
    }
  };
  
  // Refetch event after Acuity OAuth success so techStack tile flips to Connected (Issue 6)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("acuity_success") === "true") {
      const biz = params.get("business");
      // Defer refetch so the AcuityConnect component's URL cleanup runs first
      setTimeout(() => {
        if (mutateEvent) mutateEvent();
        if (biz) toast.success(`Connected to ${biz}`);
      }, 250);
    }
  }, [mutateEvent]);

  // Hosted By organization editing
  const [editingHostedBy, setEditingHostedBy] = useState(false);
  const [hostedByName, setHostedByName] = useState("");
  
  // Settings state
  const [eventSettings, setEventSettings] = useState({
    title: "",
    date: "",
    time: "",
    end_time: "",
    location: "",
    city: "",
    description: "",
    cover_image: "",
    logo_image: "",
    card_background: "", // Category-themed background for attendee cards
    envelope_background: "", // Custom RSVP invite background
    logo_background_color: "#000000",
    logo_padding: "6px",
    envelope_logo_padding: "16px",
    timezone: "America/Los_Angeles",
    timezone_abbr: "PST",
    timezone_display: "",
    date_tba: false,
    time_tba: false,
    welcome_message: "",
    // Waitlist settings
    waitlist_enabled: true,
    waitlist_title: "",
    waitlist_description: "",
    waitlist_cta: "Join Waitlist",
    waitlist_collect_phone: true,
    // Confirmation settings
    confirmation_message: "",
    instructor_confirmation_message: "",
    playlist_link: "",
    external_website_url: "",
    learn_more_text: "Learn more about this event",
    // Share links
    share_links_enabled: true,
    // Toggles
    show_partners: true,
    show_hosted_by: true,
    show_sponsors: true,
    show_date: true,
    show_time: true,
    show_location: true,
    location_after_rsvp: false,
    // Display settings (host-configurable page controls)
    display_settings: {
      show_date_time: true,
      show_location: true,
      show_description: true,
      show_experience_section: true,
      show_hosted_by: true,
      show_learn_more: true,
      show_confirmation_buttons: true,
      description_font_size: 12
    },
    // Custom texts (for branding)
    custom_texts: {
      share_title: "",
      share_text: "",
      sticker_initials: "",
      playlist_title: "",
      playlist_subtitle: ""
    }
  });
  
  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const coverImageRef = useRef(null);
  const logoImageRef = useRef(null);
  
  // Intake settings
  const [intakeSettings, setIntakeSettings] = useState({
    collect_dietary: true,
    collect_accessibility: true,
    collect_interests: true,
    collect_vibe: true,
    collect_transportation: false,
    collect_location: false,
    location_options: [],
    custom_questions: [],
    interest_options: [],
    requested_defaults: ["dietary", "accessibility"],
    share_visibility: {
      dietary: { dietary_restrictions: true, meat_preferences: true, allergies: true, dietary_notes: true },
      accessibility: { accessibility_needs: true, invisible_disabilities: true, mobility_needs: true, accessibility_notes: true },
      interests: { interests: true, audience_type: true, learning_preferences: true, vibe: true, experience_apps: true },
      show_names: true
    },
    // Pre-Invite Form Configuration
    pre_invite_fields: {
      first_name: { enabled: true, required: true },
      last_name: { enabled: true, required: true },
      email: { enabled: true, required: true },
      company: { enabled: true, required: false },
      title: { enabled: true, required: false },
      interests: { enabled: true, required: false }
    }
  });
  
  // Save intake settings to backend
  const saveIntakeSettings = async (settings) => {
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/events/${eventId}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ intake_settings: settings })
      });
      toast.success("Settings saved!");
    } catch (err) {
      toast.error("Failed to save settings");
    }
  };
  
  // Custom question modal state
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [newQuestion, setNewQuestion] = useState({ question: "", type: "text", required: false, options: [] });
  const [editingQuestionIdx, setEditingQuestionIdx] = useState(null);
  
  // Pre-invite search state
  const [searchPreInvite, setSearchPreInvite] = useState("");
  
  // Tech stack settings
  const [techStack, setTechStack] = useState({
    tapin: {
      enabled: false,
      playlist_link: "",
      dj_insights_link: "",
      description: ""
    },
    // Tools (event-specific) - empty by default, populated from event.tech_stack
    tools: []
  });
  
  // Dashboard Display Settings - synced with admin/portal settings
  const [dashboardDisplaySettings, setDashboardDisplaySettings] = useState({
    stat_cards: {
      total_attending: true,
      rsvps: true,
      checked_in: true,
      dietary_needs: true,
      accessibility: true,
      music_preferences: false
    },
    quick_actions: {
      share_with_chef: true,
      share_with_venue: true,
      share_with_dj: true,
      share_with_instructor: false
    },
    collect: {
      dietary: true,
      accessibility: true,
      interests: true,
      music_preferences: false
    },
    nfc_kiosk_enabled: false // Only show NFC Kiosk link when enabled
  });
  
  // Fetch display settings on load
  useEffect(() => {
    if (eventId) {
      const token = localStorage.getItem("session_token");
      fetch(`${API}/events/${eventId}/display-settings`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      }).then(res => res.json())
        .then(data => {
          if (data.display_settings) {
            setDashboardDisplaySettings(prev => ({
              ...prev,
              ...data.display_settings
            }));
          }
        })
        .catch(err => console.log("Display settings not available:", err));
    }
  }, [eventId]);

  // Category-aware defaults: disable dietary/accessibility/share-tiles for provider dashboards (e.g. makeup, hair, nails, lashes)
  // These are irrelevant for service-based categories. Host can opt back in via Settings (Issues 4, 5 fix).
  useEffect(() => {
    if (dashboardType === DASHBOARD_TYPES.PROVIDER) {
      const cat = (event?.pilot_category || '').toLowerCase();
      const noDietaryCategories = ['makeup', 'hair', 'nails', 'nail', 'lash', 'lashes', 'style', 'dispensary'];
      if (noDietaryCategories.includes(cat)) {
        setDashboardDisplaySettings(prev => ({
          ...prev,
          stat_cards: { ...prev.stat_cards, dietary_needs: false, accessibility: prev.stat_cards?.accessibility ?? false },
          collect: { ...prev.collect, dietary: false },
          // Default share-with-chef OFF for these categories. share-with-venue stays optional per host.
          quick_actions: { ...prev.quick_actions,
            share_with_chef: prev.quick_actions?.share_with_chef === true ? true : false,
            share_with_venue: prev.quick_actions?.share_with_venue === true ? true : false,
          }
        }));
        // Also default intake collection off for these categories unless host explicitly enabled
        setIntakeSettings(prev => ({
          ...prev,
          collect_dietary: prev?.collect_dietary === true ? true : false,
          collect_accessibility: prev?.collect_accessibility === true ? true : false,
        }));
      }
    }
  }, [dashboardType, event?.pilot_category]);
  
  // Post-Survey Configuration
  const [postSurveyConfig, setPostSurveyConfig] = useState({
    enabled: false,
    show_on_confirmation: true,
    send_via_email: true,
    email_delay_hours: 24,
    questions: []
  });
  
  // Resources Configuration - unified hub (pre-event, check-in, post-survey)
  const [eventResources, setEventResources] = useState([]);
  const [resourceAvailability, setResourceAvailability] = useState({
    pre_event: true,
    check_in: false,
    post_survey: false
  });
  const [newResource, setNewResource] = useState({
    title: "", description: "", link: "", image_url: "", category: "pre_event"
  });
  const [showAddResource, setShowAddResource] = useState(false);
  const [editingResourceId, setEditingResourceId] = useState(null);
  
  // Instructor Resources Curation
  const [submittedResources, setSubmittedResources] = useState([]);
  const [approvedResourceIds, setApprovedResourceIds] = useState(new Set());
  const [newResourceTitle, setNewResourceTitle] = useState("");
  const [newResourceUrl, setNewResourceUrl] = useState("");
  
  // Team state
  const [team, setTeam] = useState([]);
  const [partners, setPartners] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  const [editingTeamMember, setEditingTeamMember] = useState(null);
  const [newTeamMember, setNewTeamMember] = useState({
    email: "",
    name: "",
    org_name: "",
    role: "viewer",
    label: "partner"
  });
  const [addingTeamMember, setAddingTeamMember] = useState(false);
  
  // Manage VIP List Modal state
  const [showManageListModal, setShowManageListModal] = useState(false);
  const [managingList, setManagingList] = useState(null);
  const [newPlaceholder, setNewPlaceholder] = useState({ name: "" });
  const [addingPlaceholder, setAddingPlaceholder] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  
  // WebSocket connection for real-time VIP list updates in Manage List modal
  useEffect(() => {
    if (!showManageListModal || !managingList?.list_id) return;
    
    const wsBase = API.replace('https://', 'wss://').replace('http://', 'ws://').replace(/\/api\/?$/, '');
    const wsUrl = `${wsBase}/api/ws/vip-list/${managingList.list_id}`;
    
    let ws = null;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 3;
    
    const connectWebSocket = () => {
      try {
        ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log('VIP WebSocket connected for list:', managingList.list_id);
          setWsConnected(true);
          reconnectAttempts = 0;
        };
        
        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            console.log('VIP WebSocket message:', message);
            
            if (message.type === 'member_added' || message.type === 'member_joined' || message.type === 'plus_two_joined') {
              // A new member was added - refresh the VIP lists
              fetchLists();
              toast.success(`${message.data?.member?.display_name || message.data?.member?.name || 'Someone'} joined the VIP list!`);
            } else if (message.type === 'member_removed') {
              // A member was removed - refresh lists
              fetchLists();
            }
          } catch (e) {
            console.error('WebSocket message parse error:', e);
          }
        };
        
        ws.onerror = (error) => {
          console.error('VIP WebSocket error:', error);
          setWsConnected(false);
        };
        
        ws.onclose = () => {
          console.log('VIP WebSocket disconnected');
          setWsConnected(false);
          
          if (reconnectAttempts < maxReconnectAttempts) {
            reconnectAttempts++;
            setTimeout(connectWebSocket, 2000 * reconnectAttempts);
          }
        };
      } catch (e) {
        console.error('WebSocket connection failed:', e);
      }
    };
    
    connectWebSocket();
    
    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [showManageListModal, managingList?.list_id]);
  
  // WebSocket connection for real-time event analytics (live check-in counts, new RSVPs)
  const [liveStats, setLiveStats] = useState(null);
  const [analyticsWsConnected, setAnalyticsWsConnected] = useState(false);
  
  useEffect(() => {
    if (!eventId) return;
    
    let ws = null;
    let reconnectAttempts = 0;
    let reconnectTimeout = null;
    
    const connectAnalyticsWebSocket = () => {
      try {
        const wsBase = API.replace('https://', 'wss://').replace('http://', 'ws://').replace(/\/api\/?$/, '');
        const wsUrl = wsBase + `/api/ws/event-analytics/${eventId}`;
        ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log('Analytics WebSocket connected for event:', eventId);
          setAnalyticsWsConnected(true);
          reconnectAttempts = 0;
        };
        
        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            console.log('Analytics WebSocket message:', message);
            
            if (message.type === 'initial_stats' || message.type === 'stats_update') {
              setLiveStats(message.data);
            }
            
            if (message.type === 'new_rsvp') {
              // Show toast for new RSVP
              toast.success(`New RSVP: ${message.data.guest_name}`);
              // Trigger data refresh to get the new RSVP
              mutateRsvps();
            }
            
            if (message.type === 'checkin') {
              // Show toast for check-in
              toast.success(`${message.data.guest_name} checked in!`);
              setLiveStats(prev => ({
                ...prev,
                checked_in: message.data.checked_in_count,
                total_count: message.data.total_count
              }));
              // Refresh RSVPs to update check-in status
              mutateRsvps();
            }
          } catch (e) {
            console.error('Analytics WebSocket parse error:', e);
          }
        };
        
        ws.onerror = (error) => {
          console.error('Analytics WebSocket error:', error);
          setAnalyticsWsConnected(false);
        };
        
        ws.onclose = () => {
          console.log('Analytics WebSocket disconnected');
          setAnalyticsWsConnected(false);
          // Reconnect with exponential backoff
          if (reconnectAttempts < 3) {
            reconnectAttempts++;
            reconnectTimeout = setTimeout(connectAnalyticsWebSocket, 3000 * reconnectAttempts);
          } else {
            console.log('Analytics WebSocket: max reconnect attempts reached');
          }
        };
      } catch (e) {
        console.error('Analytics WebSocket connection failed:', e);
        setAnalyticsWsConnected(false);
      }
    };
    
    connectAnalyticsWebSocket();
    
    return () => {
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [eventId, mutateRsvps]);
  
  // Partner/Sponsor state for adding
  const [showAddPartnerModal, setShowAddPartnerModal] = useState(false);
  const [showAddSponsorModal, setShowAddSponsorModal] = useState(false);
  const [newPartner, setNewPartner] = useState({ name: "", website: "", logo: "", contact_name: "", contact_email: "", contact_phone: "" });
  const [newSponsor, setNewSponsor] = useState({ name: "", website: "", logo: "", tier: "standard", contact_name: "", contact_email: "", contact_phone: "" });
  
  // Magic invite link state - shows copyable link after creation
  const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
  const [inviteLinkData, setInviteLinkData] = useState({ name: "", link: "", type: "team" });
  
  // Inner Circle and Waitlist members for Host Insights
  const [innerCircleMembers, setInnerCircleMembers] = useState([]);
  const [waitlistMembers, setWaitlistMembers] = useState([]);

  // Stats computed from RSVPs
  const stats = {
    total_rsvps: rsvps?.length || 0,
    checked_in: rsvps?.filter(r => r.status === "checked_in").length || 0,
    dietary_needs: rsvps?.filter(r => r.dietary_restrictions?.length > 0 || r.dietary_notes || r.shared_defaults?.includes("dietary")).length || 0,
    accessibility: rsvps?.filter(r => r.accessibility_needs?.length > 0 || r.accessibility_notes || r.invisible_disabilities?.length > 0 || r.anything_else_to_share || r.shared_defaults?.includes("accessibility")).length || 0,
    pending: rsvps?.filter(r => r.status === "pending").length || 0,
    approved: rsvps?.filter(r => r.status === "approved").length || 0,
    cant_make_it: rsvps?.filter(r => r.status === "cant_make_it").length || 0,
    // Total attending = RSVPs + their +1s (excluding cant_make_it)
    total_attending: (rsvps || []).reduce((sum, r) => {
      if (r.status === "cant_make_it") return sum;
      const plusOnes = r.plus_one_count || r.plus_ones?.length || r.guests_count || 0;
      return sum + 1 + plusOnes;
    }, 0)
  };

  // Initialize from event data
  useEffect(() => {
    if (event) {
      setEventSettings(prev => ({
        ...prev,
        title: event.name || event.title || event.event_name || event.display_name || "",
        subtitle: event.subtitle || "",
        date: event.date ? new Date(event.date).toISOString().split('T')[0] : "",
        time: event.time || event.start_time || "",
        end_time: event.end_time || "",
        location: event.location || event.address || "",
        city: event.city || "",
        description: event.description || "",
        cover_image: event.cover_image || "",
        logo_image: event.logo_image || "",
        card_background: event.card_background || "",
        envelope_background: event.envelope_background || "",
        logo_background_color: event.logo_background_color || (dashboardType === DASHBOARD_TYPES.PROVIDER ? (getCategoryPreset(event.pilot_category)?.iconColor || "#000000") : "#000000"),
        logo_padding: event.logo_padding || "6px",
        envelope_logo_padding: event.envelope_logo_padding || "16px",
        timezone: event.timezone || "America/Los_Angeles",
        timezone_abbr: event.timezone_abbr || "PST",
        timezone_display: event.timezone_display || "",
        date_tba: event.date_tba || false,
        time_tba: event.time_tba || false,
        welcome_message: event.welcome_message || "",
        waitlist_enabled: event.waitlist_enabled !== false,
        waitlist_title: event.waitlist_title || "",
        waitlist_description: event.waitlist_description || "",
        waitlist_cta: event.waitlist_cta || "Join Waitlist",
        waitlist_collect_phone: event.waitlist_collect_phone !== false,
        confirmation_message: event.confirmation_message || "",
        instructor_confirmation_message: event.instructor_confirmation_message || "Thanks for submitting! We're setting up the event and will share updates along the way.",
        playlist_link: event.playlist_link || event.tap_in_invite_link || "",
        external_website_url: event.external_website_url || "",
        learn_more_text: event.learn_more_text || "Learn more about this event",
        show_partners: event.show_partners !== false,
        show_hosted_by: event.show_hosted_by !== false,
        show_sponsors: event.show_sponsors !== false,
        show_date: event.show_date !== false,
        show_time: event.show_time !== false,
        show_location: dashboardType === DASHBOARD_TYPES.PROVIDER ? (event.show_location === true) : (event.show_location !== false),
        location_after_rsvp: event.location_after_rsvp || false,
        show_envelope: event.show_envelope !== false,
        share_links_enabled: event.share_links_enabled !== false,
        sponsorship_links: event.sponsorship_links || [],
        custom_texts: {
          share_title: event.custom_texts?.share_title || "",
          share_text: event.custom_texts?.share_text || "",
          sticker_initials: event.custom_texts?.sticker_initials || event.custom_texts?.envelope_sticker_text || "",
          playlist_title: event.custom_texts?.playlist_title || "",
          playlist_subtitle: event.custom_texts?.playlist_subtitle || ""
        },
        display_settings: {
          show_date_time: event.display_settings?.show_date_time !== false,
          show_location: dashboardType === DASHBOARD_TYPES.PROVIDER ? (event.display_settings?.show_location === true) : (event.display_settings?.show_location !== false),
          show_description: event.display_settings?.show_description !== false,
          show_experience_section: event.display_settings?.show_experience_section !== false,
          show_hosted_by: event.display_settings?.show_hosted_by !== false,
          show_learn_more: event.display_settings?.show_learn_more !== false,
          show_confirmation_buttons: event.display_settings?.show_confirmation_buttons !== false
        }
      }));
      
      if (event.intake_settings) {
        // Sync tapin_enabled from tech_stack if it exists and intake_settings doesn't have it
        const intakeSettingsToSet = { ...event.intake_settings };
        if (event.tech_stack?.tapin?.enabled !== undefined && event.intake_settings?.tapin_enabled === undefined) {
          intakeSettingsToSet.tapin_enabled = event.tech_stack.tapin.enabled;
        }
        // Also sync if tech_stack.tapin.enabled is true but intake_settings.tapin_enabled is false (trust tech_stack)
        if (event.tech_stack?.tapin?.enabled && !intakeSettingsToSet.tapin_enabled) {
          intakeSettingsToSet.tapin_enabled = true;
        }
        setIntakeSettings(prev => ({ ...prev, ...intakeSettingsToSet }));
      }
      
      if (event.tech_stack) {
        // Also ensure tech_stack.tapin.enabled is synced from intake_settings if needed
        const techStackToSet = { ...event.tech_stack };
        if (event.intake_settings?.tapin_enabled !== undefined) {
          techStackToSet.tapin = { ...(techStackToSet.tapin || {}), enabled: event.intake_settings.tapin_enabled };
        }
        setTechStack(prev => ({ ...prev, ...techStackToSet }));
      }
      
      // Merge team and co_hosts for display
      const teamMembers = event.team || [];
      const coHosts = (event.co_hosts || []).map(ch => ({
        ...ch,
        role: ch.role || 'co_host',
        is_cohost: true
      }));
      // Combine, avoiding duplicates by email
      const allTeam = [...teamMembers];
      coHosts.forEach(ch => {
        if (!allTeam.some(t => t.email?.toLowerCase() === ch.email?.toLowerCase())) {
          allTeam.push(ch);
        }
      });
      setTeam(allTeam);
      
      if (event.partners) setPartners(event.partners);
      if (event.sponsors) setSponsors(event.sponsors);
      
      // Load Post-Survey Configuration
      if (event.post_survey_config) {
        setPostSurveyConfig(prev => ({ ...prev, ...event.post_survey_config }));
      }
      
      // Load Resources Configuration
      if (event.resources) {
        setEventResources(event.resources || []);
      }
      if (event.resource_availability) {
        setResourceAvailability(prev => ({ ...prev, ...event.resource_availability }));
      }
      
      // Load instructor resources for curation
      if (event.pilot_category === 'instructor' && eventId) {
        const loadResources = async () => {
          try {
            const token = localStorage.getItem("session_token");
            const res = await fetch(`${API}/events/${eventId}/resources/manage`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (res.ok) {
              const rdata = await res.json();
              setSubmittedResources(rdata.submitted_resources || []);
              setApprovedResourceIds(new Set((rdata.approved_resources || []).map(r => r.resource_id)));
            }
          } catch (err) {
            console.log("Resources not loaded:", err);
          }
        };
        loadResources();
      }
      
      setLoading(false);
    }
  }, [event]);

  // Fetch lists (only needs eventId)
  useEffect(() => {
    if (eventId) {
      fetchLists();
      fetchNotifications();
      fetchSpeakers();
    }
  }, [eventId]);
  
  // Fetch Inner Circle Members - separate effect that depends on event being loaded
  useEffect(() => {
    if (event) {
      fetchInnerCircleMembers();
    }
  }, [event?.pre_invite_mode?.inner_circle_list_id, event?.inner_circle_list_id, event?.waitlist_enabled]);

  // Fetch template defaults for the event type and apply as fallback values
  useEffect(() => {
    const fetchTemplateDefaults = async () => {
      if (!event?.event_id) return;
      try {
        // Fetch all content pages (welcome, intake, confirmation) to get full template defaults
        const res = await fetch(`${API}/events/${event.event_id}/page-content?page=all`);
        if (res.ok) {
          const data = await res.json();
          const allContent = data.all_content || {};
          setTemplateDefaults(allContent);
          
          // Apply template defaults as fallbacks for display_settings if not set on event
          if (allContent.welcome) {
            setEventSettings(prev => ({
              ...prev,
              display_settings: {
                show_date_time: event?.display_settings?.show_date_time ?? allContent.welcome.show_date_time ?? true,
                show_location: event?.display_settings?.show_location ?? allContent.welcome.show_location ?? true,
                show_description: event?.display_settings?.show_description ?? allContent.welcome.show_description ?? true,
                show_experience_section: event?.display_settings?.show_experience_section ?? allContent.welcome.show_ways_to_customize ?? true,
                show_hosted_by: event?.display_settings?.show_hosted_by ?? allContent.welcome.show_hosted_by ?? true,
                show_learn_more: event?.display_settings?.show_learn_more ?? allContent.welcome.show_learn_more ?? false,
                show_confirmation_buttons: event?.display_settings?.show_confirmation_buttons ?? allContent.welcome.show_confirmation_buttons ?? true
              },
              // Apply branding defaults if not set
              custom_texts: {
                share_title: event?.custom_texts?.share_title || allContent.branding?.share_title || "",
                share_text: event?.custom_texts?.share_text || allContent.branding?.share_text || "",
                sticker_initials: event?.custom_texts?.sticker_initials || allContent.branding?.sticker_initials || "",
                playlist_title: event?.custom_texts?.playlist_title || allContent.branding?.playlist_title || "",
                playlist_subtitle: event?.custom_texts?.playlist_subtitle || allContent.branding?.playlist_subtitle || ""
              }
            }));
          }
          
          // Apply intake defaults if available
          if (allContent.intake) {
            setIntakeSettings(prev => ({
              ...prev,
              // Only apply template defaults for fields that aren't already set on the event
              collect_phone: event?.intake_settings?.collect_phone ?? allContent.intake.show_phone ?? true,
              collect_company: event?.intake_settings?.collect_company ?? allContent.intake.show_company ?? true,
              collect_title: event?.intake_settings?.collect_title ?? allContent.intake.show_title ?? true,
              collect_city: event?.intake_settings?.collect_city ?? allContent.intake.show_city ?? true,
              collect_location: event?.intake_settings?.collect_location ?? event?.intake_settings?.collect_city ?? false,
              location_options: event?.intake_settings?.location_options || [],
              collect_interests: event?.intake_settings?.collect_interests ?? allContent.intake.show_interests ?? true,
              collect_dietary: event?.intake_settings?.collect_dietary ?? allContent.intake.show_dietary ?? false,
              collect_accessibility: event?.intake_settings?.collect_accessibility ?? allContent.intake.show_accessibility ?? true,
              // Question Bank settings
              question_bank_enabled: event?.intake_settings?.question_bank_enabled ?? allContent.intake.question_bank_enabled ?? false,
              question_bank_label: event?.intake_settings?.question_bank_label || allContent.intake.question_bank_label || "Questions for the instructor?"
            }));
          }
        }
      } catch (err) {
        console.log("Could not fetch template defaults:", err);
      }
    };
    fetchTemplateDefaults();
    
    // Fetch category template for inheritance tracking
    const fetchCategoryTemplate = async () => {
      if (!event?.event_id) return;
      try {
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/events/${event.event_id}/category-template`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCategoryTemplate(data);
        }
      } catch (err) {
        console.log("Could not fetch category template:", err);
      }
    };
    fetchCategoryTemplate();
  }, [event?.event_id]);

  // Reset specific fields to category template defaults
  const handleResetToTemplate = async (fields) => {
    if (!event?.event_id) return;
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${event.event_id}/reset-to-template`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields })
      });
      if (res.ok) {
        toast.success(`Reset ${fields.join(', ')} to template defaults`);
        // Refresh event data
        const eventRes = await fetch(`${API}/events/${event.event_id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (eventRes.ok) {
          const updated = await eventRes.json();
          setIntakeSettings(prev => ({
            ...prev,
            ...Object.fromEntries(fields.map(f => [f, updated.intake_settings?.[f] ?? prev[f]]))
          }));
        }
        // Refresh category template
        const tmplRes = await fetch(`${API}/events/${event.event_id}/category-template`, { headers: { Authorization: `Bearer ${token}` } });
        if (tmplRes.ok) setCategoryTemplate(await tmplRes.json());
      }
    } catch (err) {
      console.error("Reset to template failed:", err);
    }
  };
  
  // ============ UNSAVED CHANGES TRACKING ============
  // Browser beforeunload warning
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);
  
  // Mark dirty when settings change (skip initial load)
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (!initialLoadDone.current) {
      // Give initial load time to settle
      const timer = setTimeout(() => { initialLoadDone.current = true; }, 3000);
      return () => clearTimeout(timer);
    }
    setHasUnsavedChanges(true);
  }, [eventSettings, intakeSettings]);
  
  // Clear dirty flag after save - temporarily disable dirty tracking
  // so the SWR refetch doesn't re-trigger the unsaved changes bar
  const markSaved = () => {
    setHasUnsavedChanges(false);
    initialLoadDone.current = false;
    setTimeout(() => { initialLoadDone.current = true; }, 3000);
  };
  
  // Handle tab navigation with unsaved changes check
  const handleTabChange = (newTab) => {
    if (hasUnsavedChanges && activeTab === "settings") {
      setPendingTabChange(newTab);
      setShowUnsavedModal(true);
    } else {
      setActiveTab(newTab);
    }
  };
  
  // Fetch Inner Circle Members for Host Insights
  const fetchInnerCircleMembers = async () => {
    try {
      const token = localStorage.getItem("session_token");
      // Get the inner circle list id from pre_invite_mode or root level
      const listId = event?.pre_invite_mode?.inner_circle_list_id || event?.inner_circle_list_id;
      
      if (listId) {
        const res = await fetch(`${API}/inner-circle/lists/${listId}/members`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setInnerCircleMembers(data.members || []);
        }
      }
      // Also fetch waitlist if enabled
      if (event?.waitlist_enabled) {
        const waitlistRes = await fetch(`${API}/events/${eventId}/waitlist-entries`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (waitlistRes.ok) {
          const waitlistData = await waitlistRes.json();
          setWaitlistMembers(waitlistData.entries || waitlistData.waitlist || []);
        }
      }
    } catch (err) {
      console.error("Error fetching inner circle/waitlist:", err);
    }
  };

  const fetchLists = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/vip-lists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLists(data);
        
        // Fetch extra invite requests for all lists
        const allRequests = [];
        for (const list of data) {
          try {
            const reqRes = await fetch(`${API}/events/${eventId}/vip-lists/${list.list_id}/extra-invite-requests`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (reqRes.ok) {
              const reqData = await reqRes.json();
              allRequests.push(...(reqData.requests || []).map(r => ({ ...r, list_name: list.name })));
            }
          } catch (e) {
            // Ignore errors for individual list requests
          }
        }
        setExtraInviteRequests(allRequests.filter(r => r.status === 'pending'));
      }
    } catch (err) {
      console.error("Error fetching lists:", err);
    }
  };

  // Handle extra invite request (approve/deny)
  const handleExtraInviteRequest = async (requestId, action, approvedCount = null) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/extra-invite-requests/${requestId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          action,
          approved_count: approvedCount
        })
      });
      
      if (res.ok) {
        toast.success(action === 'approve' ? 'Request approved!' : 'Request denied');
        fetchLists(); // Refresh lists and requests
      } else {
        toast.error('Failed to process request');
      }
    } catch (err) {
      console.error("Error handling request:", err);
      toast.error('Error processing request');
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      setNotificationsLoading(false);
    }
  };

  // Clear all notifications
  const clearAllNotifications = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/notifications`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setNotifications([]);
        toast.success("All notifications cleared");
      }
    } catch (err) {
      toast.error("Failed to clear notifications");
    }
  };

  // Fetch speakers for the event
  const fetchSpeakers = async () => {
    setSpeakersLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/speakers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSpeakers(data.speakers || []);
      }
    } catch (err) {
      console.error("Error fetching speakers:", err);
    } finally {
      setSpeakersLoading(false);
    }
  };

  // Update speaker intake data
  const updateSpeakerIntake = async (rsvpId, speakerData) => {
    try {
      const res = await fetch(`${API}/events/${eventId}/rsvp/${rsvpId}/speaker-intake`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          speaker_intake: speakerData,
          speaker_intake_completed: true
        })
      });
      if (res.ok) {
        toast.success("Speaker info updated!");
        fetchSpeakers(); // Refresh the list
        setEditingSpeaker(null);
      } else {
        toast.error("Failed to update speaker info");
      }
    } catch (err) {
      toast.error("Error updating speaker");
    }
  };

  // Sync/repair team data - ensures bidirectional sync between team and co_hosts
  const syncTeamData = async () => {
    setSyncingTeam(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/repair-team`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || "Team data synced!");
        mutateEvent(); // Refresh event data
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to sync team data");
      }
    } catch (err) {
      toast.error("Error syncing team data");
    } finally {
      setSyncingTeam(false);
    }
  };

  // Mark notification as read
  const markNotificationRead = async (notificationId) => {
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/events/${eventId}/notifications/${notificationId}/read`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, read: true } : n
      ));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  // Save event settings
  const saveSettings = async (section = "all") => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      
      const payload = {
        name: eventSettings.title,
        subtitle: eventSettings.subtitle || "",
        date: eventSettings.date,
        time: eventSettings.time,
        end_time: eventSettings.end_time,
        location: eventSettings.location,
        city: eventSettings.city,
        description: eventSettings.description,
        cover_image: eventSettings.cover_image,
        logo_image: eventSettings.logo_image,
        card_background: eventSettings.card_background,
        envelope_background: eventSettings.envelope_background,
        logo_background_color: eventSettings.logo_background_color,
        logo_padding: eventSettings.logo_padding,
        envelope_logo_padding: eventSettings.envelope_logo_padding,
        timezone: eventSettings.timezone,
        timezone_abbr: eventSettings.timezone_abbr,
        timezone_display: eventSettings.timezone_display,
        date_tba: eventSettings.date_tba,
        time_tba: eventSettings.time_tba,
        welcome_message: eventSettings.welcome_message,
        waitlist_enabled: eventSettings.waitlist_enabled,
        waitlist_title: eventSettings.waitlist_title,
        waitlist_description: eventSettings.waitlist_description,
        waitlist_cta: eventSettings.waitlist_cta,
        waitlist_collect_phone: eventSettings.waitlist_collect_phone,
        confirmation_message: eventSettings.confirmation_message,
        instructor_confirmation_message: eventSettings.instructor_confirmation_message,
        playlist_link: eventSettings.playlist_link,
        tap_in_invite_link: eventSettings.playlist_link,
        show_partners: eventSettings.show_partners,
        show_hosted_by: eventSettings.show_hosted_by,
        show_sponsors: eventSettings.show_sponsors,
        show_date: eventSettings.show_date,
        show_time: eventSettings.show_time,
        show_location: eventSettings.show_location,
        location_after_rsvp: eventSettings.location_after_rsvp,
        external_website_url: eventSettings.external_website_url || eventSettings.learn_more_url,
        learn_more_text: eventSettings.learn_more_text,
        custom_texts: eventSettings.custom_texts,
        intake_settings: intakeSettings,
        tech_stack: techStack,
        team,
        partners,
        sponsors,
        cta_links: eventSettings.cta_links || [],
        host_social_links: eventSettings.host_social_links || {},
        host_orgs: eventSettings.host_orgs || event?.host_orgs || [],
        post_survey_config: postSurveyConfig
      };

      const res = await fetch(`${API}/events/${eventId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success("Settings saved!");
        setLastSynced(new Date()); // Update sync indicator
        markSaved(); // Clear dirty state
        mutateEvent(); // Revalidate SWR data
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch (err) {
      toast.error("Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  // Copy link helper
  const copyToClipboard = async (text, label = "Link") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied!`);
    } catch {
      toast.error("Failed to copy");
    }
  };

  // Upload image (cover or logo)
  const uploadImage = async (file, imageType = "cover") => {
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const token = localStorage.getItem("session_token");
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", imageType);
      
      const res = await fetch(`${API}/events/${eventId}/cover-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        if (imageType === "cover") {
          setEventSettings(prev => ({ ...prev, cover_image: data.url }));
        } else {
          setEventSettings(prev => ({ ...prev, logo_image: data.url }));
        }
        toast.success(`${imageType === "cover" ? "Cover" : "Logo"} image uploaded!`);
        mutateEvent();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Upload failed");
      }
    } catch (err) {
      toast.error("Error uploading image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileSelect = (e, imageType) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadImage(file, imageType);
    }
  };

  // Add team member
  const addTeamMember = async () => {
    const fullName = `${newTeamMember.first_name || ''} ${newTeamMember.last_name || ''}`.trim() || newTeamMember.name;
    if (!fullName) {
      toast.error("Name is required");
      return;
    }
    setAddingTeamMember(true);
    try {
      const token = localStorage.getItem("session_token");
      
      // If promoting from existing RSVP, update the RSVP's label instead
      if (newTeamMember.from_rsvp) {
        const label = newTeamMember.role === "owner" ? "host" : "team";
        
        // Update the RSVP to mark them as team/host
        const updateRes = await fetch(`${API}/events/${eventId}/rsvps/${newTeamMember.from_rsvp}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            label: label
          })
        });
        
        if (updateRes.ok) {
          // Also add them to the event team
          const teamRes = await fetch(`${API}/events/${eventId}/team/member`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              email: newTeamMember.email || "",
              name: fullName,
              first_name: newTeamMember.first_name,
              last_name: newTeamMember.last_name,
              org_name: newTeamMember.org_name || "",
              role: newTeamMember.role,
              label: label,
              status: "active",  // Already confirmed via RSVP
              user_id: newTeamMember.user_id,
              from_rsvp: newTeamMember.from_rsvp
            })
          });
          
          if (teamRes.ok) {
            const teamData = await teamRes.json();
            setTeam([...team, teamData.member]);
            toast.success(`${fullName} promoted to ${label === 'host' ? 'co-host' : 'team member'}!`);
          }
          
          setShowAddTeamModal(false);
          setNewTeamMember({ email: "", name: "", first_name: "", last_name: "", org_name: "", role: "viewer", label: "team" });
          mutateEvent();
          mutateRsvps();
          triggerGlobalSync('all', eventId);
        } else {
          toast.error("Failed to promote team member");
        }
        setAddingTeamMember(false);
        return;
      }
      
      // First create the placeholder to get member_id
      const placeholderRes = await fetch(`${API}/events/${eventId}/team/member`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          email: newTeamMember.email || "",
          name: fullName,
          first_name: newTeamMember.first_name,
          last_name: newTeamMember.last_name,
          org_name: newTeamMember.org_name || "",
          role: newTeamMember.role,
          label: newTeamMember.role === "owner" ? "host" : "team",
          status: "pending"
        })
      });
      
      if (!placeholderRes.ok) {
        toast.error("Failed to add team member");
        setAddingTeamMember(false);
        return;
      }
      
      const placeholderData = await placeholderRes.json();
      const memberId = placeholderData.member?.member_id;
      
      // Create magic link for team invitation (linked to member)
      const res = await fetch(`${API}/events/${eventId}/magic-links/team`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          email: newTeamMember.email || `placeholder_${Date.now()}@pending.phygital`,
          name: fullName,
          first_name: newTeamMember.first_name,
          last_name: newTeamMember.last_name,
          role: newTeamMember.role === "owner" || newTeamMember.role === "admin" ? "host" : "team",
          org_name: newTeamMember.org_name || null,
          member_id: memberId
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        
        // Update local team state with the new member including magic link token
        const newMember = {
          ...placeholderData.member,
          magic_link_token: data.token,
          magic_link_id: data.link_id
        };
        setTeam([...team, newMember]);
        
        setShowAddTeamModal(false);
        setNewTeamMember({ email: "", name: "", first_name: "", last_name: "", org_name: "", role: "viewer", label: "team" });
        
        // Show invite link modal with clear copy/paste link
        let inviteUrl = "";
        if (data.token) {
          inviteUrl = `${window.location.origin}/join/team/${data.token}`;
        } else if (data.invite_url) {
          inviteUrl = data.invite_url;
        }
        
        if (inviteUrl) {
          setInviteLinkData({ name: fullName, link: inviteUrl, type: "team" });
          setShowInviteLinkModal(true);
          navigator.clipboard.writeText(inviteUrl);
          toast.success("Link copied to clipboard!");
        } else {
          toast.success("Team member added!");
        }
        
        mutateEvent();
        triggerGlobalSync('all', eventId);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create invitation");
      }
    } catch (err) {
      toast.error("Error inviting team member");
    } finally {
      setAddingTeamMember(false);
    }
  };

  // Remove team member
  const removeTeamMember = async (memberId) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/team/member/${memberId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setTeam(team.filter(m => m.member_id !== memberId && m.user_id !== memberId));
        toast.success("Team member removed");
        mutateEvent();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to remove");
      }
    } catch (err) {
      toast.error("Error removing team member");
    }
  };

  // Wrapper with confirmation
  const confirmRemoveTeamMember = (member) => {
    setDeleteTarget({
      type: 'team_member',
      name: member.name || member.email || 'Team Member',
      onConfirm: () => removeTeamMember(member.member_id || member.user_id)
    });
    setShowDeleteConfirmModal(true);
  };

  // Fetch Question Bank for Instructor Pilot (Phase 1C)
  const fetchQuestionBank = async () => {
    setQuestionBankLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/question-bank`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setQuestionBankData(data);
      } else {
        toast.error("Could not load questions");
      }
    } catch (err) {
      console.error("Error fetching question bank:", err);
      toast.error("Failed to load question bank");
    } finally {
      setQuestionBankLoading(false);
    }
  };

  // Open Question Bank Modal
  const openQuestionBankModal = () => {
    setShowQuestionBankModal(true);
    fetchQuestionBank();
  };

  // Save Hosted By organization name
  const saveHostedByName = async (newName) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/settings`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        credentials: 'include',
        body: JSON.stringify({ 
          hosted_by_name: newName,
          host_orgs: newName ? [{ name: newName }] : [] 
        })
      });
      if (res.ok) {
        toast.success("Hosted By updated!");
        setEditingHostedBy(false);
        mutateEvent();
      } else {
        toast.error("Failed to update");
      }
    } catch (err) {
      toast.error("Error saving");
    }
  };

  // Bulk delete VIP list members
  const bulkDeleteMembers = async (listId, memberIds) => {
    if (memberIds.length === 0) {
      toast.error("No members selected");
      return;
    }
    
    setBulkDeleting(true);
    const token = localStorage.getItem("session_token");
    let successCount = 0;
    let failCount = 0;
    
    for (const memberId of memberIds) {
      try {
        const res = await fetch(`${API}/events/${eventId}/vip-lists/${listId}/members/${memberId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (err) {
        failCount++;
      }
    }
    
    setBulkDeleting(false);
    setSelectedMemberIds([]);
    
    if (successCount > 0) {
      toast.success(`Removed ${successCount} member${successCount > 1 ? 's' : ''}`);
      fetchLists(); // Refresh
    }
    if (failCount > 0) {
      toast.error(`Failed to remove ${failCount} member${failCount > 1 ? 's' : ''}`);
    }
  };

  // Toggle member selection
  const toggleMemberSelection = (memberId) => {
    setSelectedMemberIds(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  // Select all members in a list (excluding list_host)
  const selectAllMembers = (list) => {
    const selectableMembers = (list.members || [])
      .filter(m => m.role !== 'list_host')
      .map(m => m.member_id || m.rsvp_id || m.id);
    setSelectedMemberIds(selectableMembers);
  };

  // Clear selection
  const clearMemberSelection = () => {
    setSelectedMemberIds([]);
  };

  // Update team member role
  const updateTeamMemberRole = async (memberId, newRole) => {
    const updatedTeam = team.map(m => 
      (m.member_id === memberId || m.user_id === memberId) 
        ? { ...m, role: newRole } 
        : m
    );
    setTeam(updatedTeam);
    // Save immediately
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/events/${eventId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ team: updatedTeam })
      });
      toast.success("Role updated");
      mutateEvent();
    } catch (err) {
      toast.error("Failed to update role");
    }
  };

  // Add partner
  const addPartner = async () => {
    if (!newPartner.name) {
      toast.error("Partner name is required");
      return;
    }
    const newPartnerObj = {
      org_id: `org_${Date.now()}`,
      name: newPartner.name,
      website: newPartner.website,
      logo: newPartner.logo,
      clicks: 0
    };
    const updatedPartners = [...partners, newPartnerObj];
    setPartners(updatedPartners);
    setShowAddPartnerModal(false);
    setNewPartner({ name: "", website: "", logo: "" });
    
    // Save to backend
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/events/${eventId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ partners: updatedPartners })
      });
      toast.success("Partner added!");
      mutateEvent();
    } catch (err) {
      toast.error("Failed to save partner");
    }
  };

  // Remove partner
  const removePartner = async (orgId) => {
    const updatedPartners = partners.filter(p => p.org_id !== orgId);
    setPartners(updatedPartners);
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/events/${eventId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ partners: updatedPartners })
      });
      toast.success("Partner removed");
      mutateEvent();
    } catch (err) {
      toast.error("Failed to remove partner");
    }
  };

  // Wrapper with confirmation
  const confirmRemovePartner = (partner) => {
    setDeleteTarget({
      type: 'partner',
      name: partner.name || 'Partner',
      onConfirm: () => removePartner(partner.org_id)
    });
    setShowDeleteConfirmModal(true);
  };

  // Add sponsor
  const addSponsor = async () => {
    if (!newSponsor.name) {
      toast.error("Sponsor name is required");
      return;
    }
    const newSponsorObj = {
      org_id: `org_${Date.now()}`,
      name: newSponsor.name,
      website: newSponsor.website,
      logo: newSponsor.logo,
      tier: newSponsor.tier,
      clicks: 0
    };
    const updatedSponsors = [...sponsors, newSponsorObj];
    setSponsors(updatedSponsors);
    setShowAddSponsorModal(false);
    setNewSponsor({ name: "", website: "", logo: "", tier: "standard" });
    
    // Save to backend
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/events/${eventId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ sponsors: updatedSponsors })
      });
      toast.success("Sponsor added!");
      mutateEvent();
    } catch (err) {
      toast.error("Failed to save sponsor");
    }
  };

  // Remove sponsor
  const removeSponsor = async (orgId) => {
    const updatedSponsors = sponsors.filter(s => s.org_id !== orgId);
    setSponsors(updatedSponsors);
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/events/${eventId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ sponsors: updatedSponsors })
      });
      toast.success("Sponsor removed");
      mutateEvent();
    } catch (err) {
      toast.error("Failed to remove sponsor");
    }
  };

  // Wrapper with confirmation
  const confirmRemoveSponsor = (sponsor) => {
    setDeleteTarget({
      type: 'sponsor',
      name: sponsor.name || 'Sponsor',
      onConfirm: () => removeSponsor(sponsor.org_id)
    });
    setShowDeleteConfirmModal(true);
  };

  // Filter people based on search and filter
  // Combined people list - RSVPs + inner circle members + imported clients
  // (Acuity sync, host invites). The latter live in provider_clients and
  // are normalised here into the same shape as rsvps so the People tab
  // renders them correctly.
  const allPeople = useMemo(() => {
    if (peopleFilter === "pre_invite") {
      return (innerCircleMembers || []).map(m => ({
        ...m,
        rsvp_id: m.member_id,
        name: m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim(),
        is_pre_invite: true,
        label: "pre_invite"
      }));
    }
    const rsvpRows = rsvps || [];
    const rsvpEmails = new Set(rsvpRows.map(r => (r.email || "").toLowerCase()).filter(Boolean));
    const importedRows = (importedClients || [])
      .filter(c => !c.email || !rsvpEmails.has((c.email || "").toLowerCase()))
      .map(c => ({
        rsvp_id: c.client_id,
        client_id: c.client_id,
        name: c.display_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email,
        first_name: c.first_name,
        last_name: c.last_name,
        email: c.email,
        phone: c.phone,
        imported_from: c.imported_from,
        is_imported: true,
        label: c.imported_from === "acuity" ? "acuity_client" : "imported",
        category: c.category,
      }));
    return [...rsvpRows, ...importedRows];
  }, [rsvps, innerCircleMembers, peopleFilter, importedClients]);
  
  const filteredPeople = allPeople.filter(person => {
    const matchesSearch = !peopleSearch || 
      person.name?.toLowerCase().includes(peopleSearch.toLowerCase()) ||
      person.email?.toLowerCase().includes(peopleSearch.toLowerCase());
    
    const matchesFilter = peopleFilter === "all" ||
      peopleFilter === "pre_invite" ||  // Already filtered in allPeople
      (peopleFilter === "dietary" && (person.dietary_restrictions?.length > 0 || person.shared_defaults?.includes("dietary"))) ||
      (peopleFilter === "accessibility" && (person.accessibility_needs?.length > 0 || person.shared_defaults?.includes("accessibility"))) ||
      (peopleFilter === "checked_in" && person.status === "checked_in") ||
      (peopleFilter === "pending" && person.status === "pending") ||
      (peopleFilter === "confirmed" && person.status === "confirmed") ||
      (peopleFilter === "completed" && (person.status === "completed" || person.status === "checked_in")) ||
      person.label === peopleFilter;
    
    return matchesSearch && matchesFilter;
  });

  // Get label badge
  // Get label badge(s) - supports both single label and labels array
  const getLabelBadge = (labelOrPerson) => {
    let labels;
    if (typeof labelOrPerson === 'string') {
      labels = [labelOrPerson];
    } else if (labelOrPerson?.labels?.length > 0) {
      labels = labelOrPerson.labels;
    } else {
      // Default label depends on dashboard type
      const defaultLabel = dashboardType === DASHBOARD_TYPES.PROVIDER ? "client" : "attendee";
      labels = [labelOrPerson?.label || defaultLabel];
    }
    
    return (
      <div className="flex flex-wrap gap-1">
        {labels.map((label, idx) => {
          const defaultLabel = dashboardType === DASHBOARD_TYPES.PROVIDER ? "client" : "attendee";
          const labelConfig = TEAM_LABELS.find(l => l.value === label) || TEAM_LABELS.find(l => l.value === defaultLabel);
          return (
            <Badge key={idx} className={`${labelConfig?.color || 'bg-gray-100 text-gray-700'} font-medium`}>
              {labelConfig?.label || (dashboardType === DASHBOARD_TYPES.PROVIDER ? 'Client' : 'Attendee')}
            </Badge>
          );
        })}
      </div>
    );
  };

  // Get VIP badge if person has vip in tags
  const getVipBadge = (person) => {
    if (person.tags?.includes('vip') || person.label === 'vip' || person.is_vip) {
      return <Badge className="bg-yellow-100 text-yellow-700 font-medium ml-1">VIP</Badge>;
    }
    return null;
  };

  // Status options adapt based on dashboard type
  const RSVP_STATUSES = dashboardType === DASHBOARD_TYPES.PROVIDER
    ? [
        { value: "confirmed", label: "Confirmed", color: "bg-emerald-500 text-white" },
        { value: "pending", label: "Pending", color: "bg-yellow-100 text-yellow-700" },
        { value: "completed", label: "Completed", color: "bg-blue-100 text-blue-700" },
        { value: "cant_make_it", label: "Cancelled", color: "bg-red-100 text-red-700" }
      ]
    : [
        { value: "confirmed", label: "Definitely Going", color: "bg-emerald-500 text-white" },
        { value: "pending", label: "Pending", color: "bg-yellow-100 text-yellow-700" },
        { value: "checked_in", label: "Checked In", color: "bg-blue-100 text-blue-700" },
        { value: "cant_make_it", label: "Can't Make It", color: "bg-red-100 text-red-700" }
      ];

  // Pick the right label set based on dashboard type
  const TEAM_LABELS = dashboardType === DASHBOARD_TYPES.PROVIDER ? PROVIDER_LABELS : EVENT_LABELS;

  // Update person status
  const updatePersonStatus = async (person, newStatus) => {
    setUpdatingPerson(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/rsvps/${person.rsvp_id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (!res.ok) throw new Error("Failed to update status");
      toast.success(`Status updated to ${RSVP_STATUSES.find(s => s.value === newStatus)?.label || newStatus}`);
      mutateRsvps();
      setShowPreferenceCard(false);
    } catch (err) {
      toast.error("Failed to update status");
    } finally {
      setUpdatingPerson(false);
    }
  };

  // Update person label - supports multiple labels via toggle
  const updatePersonLabel = async (person, newLabel, toggle = false) => {
    setUpdatingPerson(true);
    try {
      const token = localStorage.getItem("session_token");
      
      // Support multiple labels
      let currentLabels = person.labels || (person.label ? [person.label] : ["attendee"]);
      let newLabels;
      
      if (toggle) {
        // Toggle mode: add/remove label from array
        if (currentLabels.includes(newLabel)) {
          newLabels = currentLabels.filter(l => l !== newLabel);
          if (newLabels.length === 0) newLabels = ["attendee"]; // Always have at least one
        } else {
          // Remove attendee if adding a more specific label
          newLabels = [...currentLabels.filter(l => l !== "attendee"), newLabel];
        }
      } else {
        // Replace mode: set single label
        newLabels = [newLabel];
      }
      
      // Include is_speaker and is_host flags when respective labels are present
      const updateData = { 
        label: newLabels[0], // Primary label for backward compatibility
        labels: newLabels // Full labels array
      };
      
      // Handle speaker flag
      if (newLabels.includes("speaker")) {
        updateData.is_speaker = true;
        updateData.tags = [...new Set([...(person.tags || []), "speaker"])];
      } else {
        updateData.is_speaker = false;
      }
      
      // Handle host flag
      if (newLabels.includes("host")) {
        updateData.is_host = true;
        updateData.tags = [...new Set([...(updateData.tags || person.tags || []), "host"])];
      } else {
        updateData.is_host = false;
      }
      
      const res = await fetch(`${API}/events/${eventId}/rsvps/${person.rsvp_id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to update label");
      }
      
      const labelNames = newLabels.map(l => TEAM_LABELS.find(t => t.value === l)?.label).join(" + ");
      toast.success(`Labels updated to ${labelNames}`);
      mutateRsvps();
    } catch (err) {
      console.error("Label update error:", err);
      toast.error(err.message || "Failed to update label");
    } finally {
      setUpdatingPerson(false);
    }
  };

  // Manual Check-In handler
  const handleManualCheckIn = async (person) => {
    setUpdatingPerson(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/nfc-checkin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ rsvp_id: person.rsvp_id })
      });
      if (!res.ok) throw new Error("Failed to check in guest");
      toast.success(`${person.name || 'Guest'} checked in!`);
      mutateRsvps();
    } catch (err) {
      toast.error("Failed to check in guest");
    } finally {
      setUpdatingPerson(false);
    }
  };

  // Undo Check-In handler
  const handleUndoCheckIn = async (person) => {
    setUpdatingPerson(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/rsvps/${person.rsvp_id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ checked_in: false, checked_in_at: null })
      });
      if (!res.ok) throw new Error("Failed to undo check-in");
      toast.success(`Check-in undone for ${person.name || 'Guest'}`);
      mutateRsvps();
    } catch (err) {
      toast.error("Failed to undo check-in");
    } finally {
      setUpdatingPerson(false);
    }
  };

  // Resend confirmation email
  const handleResendConfirmation = async (person) => {
    if (!person.rsvp_id || !person.email) {
      toast.error("Cannot resend: no email address");
      return;
    }
    setUpdatingPerson(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/rsvps/${person.rsvp_id}/resend-confirmation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Confirmation email sent to ${person.email}`);
      } else {
        toast.error(data.detail || "Failed to send email");
      }
    } catch (err) {
      toast.error("Failed to send confirmation email");
    } finally {
      setUpdatingPerson(false);
    }
  };

  // Open Internal Notes modal
  const openInternalNotes = (person) => {
    setSelectedPersonForNotes(person);
    setNewInternalNote("");
    setShowInternalNotesModal(true);
  };

  // Add Internal Note
  const addInternalNote = async () => {
    if (!selectedPersonForNotes?.rsvp_id || !newInternalNote.trim()) return;
    setAddingNote(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/rsvps/${selectedPersonForNotes.rsvp_id}/internal-notes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          note: newInternalNote.trim(),
          type: "staff_note"
        })
      });
      if (!res.ok) throw new Error("Failed to add note");
      toast.success("Note added!");
      setNewInternalNote("");
      mutateRsvps();
      // Update local state for the modal
      const updatedRsvps = await mutateRsvps();
      if (updatedRsvps) {
        const updatedPerson = updatedRsvps.find(r => r.rsvp_id === selectedPersonForNotes.rsvp_id);
        if (updatedPerson) {
          setSelectedPersonForNotes(updatedPerson);
        }
      }
    } catch (err) {
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  // ---- Host Inspo functions ----
  const fetchHostInspo = useCallback(async (clientUserId) => {
    if (!clientUserId) return;
    setHostInspoLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/host/clients/${clientUserId}/inspo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const items = await res.json();
        setHostInspoItems(items);
      }
    } catch { /* silent */ }
    setHostInspoLoading(false);
  }, []);

  const saveHostInspo = async (imageUrl) => {
    if (!selectedPerson) return;
    const clientUserId = selectedPerson.user_id || selectedPerson.rsvp_id;
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/host/clients/${clientUserId}/inspo`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          image_url: imageUrl,
          category: event?.pilot_category || "general",
          event_id: eventId,
          folder: event?.pilot_category || "General"
        })
      });
      if (res.ok) {
        toast.success("Inspo added for client");
        fetchHostInspo(clientUserId);
      } else {
        toast.error("Failed to add inspo");
      }
    } catch {
      toast.error("Failed to add inspo");
    }
  };

  const deleteHostInspo = async (inspoId) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/host/inspo/${inspoId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setHostInspoItems(prev => prev.filter(i => i.inspo_id !== inspoId));
        toast.success("Inspo removed");
      }
    } catch {
      toast.error("Failed to delete inspo");
    }
  };

  // Fetch host inspo when selectedPerson changes
  useEffect(() => {
    if (selectedPerson && showExpandedDetails) {
      const clientUserId = selectedPerson.user_id || selectedPerson.rsvp_id;
      fetchHostInspo(clientUserId);
    }
  }, [selectedPerson, showExpandedDetails, fetchHostInspo]);

  // Delete person (RSVP)
  const deletePerson = async () => {
    if (!personToDelete) return;
    setUpdatingPerson(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/rsvps/${personToDelete.rsvp_id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error("Failed to remove guest");
      toast.success("Guest removed");
      mutateRsvps();
      setShowDeleteConfirm(false);
      setPersonToDelete(null);
    } catch (err) {
      toast.error("Failed to remove guest");
    } finally {
      setUpdatingPerson(false);
    }
  };

  // Delete entire event (host only)
  const deleteEvent = async () => {
    if (!event?.event_id) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${event.event_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to delete event");
      }
      
      toast.success("Event deleted successfully");
      // Redirect to home or dashboard after deletion
      window.location.href = "/me";
    } catch (err) {
      toast.error(err.message || "Failed to delete event");
    }
  };

  // Open edit person modal with ALL user-submitted data
  const openEditPerson = (person) => {
    // Parse first/last name from full name if not provided
    let firstName = person.first_name || "";
    let lastName = person.last_name || "";
    if (!firstName && !lastName && person.name) {
      const nameParts = person.name.split(" ");
      firstName = nameParts[0] || "";
      lastName = nameParts.slice(1).join(" ") || "";
    }
    
    setEditPersonData({
      rsvp_id: person.rsvp_id,
      // Basic info - separate first/last name
      first_name: firstName,
      last_name: lastName,
      name: person.name || "",
      email: person.email || "",
      phone: person.phone || "",
      city: person.city || "",
      company: person.company || "",
      title: person.title || "",
      // Social handles
      instagram: person.instagram || "",
      linkedin: person.linkedin || "",
      tiktok: person.tiktok || "",
      // Status & labels
      host_note: person.host_note || "",
      label: person.label || "attendee",
      status: person.status || "confirmed",
      // User submitted preferences (READ-ONLY for host)
      dietary_restrictions: person.dietary_restrictions || [],
      dietary_notes: person.dietary_notes || "",
      accessibility_needs: person.accessibility_needs || [],
      accessibility_notes: person.accessibility_notes || "",
      invisible_disabilities: person.invisible_disabilities || [],
      anything_else_to_share: person.anything_else_to_share || "",
      interests: person.interests || [],
      vibe: person.vibe || "",
      custom_answers: person.custom_answers || {},
      // Sharing controls - what shows on preference card
      shared_defaults: person.shared_defaults || ["dietary", "accessibility"],
      show_dietary_on_card: person.show_dietary_on_card !== false,
      show_accessibility_on_card: person.show_accessibility_on_card !== false,
      show_interests_on_card: person.show_interests_on_card !== false,
      show_contact_on_card: person.show_contact_on_card !== false
    });
    setShowEditPersonModal(true);
  };

  // Save edited person
  const saveEditedPerson = async () => {
    if (!editPersonData?.rsvp_id) return;
    setUpdatingPerson(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/events/${eventId}/rsvps/${editPersonData.rsvp_id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editPersonData)
      });
      if (!res.ok) throw new Error("Failed to update guest");
      toast.success("Guest updated successfully");
      mutateRsvps();
      // Trigger global sync so UserProfilePage also updates
      triggerGlobalSync('all', eventId);
      setShowEditPersonModal(false);
      setEditPersonData(null);
    } catch (err) {
      toast.error("Failed to update guest");
    } finally {
      setUpdatingPerson(false);
    }
  };

  // Get status badge with color
  const getStatusBadge = (status) => {
    // Map checked_in to completed for providers
    const mappedStatus = (dashboardType === DASHBOARD_TYPES.PROVIDER && status === "checked_in") ? "completed" : status;
    const statusConfig = RSVP_STATUSES.find(s => s.value === mappedStatus) || RSVP_STATUSES[0];
    return (
      <Badge className={`${statusConfig.color} font-medium`}>
        {statusConfig.label}
      </Badge>
    );
  };

  if (loading || eventLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-600" />
          <p className="mt-2 text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const eventSlug = event?.slug || eventId;
  const baseUrl = window.location.origin;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="host-dashboard-v2">
      {/* Header with event cover image */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <div className="flex items-center justify-between py-2 sm:py-3">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <Button variant="ghost" size="sm" onClick={() => navigate("/me")} data-testid="back-btn" className="shrink-0 p-1 sm:p-2">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              
              {/* Event cover/logo - syncs to the SAME fallback chain used
                  on /me Hosting tile so both surfaces always match.
                  org/event logo -> event cover -> DefaultOrgIcon initial. */}
              {(() => {
                const orgLogo = event?.organization_logo_url || event?.org_logo_url;
                const logoSrc = eventSettings.logo_image || eventSettings.cover_image || orgLogo || event?.cover_image;
                const orgName = event?.organization_name || eventSettings.title || event?.name || "Host";
                if (logoSrc) {
                  return (
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg overflow-hidden shrink-0 bg-stone-100 flex items-center justify-center">
                      <img src={getImageUrl(logoSrc)} alt="" className="w-full h-full object-cover" />
                    </div>
                  );
                }
                return (
                  <div className="w-8 h-8 sm:w-10 sm:h-10 shrink-0">
                    <DefaultOrgIcon orgName={orgName} size={40} />
                  </div>
                );
              })()}
              
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h1 className="font-bold text-sm sm:text-lg truncate">
                    {eventSettings.title
                      || event?.event_name
                      || event?.name
                      || "Host Dashboard"}
                  </h1>
                  {myDashboards.length > 1 && (
                    <div className="relative group">
                      <button className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors" data-testid="dashboard-switcher-btn">
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-200 py-1 z-50 min-w-[200px] hidden group-hover:block">
                        <p className="px-3 py-1.5 text-[10px] text-gray-400 uppercase tracking-wide">Your Dashboards</p>
                        {myDashboards.map(d => (
                          <button
                            key={d.event_id}
                            onClick={() => navigate(`/host/v2/${d.event_id}`)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-gray-50 transition-colors ${d.event_id === eventId ? "bg-purple-50 text-purple-700 font-medium" : "text-gray-700"}`}
                            data-testid={`switch-dashboard-${d.pilot_category || d.event_id}`}
                          >
                            <span className="capitalize">{d.pilot_category || "Dashboard"}</span>
                            <span className="text-gray-400 truncate">{d.name || d.title || ""}</span>
                            {d.event_id === eventId && <span className="ml-auto text-purple-500 text-[10px]">Current</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-[10px] sm:text-xs text-gray-500 truncate">
                  {dashboardType === DASHBOARD_TYPES.PROVIDER ? (
                    <span className="capitalize">
                      {(event?.pilot_category || event?.event_type || "").replace("_", " ")} host
                    </span>
                  ) : (
                    <>
                      {eventSettings.date_tba ? "Date TBA" : eventSettings.date && (() => {
                        const [year, month, day] = eventSettings.date.split('T')[0].split('-');
                        const localDate = new Date(year, month - 1, day);
                        return localDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
                      })()}
                      {!eventSettings.date_tba && eventSettings.time && ` • ${formatTime12Hour(eventSettings.time)}`}
                      {!eventSettings.date_tba && eventSettings.end_time && <span className="hidden sm:inline"> - {formatTime12Hour(eventSettings.end_time)}</span>}
                      {eventSettings.timezone_abbr && <span className="hidden sm:inline"> {eventSettings.timezone_abbr}</span>}
                    </>
                  )}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-1 sm:gap-3 shrink-0">
              {/* Iter 9.37 - Trial countdown badge (hidden for host_management) */}
              {event?.subtype !== "host_management" && (
                <TrialBadge tierInfo={tierInfo} onClick={() => setShowUpgradeModal(true)} />
              )}
              <Button variant="outline" size="sm" onClick={() => mutateEvent()} data-testid="refresh-btn" className="p-1 sm:p-2">
                <RefreshCw className="w-4 h-4" />
              </Button>
              {/* Iter 9.47.r - For host_management dashboards, hide Share / Invite-Client.
                  The only visible primary action is the Pipeline tab's Invite (admin invite).
                  Iter 9.49+ - Invite button moved into Clients tab only; nav keeps only Share + Copy.
              */}
              {event?.subtype !== "host_management" && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      // Iter 9.49+ Phase 1 - Share = native share sheet (system up-arrow)
                      const slugOrId = event?.slug || eventSlug || eventId;
                      const url = `${window.location.origin}/e/${slugOrId}`;
                      const brand = event?.artist?.artist_name || event?.host_brand_name || event?.name || "RSVP";
                      const shareData = { title: brand, text: `RSVP for ${brand}`, url };
                      if (navigator.share) {
                        try { await navigator.share(shareData); return; } catch { /* fall through */ }
                      }
                      try {
                        await navigator.clipboard.writeText(url);
                        toast.success("RSVP link copied");
                      } catch {
                        window.prompt("Copy this RSVP link:", url);
                      }
                    }}
                    data-testid="host-share-btn"
                    className="hidden sm:flex"
                    title="Share booking link"
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Share
                  </Button>
                  {/* Iter 9.49+ Phase 1 - dedicated copy + open-in-new-tab button (overlapping squares icon) */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const slugOrId = event?.slug || eventSlug || eventId;
                      const url = `${window.location.origin}/e/${slugOrId}`;
                      try {
                        await navigator.clipboard.writeText(url);
                        toast.success("Booking link copied");
                      } catch {}
                      window.open(url, "_blank", "noopener,noreferrer");
                    }}
                    data-testid="host-copy-btn"
                    className="hidden sm:flex"
                    title="Copy booking URL + open in new tab"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  {/* Mobile compact share */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const slugOrId = event?.slug || eventSlug || eventId;
                      const url = `${window.location.origin}/e/${slugOrId}`;
                      const brand = event?.artist?.artist_name || event?.host_brand_name || event?.name || "RSVP";
                      if (navigator.share) {
                        try { await navigator.share({ title: brand, url }); return; } catch { /* fall through */ }
                      }
                      try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch {}
                    }}
                    data-testid="host-share-btn-mobile"
                    className="flex sm:hidden p-1"
                  >
                    <Upload className="w-4 h-4" />
                  </Button>
                </>
              )}
              
              {/* Notification Bell */}
              <div className="relative">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="relative" 
                  data-testid="notifications-btn"
                  onClick={() => setShowNotificationsPanel(!showNotificationsPanel)}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {notifications.filter(n => !n.read).length > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {notifications.filter(n => !n.read).length}
                    </span>
                  )}
                </Button>
                
                {/* Notifications Panel */}
                {showNotificationsPanel && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden">
                    <div className="p-3 border-b flex items-center justify-between bg-gray-50">
                      <h3 className="font-medium text-sm">Notifications</h3>
                      {notifications.length > 0 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-xs text-gray-500 h-6"
                          onClick={() => {
                            setDeleteTarget({
                              type: 'notifications',
                              name: 'all notifications',
                              onConfirm: clearAllNotifications
                            });
                            setShowDeleteConfirmModal(true);
                            setShowNotificationsPanel(false);
                          }}
                        >
                          Clear All
                        </Button>
                      )}
                    </div>
                    <div className="overflow-y-auto max-h-72">
                      {notificationsLoading ? (
                        <div className="p-4 text-center text-gray-500 text-sm">Loading...</div>
                      ) : notifications.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 text-sm">No notifications</div>
                      ) : (
                        notifications.slice(0, 20).map((notif) => (
                          <div 
                            key={notif.notification_id}
                            className={`p-3 border-b last:border-0 hover:bg-gray-50 cursor-pointer ${!notif.read ? 'bg-purple-50' : ''}`}
                            onClick={() => markNotificationRead(notif.notification_id)}
                          >
                            <div className="flex items-start gap-2">
                              <div className={`w-2 h-2 rounded-full mt-1.5 ${!notif.read ? 'bg-purple-500' : 'bg-gray-300'}`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{notif.title}</p>
                                <p className="text-xs text-gray-600 truncate">{notif.message}</p>
                                <p className="text-xs text-gray-400 mt-1">
                                  {notif.created_at ? new Date(notif.created_at).toLocaleString() : ''}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* User Profile Photo → /me */}
              <Link to="/me" className="flex items-center gap-2 hover:opacity-80" data-testid="user-profile-link">
                {(user?.profile_photo_url || user?.profile_image || user?.picture) ? (
                  <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-gray-200">
                    <img 
                      src={user.profile_photo_url || user.profile_image || user.picture} 
                      alt="" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <DefaultAvatar
                    firstName={(user?.first_name || user?.name || "?").split(" ")[0]}
                    lastName={user?.last_name || (user?.name || "").split(" ").slice(1).join(" ")}
                    size={32}
                    className="border-2 border-gray-200"
                  />
                )}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Iter 9.38: pass legacy/demo flags so admin-set tier doesn't gate features for grandfathered users */}
      <UpgradeProvider
        currentPlan={(user?.plan === "legacy_pilot_free" || user?.stripe_bypass === true || user?.is_pilot === true) ? "legacy_pilot_free" : (event?.pilot_category ? "team" : (event?.subscription_tier || "trial"))}
        isLegacyPilot={!!(user?.legacy_pilot_free || user?.plan === "legacy_pilot_free" || user?.stripe_bypass === true || tierInfo?.is_legacy_pilot)}
        isDemo={!!(user?.is_demo || tierInfo?.is_demo)}
      >
      <main className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-6">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          {/* Pill-style tab bar matching /me and admin */}
          <div className="flex items-center justify-center mb-6 bg-gray-100 p-1 rounded-2xl" data-testid="main-tabs">
            {/* Iter 9.47.r - When subtype === "host_management" (Naomi's Category Host Management
                dashboards), tabs are LOCKED to: Pipeline / Pilots / Subscribers / Waitlist / Insights / Settings.
                Phase 8 - regular host dashboard tabs LOCKED to: Dashboard / Clients / Insights / Settings.
                'Services' and 'Intake' are accessed inside Settings → Operations / Booking. */}
            {(event?.subtype === "host_management" ? [
              { value: "pipeline", icon: BarChart3, label: "Pipeline", testId: "tab-pipeline", show: true },
              { value: "pilots", icon: Users, label: "Pilots", testId: "tab-pilots", show: true },
              { value: "subscribers", icon: Users, label: "Subscribers", testId: "tab-subscribers", show: true },
              { value: "waitlist", icon: Clock, label: "Waitlist", testId: "tab-waitlist", show: true },
              { value: "analytics", icon: BarChart3, label: "Insights", testId: "tab-insights", show: true },
              { value: "settings", icon: Settings, label: "Settings", testId: "tab-settings", show: true },
            ] : [
              { value: "overview", icon: Home, label: airtableLabelFor[tabIdAlias.overview] || slotText(hd, "tab_dashboard_label", "Dashboard"), testId: "tab-overview", show: true },
              { value: "people", icon: Users, label: airtableLabelFor[tabIdAlias.people] || (dashboardType === DASHBOARD_TYPES.PROVIDER ? slotText(hd, "tab_clients_label", "Clients") : slotText(hd, "tab_rsvps_label", "RSVPs")), testId: "tab-rsvps", show: true },
              { value: "services", icon: Sparkles, label: airtableLabelFor[tabIdAlias.services] || slotText(hd, "tab_services_label", "Services"), testId: "tab-services", show: dashboardType !== DASHBOARD_TYPES.PROVIDER },
              { value: "questions", icon: MessageSquare, label: slotText(hd, "tab_intake_label", "Intake"), testId: "tab-questions", show: dashboardType !== DASHBOARD_TYPES.PROVIDER },
              { value: "analytics", icon: BarChart3, label: airtableLabelFor[tabIdAlias.analytics] || slotText(hd, "tab_insights_label", "Insights"), testId: "tab-insights", show: true },
              { value: "settings", icon: Settings, label: airtableLabelFor[tabIdAlias.settings] || slotText(hd, "tab_settings_label", "Settings"), testId: "tab-settings", show: true },
            ]).filter(t => t.show).map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.value}
                  onClick={() => handleTabChange(tab.value)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl transition-all ${
                    activeTab === tab.value
                      ? "bg-white shadow-sm font-medium"
                      : "text-gray-600 hover:text-black"
                  }`}
                  data-testid={tab.testId}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* ============ HOST MANAGEMENT TABS (Iter 9.47.r) ============ */}
          {event?.subtype === "host_management" && (
            <>
              <TabsContent value="pipeline" data-testid="pipeline-content">
                <HostManagementPipelineTab event={event} />
              </TabsContent>
              <TabsContent value="pilots" data-testid="pilots-content">
                <HostManagementListTab event={event} bucket="pilots" emptyHint="No pilots yet. Pilot invites you send will appear here once they accept." />
              </TabsContent>
              <TabsContent value="subscribers" data-testid="subscribers-content">
                <HostManagementListTab event={event} bucket="subscribers" emptyHint="No active subscribers yet for this category." />
              </TabsContent>
              <TabsContent value="waitlist" data-testid="waitlist-content">
                <HostManagementListTab event={event} bucket="waitlist" emptyHint="No one has joined the waitlist for this category yet." />
              </TabsContent>
            </>
          )}

          {/* ============ OVERVIEW TAB ============ */}
          <TabsContent value="overview" data-testid="overview-content">
            {/* Event Status Banners */}
            {(event?.status === "draft" || event?.dashboard_status === "draft") && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between" data-testid="draft-event-banner">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-amber-800">{slotText(hd, "banner_draft_title", "This experience is in draft mode")}</p>
                    <p className="text-sm text-amber-600">{slotText(hd, "banner_draft_body", "Only you can see it. Publish to make it live.")}</p>
                  </div>
                </div>
                <Button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("session_token");
                      const res = await fetch(`${API}/events/${eventId}/settings`, {
                        method: "PATCH",
                        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
                        body: JSON.stringify({ status: "active" })
                      });
                      if (res.ok) { mutateEvent(); toast.success("Event published! It's now live."); }
                      else { toast.error("Failed to publish event"); }
                    } catch (err) { toast.error("Failed to publish event"); }
                  }}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="publish-event-btn"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  {slotText(hd, "banner_draft_cta", "Publish Experience")}
                </Button>
              </div>
            )}

            {event?.status === "cancelled" && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between" data-testid="cancelled-event-banner">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-red-800">{slotText(hd, "banner_cancelled_title", "This experience has been cancelled")}</p>
                    <p className="text-sm text-red-600">{slotText(hd, "banner_cancelled_body", "Attendees have been notified. You can reactivate it as a draft.")}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("session_token");
                      const res = await fetch(`${API}/events/${eventId}/settings`, {
                        method: "PATCH",
                        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
                        body: JSON.stringify({ status: "draft" })
                      });
                      if (res.ok) { mutateEvent(); toast.success("Event moved back to draft"); }
                      else { toast.error("Failed to update event"); }
                    } catch (err) { toast.error("Failed to update event"); }
                  }}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="reactivate-event-btn"
                >
                  {slotText(hd, "banner_cancelled_cta", "Move to Draft")}
                </Button>
              </div>
            )}

            {event?.status === "rescheduled" && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between" data-testid="rescheduled-event-banner">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-blue-800">This event has been rescheduled</p>
                    <p className="text-sm text-blue-600">
                      New date: {event?.rescheduled_date ? new Date(event.rescheduled_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : event?.date ? new Date(event.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : 'TBA'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("session_token");
                      const res = await fetch(`${API}/events/${eventId}/settings`, {
                        method: "PATCH",
                        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
                        body: JSON.stringify({ status: "active" })
                      });
                      if (res.ok) { mutateEvent(); toast.success("Event is now live again"); }
                      else { toast.error("Failed to publish event"); }
                    } catch (err) { toast.error("Failed to publish event"); }
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="republish-event-btn"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  Publish Live
                </Button>
              </div>
            )}


            {/* Stats Cards - Provider gets Kanban/Calendar toggle, Event gets classic stats */}
            {dashboardType === DASHBOARD_TYPES.PROVIDER ? (
              <div className="mb-4 sm:mb-6 space-y-3">
                {/* View Toggle */}
                <div className="flex items-center gap-2 justify-end">
                  <div className="flex items-center border rounded-lg overflow-hidden text-xs">
                    <button
                      onClick={() => setProviderView?.("kanban")}
                      className={`px-3 py-1.5 flex items-center gap-1 ${(!providerView || providerView === "kanban") ? "bg-purple-100 text-purple-700 font-medium" : "text-gray-500 hover:bg-gray-50"}`}
                      data-testid="toggle-kanban-view"
                    >
                      <Grid3X3 className="w-3 h-3" />
                      Board
                    </button>
                    <button
                      onClick={() => setProviderView?.("calendar")}
                      className={`px-3 py-1.5 flex items-center gap-1 ${providerView === "calendar" ? "bg-purple-100 text-purple-700 font-medium" : "text-gray-500 hover:bg-gray-50"}`}
                      data-testid="toggle-calendar-view"
                    >
                      <CalendarDays className="w-3 h-3" />
                      Calendar
                    </button>
                  </div>
                </div>
                {(!providerView || providerView === "kanban") ? (
                  <ClientKanban rsvps={rsvps} getAuthHeaders={getAuthHeaders} onRefresh={mutateRsvps} />
                ) : (
                  <AppointmentCalendar rsvps={rsvps} onViewDetail={(r) => {}} />
                )}
              </div>
            ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-4 mb-4 sm:mb-6">
              {/* Real-time connection indicator */}
              {analyticsWsConnected && (
                <div className="col-span-2 md:col-span-5 flex items-center justify-end gap-2 mb-1">
                  <span className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    Live updates active
                  </span>
                </div>
              )}
              
              {/* Total Attending - Primary metric with +1s included */}
              <Card data-testid="stat-total-attending" className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                <CardContent className="p-3 sm:pt-6 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-purple-600 truncate font-medium">Total Attending</p>
                      <p className="text-2xl sm:text-3xl font-bold text-purple-700">{liveStats?.total_attending || stats.total_attending}</p>
                      {stats.total_attending > stats.total_rsvps && (
                        <p className="text-[10px] text-purple-500 mt-0.5">incl. +{stats.total_attending - stats.total_rsvps} guests</p>
                      )}
                    </div>
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                      <Users className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card data-testid="stat-total-rsvps">
                <CardContent className="p-3 sm:pt-6 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-gray-500 truncate">{dashboardType === DASHBOARD_TYPES.PROVIDER ? "Clients" : "RSVPs"}</p>
                      <p className="text-2xl sm:text-3xl font-bold">{liveStats?.total_rsvps || stats.total_rsvps}</p>
                    </div>
                    <Users className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500 shrink-0" />
                  </div>
                </CardContent>
              </Card>
              
              <Card data-testid="stat-checked-in" className={`${dashboardDisplaySettings.nfc_kiosk_enabled ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`} onClick={() => dashboardDisplaySettings.nfc_kiosk_enabled && window.open(`/check-in/${eventId}`, '_blank')}>
                <CardContent className="p-3 sm:pt-6 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-gray-500 truncate">{dashboardType === DASHBOARD_TYPES.PROVIDER ? "Completed" : "Checked In"}</p>
                      <p className="text-2xl sm:text-3xl font-bold">{liveStats?.checked_in || stats.checked_in}</p>
                      {dashboardDisplaySettings.nfc_kiosk_enabled && (
                        <p className="text-xs text-purple-600 mt-1">Open NFC Kiosk</p>
                      )}
                    </div>
                    <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8 text-green-500 shrink-0" />
                  </div>
                </CardContent>
              </Card>
              
              {/* Dietary Needs - ONLY show for Event dashboards AND when explicitly enabled */}
              {(showQuickActions && dashboardDisplaySettings.stat_cards?.dietary_needs && dashboardDisplaySettings.collect?.dietary && event?.event_type !== 'instructor_pilot' && event?.pilot_category !== 'instructor') && (
              <Card data-testid="stat-dietary">
                <CardContent className="p-3 sm:pt-6 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-gray-500 truncate">Dietary Needs</p>
                      <p className="text-2xl sm:text-3xl font-bold">{stats.dietary_needs}</p>
                    </div>
                    <Utensils className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500 shrink-0" />
                  </div>
                </CardContent>
              </Card>
              )}
              
              {/* Accessibility - ONLY show for Event dashboards when explicitly enabled */}
              {(showQuickActions && dashboardDisplaySettings.stat_cards?.accessibility && dashboardDisplaySettings.collect?.accessibility && event?.event_type !== 'instructor_pilot' && event?.pilot_category !== 'instructor') && (
              <Card data-testid="stat-accessibility">
                <CardContent className="p-3 sm:pt-6 sm:p-6">
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-gray-500 truncate">Accessibility</p>
                      <p className="text-2xl sm:text-3xl font-bold">{stats.accessibility}</p>
                    </div>
                    <Accessibility className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500 shrink-0" />
                  </div>
                </CardContent>
              </Card>
              )}
            </div>
            )}

            {/* Tools Section - "Tools that power your business" for providers, "Tech Stack" for events */}
            <Card className="mb-4 sm:mb-6" data-testid="tech-stack-section">
              <CardHeader className="p-3 sm:p-6 pb-2 sm:pb-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                      <Zap className="w-4 h-4 sm:w-5 sm:h-5" />
                      {dashboardType === DASHBOARD_TYPES.PROVIDER ? "Tools that power your business" : "Tech Stack"}
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm">
                      {dashboardType === DASHBOARD_TYPES.PROVIDER ? "Your business operating system" : "Your event tools at a glance"}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { setActiveTab("settings"); setSettingsTab("techstack"); }}
                      data-testid="manage-tech-stack-btn"
                      className="text-xs sm:text-sm px-2 sm:px-3"
                    >
                      Manage →
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open('https://phygital.notion.site', '_blank')}
                      className="text-xs sm:text-sm px-2 sm:px-3"
                    >
                      <Info className="w-4 h-4" />
                      <span className="hidden sm:inline ml-1">Help</span>
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                <div className="flex flex-wrap gap-3 sm:gap-4 overflow-visible">
                  {/* Dynamically show tools from techStack.tools (local state from event.tech_stack) - CLICKABLE LINKS */}
                  {(techStack.tools || []).map((tool, idx) => (
                    <div 
                      key={idx} 
                      className={`flex flex-col items-center gap-1 ${tool.link ? 'cursor-pointer hover:opacity-80' : ''}`}
                      onClick={() => {
                        if (tool.link) {
                          window.open(tool.link, '_blank');
                        }
                      }}
                      data-testid={`overview-tool-${idx}`}
                    >
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center bg-gray-100 overflow-visible relative">
                        {tool.logo ? (
                          <img src={tool.logo} alt={tool.name} className="w-full h-full object-cover rounded-lg" />
                        ) : (
                          <span className="text-gray-600 text-base sm:text-lg font-bold">{tool.name?.charAt(0)}</span>
                        )}
                        {tool.link && (
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-purple-500 rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                            <ExternalLink className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-white" />
                          </div>
                        )}
                      </div>
                      <span className="text-[10px] sm:text-xs text-gray-600 mt-1">{tool.name}</span>
                    </div>
                  ))}
                  
                  {/* Acuity - Show if enabled and not already in manual tools */}
                  {techStack.acuity?.enabled && !(techStack.tools || []).find(t => t.name === 'Acuity' || t.name === 'Acuity Scheduling') && (
                    <div 
                      className="flex flex-col items-center gap-1 cursor-pointer hover:opacity-80"
                      onClick={() => {
                        const acuityCfg = techStack.acuity || {};
                        // Priority: host's own scheduling URL → connected admin URL → connect flow
                        const url = acuityCfg.scheduling_url || acuityCfg.admin_url;
                        if (url) {
                          window.open(url, "_blank");
                        } else if (acuityCfg.status === "connected") {
                          window.open("https://app.acuityscheduling.com/", "_blank");
                        } else {
                          // Trigger OAuth connect flow
                          setActiveTab("settings");
                          setSettingsTab("techstack");
                          toast.info("Connect your Acuity account to sync clients and appointments.");
                        }
                      }}
                      data-testid="techstack-acuity-tile"
                    >
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center overflow-visible relative">
                        <img src="https://customer-assets.emergentagent.com/job_b6326545-361d-4d9b-a96f-1a74e4c12cbc/artifacts/caj39uiq_382266660_333002445954278_4611861879541242764_n.jpg" alt="Acuity" className="w-full h-full object-cover rounded-lg" />
                        <div className={`absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center border-2 border-white shadow-sm ${
                          techStack.acuity?.status === "connected" || techStack.acuity?.scheduling_url ? "bg-green-500" : "bg-amber-500"
                        }`}>
                          {techStack.acuity?.status === "connected" || techStack.acuity?.scheduling_url ? (
                            <CheckCircle2 className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-white" />
                          ) : (
                            <ExternalLink className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-white" />
                          )}
                        </div>
                      </div>
                      <span className="text-[10px] sm:text-xs text-gray-600 mt-1">Acuity</span>
                      <span className={`text-[8px] sm:text-[9px] ${
                        techStack.acuity?.status === "connected" || techStack.acuity?.scheduling_url ? "text-green-600" : "text-amber-600"
                      }`}>
                        {techStack.acuity?.status === "connected" || techStack.acuity?.scheduling_url ? "Connected" : "Connect"}
                      </span>
                    </div>
                  )}

                  {/* Tap In - Only show if enabled and not already in tools */}
                  {techStack.tapin?.enabled && !(techStack.tools || []).find(t => t.name === 'Tap In') && (
                    <div 
                      className="flex flex-col items-center gap-1 cursor-pointer hover:opacity-80"
                      onClick={() => {
                        if (intakeSettings.tapin_playlist_url) {
                          window.open(intakeSettings.tapin_playlist_url, '_blank');
                        }
                      }}
                    >
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center overflow-visible relative">
                        <img src={TAP_IN_LOGO} alt="Tap In" className="w-full h-full object-cover rounded-lg" />
                        {intakeSettings.tapin_playlist_url && (
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-purple-500 rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                            <ExternalLink className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-white" />
                          </div>
                        )}
                      </div>
                      <span className="text-[10px] sm:text-xs text-gray-600 mt-1">Tap In</span>
                    </div>
                  )}
                  
                  {/* Empty state */}
                  {(techStack.tools || []).length === 0 && !techStack.tapin?.enabled && !techStack.acuity?.enabled && (
                    <p className="text-xs sm:text-sm text-gray-400">No tools configured. <button className="text-purple-600 hover:underline" onClick={() => { setActiveTab("settings"); setSettingsTab("techstack"); }}>Add tools →</button></p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions - ONLY for Event dashboards (events, instructor), NOT for Provider dashboards (makeup, culinary, etc.) */}
            {showQuickActions && (
            <Card data-testid="quick-actions-section">
              <CardHeader className="p-3 sm:p-6">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                      <ArrowUp className="w-4 h-4 sm:w-5 sm:h-5" />
                      Quick Actions
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm">
                      {(dashboardDisplaySettings.quick_actions?.share_with_instructor || event?.event_type === 'instructor_pilot' || event?.pilot_category === 'instructor')
                        ? "Share summaries with your instructor and collect music preferences"
                        : "Share summaries with your vendors and team"
                      }
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                    <span className="text-[10px] sm:text-xs text-gray-500 hidden sm:inline">Share Links</span>
                    <Switch 
                      checked={eventSettings.share_links_enabled !== false}
                      onCheckedChange={(checked) => {
                        const newSettings = {...eventSettings, share_links_enabled: checked};
                        setEventSettings(newSettings);
                        const token = localStorage.getItem("session_token");
                        fetch(`${API}/events/${eventId}`, {
                          method: "PUT",
                          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ share_links_enabled: checked })
                        }).then((res) => {
                          if (res.ok) {
                            toast.success(checked ? "Share links enabled" : "Share links disabled");
                            setLastSynced(new Date());
                            mutateEvent();
                          } else {
                            setEventSettings(prev => ({...prev, share_links_enabled: !checked}));
                            toast.error("Failed to save");
                          }
                        }).catch(() => {
                          setEventSettings(prev => ({...prev, share_links_enabled: !checked}));
                          toast.error("Failed to save");
                        });
                      }}
                      data-testid="toggle-share-links"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0">
                {eventSettings.share_links_enabled === false ? (
                  <div className="text-center py-6 sm:py-8 text-gray-500">
                    <EyeOff className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-xs sm:text-sm">Share links are disabled</p>
                    <p className="text-[10px] sm:text-xs text-gray-400 mt-1">
                      {(dashboardDisplaySettings.quick_actions?.share_with_instructor || event?.event_type === 'instructor_pilot' || event?.pilot_category === 'instructor')
                        ? "Enable to share attendee info with your instructor"
                        : "Enable to share dietary and accessibility info with vendors"
                      }
                    </p>
                  </div>
                ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {/* INSTRUCTOR PILOT: Share with Instructor */}
                  {(dashboardDisplaySettings.quick_actions?.share_with_instructor || event?.event_type === 'instructor_pilot' || event?.pilot_category === 'instructor') && (
                    <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border flex flex-col">
                      <div className="flex items-center gap-2 sm:gap-3 mb-2">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-xs sm:text-sm truncate">Share with Instructor</p>
                          <p className="text-[10px] sm:text-xs text-gray-500 truncate">Attendee summary</p>
                        </div>
                      </div>
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-2 sm:mb-3 flex-grow">Share attendee list and submitted questions</p>
                      <div className="flex gap-2">
                        <Button 
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs"
                          onClick={() => {
                            const url = `${baseUrl}/events/${eventId}/share/instructor`;
                            navigator.clipboard.writeText(url);
                            toast.success("Instructor link copied!");
                          }}
                          data-testid="copy-instructor-btn"
                        >
                          <Copy className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                          <span className="truncate">Copy</span>
                        </Button>
                        <Button 
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs"
                          onClick={async () => {
                            const url = `${baseUrl}/events/${eventId}/share/instructor`;
                            if (navigator.share) {
                              try {
                                await navigator.share({ title: 'Attendee Summary for Instructor', url });
                              } catch (err) {
                                if (err.name !== 'AbortError') copyToClipboard(url, "Instructor link");
                              }
                            } else {
                              window.open(`/events/${eventId}/share/instructor`, '_blank');
                            }
                          }}
                          data-testid="share-instructor-btn"
                        >
                          <ArrowUp className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                          <span className="truncate">Share</span>
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* NON-INSTRUCTOR: Share with Chef (Dietary) - Controlled by settings */}
                  {(dashboardDisplaySettings.quick_actions?.share_with_chef && intakeSettings?.collect_dietary && event?.event_type !== 'instructor_pilot' && event?.pilot_category !== 'instructor') && (
                  <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border flex flex-col">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Utensils className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-xs sm:text-sm truncate">Share with Chef</p>
                        <p className="text-[10px] sm:text-xs text-gray-500 truncate">Dietary summary</p>
                      </div>
                    </div>
                    <p className="text-[10px] sm:text-xs text-gray-500 mb-2 sm:mb-3 flex-grow">Share dietary requirements and allergies</p>
                    <div className="flex gap-2">
                      <Button 
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={() => {
                          const url = `${baseUrl}/events/${eventId}/share/dietary`;
                          navigator.clipboard.writeText(url);
                          toast.success("Dietary link copied!");
                        }}
                        data-testid="copy-dietary-btn"
                      >
                        <Copy className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">Copy</span>
                      </Button>
                      <Button 
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={async () => {
                          const url = `${baseUrl}/events/${eventId}/share/dietary`;
                          if (navigator.share) {
                            try {
                              await navigator.share({ title: 'Dietary Requirements', url });
                            } catch (err) {
                              if (err.name !== 'AbortError') copyToClipboard(url, "Dietary link");
                            }
                          } else {
                            window.open(`/events/${eventId}/share/dietary`, '_blank');
                          }
                        }}
                        data-testid="share-dietary-btn"
                      >
                        <ArrowUp className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">Share</span>
                      </Button>
                    </div>
                  </div>
                  )}

                  {/* NON-INSTRUCTOR: Share with Venue (Accessibility) - Controlled by settings + intake toggle (Issue 5 fix) */}
                  {(dashboardDisplaySettings.quick_actions?.share_with_venue && intakeSettings?.collect_accessibility && event?.event_type !== 'instructor_pilot' && event?.pilot_category !== 'instructor') && (
                  <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border flex flex-col">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Accessibility className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-xs sm:text-sm truncate">Share with Venue</p>
                        <p className="text-[10px] sm:text-xs text-gray-500 truncate">Accessibility needs</p>
                      </div>
                    </div>
                    <p className="text-[10px] sm:text-xs text-gray-500 mb-2 sm:mb-3 flex-grow">Share accommodation requirements</p>
                    <div className="flex gap-2">
                      <Button 
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={() => {
                          const url = `${baseUrl}/events/${eventId}/share/accessibility`;
                          navigator.clipboard.writeText(url);
                          toast.success("Accessibility link copied!");
                        }}
                        data-testid="copy-accessibility-btn"
                      >
                        <Copy className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">Copy</span>
                      </Button>
                      <Button 
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={async () => {
                          const url = `${baseUrl}/events/${eventId}/share/accessibility`;
                          if (navigator.share) {
                            try {
                              await navigator.share({ title: 'Accessibility Needs', url });
                            } catch (err) {
                              if (err.name !== 'AbortError') copyToClipboard(url, "Accessibility link");
                            }
                          } else {
                            window.open(`/events/${eventId}/share/accessibility`, '_blank');
                          }
                        }}
                        data-testid="share-accessibility-btn"
                      >
                        <ArrowUp className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">Share</span>
                      </Button>
                    </div>
                  </div>
                  )}

                  {/* ALL EVENTS: Share Music Preferences (Tap In / DJ) - Controlled by settings */}
                  {dashboardDisplaySettings.quick_actions?.share_with_dj !== false && (
                  <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border flex flex-col">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Music className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-xs sm:text-sm truncate">
                          {(dashboardDisplaySettings.quick_actions?.share_with_instructor || event?.event_type === 'instructor_pilot' || event?.pilot_category === 'instructor') ? "Share Music Preferences" : "Share with DJ"}
                        </p>
                        <p className="text-[10px] sm:text-xs text-gray-500 truncate">
                          {(dashboardDisplaySettings.quick_actions?.share_with_instructor || event?.event_type === 'instructor_pilot' || event?.pilot_category === 'instructor') ? "Attendee music picks" : "Tap In playlist"}
                        </p>
                      </div>
                    </div>
                    <p className="text-[10px] sm:text-xs text-gray-500 mb-2 sm:mb-3 flex-grow">
                      {(dashboardDisplaySettings.quick_actions?.share_with_instructor || event?.event_type === 'instructor_pilot' || event?.pilot_category === 'instructor') 
                        ? "Share what music your attendees want to hear"
                        : "Share guest music preferences"
                      }
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={() => {
                          if (techStack.tapin?.dj_insights_link) {
                            navigator.clipboard.writeText(techStack.tapin.dj_insights_link);
                            toast.success("Music preferences link copied!");
                          } else {
                            toast.info("Connect Tap In first to get music preferences link");
                            setActiveTab("settings"); setSettingsTab("techstack");
                          }
                        }}
                        data-testid="copy-music-btn"
                      >
                        <Copy className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">Copy</span>
                      </Button>
                      <Button 
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={async () => {
                          if (techStack.tapin?.dj_insights_link) {
                            if (navigator.share) {
                              try {
                                await navigator.share({ title: 'Music Preferences', url: techStack.tapin.dj_insights_link });
                              } catch (err) {
                                if (err.name !== 'AbortError') window.open(techStack.tapin.dj_insights_link, '_blank');
                              }
                            } else {
                              window.open(techStack.tapin.dj_insights_link, '_blank');
                            }
                          } else {
                            setActiveTab("settings"); setSettingsTab("techstack");
                          }
                        }}
                        data-testid="share-music-btn"
                      >
                        <ArrowUp className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">Share</span>
                      </Button>
                    </div>
                  </div>
                  )}
                </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Instructor dashboards: ONE share link only - Audience Insights.
                No chef/venue/DJ tiles on instructor dashboards (per user
                direction Apr 30 2026). Instructors share aggregated student
                insights with their team or other instructors. */}
            {dashboardType === DASHBOARD_TYPES.INSTRUCTOR && (
              <Card data-testid="instructor-audience-insights-share">
                <CardHeader className="p-3 sm:p-6">
                  <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                    <ArrowUp className="w-4 h-4 sm:w-5 sm:h-5" />
                    Share Audience Insights
                  </CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Send a digest of what your students are bringing to class.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0">
                  <div className="p-4 border border-stone-200 rounded-2xl bg-stone-50">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-full bg-violet-100 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-violet-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-stone-900">Audience Insights</p>
                        <p className="text-[11px] text-stone-500">Top skill gaps, look goals, and class format preferences across your last 30 days.</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 rounded-full text-xs"
                        onClick={() => { setActiveTab("insights"); }}
                        data-testid="instructor-insights-view-btn"
                      >
                        View insights
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 rounded-full text-xs bg-violet-600 hover:bg-violet-700"
                        onClick={async () => {
                          const summary = `Audience Insights from ${event?.name || "your class"}\n\nView the full digest in your dashboard.`;
                          try {
                            await navigator.clipboard.writeText(summary);
                            toast.success("Insights summary copied. Paste it anywhere.");
                          } catch (_e) {
                            toast.error("Could not copy to clipboard");
                          }
                        }}
                        data-testid="instructor-insights-share-btn"
                      >
                        Copy summary
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ============ PEOPLE TAB ============ */}
          <TabsContent value="people" data-testid="people-content">
            {/* People Sub-tabs - Different for Provider vs Event dashboards */}
            <div className="flex items-center gap-2 mb-4 border-b pb-2 overflow-x-auto">
              {/* For Provider dashboards: Clients tab */}
              {dashboardType === DASHBOARD_TYPES.PROVIDER ? (
                <>
                  <button
                    onClick={() => setPeopleSubTab("attendees")}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                      peopleSubTab === "attendees" 
                        ? "bg-white border border-b-0 border-gray-200 text-gray-900" 
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    data-testid="directory-tab-btn"
                  >
                    <Users className="w-4 h-4 inline mr-2" />
                    Directory ({rsvps?.length || 0})
                  </button>
                  <button
                    onClick={() => setPeopleSubTab("appointments")}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                      peopleSubTab === "appointments" 
                        ? "bg-white border border-b-0 border-gray-200 text-gray-900" 
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    data-testid="appointments-tab-btn"
                  >
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Appointments
                  </button>
                  {/* Iter 9.49+ - Acuity sub-tab. Only renders when Acuity is
                      toggled ON. Single source of truth for pulling Acuity
                      clients + appointments into Phygital. */}
                  {techStack?.acuity?.enabled && (
                    <button
                      onClick={() => setPeopleSubTab("acuity")}
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                        peopleSubTab === "acuity"
                          ? "bg-white border border-b-0 border-gray-200 text-gray-900"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                      data-testid="acuity-tab-btn"
                    >
                      <RefreshCw className="w-4 h-4 inline mr-2" />
                      Acuity
                    </button>
                  )}
                  {/* Preferences Insights moved to Insights tab (Apr 30 2026) */}
                </>
              ) : (
                <>
                  {/* For Event dashboards: Attendees, Pre-Invite, VIP Lists */}
                  {/* Speakers tab ONLY for Events, NOT for Instructor */}
                  <button
                    onClick={() => setPeopleSubTab("attendees")}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                      peopleSubTab === "attendees" 
                        ? "bg-white border border-b-0 border-gray-200 text-gray-900" 
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <Users className="w-4 h-4 inline mr-2" />
                    {(event?.pilot_category === 'instructor' || event?.event_type === 'instructor_pilot') ? "Participants" : "Attendees"} ({rsvps?.length || 0})
                  </button>
                  {/* Pre-Invite tab - Event-type dashboards ONLY (Issue 3 fix). Provider/service dashboards never show it. */}
                  {dashboardType === DASHBOARD_TYPES.EVENT && (event?.pre_invite_mode?.enabled || event?.inner_circle_list_id || event?.pre_invite_mode?.inner_circle_list_id) && (
                    <button
                      onClick={() => setPeopleSubTab("pre_invite")}
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                        peopleSubTab === "pre_invite" 
                          ? "bg-white border border-b-0 border-gray-200 text-gray-900" 
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                      data-testid="pre-invite-tab-btn"
                    >
                      <Mail className="w-4 h-4 inline mr-2" />
                      Pre-Invite ({innerCircleMembers?.length || 0})
                    </button>
                  )}
                  {/* Instructor tab - ONLY for Instructor pilots */}
                  {(event?.pilot_category === 'instructor' || event?.event_type === 'instructor_pilot') && (
                  <button
                    onClick={() => setPeopleSubTab("instructor")}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                      peopleSubTab === "instructor" 
                        ? "bg-white border border-b-0 border-gray-200 text-gray-900" 
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    data-testid="instructor-tab-btn"
                  >
                    <GraduationCap className="w-4 h-4 inline mr-2" />
                    Instructor
                  </button>
                  )}
                  {/* VIP Lists - only for non-instructor events */}
                  {event?.pilot_category !== 'instructor' && event?.event_type !== 'instructor_pilot' && (
                  <button
                    onClick={() => setPeopleSubTab("vip_lists")}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                      peopleSubTab === "vip_lists" 
                        ? "bg-white border border-b-0 border-gray-200 text-gray-900" 
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <Crown className="w-4 h-4 inline mr-2" />
                    VIP Lists ({(lists || []).length})
                  </button>
                  )}
                  {/* Speakers tab - ONLY for Events, NOT for Instructor */}
                  {event?.pilot_category !== 'instructor' && event?.event_type !== 'instructor_pilot' && (
                  <button
                    onClick={() => setPeopleSubTab("speakers")}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                      peopleSubTab === "speakers" 
                        ? "bg-white border border-b-0 border-gray-200 text-gray-900" 
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <Mic className="w-4 h-4 inline mr-2" />
                    Speakers ({speakers?.length || 0})
                  </button>
                  )}
                </>
              )}
            </div>

            {/* Attendees/Clients/Participants Sub-tab - Iter 9.47.v
                Provider dashboards (makeup hosts) render the rich Kanban view
                (PeopleTabGrid) per user spec: "the directory subtab IS the
                list of clients with their preference cards and private inspo".
                Other dashboards keep the table view. */}
            {peopleSubTab === "attendees" && dashboardType === DASHBOARD_TYPES.PROVIDER && (
              <div className="space-y-3">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowFindImportModal(true)}
                    data-testid="find-or-import-client-btn"
                    className="border-stone-300"
                  >
                    <Search className="w-3 h-3 mr-1.5" /> Find or import client
                  </Button>
                </div>
                <PeopleTabGrid
                  clients={importedClients || []}
                  rsvps={rsvps || []}
                  experiences={[]}
                  acuityClients={importedClients || []}
                  eventId={eventId}
                  onViewCard={() => { /* PeopleTabGrid handles its own preview dialog */ }}
                  onCreateInvite={() => setShowInviteClientModal(true)}
                  onEditClient={(client) => setEditingClient(client)}
                  onContactClient={(client) => {
                    if (client.email) window.location.href = `mailto:${client.email}`;
                  }}
                />
              </div>
            )}

            {peopleSubTab === "attendees" && dashboardType !== DASHBOARD_TYPES.PROVIDER && (
            <Card>
              <CardHeader className="p-3 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="text-base sm:text-lg">
                      {dashboardType === DASHBOARD_TYPES.PROVIDER 
                        ? "All Clients" 
                        : (event?.pilot_category === 'instructor' || event?.event_type === 'instructor_pilot')
                          ? "All Participants"
                          : "All Attendees"}
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm">{filteredPeople.length} of {rsvps?.length || 0} people</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1 sm:flex-none">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        placeholder="Search..."
                        value={peopleSearch}
                        onChange={(e) => setPeopleSearch(e.target.value)}
                        className="pl-9 w-full sm:w-48 text-sm"
                        data-testid="people-search"
                      />
                    </div>
                    <Select value={peopleFilter} onValueChange={setPeopleFilter}>
                      <SelectTrigger className="w-28 sm:w-36 text-xs sm:text-sm" data-testid="people-filter">
                        <Filter className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        <SelectValue placeholder="Filter" />
                      </SelectTrigger>
                      <SelectContent>
                        {dashboardType === DASHBOARD_TYPES.PROVIDER ? (
                          <>
                            <SelectItem value="all">All Clients</SelectItem>
                            <SelectItem value="vip">VIP</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="dietary">Has Dietary</SelectItem>
                            <SelectItem value="accessibility">Has Accessibility</SelectItem>
                          </>
                        ) : (
                          <>
                            <SelectItem value="all">All People</SelectItem>
                            <SelectItem value="host">Hosts</SelectItem>
                            <SelectItem value="vip">VIP</SelectItem>
                            <SelectItem value="partner">Partners</SelectItem>
                            <SelectItem value="sponsor">Sponsors</SelectItem>
                            <SelectItem value="vendor">Vendors</SelectItem>
                            <SelectItem value="rsvp">RSVPs</SelectItem>
                            <SelectItem value="dietary">Has Dietary</SelectItem>
                            <SelectItem value="accessibility">Has Accessibility</SelectItem>
                            <SelectItem value="checked_in">Checked In</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" data-testid="export-csv-btn">
                      <Download className="w-4 h-4 mr-1" />
                      Export
                    </Button>
                    <Button size="sm" onClick={() => setShowInviteClientModal(true)} data-testid="invite-client-btn"
                      style={{ backgroundColor: catIconColor, color: '#fff' }}
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Invite Client
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">#</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>{dashboardType === DASHBOARD_TYPES.PROVIDER ? "Service" : "Location"}</TableHead>
                        <TableHead>Label</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>{dashboardType === DASHBOARD_TYPES.PROVIDER ? "Appointment" : "Check-In"}</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead className="w-24 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPeople.map((person, idx) => (
                        <TableRow key={person.rsvp_id || idx} data-testid={`person-row-${idx}`}>
                          <TableCell className="text-gray-500">{idx + 1}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="relative">
                                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
                                  {person.profile_image ? (
                                    <img 
                                      src={getImageUrl(person.profile_image)} 
                                      alt={person.name} 
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <User className="w-4 h-4 text-gray-500" />
                                  )}
                                </div>
                                {person.phygital_linked && (
                                  <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-purple-500 rounded-full border-2 border-white flex items-center justify-center" title="Phygital Account">
                                    <Check className="w-2 h-2 text-white" />
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-col">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{person.name || "Guest"}</span>
                                  {person.is_demo_account && (
                                    <span className="px-1.5 py-0.5 bg-orange-100 text-orange-600 text-[10px] font-medium rounded">
                                      Demo
                                    </span>
                                  )}
                                </div>
                                {person.username && (
                                  <span className="text-xs text-gray-400">@{person.username}</span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-500">{blurEmail(person.email) || "-"}</TableCell>
                          <TableCell className="text-gray-500 text-xs">
                            {dashboardType === DASHBOARD_TYPES.PROVIDER
                              ? (
                                <div>
                                  <span>{person.service_name || person.service || "-"}</span>
                                  {person.linked_experience_name && (
                                    <p className="text-[10px] text-purple-500 truncate">{person.linked_experience_name}</p>
                                  )}
                                </div>
                              )
                              : (person.city || person.location || "-")}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 flex-wrap">
                              {/* Display multiple labels if present */}
                              {(person.labels && person.labels.length > 0 
                                ? person.labels 
                                : [person.label || "attendee"]
                              ).map((lbl, idx) => (
                                <span key={idx}>{getLabelBadge(lbl)}</span>
                              ))}
                              {getVipBadge(person)}
                            </div>
                          </TableCell>
                          {/* RSVP Status - Clickable to change */}
                          <TableCell>
                            <Select 
                              value={person.status || "confirmed"}
                              onValueChange={(newStatus) => updatePersonStatus(person, newStatus)}
                            >
                              <SelectTrigger className="h-7 w-[160px] text-xs border-0 bg-transparent hover:bg-gray-100 focus:ring-0">
                                <SelectValue>
                                  {getStatusBadge(person.status || "confirmed")}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {RSVP_STATUSES.map((status) => (
                                  <SelectItem key={status.value} value={status.value}>
                                    <div className="flex items-center gap-2">
                                      <span className={`w-2 h-2 rounded-full ${status.color.split(" ")[0]}`} />
                                      {status.label}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          {/* Check-In / Appointment Status */}
                          <TableCell>
                            {dashboardType === DASHBOARD_TYPES.PROVIDER ? (
                              // Provider: Show appointment date + quick complete action
                              person.status === "completed" || person.appointment_stage === "complete" || person.appointment_stage === "completed" ? (
                                <div className="flex items-center gap-1 text-green-600">
                                  <CheckCircle2 className="w-4 h-4" />
                                  <span className="text-xs font-medium">Complete</span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  {person.appointment_date && (
                                    <span className="text-xs text-gray-500">
                                      {new Date(person.appointment_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                    </span>
                                  )}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 text-[10px] px-2 text-green-600 border-green-200 hover:bg-green-50"
                                    onClick={() => updatePersonStatus(person, "completed")}
                                    data-testid={`complete-btn-${idx}`}
                                  >
                                    <CheckCircle2 className="w-3 h-3 mr-0.5" />
                                    Done
                                  </Button>
                                </div>
                              )
                            ) : (
                              // Event: Show check-in status
                              person.checked_in ? (
                              <div className="flex flex-col">
                                <div className="flex items-center gap-1 text-green-600">
                                  <CheckCircle2 className="w-4 h-4" />
                                  <span className="text-xs font-medium">Checked In</span>
                                </div>
                                {person.checked_in_at && (
                                  <span className="text-[10px] text-gray-400 mt-0.5">
                                    {new Date(person.checked_in_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => handleManualCheckIn(person)}
                                data-testid={`checkin-btn-${idx}`}
                              >
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Check In
                              </Button>
                            ))}
                          </TableCell>
                          {/* Internal Notes */}
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs relative"
                              onClick={() => openInternalNotes(person)}
                              data-testid={`notes-btn-${idx}`}
                            >
                              <MessageSquare className="w-4 h-4" />
                              {person.internal_notes?.length > 0 && (
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 text-white text-[10px] rounded-full flex items-center justify-center">
                                  {person.internal_notes.length}
                                </span>
                              )}
                            </Button>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => { setSelectedPerson(person); setShowPreferenceCard(true); }}
                                data-testid={`view-card-${idx}`}
                                title="View Preference Card"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" data-testid={`person-actions-${idx}`}>
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => { setSelectedPerson(person); setShowPreferenceCard(true); }}>
                                    <Eye className="w-4 h-4 mr-2" />
                                    View Preference Card
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openInternalNotes(person)}>
                                    <MessageSquare className="w-4 h-4 mr-2" />
                                    Internal Notes {person.internal_notes?.length > 0 && `(${person.internal_notes.length})`}
                                  </DropdownMenuItem>
                                  {!person.checked_in && (
                                    <DropdownMenuItem onClick={() => handleManualCheckIn(person)}>
                                      <CheckCircle2 className="w-4 h-4 mr-2" />
                                      Manual Check-In
                                    </DropdownMenuItem>
                                  )}
                                  {person.checked_in && (
                                    <DropdownMenuItem onClick={() => handleUndoCheckIn(person)} className="text-orange-600">
                                      <XCircle className="w-4 h-4 mr-2" />
                                      Undo Check-In
                                    </DropdownMenuItem>
                                  )}
                                  {person.email && (
                                    <DropdownMenuItem onClick={() => handleResendConfirmation(person)}>
                                      <Mail className="w-4 h-4 mr-2" />
                                      Resend Confirmation
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuSub>
                                    <DropdownMenuSubTrigger>
                                      <Tag className="w-4 h-4 mr-2" />
                                      Toggle Labels
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent>
                                      {TEAM_LABELS.map((label) => {
                                        const currentLabels = person.labels || (person.label ? [person.label] : ["attendee"]);
                                        const isActive = currentLabels.includes(label.value);
                                        return (
                                          <DropdownMenuItem 
                                            key={label.value}
                                            onClick={() => updatePersonLabel(person, label.value, true)}
                                            className={isActive ? "bg-gray-100" : ""}
                                          >
                                            <span className={`w-2 h-2 rounded-full mr-2 ${label.color.split(" ")[0]}`} />
                                            {label.label}
                                            {isActive && <Check className="w-4 h-4 ml-auto text-green-600" />}
                                          </DropdownMenuItem>
                                        );
                                      })}
                                    </DropdownMenuSubContent>
                                  </DropdownMenuSub>
                                  <DropdownMenuSub>
                                    <DropdownMenuSubTrigger>
                                      <UserCog className="w-4 h-4 mr-2" />
                                      Change Status
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuSubContent>
                                      {RSVP_STATUSES.map((status) => (
                                        <DropdownMenuItem 
                                          key={status.value}
                                          onClick={() => updatePersonStatus(person, status.value)}
                                          className={person.status === status.value ? "bg-gray-100" : ""}
                                        >
                                          <span className={`w-2 h-2 rounded-full mr-2 ${status.color.split(" ")[0]}`} />
                                          {status.label}
                                          {person.status === status.value && <Check className="w-4 h-4 ml-auto" />}
                                        </DropdownMenuItem>
                                      ))}
                                    </DropdownMenuSubContent>
                                  </DropdownMenuSub>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    className="text-red-600 focus:text-red-600"
                                    onClick={() => { setPersonToDelete(person); setShowDeleteConfirm(true); }}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Remove Guest
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredPeople.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                            {t("host.people.empty.title", "No clients yet")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
            )}

            {/* Pre-Invite Sub-tab */}
            {peopleSubTab === "pre_invite" && (event?.pre_invite_mode?.enabled || event?.inner_circle_list_id || event?.pre_invite_mode?.inner_circle_list_id) && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-violet-500" />
                        Pre-Invite List
                      </CardTitle>
                      <CardDescription>
                        {innerCircleMembers?.length || 0} members in your pre-invite list
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Search members..."
                        value={searchPreInvite || ""}
                        onChange={(e) => setSearchPreInvite(e.target.value)}
                        className="w-48"
                      />
                      <Button variant="outline" size="sm" onClick={() => fetchInnerCircleMembers()}>
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {innerCircleMembers?.length > 0 ? (
                    <ScrollArea className="h-[500px]">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50/50">
                            <TableHead className="w-[50px]">#</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Company</TableHead>
                            <TableHead>Source</TableHead>
                            <TableHead>Added</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(innerCircleMembers || [])
                            .filter(m => {
                              if (!searchPreInvite) return true;
                              const search = searchPreInvite.toLowerCase();
                              return (m.name || m.display_name || "").toLowerCase().includes(search) ||
                                     (m.email || "").toLowerCase().includes(search) ||
                                     (m.company || "").toLowerCase().includes(search);
                            })
                            .map((member, idx) => (
                            <TableRow key={member.member_id || idx} className="hover:bg-gray-50" data-testid={`preinvite-row-${idx}`}>
                              <TableCell className="text-gray-400 text-sm">{idx + 1}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center text-white text-xs font-medium">
                                    {(member.name || member.display_name || member.email || "?").substring(0, 2).toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">{member.name || member.display_name || "-"}</p>
                                    {member.title && <p className="text-xs text-gray-500">{member.title}</p>}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="text-gray-600">{member.email}</TableCell>
                              <TableCell className="text-gray-600 text-sm">{member.company || "-"}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-xs capitalize">
                                  {member.source || "manual"}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-gray-500 text-sm">
                                {member.added_at ? new Date(member.added_at).toLocaleDateString() : "-"}
                              </TableCell>
                              <TableCell>
                                {member.rsvp_status ? (
                                  <Badge className="bg-green-100 text-green-700 border-green-200">{member.rsvp_status}</Badge>
                                ) : member.converted_to_rsvp ? (
                                  <Badge className="bg-blue-100 text-blue-700 border-blue-200">Converted</Badge>
                                ) : (
                                  <Badge variant="secondary" className="bg-violet-100 text-violet-700">Pending</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm">
                                      <MoreHorizontal className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => {
                                      navigator.clipboard.writeText(member.email);
                                      toast.success("Email copied!");
                                    }}>
                                      <Copy className="w-4 h-4 mr-2" />
                                      Copy Email
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => window.open(`mailto:${member.email}`)}>
                                      <Mail className="w-4 h-4 mr-2" />
                                      Send Email
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                      className="text-red-600"
                                      onClick={async () => {
                                        try {
                                          const listId = event?.pre_invite_mode?.inner_circle_list_id || event?.inner_circle_list_id;
                                          await fetch(`${API}/inner-circle/lists/${listId}/members/${member.member_id}`, {
                                            method: "DELETE",
                                            headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
                                          });
                                          fetchInnerCircleMembers();
                                          toast.success("Member removed");
                                        } catch (err) {
                                          toast.error("Failed to remove member");
                                        }
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Remove
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="font-medium">No pre-invite members yet</p>
                      <p className="text-sm">Add members to your pre-invite list to give them early access</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* VIP Lists Sub-tab */}
            {/* Instructor Sub-tab - For instructor pilot events */}
            {peopleSubTab === "instructor" && (
              <InstructorSubmissionPanel 
                event={event}
                eventId={eventId}
                API={API}
                instructorProfile={instructorProfile}
                setInstructorProfile={setInstructorProfile}
                instructorProfileLoading={instructorProfileLoading}
                setInstructorProfileLoading={setInstructorProfileLoading}
              />
            )}

            {peopleSubTab === "vip_lists" && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>VIP Lists</CardTitle>
                      <CardDescription>Create lists with List Hosts who can invite guests with customizable limits</CardDescription>
                    </div>
                    <Button onClick={() => {
                      setNewListName("");
                      setNewListHostName("");
                      setShowCreateListModal(true);
                    }} data-testid="create-list-btn">
                      <Plus className="w-4 h-4 mr-1" />
                      Create List
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Extra Invite Requests Section */}
                  {extraInviteRequests.length > 0 && (
                    <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Bell className="w-5 h-5 text-purple-600" />
                        <h4 className="font-medium text-black">Extra Invite Requests ({extraInviteRequests.length})</h4>
                      </div>
                      <div className="space-y-3">
                        {extraInviteRequests.map((req) => (
                          <div key={req.request_id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-100">
                            <div className="flex-1">
                              <p className="font-medium text-sm">{req.requester_name}</p>
                              <p className="text-xs text-gray-600">
                                Requested <span className="font-semibold">{req.requested_count}</span> extra invites for {req.list_name}
                              </p>
                              {req.reason && (
                                <p className="text-xs text-gray-500 mt-1 italic">"{req.reason}"</p>
                              )}
                            </div>
                            <div className="flex gap-2 ml-4">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => handleExtraInviteRequest(req.request_id, 'deny')}
                              >
                                Deny
                              </Button>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleExtraInviteRequest(req.request_id, 'approve', req.requested_count)}
                              >
                                Approve +{req.requested_count}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {(lists || []).length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Crown className="w-12 h-12 mx-auto mb-3 text-yellow-300" />
                      <p className="font-medium">No VIP lists yet</p>
                      <p className="text-sm mb-4">Create lists to organize VIP guests with hierarchical invites</p>
                      <Button variant="outline" onClick={() => {
                        setNewListName("");
                        setNewListHostName("");
                        setShowCreateListModal(true);
                      }}>
                        <Plus className="w-4 h-4 mr-1" />
                        Create First List
                      </Button>
                    </div>
                  ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(lists || []).map((list, idx) => (
                      <div 
                        key={list.list_id || idx}
                        className="border rounded-lg overflow-hidden"
                        data-testid={`list-${idx}`}
                      >
                        {/* List Header - Black background, white text, gold crown */}
                        <div className="p-4 bg-black">
                          {/* Top row: Crown + List name + Edit button */}
                          <div className="flex items-start gap-3 mb-2">
                            <Crown className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              {editingListId === list.list_id ? (
                                <div className="space-y-3">
                                  <Input
                                    value={editListData.name || ""}
                                    onChange={(e) => setEditListData({...editListData, name: e.target.value})}
                                    placeholder="List Name"
                                    className="font-medium bg-white"
                                    data-testid={`edit-list-name-${idx}`}
                                  />
                                  <Input
                                    value={editListData.list_host_name || ""}
                                    onChange={(e) => setEditListData({...editListData, list_host_name: e.target.value})}
                                    placeholder="List Host Name"
                                    className="text-sm bg-white"
                                    data-testid={`edit-list-host-${idx}`}
                                  />
                                  {/* VIP Limit & Plus Settings */}
                                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/20">
                                    <div>
                                      <label className="text-xs text-gray-300 block mb-1">VIP Limit</label>
                                      <Input
                                        type="number"
                                        min="1"
                                        max="500"
                                        value={editListData.max_guests || 10}
                                        onChange={(e) => setEditListData({...editListData, max_guests: parseInt(e.target.value) || 10})}
                                        className="bg-white text-sm h-8"
                                        data-testid={`edit-list-max-guests-${idx}`}
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-gray-300 block mb-1">+N per Guest</label>
                                      <Input
                                        type="number"
                                        min="0"
                                        max="10"
                                        value={editListData.max_plus_per_guest ?? 2}
                                        onChange={(e) => setEditListData({...editListData, max_plus_per_guest: parseInt(e.target.value) || 0})}
                                        className="bg-white text-sm h-8"
                                        data-testid={`edit-list-max-plus-${idx}`}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <p className="font-medium text-white">{list.name}</p>
                                  <div className="flex items-center gap-2 text-sm text-gray-300 mt-1">
                                    <span className="shrink-0">List Host:</span>
                                    {list.is_claimed && list.list_host_user_id ? (
                                      <div className="flex items-center gap-1.5 px-2 py-0.5 bg-green-900/50 rounded-full border border-green-700">
                                        <div className="w-4 h-4 rounded-full bg-green-800 overflow-hidden shrink-0">
                                          {list.list_host_profile_photo ? (
                                            <img src={getImageUrl(list.list_host_profile_photo)} alt="" className="w-full h-full object-cover" />
                                          ) : (
                                            <User className="w-2.5 h-2.5 m-0.5 text-green-400" />
                                          )}
                                        </div>
                                        <span className="font-medium text-green-300">{list.list_host_name}</span>
                                        <Check className="w-3 h-3 text-green-400 shrink-0" />
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">{list.list_host_name || "Not assigned"}</span>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                            {/* Edit button - top right */}
                            {editingListId !== list.list_id && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-white hover:bg-white/10 h-8 w-8 p-0 shrink-0"
                                onClick={() => {
                                  setEditingListId(list.list_id);
                                  setEditListData({
                                    name: list.name,
                                    list_host_name: list.list_host_name,
                                    max_guests: list.max_guests,
                                    max_plus_per_guest: list.max_plus_per_guest
                                  });
                                }}
                                data-testid={`edit-list-btn-${idx}`}
                              >
                                <Edit3 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          
                          {/* Action buttons row */}
                          <div className="flex items-center gap-2 mt-3 flex-wrap">
                            {editingListId === list.list_id ? (
                              <>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="bg-white text-black border-white hover:bg-gray-100 h-8"
                                  onClick={async () => {
                                    const token = localStorage.getItem("session_token");
                                    try {
                                      await fetch(`${API}/events/${eventId}/vip-lists/${list.list_id}`, {
                                        method: "PUT",
                                        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                        body: JSON.stringify(editListData)
                                      });
                                      toast.success("List updated!");
                                      setEditingListId(null);
                                      fetchLists();
                                    } catch (err) {
                                      toast.error("Failed to update list");
                                    }
                                  }}
                                  data-testid={`save-list-${idx}`}
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Save
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-white hover:bg-white/10 h-8"
                                  onClick={() => setEditingListId(null)}
                                >
                                  <X className="w-4 h-4" />
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <>
                                {/* Copy List Host Invite Link */}
                                {list.list_host_url && (
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    className="bg-transparent text-white border-white/30 hover:bg-white/10 h-8 text-xs"
                                    onClick={() => {
                                      const fullUrl = window.location.origin + list.list_host_url;
                                      navigator.clipboard.writeText(fullUrl);
                                      toast.success("List Host invite link copied!");
                                    }}
                                    data-testid={`copy-host-link-${idx}`}
                                  >
                                    <Copy className="w-3.5 h-3.5 mr-1" />
                                    Host Link
                                  </Button>
                                )}
                                {/* View List */}
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="bg-transparent text-white border-white/30 hover:bg-white/10 h-8 text-xs"
                                  onClick={() => {
                                    setSelectedVipListForView(list);
                                    setShowVipHostViewModal(true);
                                  }}
                                  data-testid={`view-host-list-${idx}`}
                                >
                                  <Eye className="w-3.5 h-3.5 mr-1" />
                                  View
                                </Button>
                                {/* Manage List */}
                                <Button 
                                  size="sm"
                                  className="bg-purple-600 text-white hover:bg-purple-700 h-8 text-xs font-medium"
                                  onClick={() => {
                                    setManagingList(list);
                                    setShowManageListModal(true);
                                  }}
                                  data-testid={`manage-list-${idx}`}
                                >
                                  <UserPlus className="w-3.5 h-3.5 mr-1" />
                                  Manage
                                </Button>
                                {/* Delete List */}
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-red-400 hover:bg-red-900/20 h-8 w-8 p-0"
                                  onClick={() => {
                                    setDeleteTarget({
                                      type: 'vip_list',
                                      name: list.name,
                                      onConfirm: async () => {
                                        const token = localStorage.getItem("session_token");
                                        try {
                                          await fetch(`${API}/events/${eventId}/vip-lists/${list.list_id}`, {
                                            method: "DELETE",
                                            headers: { Authorization: `Bearer ${token}` }
                                          });
                                          toast.success(`"${list.name}" deleted`);
                                          fetchLists();
                                        } catch (err) {
                                          toast.error("Failed to delete list");
                                        }
                                      }
                                    });
                                    setShowDeleteConfirmModal(true);
                                  }}
                                  data-testid={`delete-list-${idx}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* List Stats & Settings */}
                        <div className="p-4 bg-white border-b">
                          <div className="flex items-center justify-between mb-3">
                            {/* Calculate spots used: ONLY primary VIPs count against the limit, +1s are extra */}
                            {(() => {
                              const allMembers = (list.members || []).filter(m => m.role !== 'list_host');
                              // Primary VIPs: those NOT invited by another member
                              const primaryVIPs = allMembers.filter(m => !m.vip_invited_by && !m.invited_by_member_id);
                              const primaryCount = primaryVIPs.length;
                              // +1s: those invited by another member (don't count against limit)
                              const plusOnes = allMembers.filter(m => m.vip_invited_by || m.invited_by_member_id);
                              const plusOnesCount = plusOnes.length;
                              // Also count plus_count from legacy data
                              const legacyPlusOnes = primaryVIPs.reduce((sum, m) => sum + (m.plus_count_used || 0), 0);
                              const totalPlusOnes = Math.max(plusOnesCount, legacyPlusOnes);
                              
                              const maxGuests = list.max_guests || 10;
                              const isFull = primaryCount >= maxGuests;
                              const totalAttending = primaryCount + totalPlusOnes;
                              
                              return (
                                <div className="flex items-center gap-6 text-sm text-gray-600">
                                  <div className="flex items-center gap-2">
                                    <Users className="w-4 h-4" />
                                    <span className={`font-medium ${isFull ? 'text-red-600' : ''}`}>
                                      {primaryCount} / {maxGuests} VIP spots used
                                    </span>
                                    {totalPlusOnes > 0 && (
                                      <span className="text-xs text-gray-400">(+{totalPlusOnes} guests)</span>
                                    )}
                                    {isFull && (
                                      <Badge className="bg-red-100 text-red-700 text-xs">Full</Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <UserPlus className="w-4 h-4" />
                                    <span>+{list.max_plus_per_guest || 2} per guest</span>
                                  </div>
                                  {list.is_closed && (
                                    <Badge variant="destructive">Closed</Badge>
                                  )}
                                </div>
                              );
                            })()}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setExpandedListId(expandedListId === list.list_id ? null : list.list_id)}
                              data-testid={`toggle-list-${idx}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              {expandedListId === list.list_id ? "Hide" : "View"} Members
                            </Button>
                          </div>
                          {/* Visual Progress Bar - Only primary VIPs count against limit */}
                          {(() => {
                            const allMembers = (list.members || []).filter(m => m.role !== 'list_host');
                            // Primary VIPs only (not +1s)
                            const primaryVIPs = allMembers.filter(m => !m.vip_invited_by && !m.invited_by_member_id);
                            const primaryCount = primaryVIPs.length;
                            const maxGuests = list.max_guests || 10;
                            const percentFilled = Math.min(100, (primaryCount / maxGuests) * 100);
                            const spotsLeft = maxGuests - primaryCount;
                            
                            return (
                              <div className="w-full">
                                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full transition-all duration-300 rounded-full ${
                                      primaryCount >= maxGuests
                                        ? 'bg-red-500'
                                        : primaryCount >= maxGuests * 0.8
                                          ? 'bg-yellow-500'
                                          : 'bg-green-500'
                                    }`}
                                    style={{ width: `${percentFilled}%` }}
                                  />
                                </div>
                                <div className="flex justify-between text-xs text-gray-400 mt-1">
                                  <span>{Math.round(percentFilled)}% filled</span>
                                  <span>{spotsLeft > 0 ? `${spotsLeft} VIP spots left` : 'List full'}</span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        
                        {/* List Members - Expandable */}
                        {expandedListId === list.list_id && (
                          <div className="p-4 bg-gray-50">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                {(() => {
                                  const allMembers = (list.members || []).filter(m => m.role !== 'list_host');
                                  const primaryVIPs = allMembers.filter(m => !m.vip_invited_by && !m.invited_by_member_id);
                                  const plusOnes = allMembers.filter(m => m.vip_invited_by || m.invited_by_member_id);
                                  return (
                                    <h4 className="font-medium text-sm text-gray-700">
                                      VIPs ({primaryVIPs.length} / {list.max_guests || 10})
                                      {plusOnes.length > 0 && <span className="text-gray-400 font-normal"> + {plusOnes.length} guests</span>}
                                    </h4>
                                  );
                                })()}
                                {/* Bulk Selection Controls */}
                                {(list.members || []).filter(m => m.role !== 'list_host').length > 0 && (
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        const selectableIds = (list.members || [])
                                          .filter(m => m.role !== 'list_host')
                                          .map(m => m.member_id || m.rsvp_id || m.id);
                                        const allSelected = selectableIds.every(id => selectedMemberIds.includes(id));
                                        if (allSelected) {
                                          clearMemberSelection();
                                        } else {
                                          selectAllMembers(list);
                                        }
                                      }}
                                      className="text-xs"
                                      data-testid="select-all-members"
                                    >
                                      {selectedMemberIds.length > 0 ? "Deselect All" : "Select All"}
                                    </Button>
                                    {selectedMemberIds.length > 0 && (
                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() => bulkDeleteMembers(list.list_id, selectedMemberIds)}
                                        disabled={bulkDeleting}
                                        className="text-xs"
                                        data-testid="bulk-delete-members"
                                      >
                                        <Trash2 className="w-3 h-3 mr-1" />
                                        {bulkDeleting ? "Removing..." : `Remove ${selectedMemberIds.length}`}
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </div>
                              {(() => {
                                const allMembers = (list.members || []).filter(m => m.role !== 'list_host');
                                const primaryVIPs = allMembers.filter(m => !m.vip_invited_by && !m.invited_by_member_id);
                                const isFull = primaryVIPs.length >= (list.max_guests || 10);
                                return (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      if (isFull) {
                                        toast.error(`VIP list is full (${list.max_guests || 10} VIPs max)`);
                                        return;
                                      }
                                      setAddMemberToListId(list.list_id);
                                      setShowAddMemberToListModal(true);
                                    }}
                                    disabled={isFull}
                                    data-testid={`add-member-to-list-${idx}`}
                                  >
                                    <Plus className="w-4 h-4 mr-1" />
                                    {isFull ? "VIP List Full" : "Add VIP"}
                                  </Button>
                                );
                              })()}
                            </div>
                            
                            {/* Filter out list_host from displayed members - they don't count */}
                            {(list.members || []).filter(m => m.role !== 'list_host').length === 0 ? (
                              <p className="text-sm text-gray-500 text-center py-4">
                                No members yet. Share the invite links to get started.
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {/* Group members: primary members (no vip_invited_by) at top, +1s nested under their inviter */}
                                {(() => {
                                  const allMembers = (list.members || []).filter(m => m.role !== 'list_host');
                                  
                                  // Primary members: those who weren't invited by another member
                                  const primaryMembers = allMembers.filter(m => !m.vip_invited_by && !m.invited_by_member_id);
                                  
                                  // +1s: those who were invited by another member
                                  const plusOnes = allMembers.filter(m => m.vip_invited_by || m.invited_by_member_id);
                                  
                                  // Create a map of inviter_id -> their +1s
                                  const plusOnesByInviter = {};
                                  plusOnes.forEach(po => {
                                    const inviterId = po.vip_invited_by || po.invited_by_member_id;
                                    if (!plusOnesByInviter[inviterId]) {
                                      plusOnesByInviter[inviterId] = [];
                                    }
                                    plusOnesByInviter[inviterId].push(po);
                                  });
                                  
                                  return primaryMembers.map((member, mIdx) => {
                                    const memberId = member.member_id || member.rsvp_id || member.id;
                                    const isSelected = selectedMemberIds.includes(memberId);
                                    const memberPlusOnes = plusOnesByInviter[memberId] || [];
                                    
                                    return (
                                    <React.Fragment key={member.rsvp_id || mIdx}>
                                    <div 
                                      className={`flex items-center justify-between p-3 bg-white rounded-lg border group ${isSelected ? 'ring-2 ring-purple-500 bg-purple-50' : ''}`}
                                    >
                                      <div className="flex items-center gap-3">
                                        {/* Selection Checkbox */}
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => toggleMemberSelection(memberId)}
                                          className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                          data-testid={`select-member-${mIdx}`}
                                        />
                                        {/* Member Photo */}
                                        <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center">
                                          {member.profile_photo_url || member.profile_image ? (
                                            <img 
                                              src={getImageUrl(member.profile_photo_url || member.profile_image)} 
                                              alt={member.name}
                                              className="w-full h-full object-cover"
                                            />
                                          ) : (
                                            <span className="text-sm font-medium text-gray-500">
                                              {(member.first_name || member.name || "G")[0].toUpperCase()}
                                            </span>
                                          )}
                                        </div>
                                        {/* Member Info */}
                                        <div>
                                          <p className="font-medium text-sm">
                                            {member.first_name && member.last_name 
                                              ? `${member.first_name} ${member.last_name}`
                                              : member.name || "Guest"}
                                          </p>
                                          <div className="flex items-center gap-2 text-xs text-gray-500">
                                            {member.email && <span>{member.email}</span>}
                                            {member.company && <span>• {member.company}</span>}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Badge className="bg-yellow-100 text-yellow-800 text-xs">VIP</Badge>
                                        {(member.plus_count > 0 || memberPlusOnes.length > 0) && (
                                          <Badge variant="outline" className="text-xs">+{memberPlusOnes.length || member.plus_count}</Badge>
                                        )}
                                        {/* View in People */}
                                        {member.rsvp_id && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                              const person = rsvps?.find(r => r.rsvp_id === member.rsvp_id);
                                              if (person) {
                                                setSelectedPerson(person);
                                                setShowPreferenceCard(true);
                                              }
                                            }}
                                            className="opacity-0 group-hover:opacity-100"
                                            data-testid={`view-member-${mIdx}`}
                                          >
                                            <Eye className="w-4 h-4" />
                                          </Button>
                                        )}
                                        {/* Send Invite Link - for placeholders without RSVP */}
                                        {!member.rsvp_id && (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={async () => {
                                              const token = localStorage.getItem("session_token");
                                              try {
                                                // Generate/send magic link for this placeholder
                                                const res = await fetch(`${API}/events/${eventId}/vip-lists/${list.list_id}/members/${member.member_id || member.id}/send-invite`, {
                                                  method: "POST",
                                                  headers: { 
                                                    "Content-Type": "application/json",
                                                    Authorization: `Bearer ${token}` 
                                                  },
                                                  body: JSON.stringify({
                                                    email: member.email || '',
                                                    name: member.name || `${member.first_name || ''} ${member.last_name || ''}`.trim()
                                                  })
                                                });
                                              
                                                if (res.ok) {
                                                  const data = await res.json();
                                                  const inviteUrl = data.invite_url || `${window.location.origin}/e/${eventSlug}?vip=gi_${list.guest_invite_code}&member=${member.member_id || member.id}`;
                                                  navigator.clipboard.writeText(inviteUrl);
                                                  toast.success("Invite link copied! Share it with " + (member.name || member.email || "guest"));
                                                  setInviteLinkData({ name: member.name || member.email || "Guest", link: inviteUrl, type: "vip" });
                                                  setShowInviteLinkModal(true);
                                                } else {
                                                  // Fallback - just copy the list guest link
                                                  const fallbackUrl = `${window.location.origin}${list.guest_invite_url}`;
                                                  navigator.clipboard.writeText(fallbackUrl);
                                                  toast.success("Guest invite link copied!");
                                                }
                                              } catch (err) {
                                                // Fallback - copy generic guest invite link
                                                const fallbackUrl = `${window.location.origin}${list.guest_invite_url}`;
                                                navigator.clipboard.writeText(fallbackUrl);
                                                toast.success("Guest invite link copied!");
                                              }
                                            }}
                                            data-testid={`send-invite-${mIdx}`}
                                          >
                                            <Send className="w-4 h-4 mr-1" />
                                            Send Invite
                                          </Button>
                                        )}
                                        {/* Copy Invite Link - for placeholders with magic link */}
                                        {!member.rsvp_id && member.magic_link_token && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                              const inviteUrl = `${window.location.origin}/vip/claim/${member.claim_token}?auth=${member.magic_link_token}`;
                                              navigator.clipboard.writeText(inviteUrl);
                                              toast.success("Personal invite link copied!");
                                            }}
                                            className="opacity-0 group-hover:opacity-100"
                                          >
                                            <Copy className="w-4 h-4" />
                                          </Button>
                                        )}
                                        {/* Remove from list */}
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={async () => {
                                            const token = localStorage.getItem("session_token");
                                            try {
                                              const delMemberId = member.member_id || member.rsvp_id || member.id;
                                              await fetch(`${API}/events/${eventId}/vip-lists/${list.list_id}/members/${delMemberId}`, {
                                                method: "DELETE",
                                                headers: { Authorization: `Bearer ${token}` }
                                              });
                                              toast.success("Member removed from list");
                                              fetchLists();  // Refresh to update counts
                                            } catch (err) {
                                              toast.error("Failed to remove member");
                                            }
                                          }}
                                          className="opacity-0 group-hover:opacity-100"
                                        >
                                          <X className="w-4 h-4 text-red-500" />
                                        </Button>
                                      </div>
                                    </div>
                                    
                                    {/* Show +1s from database (members with vip_invited_by pointing to this member) */}
                                    {memberPlusOnes.length > 0 && (
                                      <div className="ml-12 space-y-1.5 pt-1">
                                        {memberPlusOnes.map((plusOne, pIdx) => {
                                          const plusOneId = plusOne.member_id || plusOne.rsvp_id || plusOne.id;
                                          const isPlusOneSelected = selectedMemberIds.includes(plusOneId);
                                          
                                          return (
                                            <div 
                                              key={pIdx} 
                                              className={`flex items-center justify-between p-2 bg-purple-50 rounded-lg border-l-4 border-purple-300 group ${isPlusOneSelected ? 'ring-2 ring-purple-500' : ''}`}
                                            >
                                              <div className="flex items-center gap-2">
                                                <input
                                                  type="checkbox"
                                                  checked={isPlusOneSelected}
                                                  onChange={() => toggleMemberSelection(plusOneId)}
                                                  className="w-3.5 h-3.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                                />
                                                <UserPlus className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                  <p className="text-xs font-medium truncate">
                                                    {plusOne.first_name && plusOne.last_name 
                                                      ? `${plusOne.first_name} ${plusOne.last_name}`
                                                      : plusOne.name || `+1 of ${member.first_name || member.name || 'VIP'}`}
                                                  </p>
                                                  {plusOne.email && (
                                                    <p className="text-[10px] text-gray-500 truncate">{plusOne.email}</p>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="flex items-center gap-1.5">
                                                <Badge className="bg-purple-100 text-purple-700 text-[10px] shrink-0">+1</Badge>
                                                {plusOne.rsvp_id && (
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                      const person = rsvps?.find(r => r.rsvp_id === plusOne.rsvp_id);
                                                      if (person) {
                                                        setSelectedPerson(person);
                                                        setShowPreferenceCard(true);
                                                      }
                                                    }}
                                                    className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0"
                                                  >
                                                    <Eye className="w-3 h-3" />
                                                  </Button>
                                                )}
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={async () => {
                                                    const token = localStorage.getItem("session_token");
                                                    try {
                                                      await fetch(`${API}/events/${eventId}/vip-lists/${list.list_id}/members/${plusOneId}`, {
                                                        method: "DELETE",
                                                        headers: { Authorization: `Bearer ${token}` }
                                                      });
                                                      toast.success("Guest removed from list");
                                                      fetchLists();
                                                    } catch (err) {
                                                      toast.error("Failed to remove guest");
                                                    }
                                                  }}
                                                  className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0"
                                                >
                                                  <X className="w-3 h-3 text-red-500" />
                                                </Button>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                    
                                    {/* Show placeholder for +1s not yet claimed (if member has plus_count but fewer actual +1s) */}
                                    {member.plus_count > 0 && memberPlusOnes.length < member.plus_count && (
                                      <div className="ml-12 pt-1">
                                        <div className="flex items-center gap-2 p-2 bg-purple-50/50 rounded-lg border border-dashed border-purple-200">
                                          <UserPlus className="w-3.5 h-3.5 text-purple-400" />
                                          <p className="text-xs text-purple-600">
                                            {member.plus_count - memberPlusOnes.length} more guest{member.plus_count - memberPlusOnes.length > 1 ? 's' : ''} can be invited
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                    </React.Fragment>
                                    );
                                  });
                                })()}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Speakers Sub-tab */}
            {peopleSubTab === "speakers" && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">Speakers & Panelists</h2>
                    <p className="text-sm text-gray-500">View and manage speaker information submitted via intake forms</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchSpeakers} disabled={speakersLoading}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${speakersLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>

                {speakersLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
                  </div>
                ) : speakers.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Mic className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Speakers Yet</h3>
                      <p className="text-sm text-gray-500 mb-4">
                        Assign the "Speaker" label to guests in the Attendees tab to send them the speaker intake form.
                      </p>
                      <Button variant="outline" onClick={() => setPeopleSubTab("attendees")}>
                        Go to Attendees
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {speakers.map((speaker) => (
                      <Card key={speaker.rsvp_id} className={`overflow-hidden ${speaker.speaker_intake_completed ? 'border-green-200' : 'border-yellow-200'}`}>
                        <CardContent className="p-0">
                          <div className="flex flex-col md:flex-row">
                            {/* Left: Speaker Photo & Basic Info */}
                            <div className="md:w-1/3 p-4 bg-gradient-to-br from-purple-50 to-pink-50 flex flex-col items-center justify-center">
                              {speaker.speaker_intake?.headshot_url || speaker.headshot_url ? (
                                <img 
                                  src={getImageUrl(speaker.speaker_intake?.headshot_url || speaker.headshot_url)} 
                                  alt={speaker.name}
                                  className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg mb-3"
                                />
                              ) : (
                                <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center mb-3">
                                  <User className="w-10 h-10 text-gray-400" />
                                </div>
                              )}
                              <h3 className="font-semibold text-lg text-center">{speaker.speaker_intake?.name || speaker.name}</h3>
                              {speaker.speaker_intake?.pronunciation && (
                                <p className="text-sm text-gray-500 italic">({speaker.speaker_intake.pronunciation})</p>
                              )}
                              <p className="text-sm text-purple-600 text-center">
                                {speaker.speaker_intake?.title || speaker.title || "Speaker"}
                              </p>
                              {(speaker.speaker_intake?.company || speaker.company) && (
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                  <Building2 className="w-3 h-3" />
                                  {speaker.speaker_intake?.company || speaker.company}
                                </p>
                              )}
                              <Badge className={`mt-2 ${speaker.speaker_intake_completed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                {speaker.speaker_intake_completed ? 'Intake Complete' : 'Pending Intake'}
                              </Badge>
                            </div>

                            {/* Right: Speaker Details */}
                            <div className="md:w-2/3 p-4 space-y-4">
                              {speaker.speaker_intake_completed ? (
                                <>
                                  {speaker.speaker_intake?.bio && (
                                    <div>
                                      <p className="text-xs font-medium text-gray-500 mb-1">Bio</p>
                                      <p className="text-sm text-gray-700">{speaker.speaker_intake.bio}</p>
                                    </div>
                                  )}
                                  {speaker.speaker_intake?.link_url && (
                                    <div>
                                      <p className="text-xs font-medium text-gray-500 mb-1">Featured Link</p>
                                      <a 
                                        href={ensureAbsoluteUrl(speaker.speaker_intake.link_url)} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-sm text-purple-600 hover:underline flex items-center gap-1"
                                      >
                                        <Link2 className="w-3 h-3" />
                                        {speaker.speaker_intake.link_label || speaker.speaker_intake.link_url}
                                      </a>
                                    </div>
                                  )}
                                  {speaker.speaker_intake?.talking_points && (
                                    <div>
                                      <p className="text-xs font-medium text-gray-500 mb-1">Talking Points</p>
                                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{speaker.speaker_intake.talking_points}</p>
                                    </div>
                                  )}
                                  {speaker.speaker_intake?.topics_to_avoid && (
                                    <div className="bg-red-50 rounded-lg p-3">
                                      <p className="text-xs font-medium text-red-600 mb-1">Topics to Avoid</p>
                                      <p className="text-sm text-red-700">{speaker.speaker_intake.topics_to_avoid}</p>
                                    </div>
                                  )}
                                  {speaker.speaker_intake?.promotions && (
                                    <div className="bg-purple-50 rounded-lg p-3">
                                      <p className="text-xs font-medium text-purple-600 mb-1">Promotions & Announcements</p>
                                      <p className="text-sm text-purple-700">{speaker.speaker_intake.promotions}</p>
                                    </div>
                                  )}
                                  {speaker.speaker_intake?.logo_url && (
                                    <div>
                                      <p className="text-xs font-medium text-gray-500 mb-1">Company Logo</p>
                                      <img 
                                        src={getImageUrl(speaker.speaker_intake.logo_url)} 
                                        alt="Company Logo" 
                                        className="h-12 object-contain"
                                      />
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="flex flex-col items-center justify-center h-full py-8">
                                  <AlertCircle className="w-8 h-8 text-yellow-500 mb-2" />
                                  <p className="text-sm text-gray-500 text-center">
                                    This speaker hasn't completed their intake form yet.
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    They can fill it out from their confirmation page.
                                  </p>
                                </div>
                              )}
                              {/* Contact Info */}
                              <div className="pt-3 border-t flex items-center justify-between">
                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                  {speaker.email && (
                                    <span className="flex items-center gap-1">
                                      <Mail className="w-3 h-3" />
                                      {blurEmail(speaker.email)}
                                    </span>
                                  )}
                                  {speaker.phone && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="w-3 h-3" />
                                      {speaker.phone}
                                    </span>
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const shareUrl = `${window.location.origin}/confirmation/${speaker.rsvp_id}`;
                                      navigator.clipboard.writeText(shareUrl);
                                      toast.success("Confirmation link copied!");
                                    }}
                                  >
                                    <Copy className="w-3 h-3 mr-1" />
                                    Copy Link
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Preference Cards subtab removed - per-client preference cards now accessible via the Eye / View Preference Card action on each Directory row */}
            
            {peopleSubTab === "appointments" && dashboardType === DASHBOARD_TYPES.PROVIDER && (
              techStack.acuity?.enabled ? (
                <AcuityAppointmentsPanel
                  eventId={eventId}
                  rsvps={rsvps || []}
                  onLinked={() => mutateRsvps && mutateRsvps()}
                  onConnect={() => { setActiveTab("settings"); setSettingsTab("techstack"); }}
                />
              ) : (
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-8 text-center" data-testid="acuity-disabled-state">
                  <p className="text-sm font-medium text-stone-700 mb-2">Acuity is off for this dashboard</p>
                  <p className="text-xs text-stone-500 mb-4">Connect Acuity in Settings → Tech stack to import appointments and sync clients automatically.</p>
                  <Button size="sm" variant="outline" onClick={() => { setActiveTab("settings"); setSettingsTab("techstack"); }} data-testid="acuity-disabled-connect-btn">
                    Open Tech stack
                  </Button>
                </div>
              )
            )}

            {/* Iter 9.49+ - Acuity sub-tab. Combined clients + appointments
                sync from Acuity is the ONE place this lives in the host
                dashboard's Clients tab. */}
            {peopleSubTab === "acuity" && dashboardType === DASHBOARD_TYPES.PROVIDER && techStack?.acuity?.enabled && (
              <AcuitySyncHub
                eventId={eventId}
                connected={!!(techStack?.acuity?.status === "connected" || techStack?.acuity?.scheduling_url)}
                onAfterSync={() => { mutateRsvps && mutateRsvps(); refetchImportedClients && refetchImportedClients(); }}
                onConnect={() => { setActiveTab("settings"); setSettingsTab("techstack"); }}
              />
            )}
            
            {peopleSubTab === "client_prefs" && dashboardType === DASHBOARD_TYPES.PROVIDER && (
              <div className="p-6 text-center text-sm text-gray-500" data-testid="client-prefs-moved-notice">
                Preferences Insights moved to the Insights tab.
              </div>
            )}
          </TabsContent>


          {/* ============ SERVICES TAB (Provider Dashboards) ============ */}
          <TabsContent value="services" data-testid="services-content">
            <div className="space-y-8">
              <ServicesCRUD API={API} getAuthHeaders={getAuthHeaders} eventId={eventId} category={event?.pilot_category || event?.event_type?.replace('_pilot', '') || ''} />
              
              <ProductDirectory API={API} getAuthHeaders={getAuthHeaders} category={event?.pilot_category || ''} />
              
              {/* 8.3 - Brand One-Pager link (shareable to all the brands the host works with) */}
              <div className="bg-gradient-to-r from-violet-50 to-pink-50 border border-violet-200 rounded-2xl p-4 mt-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-600 flex items-center justify-center flex-shrink-0">
                    <ShoppingBag className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">Brand value one-pager</p>
                    <p className="text-xs text-gray-600 mt-0.5">A single shareable page you send to ALL the brands you work with. Edit your story, list the products you use, customize images.</p>
                    <button
                      onClick={() => navigate(`/host/v2/${eventId}/brand-onepager`)}
                      className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-violet-700 hover:underline"
                      data-testid="open-brand-onepager-cta"
                    >
                      Open my one-pager
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
              
              <AcuityConnect API={API} getAuthHeaders={getAuthHeaders} eventId={eventId} category={event?.pilot_category || ''} />
            </div>
            <AcuityServiceMatchModal
              open={showAcuityServiceMatch}
              onClose={() => setShowAcuityServiceMatch(false)}
              eventId={eventId}
            />
          </TabsContent>

          {/* ============ QUESTIONS TAB (Instructor Events) ============ */}
          <TabsContent value="questions" data-testid="questions-content">
            <div className="space-y-6">
              {/* Iter 9.40 Sync Architecture - Host toggles which canonical preference fields to ask at RSVP */}
              <HostIntakeFieldsTab eventId={eventId} />

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="w-5 h-5" /> Question Bank
                      </CardTitle>
                      <CardDescription>All submitted questions from attendees. Curate which to share with the instructor.</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchQuestionBank} data-testid="refresh-questions-btn">
                      <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {questionBankLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="w-6 h-6 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
                    </div>
                  ) : (questionBankData?.questions?.length || 0) === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">No questions submitted yet</p>
                      <p className="text-sm mt-1">Attendees can submit questions during the RSVP process</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Question Stats */}
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-blue-700">{questionBankData.total_questions || 0}</p>
                          <p className="text-xs text-blue-600">Total</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-purple-700">
                            {questionBankData.questions?.filter(q => q.recipient === 'instructor' || q.recipient === 'both').length || 0}
                          </p>
                          <p className="text-xs text-purple-600">For Instructor</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-green-700">
                            {questionBankData.questions?.filter(q => q.shared_with_instructor).length || 0}
                          </p>
                          <p className="text-xs text-green-600">Shared</p>
                        </div>
                      </div>

                      {/* Questions List */}
                      {questionBankData.questions.map((q, idx) => (
                        <div key={idx} className="p-4 border rounded-lg bg-white hover:bg-gray-50 transition-colors">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <p className="text-sm font-medium">{q.question}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <Badge variant="outline" className="text-xs">
                                  {q.attendee_name || "Anonymous"}
                                </Badge>
                                <Badge variant={q.recipient === 'instructor' ? 'default' : q.recipient === 'both' ? 'secondary' : 'outline'} className="text-xs">
                                  For: {q.recipient || "Platform"}
                                </Badge>
                                {q.shared_with_instructor && (
                                  <Badge className="bg-green-100 text-green-700 text-xs">Shared</Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem("session_token");
                                    const res = await fetch(`${API}/events/${eventId}/question-bank/${q.id || q.question_id}`, {
                                      method: "DELETE",
                                      headers: token ? { Authorization: `Bearer ${token}` } : {}
                                    });
                                    if (res.ok) {
                                      setQuestionBankData(prev => ({
                                        ...prev,
                                        questions: prev.questions.filter((_, i) => i !== idx),
                                        total_questions: (prev.total_questions || 1) - 1
                                      }));
                                      toast.success("Question removed");
                                    }
                                  } catch (err) {
                                    toast.error("Failed to delete question");
                                  }
                                }}
                                className="p-1.5 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                                data-testid={`delete-question-${idx}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                              <Checkbox
                                checked={questionBankData.selectedQuestions?.includes(q.question_id || idx)}
                                onCheckedChange={(checked) => {
                                  setQuestionBankData(prev => ({
                                    ...prev,
                                    selectedQuestions: checked
                                      ? [...(prev.selectedQuestions || []), q.question_id || idx]
                                      : (prev.selectedQuestions || []).filter(id => id !== (q.question_id || idx))
                                  }));
                                }}
                                data-testid={`select-question-${idx}`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Share Selected Button */}
                      <div className="flex justify-end pt-4 border-t">
                        <Button
                          disabled={!(questionBankData.selectedQuestions?.length > 0)}
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem("session_token");
                              const res = await fetch(`${API}/events/${eventId}/question-bank/share`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ question_ids: questionBankData.selectedQuestions })
                              });
                              if (res.ok) {
                                toast.success("Questions shared with instructor!");
                                fetchQuestionBank();
                              }
                            } catch (err) {
                              toast.error("Failed to share questions");
                            }
                          }}
                          data-testid="share-questions-btn"
                        >
                          <Share className="w-4 h-4 mr-2" /> Share with Instructor ({questionBankData.selectedQuestions?.length || 0})
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ============ ANALYTICS TAB ============ */}
          <TabsContent value="analytics" data-testid="analytics-content">
            <GatedFeature featureKey="analytics">
            <div className="space-y-6">
              {/* Per-host 4-bucket rollup (Clients / Bookings & Revenue / Products / Brand) */}
              <HostRollupSummary eventId={eventId} />

              {/* Iter 9.40 Pass H - Host Financials (booked + pipeline + projections toggle) */}
              <HostFinancialsTab />
              
              {/* Live (real-time) analytics removed per spec - client preference graphs are now under Clients > Preferences Insights */}
              
              {/* Preferences Insights - shown for ALL dashboard types now (moved
                  out of People tab Apr 30 2026 per user request). */}
              <UpgradeBanner feature="analytics">
                <HostInsightsDashboard 
                  event={event}
                  rsvps={rsvps || []}
                  innerCircleMembers={innerCircleMembers}
                  waitlistMembers={waitlistMembers}
                  hideDietary={(() => {
                    const cat = (event?.pilot_category || '').toLowerCase();
                    const noDietaryCats = ['makeup', 'hair', 'nails', 'nail', 'lash', 'lashes', 'style', 'dispensary', 'instructor'];
                    return noDietaryCats.includes(cat) || event?.event_type === 'instructor_pilot';
                  })()}
                />
              </UpgradeBanner>
              
              {/* Instructor Insights Link & Visibility Controls */}
              {(event?.pilot_category === 'instructor' || event?.event_type === 'instructor_pilot') && (
                <Card data-testid="instructor-insights-card">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-indigo-500" />
                      <div>
                        <CardTitle className="text-base">Instructor Insights Link</CardTitle>
                        <CardDescription>Share read-only aggregate insights with your instructor. No names or emails are exposed.</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Input readOnly value={`${window.location.origin}/events/${eventId}/share/instructor`} className="text-xs font-mono bg-gray-50" />
                      <Button variant="outline" size="sm"
                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/events/${eventId}/share/instructor`); toast.success("Insights link copied!"); }}
                      >Copy</Button>
                      <Button variant="outline" size="sm"
                        onClick={() => window.open(`/events/${eventId}/share/instructor`, '_blank')}
                      >Open</Button>
                    </div>
                    <InsightVisibilityControls eventId={eventId} />
                  </CardContent>
                </Card>
              )}
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Additional Analytics */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Partner Analytics - Show only if partners exist */}
                {(partners.length > 0 || sponsors.length > 0) ? (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Partner Analytics</CardTitle>
                      <CardDescription>Link tracking from confirmation page</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {partners.map((partner, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium">{partner.name || "Partner"}</p>
                              <p className="text-xs text-gray-500">{partner.clicks || 0} clicks</p>
                            </div>
                          </div>
                        </div>
                      ))}
                      {sponsors.map((sponsor, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                              <Crown className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium">{sponsor.name || "Sponsor"}</p>
                              <p className="text-xs text-gray-500">{sponsor.clicks || 0} clicks</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Partner Analytics</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-400 text-sm text-center py-4">Add partners in Settings → Team</p>
                    </CardContent>
                  </Card>
                )}

                {/* Your Links */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Your Links</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-gray-400" />
                        <span>RSVP Link</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(`${baseUrl}/e/${eventSlug}`)}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-gray-400" />
                        <span>Waitlist Link</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(`${baseUrl}/e/${eventSlug}/waitlist`)}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {/* Dietary Share - Hide for instructor pilots and non-food provider categories */}
                    {event?.event_type !== 'instructor_pilot' && event?.pilot_category !== 'instructor' && dashboardDisplaySettings.collect?.dietary && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Utensils className="w-4 h-4 text-gray-400" />
                        <span>Dietary Share</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(`${baseUrl}/events/${eventId}/share/dietary`)}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    )}
                    {/* Accessibility Share - Hide for instructor pilots */}
                    {event?.event_type !== 'instructor_pilot' && event?.pilot_category !== 'instructor' && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Accessibility className="w-4 h-4 text-gray-400" />
                        <span>Accessibility Share</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(`${baseUrl}/events/${eventId}/share/accessibility`)}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    )}
                    {/* Music Preferences - Show for instructor pilots */}
                    {event?.event_type === 'instructor_pilot' || event?.pilot_category === 'instructor' && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Music className="w-4 h-4 text-gray-400" />
                        <span>Music Preferences</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-purple-100 text-purple-700">Active</Badge>
                        <Button variant="ghost" size="sm" onClick={() => {
                          if (techStack.tapin?.dj_insights_link) {
                            copyToClipboard(techStack.tapin.dj_insights_link);
                          } else {
                            toast.info("Connect Tap In first to get music preferences link");
                          }
                        }}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    )}
                  </CardContent>
                </Card>

                {/* Action Items - Show only if there's data and not instructor pilot */}
                {event?.event_type !== 'instructor_pilot' && event?.pilot_category !== 'instructor' && (stats.dietary_needs > 0 || stats.accessibility > 0) && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">Action Items</CardTitle>
                        <div className="flex gap-2">
                          {stats.dietary_needs > 0 && (
                            <Button size="sm" variant="outline" onClick={async () => {
                              const url = `${baseUrl}/events/${eventId}/share/dietary`;
                              if (navigator.share) {
                                try {
                                  await navigator.share({ title: 'Dietary Requirements', url });
                                } catch (err) {
                                  if (err.name !== 'AbortError') copyToClipboard(url, "Dietary link");
                                }
                              } else {
                                copyToClipboard(url, "Dietary link");
                              }
                            }}>
                              <Utensils className="w-4 h-4 mr-1" />
                              Share Dietary
                            </Button>
                          )}
                          {stats.accessibility > 0 && (
                            <Button size="sm" variant="outline" onClick={async () => {
                              const url = `${baseUrl}/events/${eventId}/share/accessibility`;
                              if (navigator.share) {
                                try {
                                  await navigator.share({ title: 'Accessibility Needs', url });
                                } catch (err) {
                                  if (err.name !== 'AbortError') copyToClipboard(url, "Accessibility link");
                                }
                              } else {
                                copyToClipboard(url, "Accessibility link");
                              }
                            }}>
                              <Accessibility className="w-4 h-4 mr-1" />
                              Share Access
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {stats.dietary_needs > 0 && (
                        <div className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
                          <span className="flex items-center gap-2">
                            <Utensils className="w-4 h-4 text-orange-600" />
                            Dietary requirements
                          </span>
                          <Badge variant="secondary">{stats.dietary_needs}</Badge>
                        </div>
                      )}
                      {stats.accessibility > 0 && (
                        <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                          <span className="flex items-center gap-2">
                            <Accessibility className="w-4 h-4 text-blue-600" />
                            Accessibility needs
                          </span>
                          <Badge variant="secondary">{stats.accessibility}</Badge>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Guest Interests - Show only from actual RSVP data */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Guest Interests</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {rsvps && rsvps.length > 0 ? (
                      <div className="space-y-3">
                        {/* Calculate interests from actual RSVP data */}
                        {(() => {
                          const interestCounts = {};
                          rsvps.forEach(r => {
                            (r.interests || []).forEach(int => {
                              interestCounts[int] = (interestCounts[int] || 0) + 1;
                            });
                          });
                          const total = rsvps.length;
                          return Object.entries(interestCounts)
                            .sort((a, b) => b[1] - a[1])
                            .slice(0, 5)
                            .map(([name, count], idx) => {
                              const percentage = Math.round((count / total) * 100);
                              return (
                                <div key={idx} className="space-y-1">
                                  <div className="flex items-center justify-between text-sm">
                                    <span>{name}</span>
                                    <span className="text-gray-500">{percentage}%</span>
                                  </div>
                                  <div className="w-full bg-gray-100 rounded-full h-2">
                                    <div 
                                      className="bg-purple-600 h-2 rounded-full" 
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            });
                        })()}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm text-center py-4">No interest data yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right Sidebar */}
              <div className="space-y-6">
                {/* Recent RSVPs */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Recent RSVPs</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {(rsvps || []).slice(0, 5).map((rsvp, idx) => {
                      // Get display name - prefer first_name + last_name, fallback to name
                      const displayName = (rsvp.first_name || rsvp.last_name) 
                        ? `${rsvp.first_name || ''} ${rsvp.last_name || ''}`.trim()
                        : (rsvp.name || "Guest");
                      return (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium overflow-hidden">
                            {(rsvp.profile_photo_url || rsvp.profile_image) ? (
                              <img src={getImageUrl(rsvp.profile_photo_url || rsvp.profile_image)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              (displayName || "G")[0].toUpperCase()
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{displayName}</p>
                            <p className="text-xs text-gray-400">{formatRelativeTime(rsvp.created_at)}</p>
                          </div>
                        </div>
                      );
                    })}
                    {(!rsvps || rsvps.length === 0) && (
                      <p className="text-gray-400 text-sm text-center py-4">No RSVPs yet</p>
                    )}
                    {rsvps && rsvps.length > 0 && (
                      <Button variant="link" className="w-full" onClick={() => setActiveTab("people")}>
                        View All Guests
                      </Button>
                    )}
                  </CardContent>
                </Card>

                {/* Team Access */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Team</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Show actual team members */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center overflow-hidden">
                          {(user?.profile_photo_url || user?.profile_image) ? (
                            <img src={getImageUrl(user.profile_photo_url || user.profile_image)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-3 h-3 text-purple-600" />
                          )}
                        </div>
                        <span className="text-sm">
                          {user?.first_name && user?.last_name 
                            ? `${user.first_name} ${user.last_name}` 
                            : user?.name || "You"}
                        </span>
                      </div>
                      <Badge variant="secondary" className="text-xs">Host</Badge>
                    </div>
                    {team.map((member, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                            {(member.profile_photo_url || member.profile_image) ? (
                              <img src={getImageUrl(member.profile_photo_url || member.profile_image)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <User className="w-3 h-3 text-gray-600" />
                            )}
                          </div>
                          <span className="text-sm">
                            {member.first_name && member.last_name 
                              ? `${member.first_name} ${member.last_name}` 
                              : member.name || member.email}
                          </span>
                        </div>
                        <Badge variant="outline" className="text-xs">{member.role}</Badge>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" className="w-full" onClick={() => { setActiveTab("settings"); setSettingsTab("team"); }}>
                      Manage Team
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
            </div>
            </GatedFeature>
          </TabsContent>

          {/* ============ SETTINGS TAB ============ */}
          <TabsContent value="settings" data-testid="settings-content">
            <div className="flex gap-6">
              {/* Iter 9.40 Slice I.d - Host Settings 3-section nav:
                  Booking · Operations · Host portal */}
              <div className="w-56 shrink-0">
                <nav className="space-y-1" data-testid="host-settings-nav">
                  {/* Section: Booking */}
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 px-3 pt-1 pb-1">Booking</p>
                  {[
                    { id: "welcome", label: "Welcome", icon: User },
                    { id: "intake", label: "Intake", icon: Heart },
                    { id: "confirmation", label: "Confirmation", icon: CheckCircle2 },
                  ].map(item => (
                    <button
                      key={item.id}
                      onClick={() => setSettingsTab(item.id)}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-left text-sm transition-colors ${
                        settingsTab === item.id ? 'bg-purple-100 text-purple-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      data-testid={`settings-${item.id}-btn`}
                    >
                      <item.icon className="w-3.5 h-3.5" />
                      {item.label}
                    </button>
                  ))}

                  <div className="border-t border-stone-200 my-3" />

                  {/* Section: Operations */}
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 px-3 pb-1">Operations</p>
                  {[
                    { id: "services", label: "Services", icon: ShoppingBag },
                    { id: "products", label: "Products", icon: Package },
                    { id: "media", label: "Media library", icon: ImageIcon },
                    { id: "host-notes", label: "Host notes", icon: FileText },
                    { id: "financials", label: "Financials", icon: DollarSign },
                  ].map(item => (
                    <button
                      key={item.id}
                      onClick={() => setSettingsTab(item.id)}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-left text-sm transition-colors ${
                        settingsTab === item.id ? 'bg-purple-100 text-purple-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      data-testid={`settings-${item.id}-btn`}
                    >
                      <item.icon className="w-3.5 h-3.5" />
                      {item.label}
                    </button>
                  ))}

                  <div className="border-t border-stone-200 my-3" />

                  {/* Phase 9 - Instructor add-on links (if this parent host has any).
                       Each entry deep-links to the instructor add-on dashboard. */}
                  {instructorAddons.length > 0 && (
                    <>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 px-3 pb-1">Instructor add-ons</p>
                      {instructorAddons.map(a => (
                        <a
                          key={a.host_dashboard_id}
                          href={`/host/v2/${a.host_dashboard_id}`}
                          className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-left text-sm text-gray-700 hover:bg-gray-100"
                          data-testid={`addon-link-${a.host_dashboard_id}`}
                        >
                          <GraduationCap className="w-3.5 h-3.5" />
                          <span className="truncate">{a.display_name || "Lessons"}</span>
                        </a>
                      ))}
                      <div className="border-t border-stone-200 my-3" />
                    </>
                  )}

                  {/* Section: Host portal */}
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 px-3 pb-1">Host portal</p>
                  {[
                    { id: "about", label: "About", icon: Building2 },
                    { id: "updates", label: "Updates", icon: Bell },
                    { id: "subscription", label: "Subscription", icon: Crown },
                  ].map(item => (
                    <button
                      key={item.id}
                      onClick={() => setSettingsTab(item.id)}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-left text-sm transition-colors ${
                        settingsTab === item.id ? 'bg-purple-100 text-purple-700 font-medium' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      data-testid={`settings-${item.id}-btn`}
                    >
                      <item.icon className="w-3.5 h-3.5" />
                      {item.label}
                    </button>
                  ))}

                  {/* Tech stack / integrations stays accessible as a utility row */}
                  <button
                    onClick={() => setSettingsTab("techstack")}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 mt-3 rounded-lg text-left text-xs transition-colors ${
                      settingsTab === "techstack" ? 'bg-purple-100 text-purple-700 font-medium' : 'text-gray-500 hover:bg-gray-50'
                    }`}
                    data-testid="settings-techstack-btn"
                  >
                    <Zap className="w-3.5 h-3.5" />
                    Tech stack
                  </button>
                  <button
                    onClick={() => setSettingsTab("help")}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-left text-xs transition-colors ${
                      settingsTab === "help" ? 'bg-purple-100 text-purple-700 font-medium' : 'text-gray-500 hover:bg-gray-50'
                    }`}
                    data-testid="settings-help-btn"
                  >
                    <HelpCircle className="w-3.5 h-3.5" />
                    Help
                  </button>

                  {/* Advanced Overrides removed (Apr 30 2026) per user
                      direction. Overrides are admin-controlled; hosts get a
                      clean 3-tab settings view (Brand · Services & Products
                      · Integrations) plus Help. */}
                  
                  {/* Danger Zone - Unpublish, Cancel, Delete */}
                  <div className="pt-4 mt-4 border-t">
                    {event?.status && !["draft", "cancelled", "completed"].includes(event.status) && (
                      <>
                        <button
                          onClick={async () => {
                            if (!window.confirm("Move event back to draft? It will no longer be publicly visible.")) return;
                            try {
                              const token = localStorage.getItem("session_token");
                              const res = await fetch(`${API}/events/${eventId}/settings`, {
                                method: "PATCH",
                                headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
                                body: JSON.stringify({ status: "draft" })
                              });
                              if (res.ok) { mutateEvent(); toast.success("Event unpublished, moved to draft"); }
                              else { toast.error("Failed to unpublish event"); }
                            } catch (err) { toast.error("Failed to unpublish event"); }
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-gray-600 hover:bg-gray-100 transition-colors"
                          data-testid="unpublish-event-btn"
                        >
                          <EyeOff className="w-4 h-4" />
                          Unpublish
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(dashboardType === DASHBOARD_TYPES.PROVIDER ? "Cancel this dashboard? All clients will be notified." : "Cancel this event? All attendees will be notified.")) return;
                            try {
                              const token = localStorage.getItem("session_token");
                              const res = await fetch(`${API}/events/${eventId}/lifecycle`, {
                                method: "POST",
                                headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
                                body: JSON.stringify({ action: "cancel" })
                              });
                              if (res.ok) { mutateEvent(); toast.success("Event cancelled"); }
                              else { toast.error("Failed to cancel event"); }
                            } catch (err) { toast.error("Failed to cancel event"); }
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-red-500 hover:bg-red-50 transition-colors"
                          data-testid="cancel-event-btn"
                        >
                          <XCircle className="w-4 h-4" />
                          Cancel {dashboardType === DASHBOARD_TYPES.PROVIDER ? "Dashboard" : "Event"}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setShowDeleteEventConfirm(true)}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-red-600 hover:bg-red-50 transition-colors"
                      data-testid="settings-delete-event-btn"
                    >
                      <AlertCircle className="w-4 h-4" />
                      Delete {dashboardType === DASHBOARD_TYPES.PROVIDER ? "Dashboard" : "Event"}
                    </button>
                  </div>
                </nav>
                
                {/* Sync Status Indicator */}
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2 px-3 py-2">
                    {saving ? (
                      <>
                        <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                        <span className="text-xs text-amber-600">Saving...</span>
                      </>
                    ) : lastSynced ? (
                      <>
                        <div className="w-2 h-2 rounded-full bg-green-400" />
                        <span className="text-xs text-green-600">
                          Synced {lastSynced.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="w-2 h-2 rounded-full bg-gray-300" />
                        <span className="text-xs text-gray-500">Not synced yet</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Settings Content */}
              <div className="flex-1">
                {/* WELCOME PAGE - Fully Editable Preview */}
                {/* ====== WELCOME TAB (includes Pre-Invite & Branding) ====== */}
                {(settingsTab === "welcome" || settingsTab === "envelope") && (
                  <Card data-testid="settings-welcome">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Eye className="w-5 h-5" />
                        Welcome Page Preview
                      </CardTitle>
                      <CardDescription>
                        {dashboardType === DASHBOARD_TYPES.PROVIDER
                          ? "Edit your booking landing page - changes sync instantly to the live page. Service-specific date/time stay on each booking, not here."
                          : "Edit your event landing page - changes sync instantly to the live page"}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {/* WYSIWYG Preview Container - WHITE BACKGROUND */}
                      <div className="bg-white rounded-xl overflow-hidden shadow-lg border">
                        <div className="max-w-lg mx-auto py-8 px-6">
                          {/* Event Logo/Image */}
                          <div className="flex justify-center mb-6">
                            <div 
                              className="relative group cursor-pointer"
                              onClick={() => document.getElementById('welcome-logo-upload').click()}
                            >
                              {/* Light category bg + category icon (R11/R27 - synced with /me hosting tile) */}
                              <div className="w-32 h-32 rounded-full overflow-hidden flex items-center justify-center border-4 border-white shadow-lg" style={{ backgroundColor: (catColors?.bgColor || '#F3F4F6') }}>
                                {event?.logo_url ? (
                                  <img 
                                    src={getImageUrl(event.logo_url)} 
                                    alt="Event Logo" 
                                    className="w-full h-full object-cover"
                                  />
                                ) : event?.cover_image ? (
                                  <img 
                                    src={getImageUrl(event.cover_image)} 
                                    alt="Event" 
                                    className="w-full h-full object-cover"
                                  />
                                ) : CatIcon ? (
                                  <CatIcon className="w-16 h-16" style={{ color: catIconColor || '#9c57f5' }} />
                                ) : (
                                  <ImageIcon className="w-12 h-12" style={{ color: catIconColor || '#9c57f5' }} />
                                )}
                              </div>
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-full flex items-center justify-center">
                                <Upload className="w-6 h-6 text-white" />
                              </div>
                              <input 
                                id="welcome-logo-upload" 
                                type="file" 
                                className="hidden" 
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    const token = localStorage.getItem('session_token');
                                    const res = await fetch(`${API}/events/${eventId}/upload-logo`, {
                                      method: 'POST',
                                      headers: { Authorization: `Bearer ${token}` },
                                      body: formData
                                    });
                                    if (res.ok) {
                                      mutateEvent();
                                      triggerGlobalSync('all', eventId);
                                      toast.success("Logo updated");
                                    }
                                  }
                                }}
                              />
                            </div>
                          </div>

                          {/* Envelope Animation Preview - Envelope with logo sticker on category background */}
                          <div className="mb-6">
                            <div className="flex items-center justify-between mb-3">
                              <p className="text-xs font-medium text-gray-700">Invitation Intro Animation</p>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">Enabled</span>
                                <Switch 
                                  checked={eventSettings.show_envelope !== false}
                                  onCheckedChange={(checked) => {
                                    const newSettings = {...eventSettings, show_envelope: checked};
                                    setEventSettings(newSettings);
                                    const token = localStorage.getItem("session_token");
                                    fetch(`${API}/events/${eventId}`, {
                                      method: "PUT",
                                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                      body: JSON.stringify({ show_envelope: checked })
                                    }).then((res) => {
                                      if (res.ok) {
                                        toast.success("Animation setting saved");
                                        setLastSynced(new Date());
                                        mutateEvent();
                                      } else {
                                        setEventSettings(prev => ({...prev, show_envelope: !checked}));
                                        toast.error("Failed to save setting");
                                      }
                                    }).catch(() => {
                                      setEventSettings(prev => ({...prev, show_envelope: !checked}));
                                      toast.error("Failed to save setting");
                                    });
                                  }}
                                />
                              </div>
                            </div>
                            
                            {/* Preview - Envelope with dynamic background based on event settings */}
                            <div 
                              className="relative mx-auto w-64 h-48 rounded-xl overflow-hidden flex items-center justify-center"
                              style={{
                                backgroundColor: eventSettings.envelope_background 
                                  ? undefined 
                                  : catIconColor,
                                backgroundImage: eventSettings.envelope_background 
                                  ? `url(${getImageUrl(eventSettings.envelope_background)})` 
                                  : undefined,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                              }}
                            >
                              {/* Envelope */}
                              <div className="relative">
                                {/* White envelope card */}
                                <div className="w-40 h-28 bg-white rounded-lg shadow-lg relative overflow-hidden">
                                  {/* Envelope flap */}
                                  <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-gray-100 to-white" 
                                       style={{ clipPath: 'polygon(0 0, 50% 100%, 100% 0)' }} />
                                  {/* Inner card */}
                                  <div className="absolute inset-2 top-4 bg-gray-50 rounded border border-gray-200" />
                                </div>
                                
                                {/* Logo Sticker - Uses same logo as main event logo */}
                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                                  <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center shadow-md overflow-hidden">
                                    {event?.logo_url || event?.logo_image ? (
                                      <img src={getImageUrl(event?.logo_url || event?.logo_image)} alt="Logo" className="w-full h-full object-cover" />
                                    ) : (
                                      <span className="text-white font-bold text-[8px] text-center leading-tight flex flex-col">
                                        {(event?.name || "PH").split(/\s+/).slice(0, 4).map((w, i) => <span key={i}>{w[0]}</span>)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* "You're Invited" text */}
                              <div className="absolute bottom-3 left-0 right-0 text-center">
                                <p className="text-white/90 text-[8px] tracking-[0.2em] uppercase drop-shadow-lg">YOU'RE INVITED</p>
                              </div>
                            </div>
                            
                            <p className="text-center text-[10px] text-gray-400 mt-2">
                              Guests see this before the RSVP form - uses event logo above
                            </p>
                            
                            {/* Custom Background Image Upload */}
                            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                              <ImageUpload
                                label="Custom Background Image"
                                value={eventSettings.envelope_background}
                                onChange={(url) => setEventSettings({...eventSettings, envelope_background: url})}
                                description={templateDefaults?.welcome?.background_image 
                                  ? "Override the template default background" 
                                  : "Add a custom background (uses gradient if empty)"}
                                previewSize="large"
                                aspectRatio="banner"
                              />
                              {templateDefaults?.welcome?.background_image && !eventSettings.envelope_background && (
                                <div className="mt-2 p-2 bg-violet-50 rounded border border-violet-200">
                                  <p className="text-xs text-violet-600 mb-1">Template default:</p>
                                  <div className="w-full h-12 rounded overflow-hidden">
                                    <img 
                                      src={templateDefaults.welcome.background_image} 
                                      alt="Template default" 
                                      className="w-full h-full object-cover opacity-70"
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Hosted By Section - Shows orgs from Team settings */}
                          {(partners?.length > 0 || team?.filter(m => m.label === 'host').length > 0) && (
                            <div className="text-center mb-4">
                              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Hosted By</p>
                              <div className="flex flex-wrap justify-center gap-3">
                                {partners?.slice(0, 3).map((org, idx) => (
                                  <div key={idx} className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full border shadow-sm">
                                    {org.logo && (
                                      <img src={getImageUrl(org.logo)} alt={org.name} className="w-5 h-5 rounded-full object-cover" />
                                    )}
                                    <span className="text-xs font-medium text-gray-700">{org.name}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Event Title - Click to Edit */}
                          <div className="text-center mb-2">
                            <Input
                              value={eventSettings.title}
                              onChange={(e) => setEventSettings({...eventSettings, title: e.target.value})}
                              className="bg-transparent border-none text-gray-900 text-2xl font-bold text-center focus:ring-2 focus:ring-purple-400 placeholder:text-gray-400"
                              placeholder="Event Title"
                              data-testid="welcome-title-input"
                            />
                            <Input
                              value={eventSettings.subtitle || ''}
                              onChange={(e) => setEventSettings({...eventSettings, subtitle: e.target.value})}
                              className="bg-transparent border-none text-gray-500 text-sm text-center focus:ring-2 focus:ring-purple-400 placeholder:text-gray-300 mt-0"
                              placeholder="Optional subtitle (e.g., A hands-on workshop)"
                              data-testid="welcome-subtitle-input"
                            />
                          </div>

                          {/* Date & Time - Click to Edit
                              Iter 9.40 Slice I.e - providers don't run "events", they run bookings.
                              Hide the page-level date/time fields for provider dashboards. */}
                          {dashboardType !== DASHBOARD_TYPES.PROVIDER && (
                          <div className="text-center text-gray-600 text-sm mb-2 flex items-center justify-center gap-2 flex-wrap">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <Input
                                type="date"
                                value={eventSettings.date}
                                onChange={(e) => setEventSettings({...eventSettings, date: e.target.value})}
                                className="bg-transparent border-none text-gray-600 w-auto text-center p-0 h-auto focus:ring-0"
                                data-testid="welcome-date-input"
                              />
                            </div>
                            <span>•</span>
                            <Input
                              type="time"
                              value={eventSettings.time}
                              onChange={(e) => setEventSettings({...eventSettings, time: e.target.value})}
                              className="bg-transparent border-none text-gray-600 w-20 text-center p-0 h-auto focus:ring-0"
                              disabled={eventSettings.time_tba}
                            />
                            <span>-</span>
                            <Input
                              type="time"
                              value={eventSettings.end_time}
                              onChange={(e) => setEventSettings({...eventSettings, end_time: e.target.value})}
                              className="bg-transparent border-none text-gray-600 w-20 text-center p-0 h-auto focus:ring-0"
                              disabled={eventSettings.time_tba}
                            />
                            <label className="flex items-center gap-1 ml-2 cursor-pointer text-xs text-gray-500">
                              <input 
                                type="checkbox"
                                checked={eventSettings.time_tba}
                                onChange={(e) => setEventSettings({
                                  ...eventSettings, 
                                  time_tba: e.target.checked,
                                  time: e.target.checked ? "" : eventSettings.time,
                                  end_time: e.target.checked ? "" : eventSettings.end_time
                                })}
                                className="w-3 h-3 rounded"
                              />
                              TBA
                            </label>
                          </div>
                          )}
                          {/* Location - Google Places Autocomplete */}
                          <div className="text-center text-gray-500 text-sm mb-4">
                            <div className="flex items-center justify-center gap-1">
                              <GooglePlacesAutocomplete
                                value={eventSettings.location}
                                onChange={(locationData) => {
                                  if (locationData) {
                                    setEventSettings({
                                      ...eventSettings, 
                                      location: locationData.address || locationData,
                                      location_lat: locationData.lat,
                                      location_lng: locationData.lng,
                                      venue_name: locationData.name,
                                      city: locationData.components?.city,
                                      state: locationData.components?.state
                                    });
                                  } else {
                                    setEventSettings({...eventSettings, location: ""});
                                  }
                                }}
                                onInputChange={(text) => setEventSettings({...eventSettings, location: text})}
                                placeholder="Search venue or address..."
                                className="max-w-md mx-auto"
                              />
                            </div>
                            <label className="flex items-center justify-center gap-2 mt-2 cursor-pointer">
                              <input 
                                type="checkbox"
                                checked={eventSettings.location_after_rsvp}
                                onChange={(e) => setEventSettings({...eventSettings, location_after_rsvp: e.target.checked})}
                                className="w-3 h-3 rounded"
                              />
                              <span className="text-[10px] text-gray-400">Location will be sent the week of the event</span>
                            </label>
                          </div>

                          {/* Description - Click to Edit */}
                          <div className="mb-6">
                            <Textarea
                              value={eventSettings.description}
                              onChange={(e) => setEventSettings({...eventSettings, description: e.target.value})}
                              className="bg-transparent border-none text-gray-600 text-center text-sm focus:ring-2 focus:ring-purple-400 resize-none placeholder:text-gray-400"
                              placeholder="Event description..."
                              rows={3}
                              data-testid="welcome-description-input"
                            />
                          </div>

                          {/* Phygital Profile Message - HARDCODED */}
                          <div className="text-center mb-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-gray-600 text-xs">
                              Share your preferences once, use them for any event.
                            </p>
                            <p className="text-gray-500 text-xs mt-1">
                              Your profile stays with you on Phygital.
                            </p>
                          </div>

                          {/* Ways to make your experience */}
                          <div className="text-center mb-4">
                            <p className="text-gray-500 text-xs mb-3">Ways to make your experience, yours</p>
                            <div className="flex justify-center gap-4">
                              {intakeSettings.collect_dietary && (
                                <div className="flex flex-col items-center gap-1">
                                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                                    <Utensils className="w-5 h-5 text-orange-600" />
                                  </div>
                                  <span className="text-[10px] text-gray-500">Dietary</span>
                                </div>
                              )}
                              {intakeSettings.collect_accessibility && (
                                <div className="flex flex-col items-center gap-1">
                                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                    <Accessibility className="w-5 h-5 text-blue-600" />
                                  </div>
                                  <span className="text-[10px] text-gray-500">Access</span>
                                </div>
                              )}
                              {intakeSettings.collect_interests && (
                                <div className="flex flex-col items-center gap-1">
                                  <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                                    <Heart className="w-5 h-5 text-pink-600" />
                                  </div>
                                  <span className="text-[10px] text-gray-500">Interests</span>
                                </div>
                              )}
                              {intakeSettings.collect_vibe && (
                                <div className="flex flex-col items-center gap-1">
                                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                    <Sparkles className="w-5 h-5 text-purple-600" />
                                  </div>
                                  <span className="text-[10px] text-gray-500">Vibe</span>
                                </div>
                              )}
                              {intakeSettings.tapin_enabled && (
                                <div className="flex flex-col items-center gap-1">
                                  <div className="w-10 h-10 rounded-full overflow-hidden">
                                    <img 
                                      src={TAP_IN_LOGO} 
                                      alt="Tap In" 
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  <span className="text-[10px] text-gray-500">Tap In</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Begin Button */}
                          <div className="text-center">
                            <div className="inline-flex items-center gap-2 px-8 py-3 bg-gray-900 text-white rounded-full font-medium">
                              Begin
                              <ChevronRight className="w-4 h-4" />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Hosted By Section Preview - with toggle and edit */}
                      <div className="mt-6 pt-4 border-t">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-gray-700">Show "Hosted By" Section</p>
                          <Switch 
                            checked={eventSettings.show_hosted_by !== false}
                            onCheckedChange={(checked) => {
                              const newSettings = {...eventSettings, show_hosted_by: checked};
                              setEventSettings(newSettings);
                              // Save immediately
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}`, {
                                method: "PUT",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ show_hosted_by: checked })
                              }).then((res) => {
                                if (res.ok) {
                                  toast.success("Setting saved");
                                  setLastSynced(new Date());
                                  mutateEvent();
                                } else {
                                  setEventSettings(prev => ({...prev, show_hosted_by: !checked}));
                                  toast.error("Failed to save");
                                }
                              }).catch(() => {
                                setEventSettings(prev => ({...prev, show_hosted_by: !checked}));
                                toast.error("Failed to save");
                              });
                            }}
                          />
                        </div>
                        {eventSettings.show_hosted_by !== false && (
                          <div className="text-center p-3 bg-gray-50 rounded-lg">
                            <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Hosted By</p>
                            
                            {/* Editable Organization Name */}
                            {editingHostedBy ? (
                              <div className="flex items-center gap-2 justify-center mb-2">
                                <Input
                                  value={hostedByName}
                                  onChange={(e) => setHostedByName(e.target.value)}
                                  placeholder="Organization name"
                                  className="max-w-[200px] text-sm h-8"
                                  autoFocus
                                />
                                <Button size="sm" variant="ghost" onClick={() => { saveHostedByName(hostedByName); }}>
                                  <Check className="w-4 h-4 text-green-600" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingHostedBy(false)}>
                                  <X className="w-4 h-4 text-gray-400" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex flex-wrap justify-center gap-2">
                                {/* Show all hosts from host_orgs */}
                                {(event?.host_orgs || []).map((org, idx) => (
                                  <div key={`hostorg-${idx}`} className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full border shadow-sm group">
                                    {org.logo && (
                                      <img src={getImageUrl(org.logo)} alt={org.name} className="w-4 h-4 rounded-full object-cover" />
                                    )}
                                    <span className="text-xs font-medium text-gray-700">{org.name}</span>
                                    <button 
                                      onClick={() => { setHostedByName(org.name); setEditingHostedBy(true); }}
                                      className="w-4 h-4 rounded-full hover:bg-gray-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <Edit3 className="w-3 h-3 text-gray-400" />
                                    </button>
                                  </div>
                                ))}
                                {/* Fallback to user org if no host_orgs */}
                                {(!event?.host_orgs || event.host_orgs.length === 0) && user?.org_name && (
                                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full border shadow-sm group">
                                    {user?.org_logo && (
                                      <img src={getImageUrl(user.org_logo)} alt={user.org_name} className="w-4 h-4 rounded-full object-cover" />
                                    )}
                                    <span className="text-xs font-medium text-gray-700">{user.org_name}</span>
                                    <button 
                                      onClick={() => { setHostedByName(user.org_name); setEditingHostedBy(true); }}
                                      className="w-4 h-4 rounded-full hover:bg-gray-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <Edit3 className="w-3 h-3 text-gray-400" />
                                    </button>
                                  </div>
                                )}
                                {/* Show co-hosts from team with label "host" */}
                                {(team || []).filter(m => m.label === "host" && m.org_name && !event?.host_orgs?.some(h => h.name === m.org_name)).map((m, idx) => (
                                  <div key={`teamhost-${idx}`} className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full border shadow-sm">
                                    <span className="text-xs font-medium text-gray-700">{m.org_name}</span>
                                  </div>
                                ))}
                                {/* Add new org button if nothing exists */}
                                {(!event?.host_orgs || event.host_orgs.length === 0) && !user?.org_name && (
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => { setHostedByName(""); setEditingHostedBy(true); }}
                                    className="text-xs"
                                  >
                                    <Plus className="w-3 h-3 mr-1" />
                                    Add Organization
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Event Partners Section Preview - with toggle */}
                      <div className="mt-4 pt-4 border-t">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-gray-700">Show "Event Partners" Section</p>
                          <Switch 
                            checked={eventSettings.show_partners !== false}
                            onCheckedChange={(checked) => {
                              const newSettings = {...eventSettings, show_partners: checked};
                              setEventSettings(newSettings);
                              // Save immediately
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}`, {
                                method: "PUT",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ show_partners: checked })
                              }).then((res) => {
                                if (res.ok) {
                                  toast.success("Setting saved");
                                  setLastSynced(new Date());
                                  mutateEvent();
                                } else {
                                  setEventSettings(prev => ({...prev, show_partners: !checked}));
                                  toast.error("Failed to save");
                                }
                              }).catch(() => {
                                setEventSettings(prev => ({...prev, show_partners: !checked}));
                                toast.error("Failed to save");
                              });
                            }}
                          />
                        </div>
                        {eventSettings.show_partners !== false && event?.partners?.length > 0 && (
                          <div className="text-center p-3 bg-gray-50 rounded-lg">
                            <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-2">Event Partners</p>
                            <div className="flex flex-wrap justify-center gap-2">
                              {event.partners.slice(0, 3).map((partner, idx) => (
                                <div key={idx} className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full border shadow-sm">
                                  {partner.logo && (
                                    <img src={getImageUrl(partner.logo)} alt={partner.name} className="w-4 h-4 rounded-full object-cover" />
                                  )}
                                  <span className="text-xs font-medium text-gray-700">{partner.name}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Learn More Link - Editable */}
                      {/* Learn More Link */}
                      <div className="mt-6 text-center">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-gray-700">Show "Learn More" Link</p>
                          <Switch 
                            checked={eventSettings.display_settings?.show_learn_more !== false}
                            onCheckedChange={(checked) => {
                              const newSettings = {
                                ...eventSettings, 
                                display_settings: { ...eventSettings.display_settings, show_learn_more: checked }
                              };
                              setEventSettings(newSettings);
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}/display-settings`, {
                                method: "PATCH",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                credentials: 'include',
                                body: JSON.stringify({ show_learn_more: checked })
                              }).then((res) => {
                                if (res.ok) {
                                  toast.success("Setting saved");
                                  mutateEvent();
                                } else {
                                  toast.error("Failed to save");
                                }
                              });
                            }}
                          />
                        </div>
                        {eventSettings.display_settings?.show_learn_more !== false && (
                        <div className="space-y-2">
                          <Input
                            value={eventSettings.learn_more_text || 'Learn more about this event'}
                            onChange={(e) => setEventSettings({...eventSettings, learn_more_text: e.target.value})}
                            className="bg-transparent border-none text-purple-600 text-center text-sm focus:ring-0 underline cursor-pointer"
                            placeholder="Link text..."
                          />
                          <Input
                            value={eventSettings.external_website_url || eventSettings.learn_more_url || ''}
                            onChange={(e) => setEventSettings({...eventSettings, external_website_url: e.target.value, learn_more_url: e.target.value})}
                            className="text-xs text-center"
                            placeholder="https://... (link URL)"
                          />
                        </div>
                        )}
                      </div>

                      {/* Additional Display Settings */}
                      <div className="mt-6 pt-4 border-t space-y-3">
                        <h4 className="text-xs font-medium text-gray-700 mb-3">Page Display Controls</h4>
                        
                        {/* Show Date/Time - only shown for non-provider events (Iter 9.40 Slice I.e) */}
                        {dashboardType !== DASHBOARD_TYPES.PROVIDER && (
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-600">Show Date & Time</p>
                          <Switch 
                            checked={eventSettings.display_settings?.show_date_time !== false}
                            onCheckedChange={(checked) => {
                              const newSettings = {
                                ...eventSettings, 
                                display_settings: { ...eventSettings.display_settings, show_date_time: checked }
                              };
                              setEventSettings(newSettings);
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}/display-settings`, {
                                method: "PATCH",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                credentials: 'include',
                                body: JSON.stringify({ show_date_time: checked })
                              }).then((res) => res.ok ? toast.success("Setting saved") : toast.error("Failed"));
                            }}
                          />
                        </div>
                        )}
                        
                        {/* Show Location */}
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-600">Show Location</p>
                          <Switch 
                            checked={eventSettings.display_settings?.show_location !== false}
                            onCheckedChange={(checked) => {
                              const newSettings = {
                                ...eventSettings, 
                                display_settings: { ...eventSettings.display_settings, show_location: checked }
                              };
                              setEventSettings(newSettings);
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}/display-settings`, {
                                method: "PATCH",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                credentials: 'include',
                                body: JSON.stringify({ show_location: checked })
                              }).then((res) => res.ok ? toast.success("Setting saved") : toast.error("Failed"));
                            }}
                          />
                        </div>
                        
                        {/* Show Description */}
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-600">Show Description</p>
                          <Switch 
                            checked={eventSettings.display_settings?.show_description !== false}
                            onCheckedChange={(checked) => {
                              const newSettings = {
                                ...eventSettings, 
                                display_settings: { ...eventSettings.display_settings, show_description: checked }
                              };
                              setEventSettings(newSettings);
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}/display-settings`, {
                                method: "PATCH",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                credentials: 'include',
                                body: JSON.stringify({ show_description: checked })
                              }).then((res) => res.ok ? toast.success("Setting saved") : toast.error("Failed"));
                            }}
                          />
                        </div>
                        
                        {/* Description Font Size */}
                        {eventSettings.display_settings?.show_description !== false && (
                          <div className="flex items-center justify-between pl-2 border-l-2 border-gray-100">
                            <p className="text-xs text-gray-600">Description Text Size</p>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-400 tabular-nums w-8 text-right">{eventSettings.display_settings?.description_font_size || 12}px</span>
                              <input
                                type="range"
                                min={10}
                                max={28}
                                step={1}
                                value={eventSettings.display_settings?.description_font_size || 12}
                                onChange={(e) => {
                                  const size = parseInt(e.target.value);
                                  setEventSettings(prev => ({
                                    ...prev,
                                    display_settings: { ...prev.display_settings, description_font_size: size }
                                  }));
                                }}
                                onMouseUp={(e) => {
                                  const size = parseInt(e.target.value);
                                  const token = localStorage.getItem("session_token");
                                  fetch(`${API}/events/${eventId}/display-settings`, {
                                    method: "PATCH",
                                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                    credentials: 'include',
                                    body: JSON.stringify({ description_font_size: size })
                                  }).then((res) => res.ok ? toast.success("Font size saved") : toast.error("Failed"));
                                }}
                                onTouchEnd={(e) => {
                                  const size = parseInt(e.target.value);
                                  const token = localStorage.getItem("session_token");
                                  fetch(`${API}/events/${eventId}/display-settings`, {
                                    method: "PATCH",
                                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                    credentials: 'include',
                                    body: JSON.stringify({ description_font_size: size })
                                  }).then((res) => res.ok ? toast.success("Font size saved") : toast.error("Failed"));
                                }}
                                className="w-24 accent-gray-900"
                                data-testid="description-font-size-range"
                              />
                            </div>
                          </div>
                        )}
                        
                        {/* Show Experience Section */}
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-600">Show "Ways to make your experience yours"</p>
                          <Switch 
                            checked={eventSettings.display_settings?.show_experience_section !== false}
                            onCheckedChange={(checked) => {
                              const newSettings = {
                                ...eventSettings, 
                                display_settings: { ...eventSettings.display_settings, show_experience_section: checked }
                              };
                              setEventSettings(newSettings);
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}/display-settings`, {
                                method: "PATCH",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                credentials: 'include',
                                body: JSON.stringify({ show_experience_section: checked })
                              }).then((res) => res.ok ? toast.success("Setting saved") : toast.error("Failed"));
                            }}
                          />
                        </div>
                        
                        {/* Show Confirmation Buttons */}
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-600">Show Confirmation Page Buttons</p>
                          <Switch 
                            checked={eventSettings.display_settings?.show_confirmation_buttons !== false}
                            onCheckedChange={(checked) => {
                              const newSettings = {
                                ...eventSettings, 
                                display_settings: { ...eventSettings.display_settings, show_confirmation_buttons: checked }
                              };
                              setEventSettings(newSettings);
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}/display-settings`, {
                                method: "PATCH",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                credentials: 'include',
                                body: JSON.stringify({ show_confirmation_buttons: checked })
                              }).then((res) => res.ok ? toast.success("Setting saved") : toast.error("Failed"));
                            }}
                          />
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">Controls Share Link & Add to Calendar buttons on confirmation page</p>
                      </div>

                      {/* Hosted By Organizations - Controlled from Team, toggled per-org */}
                      <div className="pt-6 border-t">
                        <h4 className="font-medium text-sm mb-4 flex items-center gap-2">
                          <Globe className="w-4 h-4 text-purple-500" />
                          Hosted By & Social Links
                        </h4>
                        
                        {/* Org Visibility Toggles - Source from host_orgs + Team */}
                        <div className="mb-4">
                          <Label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">Organizations shown on RSVP page</Label>
                          <div className="space-y-2">
                            {(() => {
                              // Build org list from host_orgs, team hosts, and partners
                              const allOrgs = [];
                              const seen = new Set();
                              
                              // From host_orgs (canonical)
                              (event?.host_orgs || []).forEach((org, i) => {
                                const name = org.name || org;
                                if (name && !seen.has(name)) {
                                  seen.add(name);
                                  allOrgs.push({
                                    id: `host_org_${i}`,
                                    name,
                                    logo: org.logo || null,
                                    type: 'host',
                                    source: 'host_orgs',
                                    show_in_hosted_by: partners.find(p => p.name === name)?.show_in_hosted_by !== false
                                  });
                                }
                              });
                              
                              // From team members with label "host"
                              (team || []).filter(m => (m.label === 'host' || m.role === 'host') && m.org_name).forEach((m, i) => {
                                if (!seen.has(m.org_name)) {
                                  seen.add(m.org_name);
                                  allOrgs.push({
                                    id: `team_org_${i}`,
                                    name: m.org_name,
                                    logo: m.org_logo || null,
                                    type: 'host',
                                    source: 'team',
                                    show_in_hosted_by: partners.find(p => p.name === m.org_name)?.show_in_hosted_by !== false
                                  });
                                }
                              });
                              
                              // From sponsors
                              (sponsors || []).forEach((s, i) => {
                                if (s.name && !seen.has(s.name)) {
                                  seen.add(s.name);
                                  allOrgs.push({
                                    id: `sponsor_${i}`,
                                    name: s.name,
                                    logo: s.logo_url || null,
                                    type: 'sponsor',
                                    source: 'sponsors',
                                    show_in_hosted_by: partners.find(p => p.name === s.name)?.show_in_hosted_by !== false
                                  });
                                }
                              });
                              
                              if (allOrgs.length === 0) {
                                return (
                                  <p className="text-xs text-gray-400 text-center py-3 bg-gray-50 rounded-lg">
                                    Add organizations in the <button className="text-purple-600 hover:underline" onClick={() => setSettingsTab("team")}>Team tab</button>
                                  </p>
                                );
                              }
                              
                              return allOrgs.map((org, idx) => (
                                <div key={org.id} className="flex items-center justify-between p-2.5 rounded-lg border border-gray-100 bg-gray-50/50 hover:bg-white transition-colors">
                                  <div className="flex items-center gap-2.5">
                                    {org.logo ? (
                                      <img src={org.logo} alt={org.name} className="w-7 h-7 rounded-md object-contain bg-white border border-gray-100" />
                                    ) : (
                                      <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: org.type === 'sponsor' ? '#F59E0B' : (catIconColor || '#9c57f5') }}>
                                        <span className="text-white font-bold text-[10px]">{org.name?.charAt(0)}</span>
                                      </div>
                                    )}
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">{org.name}</p>
                                      <p className="text-[10px] text-gray-400 capitalize">{org.type} · from {org.source === 'host_orgs' ? 'orgs' : org.source}</p>
                                    </div>
                                  </div>
                                  <Switch
                                    checked={org.show_in_hosted_by !== false}
                                    onCheckedChange={(v) => {
                                      // Sync to event's host_orgs
                                      const updatedHostOrgs = (event?.host_orgs || []).map(ho => {
                                        const name = ho.name || ho;
                                        if (name === org.name) return { ...ho, show_in_hosted_by: v };
                                        return ho;
                                      });
                                      // Also sync into partners for the RSVP page
                                      const existingPartner = partners.find(p => p.name === org.name);
                                      if (existingPartner) {
                                        setPartners(partners.map(p => p.name === org.name ? {...p, show_in_hosted_by: v} : p));
                                      }
                                      setEventSettings(prev => ({ ...prev, host_orgs: updatedHostOrgs }));
                                      setHasUnsavedChanges(true);
                                    }}
                                    data-testid={`toggle-org-visibility-${idx}`}
                                  />
                                </div>
                              ));
                            })()}
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs text-gray-600">Hosted By Organization</Label>
                            <Input
                              value={eventSettings.host_social_links?.org_name || event?.organization?.name || ""}
                              onChange={(e) => setEventSettings(prev => ({
                                ...prev,
                                host_social_links: { ...prev.host_social_links, org_name: e.target.value }
                              }))}
                              placeholder="Organization name"
                              data-testid="host-org-name-input"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-gray-600">Website</Label>
                              <Input
                                value={eventSettings.host_social_links?.website || ""}
                                onChange={(e) => setEventSettings(prev => ({
                                  ...prev,
                                  host_social_links: { ...prev.host_social_links, website: e.target.value }
                                }))}
                                placeholder="https://..."
                                data-testid="host-website-input"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-600">Instagram</Label>
                              <Input
                                value={eventSettings.host_social_links?.instagram || ""}
                                onChange={(e) => setEventSettings(prev => ({
                                  ...prev,
                                  host_social_links: { ...prev.host_social_links, instagram: e.target.value }
                                }))}
                                placeholder="@handle"
                                data-testid="host-instagram-input"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-600">LinkedIn</Label>
                              <Input
                                value={eventSettings.host_social_links?.linkedin || ""}
                                onChange={(e) => setEventSettings(prev => ({
                                  ...prev,
                                  host_social_links: { ...prev.host_social_links, linkedin: e.target.value }
                                }))}
                                placeholder="https://linkedin.com/..."
                                data-testid="host-linkedin-input"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-600">Twitter/X</Label>
                              <Input
                                value={eventSettings.host_social_links?.twitter || ""}
                                onChange={(e) => setEventSettings(prev => ({
                                  ...prev,
                                  host_social_links: { ...prev.host_social_links, twitter: e.target.value }
                                }))}
                                placeholder="@handle"
                                data-testid="host-twitter-input"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* View Live Page link */}
                      <div className="flex justify-end items-center pt-6">
                        <a 
                          href={`/e/${event?.slug}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-purple-600 hover:underline flex items-center gap-1"
                        >
                          <ExternalLink className="w-4 h-4" />
                          View Live Page
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* BRANDING - Now part of Welcome tab. Single source: Brand tab is canonical (Issue 4 fix). */}
                {(settingsTab === "welcome" || settingsTab === "branding") && (
                  <GatedFeature featureKey="custom_branding">
                  <Card data-testid="settings-branding">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Palette className="w-5 h-5" />
                        Branding
                      </CardTitle>
                      <CardDescription>
                        Customize text displayed throughout your event experience
                      </CardDescription>
                      {/* Single-source banner (Issue 4 fix) */}
                      <div className="mt-3 flex items-start gap-2 p-2.5 rounded-lg bg-purple-50 border border-purple-200" data-testid="branding-source-banner">
                        <Palette className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-purple-800 flex-1">
                          <strong>Logo, tagline, hero image, and host intro</strong> live on the <button onClick={() => setActiveTab("brand")} className="underline font-medium" data-testid="open-brand-tab-btn">Brand tab</button>. The fields below are advanced settings for this dashboard only.
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      
                      {/* Envelope Sticker */}
                      <div className="space-y-3">
                        <h4 className="font-medium text-sm">Envelope Sticker</h4>
                        <div className="grid gap-4">
                          <div>
                            <Label className="text-xs text-gray-600">Sticker Initials (e.g., HTHF)</Label>
                            <Input
                              value={eventSettings.custom_texts?.sticker_initials || ""}
                              onChange={(e) => setEventSettings(prev => ({
                                ...prev,
                                custom_texts: { ...prev.custom_texts, sticker_initials: e.target.value.toUpperCase() }
                              }))}
                              placeholder="Auto-generated from event name"
                              maxLength={6}
                              className="mt-1"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Leave blank to auto-generate from event name
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <Separator />
                      
                      {/* Share Settings */}
                      <div className="space-y-3">
                        <h4 className="font-medium text-sm">Share Messages</h4>
                        <div className="grid gap-4">
                          <div>
                            <Label className="text-xs text-gray-600">Share Title</Label>
                            <Input
                              value={eventSettings.custom_texts?.share_title || ""}
                              onChange={(e) => setEventSettings(prev => ({
                                ...prev,
                                custom_texts: { ...prev.custom_texts, share_title: e.target.value }
                              }))}
                              placeholder={`${event?.name || 'Event'} - Event Pass`}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-600">Share Text</Label>
                            <Input
                              value={eventSettings.custom_texts?.share_text || ""}
                              onChange={(e) => setEventSettings(prev => ({
                                ...prev,
                                custom_texts: { ...prev.custom_texts, share_text: e.target.value }
                              }))}
                              placeholder={`My pass for ${event?.name || 'this event'}`}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <Separator />
                      
                      {/* Playlist Settings */}
                      <div className="space-y-3">
                        <h4 className="font-medium text-sm">Playlist Section</h4>
                        <div className="grid gap-4">
                          <div>
                            <Label className="text-xs text-gray-600">Playlist Title</Label>
                            <Input
                              value={eventSettings.custom_texts?.playlist_title || ""}
                              onChange={(e) => setEventSettings(prev => ({
                                ...prev,
                                custom_texts: { ...prev.custom_texts, playlist_title: e.target.value }
                              }))}
                              placeholder={`${event?.name || 'Event'} Playlist`}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-600">Playlist Subtitle</Label>
                            <Input
                              value={eventSettings.custom_texts?.playlist_subtitle || ""}
                              onChange={(e) => setEventSettings(prev => ({
                                ...prev,
                                custom_texts: { ...prev.custom_texts, playlist_subtitle: e.target.value }
                              }))}
                              placeholder={`Curated by ${event?.host_name || 'the host'}`}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>
                      
                    </CardContent>
                  </Card>
                  </GatedFeature>
                )}

                {/* LOCATION RELEASE SETTINGS */}
                {settingsTab === "location" && (
                  <LocationReleaseSettings
                    location={event?.location_settings}
                    saving={saving}
                    onSave={async (settings) => {
                      try {
                        const res = await fetch(`${API}/events/${eventId}/location-settings`, {
                          method: "PUT",
                          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                          body: JSON.stringify(settings)
                        });
                        if (res.ok) {
                          toast.success("Location settings saved");
                          mutateEvent();
                        } else { toast.error("Failed to save"); }
                      } catch { toast.error("Error saving"); }
                    }}
                  />
                )}

                {/* DISPLAY SETTINGS - Price visibility, location toggle */}
                {settingsTab === "display" && (
                  <Card data-testid="settings-display">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Eye className="w-5 h-5" />
                        Display Settings
                      </CardTitle>
                      <CardDescription>
                        Control what clients see during the booking flow
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Service Price Visibility */}
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium text-sm">Show Service Prices</p>
                          <p className="text-xs text-gray-500">Display prices on the service selection step</p>
                        </div>
                        <Switch
                          checked={event?.service_settings?.show_price !== false}
                          onCheckedChange={async (val) => {
                            const token = localStorage.getItem('session_token');
                            await fetch(`${API}/events/${eventId}/service-settings`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                              body: JSON.stringify({ show_service_price: val })
                            });
                            toast.success(`Prices ${val ? 'shown' : 'hidden'} for clients`);
                            mutateEvent();
                          }}
                          data-testid="show-price-toggle"
                        />
                      </div>

                      {/* Location Visibility */}
                      <div className="p-4 border rounded-lg space-y-3">
                        <div>
                          <p className="font-medium text-sm">Location Visibility</p>
                          <p className="text-xs text-gray-500">Control when clients see your location</p>
                        </div>
                        <div className="space-y-2">
                          {[
                            { value: "show", label: "Show upfront", desc: "Location visible on welcome & confirmation" },
                            { value: "hidden", label: "Hidden until confirmed", desc: "Released when you confirm the appointment" },
                            { value: "on_confirm", label: "Release day before", desc: "Sent automatically the day before the appointment" },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              onClick={async () => {
                                const token = localStorage.getItem('session_token');
                                await fetch(`${API}/events/${eventId}/service-settings`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                  body: JSON.stringify({ location_visibility: opt.value })
                                });
                                toast.success(`Location: ${opt.label}`);
                                mutateEvent();
                              }}
                              className={`w-full p-3 rounded-lg border text-left transition-all ${
                                (event?.service_settings?.location_visibility || "show") === opt.value
                                  ? "border-purple-500 bg-purple-50"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                              data-testid={`location-vis-${opt.value}`}
                            >
                              <p className="text-sm font-medium">{opt.label}</p>
                              <p className="text-xs text-gray-500">{opt.desc}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Date Display Toggle */}
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium text-sm">Show Date & Time</p>
                          <p className="text-xs text-gray-500">Display date and time on the event page</p>
                        </div>
                        <Switch
                          checked={eventSettings.display_settings?.show_date_time !== false}
                          onCheckedChange={async (val) => {
                            const token = localStorage.getItem('session_token');
                            await fetch(`${API}/events/${eventId}`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                              body: JSON.stringify({ display_settings: { ...eventSettings.display_settings, show_date_time: val } })
                            });
                            toast.success(`Date/time ${val ? 'shown' : 'hidden'}`);
                            mutateEvent();
                          }}
                          data-testid="show-datetime-toggle"
                        />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* HELP & UPDATES - platform announcements + support ticket submission */}
                {settingsTab === "help" && (
                  <HostHelpUpdatesPanel eventId={eventId} user={user} />
                )}

                {/* CLIENT EXPERIENCE - new sub-section with stage toggles */}
                {settingsTab === "client-experience" && (
                  <>
                    <ClientExperiencePanel
                      API={API}
                      eventId={eventId}
                      event={event}
                      onSaved={() => mutateEvent && mutateEvent()}
                      onJumpToBrand={() => setSettingsTab("welcome")}
                      onJumpToMedia={() => setSettingsTab("media")}
                      onJumpToServices={() => setSettingsTab("services")}
                    />
                    {/* Phase D: Payment Links + Product Upsells (no integration needed) */}
                    <div className="mt-6">
                      <PaymentsUpsellsPanel eventId={eventId} />
                    </div>
                    {/* Personal Invites: Amayah-style native availability invites
                        (DM/text/email a custom availability link). Surfaces for
                        any host who hasn't connected Acuity, or who simply wants
                        to send one-off invites. */}
                    <div className="mt-6">
                      <HostInvitesPanel eventId={eventId} />
                    </div>
                  </>
                )}

                {/* MEDIA LIBRARY - host private inspo with master-product tagging */}
                {settingsTab === "media" && (
                  <MediaLibraryPanel
                    eventId={eventId}
                    category={(event?.pilot_category || event?.event_type?.replace?.('_pilot', '') || '').toLowerCase()}
                    masterProducts={[]}
                  />
                )}

                {/* ACUITY now lives inside Settings → Tech stack as a toggleable connection (Iter 9.47.t) */}

                {/* HOST NOTES LIBRARY - Pre/During/Post notes (Iter 9.40 Pass C) */}
                {settingsTab === "host-notes" && (
                  <div className="space-y-4" data-testid="settings-host-notes-content">
                    <div>
                      <h3 className="text-lg font-semibold">Host notes library</h3>
                      <p className="text-sm text-gray-500">
                        Stage-linked guides that show on your client's preference cards.
                        Pre = before service. During = active service. Post = after.
                      </p>
                    </div>
                    <HostNotesEditor
                      hostUserId={event?.created_by || event?.host_user_id}
                      categoryId={(event?.pilot_category || event?.event_type?.replace?.('_pilot', '') || '').toLowerCase()}
                    />
                  </div>
                )}

                {/* FINANCIALS - standalone payment-info share (Iter 9.40 Slice I.d) */}
                {settingsTab === "financials" && dashboardType === DASHBOARD_TYPES.PROVIDER && (
                  <div className="space-y-6" data-testid="settings-financials-content">
                    <div>
                      <h3 className="text-lg font-semibold">Financials</h3>
                      <p className="text-sm text-gray-500">Payment methods clients can see on the confirmation page (info-only - no integration). Default copy, deposits and price visibility live here.</p>
                    </div>
                    <HostPaymentSettingsTab eventId={eventId} />
                  </div>
                )}

                {/* SERVICES & PRODUCTS - moved from top-level Services tab */}
                {(settingsTab === "services" || settingsTab === "products") && dashboardType === DASHBOARD_TYPES.PROVIDER && (
                  <div className="space-y-8" data-testid="settings-services-content">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold">{settingsTab === "products" ? "Products" : "Services"}</h3>
                        <p className="text-sm text-gray-500">
                          {settingsTab === "products"
                            ? "Search master products & brands, link them to your services, and submit your own (admin will approve and they'll sync as Master after."
                            : "Manage your service catalog and link each service to the Phygital service catalog. Acuity-synced services land here."}
                        </p>
                      </div>
                    </div>

                    {settingsTab === "services" && techStack?.acuity?.enabled && (
                      <div className="inline-flex items-center bg-gray-100 rounded-lg p-1 text-xs">
                        <button
                          onClick={() => setServicesSubTab?.("host")}
                          className={`px-3 py-1.5 rounded-md font-medium ${
                            (servicesSubTab || "host") === "host" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"
                          }`}
                          data-testid="settings-services-host-tab"
                        >
                          Host Services
                        </button>
                        <button
                          onClick={() => setServicesSubTab?.("acuity")}
                          className={`px-3 py-1.5 rounded-md font-medium ${
                            servicesSubTab === "acuity" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"
                          }`}
                          data-testid="settings-services-acuity-tab"
                        >
                          <RefreshCw className="w-3 h-3 inline mr-1" /> Acuity
                        </button>
                      </div>
                    )}

                    {settingsTab === "services" && servicesSubTab === "acuity" && techStack?.acuity?.enabled ? (
                      <AcuityServicesPanel
                        eventId={eventId}
                        onAfterSync={() => { /* ServicesCRUD refetches on its own when remounted */ }}
                        onConnect={() => setSettingsTab("techstack")}
                      />
                    ) : (
                      <>
                        <ServicesCRUD API={API} getAuthHeaders={getAuthHeaders} eventId={eventId} category={event?.pilot_category || event?.event_type?.replace('_pilot', '') || ''} hideSyncConfigCard={true} />
                        <ProductDirectory API={API} getAuthHeaders={getAuthHeaders} category={event?.pilot_category || ''} />
                      </>
                    )}

                    <div className="bg-gradient-to-r from-violet-50 to-pink-50 border border-violet-200 rounded-2xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-violet-600 flex items-center justify-center flex-shrink-0">
                          <ShoppingBag className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900">Brand value one-pager</p>
                          <p className="text-xs text-gray-600 mt-0.5">A single shareable page you send to ALL the brands you work with. Edit your story, list the products you use, customize images.</p>
                          <button
                            onClick={() => navigate(`/host/v2/${eventId}/brand-onepager`)}
                            className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-violet-700 hover:underline"
                            data-testid="settings-open-brand-onepager-cta"
                          >
                            Open my one-pager
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* LINKS & OUTREACH - category-aware unified link hub */}
                {settingsTab === "links" && (
                  <Card data-testid="settings-links">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Link2 className="w-5 h-5" />
                        Links & Outreach
                      </CardTitle>
                      <CardDescription>
                        All your category-specific shareable links in one place. Synced to invites, RSVPs, share pages and brand one-pager.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Category-specific quick-share grid */}
                      <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100 rounded-xl p-4">
                        <p className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                          <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                          Category links for {event?.pilot_category || "your dashboard"}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {[
                            { id: "rsvp", label: "Public RSVP page", path: `/e/${eventSlug || eventId}`, audience: "Clients" },
                            { id: "invite", label: "Invite portal", path: `/i/${eventId}`, audience: "Clients" },
                            { id: "share-card", label: "Preference card share", path: `/c/${eventId}`, audience: "Clients" },
                            { id: "onepager", label: "Brand one-pager", path: `/b/${event?.host_user_id || event?.created_by || ""}`, audience: "Brands" },
                            { id: "host-portal", label: "Host portal (you)", path: `/host/v2/${eventId}`, audience: "Internal" },
                            { id: "host-onepager", label: "Edit one-pager", path: `/host/v2/${eventId}/brand-onepager`, audience: "Internal" },
                          ].map(link => {
                            const fullUrl = `${window.location.origin}${link.path}`;
                            return (
                              <div key={link.id} className="bg-white rounded-lg p-3 border border-gray-100 flex items-center justify-between gap-2" data-testid={`link-row-${link.id}`}>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900 truncate">{link.label}</p>
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <Badge variant="outline" className="text-[10px] capitalize">{link.audience}</Badge>
                                    <p className="text-[11px] text-gray-500 truncate font-mono">{link.path}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      navigator.clipboard.writeText(fullUrl);
                                      toast.success(`${link.label} link copied`);
                                    }}
                                    data-testid={`copy-link-${link.id}`}
                                  >
                                    <Copy className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => window.open(fullUrl, "_blank")}
                                    data-testid={`open-link-${link.id}`}
                                  >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Shareable Links */}
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          <Globe className="w-4 h-4 text-purple-500" />
                          Event Links
                        </h4>
                        
                        {/* Event URL/Slug Editor */}
                        <div className="p-4 bg-purple-50 rounded-lg border border-purple-100" data-testid="slug-editor-section">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700">Event URL (Slug)</label>
                          </div>
                          <div className="flex items-center gap-2 bg-white rounded-md px-3 py-2 border">
                            <span className="text-gray-500 text-sm whitespace-nowrap">{baseUrl}/e/</span>
                            <input
                              type="text"
                              defaultValue={eventSlug}
                              className="font-mono font-medium text-purple-700 bg-transparent border-none focus:outline-none focus:ring-0 w-full text-sm"
                              data-testid="slug-edit-input"
                              onBlur={(e) => {
                                const newSlug = e.target.value;
                                if (newSlug && newSlug !== eventSlug) {
                                  const cleanSlug = newSlug.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
                                  if (cleanSlug.length < 3) {
                                    toast.error("Slug must be at least 3 characters");
                                    e.target.value = eventSlug;
                                    return;
                                  }
                                  fetch(`${API}/events/${eventId}/settings`, {
                                    method: 'PATCH',
                                    headers: { 'Content-Type': 'application/json' },
                                    credentials: 'include',
                                    body: JSON.stringify({ slug: cleanSlug })
                                  })
                                  .then(res => {
                                    if (res.ok) {
                                      toast.success("Event URL updated! New link: /e/" + cleanSlug);
                                      setTimeout(() => window.location.reload(), 1000);
                                    } else {
                                      toast.error("Failed to update URL. The slug may already be taken.");
                                      e.target.value = eventSlug;
                                    }
                                  })
                                  .catch(() => { toast.error("Failed to update URL"); e.target.value = eventSlug; });
                                }
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-2">Click to edit. Press Enter or click away to save. Caution: existing links will stop working if changed.</p>
                        </div>
                        
                        <div className="space-y-3">
                          {/* Event Page */}
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <Globe className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">Event Page</p>
                                <p className="text-xs text-gray-500 truncate max-w-[200px]">{baseUrl}/e/{eventSlug}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(`${baseUrl}/e/${eventSlug}`); toast.success("Link copied!"); }}>
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => window.open(`/e/${eventSlug}`, '_blank')}>
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          {/* RSVP Direct Link */}
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">RSVP Page</p>
                                <p className="text-xs text-gray-500 truncate max-w-[200px]">{baseUrl}/e/{eventSlug}?rsvp=true</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(`${baseUrl}/e/${eventSlug}?rsvp=true`); toast.success("Link copied!"); }}>
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => window.open(`/e/${eventSlug}?rsvp=true`, '_blank')}>
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Sponsorship Links */}
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          <Crown className="w-4 h-4 text-amber-500" />
                          Sponsorship Resources
                        </h4>
                        {eventSettings?.sponsorship_links?.length > 0 ? (
                          <div className="space-y-3">
                            {eventSettings.sponsorship_links.map((link, idx) => (
                              <div key={idx} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                                    <Link2 className="w-5 h-5 text-amber-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">{link.title}</p>
                                    <p className="text-xs text-gray-500">{link.description}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(link.url); toast.success("Link copied!"); }}>
                                    <Copy className="w-4 h-4" />
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={() => window.open(link.url, '_blank')}>
                                    <ExternalLink className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={async () => {
                                    const updatedLinks = eventSettings.sponsorship_links.filter((_, i) => i !== idx);
                                    try {
                                      const token = localStorage.getItem("session_token");
                                      await axios.patch(`${API}/events/${eventId}/settings`, { sponsorship_links: updatedLinks }, { headers: { Authorization: `Bearer ${token}` }});
                                      setEventSettings(prev => ({...prev, sponsorship_links: updatedLinks}));
                                      toast.success("Link removed");
                                    } catch (err) {
                                      toast.error("Failed to remove link");
                                    }
                                  }}>
                                    <X className="w-4 h-4 text-red-500" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 text-center py-4">No sponsorship links added yet</p>
                        )}
                        
                        {/* Add new sponsorship link */}
                        <div className="p-4 border-2 border-dashed rounded-lg">
                          <p className="font-medium text-sm mb-3">Add Sponsorship Link</p>
                          <div className="space-y-3">
                            <Input placeholder="Link title (e.g., Sponsorship Deck)" id="settings-sponsorship-title" className="text-sm" />
                            <Input placeholder="Description (optional)" id="settings-sponsorship-desc" className="text-sm" />
                            <Input placeholder="URL (e.g., https://notion.so/...)" id="settings-sponsorship-url" className="text-sm" />
                            <Button className="w-full" onClick={async () => {
                              const title = document.getElementById('settings-sponsorship-title')?.value;
                              const description = document.getElementById('settings-sponsorship-desc')?.value;
                              const url = document.getElementById('settings-sponsorship-url')?.value;
                              if (!title || !url) { toast.error("Please enter a title and URL"); return; }
                              const newLink = { title, description: description || '', url };
                              const updatedLinks = [...(eventSettings?.sponsorship_links || []), newLink];
                              try {
                                const token = localStorage.getItem("session_token");
                                await axios.patch(`${API}/events/${eventId}/settings`, { sponsorship_links: updatedLinks }, { headers: { Authorization: `Bearer ${token}` }});
                                setEventSettings(prev => ({...prev, sponsorship_links: updatedLinks}));
                                document.getElementById('settings-sponsorship-title').value = '';
                                document.getElementById('settings-sponsorship-desc').value = '';
                                document.getElementById('settings-sponsorship-url').value = '';
                                toast.success("Sponsorship link added!");
                              } catch (err) {
                                toast.error("Failed to add link");
                              }
                            }}>
                              <Plus className="w-4 h-4 mr-2" />
                              Add Link
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Iter 9.40 Slice I.f - Host's Preference-field toggles
                    Drives: (1) what shows on the booking intake form,
                    (2) what shows on the preference card,
                    (3) which fields appear in Insights distribution. */}
                {settingsTab === "intake" && categoryTemplate?.template?.preference_fields && (
                  <Card data-testid="settings-pref-field-toggles" className="mb-4">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Heart className="w-5 h-5 text-purple-600" />
                        Preference fields to collect
                      </CardTitle>
                      <CardDescription>
                        Pick which preference sections you want to ask each client. The same toggles drive your Insights charts and the universal preference card.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <PreferenceFieldToggles
                        eventId={eventId}
                        category={event?.pilot_category || event?.category || "makeup"}
                        fields={categoryTemplate.template.preference_fields}
                      />
                    </CardContent>
                  </Card>
                )}

                {/* Phase 9 / Iter 9.47.i - Per-experience preference toggles.
                     One card per Master Experience the host offers. Lets the
                     host pick exactly which preferences clients answer per
                     experience + flip the universal Allergies/Dietary/Accessibility
                     toggles. Saves to /api/host/{provider_id}/experiences/{eid}/preference-config. */}
                {(settingsTab === "intake") && services.length > 0 && (
                  <Card data-testid="settings-experience-preferences">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        Per-experience preferences
                      </CardTitle>
                      <CardDescription className="text-xs">
                        For each experience you offer, choose which preferences clients answer when they're invited.
                        Universal toggles (Allergies / Dietary / Accessibility) appear at the bottom of the form near submit.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {services.slice(0, 8).map(svc => (
                        <ExperiencePreferenceConfig
                          key={svc.id}
                          providerId={svc.provider_id || event?.provider_id}
                          experienceId={svc.experience_id || svc.id}
                          experienceLabel={svc.name || svc.label}
                          category={event?.pilot_category || event?.category || "makeup"}
                        />
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* RSVP FLOW STEPS - Audience intake from admin template */}
                {(settingsTab === "intake") && (categoryTemplate?.template_rsvp_steps?.length > 0 || event?.intake_settings?.audience_steps?.length > 0) && (
                  <Card data-testid="settings-rsvp-flow-template">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <ListChecks className="w-5 h-5" style={{ color: categoryTemplate?.template?.primary_color || '#6366f1' }} />
                            Audience RSVP Intake
                          </CardTitle>
                          <CardDescription>
                            What participants fill out when RSVP'ing. Synced from the <strong className="capitalize">{categoryTemplate?.category}</strong> admin template.
                          </CardDescription>
                        </div>
                        <Badge 
                          className="text-xs" 
                          style={{ backgroundColor: `${categoryTemplate?.template?.primary_color || '#6366f1'}20`, color: categoryTemplate?.template?.primary_color || '#6366f1' }}
                        >
                          Synced from Admin
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* RSVP Flow Visual */}
                      {categoryTemplate?.template_rsvp_steps?.length > 0 && (
                        <div className="flex items-center gap-2 overflow-x-auto pb-2">
                          {categoryTemplate.template_rsvp_steps.map((step, idx) => (
                            <div key={idx} className="flex items-center gap-2 shrink-0">
                              <div className="flex items-center gap-2 px-3 py-2 rounded-xl border bg-white shadow-sm">
                                <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold" 
                                  style={{ backgroundColor: categoryTemplate?.template?.primary_color || '#6366f1' }}>
                                  {step.step ?? idx}
                                </div>
                                <span className="text-xs font-medium whitespace-nowrap">{step.name}</span>
                                <span className="text-[10px] text-gray-400">{(step.fields || []).length} fields</span>
                              </div>
                              {idx < categoryTemplate.template_rsvp_steps.length - 1 && (
                                <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Audience Steps Detail */}
                      <div className="space-y-3">
                        {(event?.intake_settings?.audience_steps || categoryTemplate?.template_rsvp_steps || []).map((section, sIdx) => (
                          <details key={sIdx} className="group border rounded-lg overflow-hidden" data-testid={`audience-step-${sIdx}`}>
                            <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                                  style={{ backgroundColor: categoryTemplate?.template?.primary_color || '#6366f1' }}>
                                  {section.order || section.step || sIdx + 1}
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{section.name}</p>
                                  <p className="text-xs text-gray-500">{section.description || `${(section.fields || []).length} fields`}</p>
                                </div>
                              </div>
                              <ChevronRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
                            </summary>
                            <div className="border-t bg-gray-50/50 p-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {(section.fields || []).map((field, fIdx) => {
                                  const fieldLabel = typeof field === 'string' ? field : field.label;
                                  const fieldType = typeof field === 'string' ? 'text' : field.type;
                                  const fieldRequired = typeof field === 'string' ? false : field.required;
                                  const fieldOptions = typeof field === 'string' ? null : field.options;
                                  return (
                                    <div key={fIdx} className="flex items-start gap-2 p-3 bg-white rounded-lg border text-sm">
                                      <div className="mt-0.5">
                                        <div className={`w-2 h-2 rounded-full ${fieldRequired ? 'bg-red-400' : 'bg-gray-300'}`} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-gray-800 capitalize">{fieldLabel?.replace(/_/g, ' ')}</p>
                                        <div className="flex items-center gap-2 mt-0.5">
                                          <Badge variant="outline" className="text-[10px] capitalize">{fieldType}</Badge>
                                          {fieldRequired && <Badge variant="outline" className="text-[10px] text-red-600 border-red-200">Required</Badge>}
                                        </div>
                                        {fieldOptions && fieldOptions.length > 0 && (
                                          <p className="text-xs text-gray-400 mt-1 truncate">Options: {fieldOptions.slice(0, 4).join(', ')}{fieldOptions.length > 4 ? ` +${fieldOptions.length - 4} more` : ''}</p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </details>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* INSTRUCTOR INTAKE STEPS - What instructors fill out when onboarded to this event */}
                {(settingsTab === "intake") && event?.intake_settings?.instructor_steps?.length > 0 && (
                  <Card data-testid="settings-instructor-intake">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <GraduationCap className="w-5 h-5 text-blue-500" />
                            Instructor Intake
                          </CardTitle>
                          <CardDescription>
                            What instructors fill out when onboarded to this event. {event.intake_settings.instructor_steps.length} sections.
                          </CardDescription>
                        </div>
                        <Badge className="text-xs bg-blue-50 text-blue-600">Instructor-facing</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {event.intake_settings.instructor_steps.map((section, sIdx) => (
                          <details key={sIdx} className="group border rounded-lg overflow-hidden" data-testid={`instructor-step-${sIdx}`}>
                            <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold bg-blue-500">
                                  {section.order || sIdx + 1}
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{section.name}</p>
                                  <p className="text-xs text-gray-500">{section.description} - {(section.fields || []).length} fields</p>
                                </div>
                              </div>
                              <ChevronRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
                            </summary>
                            <div className="border-t bg-gray-50/50 p-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {(section.fields || []).map((field, fIdx) => (
                                  <div key={fIdx} className="flex items-start gap-2 p-3 bg-white rounded-lg border text-sm">
                                    <div className="mt-0.5">
                                      <div className={`w-2 h-2 rounded-full ${field.required ? 'bg-red-400' : 'bg-gray-300'}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-gray-800">{field.label}</p>
                                      <div className="flex items-center gap-2 mt-0.5">
                                        <Badge variant="outline" className="text-[10px] capitalize">{field.type}</Badge>
                                        {field.required && <Badge variant="outline" className="text-[10px] text-red-600 border-red-200">Required</Badge>}
                                      </div>
                                      {field.options && field.options.length > 0 && (
                                        <p className="text-xs text-gray-400 mt-1 truncate">Options: {field.options.slice(0, 4).join(', ')}{field.options.length > 4 ? ` +${field.options.length - 4} more` : ''}</p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </details>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* PEOPLE INFO - Now part of Intake tab */}
                {(settingsTab === "intake" || settingsTab === "people-info") && (
                  <Card data-testid="settings-people-info">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        People Info
                      </CardTitle>
                      <CardDescription>
                        Choose what information to collect from attendees. Each field shows in the preview below.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      
                      {/* === BASIC INFO SECTION === */}
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          <User className="w-4 h-4 text-purple-500" />
                          Basic Information
                        </h4>
                        
                        {/* First Name and Last Name - Always required */}
                        <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">First Name & Last Name</span>
                              <span className="text-[10px] bg-purple-200 text-purple-700 px-2 py-0.5 rounded-full">Required</span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">Always collected for identification - stored separately</p>
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            <div className="bg-white rounded-lg p-2 border text-sm text-gray-400">First name</div>
                            <div className="bg-white rounded-lg p-2 border text-sm text-gray-400">Last name</div>
                          </div>
                        </div>
                        
                        {/* Email - Always required */}
                        <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">Email Address</span>
                              <span className="text-[10px] bg-purple-200 text-purple-700 px-2 py-0.5 rounded-full">Required</span>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">For confirmation page link and event updates</p>
                          <div className="mt-2 bg-white rounded-lg p-2 border text-sm text-gray-400">guest@email.com</div>
                        </div>
                        
                        {/* Phone */}
                        <div className={`p-4 rounded-xl border ${intakeSettings.collect_phone ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">Phone Number</span>
                            <Switch
                              checked={intakeSettings.collect_phone !== false}
                              onCheckedChange={(v) => setIntakeSettings({...intakeSettings, collect_phone: v})}
                            />
                          </div>
                          <p className="text-xs text-gray-500">For SMS updates and check-in</p>
                          {intakeSettings.collect_phone !== false && (
                            <>
                              <div className="mt-2 flex items-center gap-2">
                                <input 
                                  type="checkbox" 
                                  id="require_phone"
                                  checked={intakeSettings.require_phone === true}
                                  onChange={(e) => setIntakeSettings({...intakeSettings, require_phone: e.target.checked})}
                                  className="rounded border-gray-300"
                                />
                                <label htmlFor="require_phone" className="text-xs text-gray-600">Required field</label>
                              </div>
                              <div className="mt-2 bg-gray-50 rounded-lg p-2 border text-sm text-gray-400">+1 (555) 000-0000</div>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* === PROFESSIONAL INFO === */}
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-blue-500" />
                          Professional Info
                        </h4>
                        
                        {/* Company */}
                        <div className={`p-4 rounded-xl border ${intakeSettings.collect_company ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">Company / Organization</span>
                            <Switch
                              checked={intakeSettings.collect_company !== false}
                              onCheckedChange={(v) => setIntakeSettings({...intakeSettings, collect_company: v})}
                            />
                          </div>
                          <p className="text-xs text-gray-500">Where they work or what org they represent</p>
                          {intakeSettings.collect_company !== false && (
                            <div className="mt-2 bg-gray-50 rounded-lg p-2 border text-sm text-gray-400">Company name</div>
                          )}
                        </div>
                        
                        {/* Title */}
                        <div className={`p-4 rounded-xl border ${intakeSettings.collect_title ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">Job Title / Role</span>
                            <Switch
                              checked={intakeSettings.collect_title !== false}
                              onCheckedChange={(v) => setIntakeSettings({...intakeSettings, collect_title: v})}
                            />
                          </div>
                          <p className="text-xs text-gray-500">Their professional role or position</p>
                          {intakeSettings.collect_title !== false && (
                            <div className="mt-2 bg-gray-50 rounded-lg p-2 border text-sm text-gray-400">CEO, Designer, etc.</div>
                          )}
                        </div>
                        
                        {/* Location Toggle - Shows configured cities/countries */}
                        <div className={`p-4 rounded-xl border ${intakeSettings.collect_location ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-blue-500" />
                              <span className="font-medium text-gray-900">Location</span>
                            </div>
                            <Switch
                              checked={intakeSettings.collect_location === true}
                              onCheckedChange={(v) => {
                                const updated = {...intakeSettings, collect_location: v, collect_city: v};
                                setIntakeSettings(updated);
                                saveIntakeSettings(updated);
                              }}
                              data-testid="toggle-collect-location"
                            />
                          </div>
                          <p className="text-xs text-gray-500">Show a city/country dropdown on the Welcome step for all RSVPs</p>
                          {intakeSettings.collect_location === true && (
                            <div className="mt-3 space-y-2">
                              <p className="text-xs text-gray-600 font-medium">Pre-configured cities will appear as dropdown options</p>
                              <div className="flex flex-wrap gap-1.5">
                                {(intakeSettings.location_options || []).slice(0, 8).map((loc, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] rounded-full border border-blue-200">
                                    {loc}
                                  </span>
                                ))}
                                {(intakeSettings.location_options || []).length === 0 && (
                                  <span className="text-[10px] text-gray-400">All configured cities from admin will be shown</span>
                                )}
                                {(intakeSettings.location_options || []).length > 8 && (
                                  <span className="text-[10px] text-gray-400">+{(intakeSettings.location_options || []).length - 8} more</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* What Best Describes You - MULTIPLE CHOICE */}
                        {/* Hide for Instructor Pilot - Builder Profile replaces this */}
                        {event?.pilot_category !== 'instructor' && event?.pilot_category !== 'workshop' && (
                        <div className={`p-4 rounded-xl border ${intakeSettings.collect_describes_you ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className="font-medium text-gray-900">What best describes you?</span>
                              <span className="ml-2 text-[10px] bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">Multiple Choice</span>
                            </div>
                            <Switch
                              checked={intakeSettings.collect_describes_you !== false}
                              onCheckedChange={(v) => setIntakeSettings({...intakeSettings, collect_describes_you: v})}
                            />
                          </div>
                          <p className="text-xs text-gray-500 mb-3">Guests select one option to self-identify</p>
                          {intakeSettings.collect_describes_you !== false && (
                            <div className="flex flex-wrap gap-2">
                              {(intakeSettings.describes_you_options || ['Host', 'Client', 'Guest', 'Team', 'Press', 'Other']).map((opt, idx) => (
                                <span key={idx} className="px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-700">{opt}</span>
                              ))}
                              <button className="px-3 py-1 rounded-full text-xs text-white hover:opacity-90" style={{ backgroundColor: catIconColor || '#9c57f5' }}>+ Add option</button>
                            </div>
                          )}
                        </div>
                        )}
                      </div>
                      
                      {/* === SOCIAL LINKS === */}
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          <Globe className="w-4 h-4 text-pink-500" />
                          Social Links
                          <span className="text-xs text-gray-400 font-normal">(clickable on their profile)</span>
                        </h4>
                        
                        <div className="grid grid-cols-3 gap-3">
                          {/* Instagram */}
                          <div className={`p-3 rounded-xl border text-center ${intakeSettings.collect_instagram ? 'bg-gradient-to-br from-purple-50 to-pink-50 border-pink-200' : 'bg-gray-50 border-gray-200'}`}>
                            <div className="flex justify-center mb-2">
                              <Switch
                                checked={intakeSettings.collect_instagram !== false}
                                onCheckedChange={(v) => setIntakeSettings({...intakeSettings, collect_instagram: v})}
                              />
                            </div>
                            <p className="font-medium text-sm">Instagram</p>
                            <p className="text-xs text-gray-500">@handle</p>
                          </div>
                          
                          {/* TikTok */}
                          <div className={`p-3 rounded-xl border text-center ${intakeSettings.collect_tiktok ? 'bg-gray-900 text-white border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
                            <div className="flex justify-center mb-2">
                              <Switch
                                checked={intakeSettings.collect_tiktok !== false}
                                onCheckedChange={(v) => setIntakeSettings({...intakeSettings, collect_tiktok: v})}
                              />
                            </div>
                            <p className={`font-medium text-sm ${intakeSettings.collect_tiktok ? 'text-white' : ''}`}>TikTok</p>
                            <p className={`text-xs ${intakeSettings.collect_tiktok ? 'text-gray-400' : 'text-gray-500'}`}>@handle</p>
                          </div>
                          
                          {/* LinkedIn */}
                          <div className={`p-3 rounded-xl border text-center ${intakeSettings.collect_linkedin ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                            <div className="flex justify-center mb-2">
                              <Switch
                                checked={intakeSettings.collect_linkedin !== false}
                                onCheckedChange={(v) => setIntakeSettings({...intakeSettings, collect_linkedin: v})}
                              />
                            </div>
                            <p className="font-medium text-sm">LinkedIn</p>
                            <p className="text-xs text-gray-500">Profile URL</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* === INNER CIRCLE - Special Section (R27: solid category color, no purple→pink gradient) === */}
                      <div className="p-5 rounded-2xl text-white" style={{ backgroundColor: catIconColor || '#9c57f5' }}>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-bold text-lg flex items-center gap-2">
                              <Crown className="w-5 h-5 text-yellow-300" />
                              Inner Circle
                            </h4>
                            <p className="text-sm text-purple-200 mt-1">
                              People who opt-in get added to this event's exclusive newsletter list
                            </p>
                          </div>
                          <Switch
                            checked={intakeSettings.collect_inner_circle !== false}
                            onCheckedChange={(v) => setIntakeSettings({...intakeSettings, collect_inner_circle: v})}
                            className="data-[state=checked]:bg-yellow-400"
                          />
                        </div>
                        {intakeSettings.collect_inner_circle !== false && (
                          <div className="bg-white/10 rounded-xl p-3 mt-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium">Add me to the inner circle</p>
                                <p className="text-xs text-purple-200">Get exclusive updates, early access, and VIP invites</p>
                              </div>
                              <div className="w-10 h-6 bg-purple-400 rounded-full"></div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* === PRE-INVITE FORM CONFIGURATION === */}
                      <div className="space-y-4 pt-4 border-t">
                        <h4 className="font-medium text-gray-900 flex items-center gap-2">
                          <Mail className="w-4 h-4 text-violet-500" />
                          Pre-Invite Form Fields
                          <span className="text-xs text-gray-400 font-normal">(Before RSVP opens)</span>
                        </h4>
                        <p className="text-xs text-gray-500">Configure which fields appear on the pre-invite signup form and whether they're required.</p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {[
                            { key: "first_name", label: "First Name", locked: true },
                            { key: "last_name", label: "Last Name", locked: true },
                            { key: "email", label: "Email", locked: true },
                            { key: "company", label: "Company", locked: false },
                            { key: "title", label: "Title", locked: false },
                            { key: "interests", label: "Interests", locked: false }
                          ].map(field => {
                            const fieldConfig = intakeSettings.pre_invite_fields?.[field.key] || { enabled: true, required: field.locked };
                            return (
                              <div key={field.key} className={`p-3 rounded-lg border ${field.locked ? 'bg-violet-50 border-violet-200' : 'bg-white border-gray-200'}`}>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">{field.label}</span>
                                    {field.locked && (
                                      <span className="text-[10px] bg-violet-200 text-violet-700 px-1.5 py-0.5 rounded">Always Required</span>
                                    )}
                                  </div>
                                  {!field.locked && (
                                    <div className="flex items-center gap-2">
                                      <Switch
                                        checked={fieldConfig.enabled !== false}
                                        onCheckedChange={(v) => setIntakeSettings(prev => ({
                                          ...prev,
                                          pre_invite_fields: {
                                            ...prev.pre_invite_fields,
                                            [field.key]: { ...fieldConfig, enabled: v }
                                          }
                                        }))}
                                        data-testid={`preinvite-field-${field.key}`}
                                      />
                                    </div>
                                  )}
                                </div>
                                {!field.locked && fieldConfig.enabled !== false && (
                                  <div className="mt-2 flex items-center gap-2">
                                    <Checkbox
                                      checked={fieldConfig.required === true}
                                      onCheckedChange={(v) => setIntakeSettings(prev => ({
                                        ...prev,
                                        pre_invite_fields: {
                                          ...prev.pre_invite_fields,
                                          [field.key]: { ...fieldConfig, required: v }
                                        }
                                      }))}
                                    />
                                    <span className="text-xs text-gray-500">Required field</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                    </CardContent>
                  </Card>
                )}

                {/* DEFAULTS - Now part of Intake tab */}
                {(settingsTab === "intake" || settingsTab === "defaults") && (
                  <Card data-testid="settings-defaults">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Settings className="w-5 h-5" />
                        Event Settings & Defaults
                      </CardTitle>
                      <CardDescription>
                        Control event access and customize the intake experience.
                        {categoryTemplate?.category && (
                          <span className="inline-flex items-center gap-1 ml-2 text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full" data-testid="template-inheritance-badge">
                            Inherits from <strong className="capitalize">{categoryTemplate.category}</strong> template
                          </span>
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-8">
                      {/* RSVP Status Section */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 rounded-xl border-2 border-gray-200 bg-white">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${event?.rsvps_closed ? 'bg-red-100' : 'bg-green-100'}`}>
                              {event?.rsvps_closed ? (
                                <XCircle className="w-5 h-5 text-red-600" />
                              ) : (
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                              )}
                            </div>
                            <div>
                              <h3 className="font-medium">RSVP Status</h3>
                              <p className="text-sm text-gray-500">
                                {event?.rsvps_closed 
                                  ? "RSVPs are closed - no new registrations allowed" 
                                  : "RSVPs are open - guests can register"}
                              </p>
                            </div>
                          </div>
                          <Switch
                            checked={!event?.rsvps_closed}
                            onCheckedChange={(isOpen) => {
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}`, {
                                method: "PUT",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ rsvps_closed: !isOpen })
                              }).then((res) => {
                                if (res.ok) {
                                  toast.success(isOpen ? "RSVPs are now open" : "RSVPs are now closed");
                                  setLastSynced(new Date());
                                  mutateEvent();
                                } else {
                                  toast.error("Failed to update RSVP status");
                                }
                              }).catch(() => {
                                toast.error("Failed to update RSVP status");
                              });
                            }}
                            data-testid="toggle-rsvp-status"
                          />
                        </div>
                      </div>

                      {/* Dietary Section - Hide for instructor pilots */}
                      {event?.event_type !== 'instructor_pilot' && event?.pilot_category !== 'instructor' && (
                      <>
                      <Separator />

                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium flex items-center gap-2">
                              <Utensils className="w-4 h-4 text-orange-500" />
                              Dietary Preferences
                              {categoryTemplate?.detected_overrides?.includes("collect_dietary") && (
                                <>
                                  <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full" data-testid="dietary-override-badge">Override</span>
                                  <button onClick={() => handleResetToTemplate(["collect_dietary"])} className="text-[10px] text-blue-600 hover:text-blue-700 underline" data-testid="reset-dietary-btn">Reset</button>
                                </>
                              )}
                              {categoryTemplate?.template_intake?.collect_dietary !== undefined && !categoryTemplate?.detected_overrides?.includes("collect_dietary") && (
                                <span className="text-[10px] bg-blue-50 text-blue-500 px-1.5 py-0.5 rounded-full">Template</span>
                              )}
                            </h3>
                            <p className="text-sm text-gray-500">Food restrictions & preferences</p>
                          </div>
                          <Switch
                            checked={intakeSettings.collect_dietary}
                            onCheckedChange={(v) => {
                              const newSettings = {...intakeSettings, collect_dietary: v};
                              setIntakeSettings(newSettings);
                              // Save to backend
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}`, {
                                method: "PUT",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ intake_settings: newSettings })
                              }).then((res) => {
                                if (res.ok) {
                                  toast.success("Dietary setting saved");
                                  setLastSynced(new Date());
                                  // Only mutate after confirmed save
                                  mutateEvent();
                                } else {
                                  // Revert on error
                                  setIntakeSettings(prev => ({...prev, collect_dietary: !v}));
                                  toast.error("Failed to save setting");
                                }
                              }).catch(() => {
                                setIntakeSettings(prev => ({...prev, collect_dietary: !v}));
                                toast.error("Failed to save setting");
                              });
                            }}
                            data-testid="toggle-dietary"
                          />
                        </div>
                        
                        {/* Dietary Preview */}
                        <div className={`p-6 rounded-xl border-2 ${intakeSettings.collect_dietary ? 'border-orange-200 bg-orange-50' : 'border-gray-200 bg-gray-50 opacity-50'}`}>
                          <p className="text-xs text-gray-500 mb-1">STEP 2 OF 4</p>
                          <h4 className="font-semibold text-lg mb-1">Your Dietary</h4>
                          <p className="text-sm text-gray-600 mb-4">Share your food preferences</p>
                          
                          <div className="space-y-2">
                            {[
                              { id: 'none', label: 'No restrictions', default: true },
                              { id: 'vegan', label: 'Vegan' },
                              { id: 'vegetarian', label: 'Vegetarian' },
                              { id: 'gluten-free', label: 'Gluten-free' },
                              { id: 'dairy-free', label: 'Dairy-free' },
                              { id: 'nut-allergy', label: 'Nut allergy' },
                              { id: 'kosher', label: 'Kosher' },
                              { id: 'halal', label: 'Halal' }
                            ].map(option => (
                              <label key={option.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/50 cursor-pointer">
                                <div className={`w-5 h-5 rounded-full border-2 ${option.default ? 'border-orange-500 bg-orange-500' : 'border-gray-300'} flex items-center justify-center`}>
                                  {option.default && <div className="w-2 h-2 bg-white rounded-full" />}
                                </div>
                                <span className="text-sm">{option.label}</span>
                              </label>
                            ))}
                          </div>
                          
                          <div className="mt-4">
                            <p className="text-sm text-gray-600 mb-2">Share notes (optional)</p>
                            <div className="bg-white rounded-lg border p-3 text-sm text-gray-400">
                              Other restrictions or notes
                            </div>
                          </div>
                        </div>
                      </div>
                      </>
                      )}

                      <Separator />

                      {/* Accessibility Section */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium flex items-center gap-2">
                              <Accessibility className="w-4 h-4 text-blue-500" />
                              Accessibility Needs
                              {categoryTemplate?.detected_overrides?.includes("collect_accessibility") && (
                                <>
                                  <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full" data-testid="accessibility-override-badge">Override</span>
                                  <button onClick={() => handleResetToTemplate(["collect_accessibility"])} className="text-[10px] text-blue-600 hover:text-blue-700 underline" data-testid="reset-accessibility-btn">Reset</button>
                                </>
                              )}
                              {categoryTemplate?.template_intake?.collect_accessibility !== undefined && !categoryTemplate?.detected_overrides?.includes("collect_accessibility") && (
                                <span className="text-[10px] bg-blue-50 text-blue-500 px-1.5 py-0.5 rounded-full">Template</span>
                              )}
                            </h3>
                            <p className="text-sm text-gray-500">Physical accommodations & invisible disabilities</p>
                          </div>
                          <Switch
                            checked={intakeSettings.collect_accessibility}
                            onCheckedChange={(v) => {
                              const newSettings = {...intakeSettings, collect_accessibility: v};
                              setIntakeSettings(newSettings);
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}`, {
                                method: "PUT",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ intake_settings: newSettings })
                              }).then((res) => {
                                if (res.ok) {
                                  toast.success("Accessibility setting saved");
                                  setLastSynced(new Date());
                                  mutateEvent();
                                } else {
                                  setIntakeSettings(prev => ({...prev, collect_accessibility: !v}));
                                  toast.error("Failed to save setting");
                                }
                              }).catch(() => {
                                setIntakeSettings(prev => ({...prev, collect_accessibility: !v}));
                                toast.error("Failed to save setting");
                              });
                            }}
                            data-testid="toggle-accessibility"
                          />
                        </div>
                        
                        {/* Accessibility Preview */}
                        <div className={`p-6 rounded-xl border-2 ${intakeSettings.collect_accessibility ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50 opacity-50'}`}>
                          <p className="text-xs text-gray-500 mb-1">STEP 1 OF 4</p>
                          <h4 className="font-semibold text-lg mb-1">Accessibility</h4>
                          <p className="text-sm text-gray-600 mb-4">Let us know how we can best accommodate you</p>
                          
                          {/* Physical Accommodations */}
                          <p className="text-xs font-medium text-gray-500 mb-2">PHYSICAL ACCOMMODATIONS</p>
                          <div className="grid grid-cols-2 gap-2 mb-6">
                            {[
                              { id: 'wheelchair', label: 'Wheelchair accessible', desc: 'Need wheelchair-accessible space' },
                              { id: 'extra-time', label: 'Extra time needed', desc: 'May need more time between activities' },
                              { id: 'hearing', label: 'Hearing assistance', desc: 'May need visual cues or written communication' },
                              { id: 'service-animal', label: 'Service animal', desc: 'Will have a service animal present' },
                              { id: 'fragrance-free', label: 'Fragrance-free', desc: 'Sensitive to scents and fragrances' },
                              { id: 'limited-mobility', label: 'Limited mobility', desc: 'May need assistance with positioning' }
                            ].map(option => (
                              <div key={option.id} className="p-3 rounded-lg border bg-white hover:border-blue-300 cursor-pointer">
                                <p className="text-sm font-medium">{option.label}</p>
                                <p className="text-xs text-gray-500">{option.desc}</p>
                              </div>
                            ))}
                          </div>
                          
                          {/* Invisible Disabilities */}
                          <p className="text-xs font-medium text-gray-500 mb-2">INVISIBLE DISABILITIES (optional)</p>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { id: 'vocal_tics', label: 'Vocal tics', desc: 'I may make sudden sounds or verbal expressions' },
                              { id: 'motor_tics', label: 'Motor tics', desc: 'I may have sudden movements or gestures' },
                              { id: 'tourettes', label: "Tourette's Syndrome", desc: 'Combination of vocal and motor tics' },
                              { id: 'anxiety', label: 'Anxiety', desc: 'I may need extra patience or breaks' },
                              { id: 'chronic_pain', label: 'Chronic pain', desc: 'I may need to adjust position frequently' },
                              { id: 'chronic_fatigue', label: 'Chronic fatigue', desc: 'I may need rest breaks during the event' },
                              { id: 'adhd', label: 'ADHD', desc: 'I may need reminders or have difficulty with long sessions' },
                              { id: 'autism', label: 'Autism spectrum', desc: 'I may have specific sensory or communication preferences' },
                              { id: 'epilepsy', label: 'Epilepsy', desc: 'Please be aware of potential seizures' }
                            ].map(option => (
                              <div key={option.id} className="p-3 rounded-lg border bg-white hover:border-blue-300 cursor-pointer">
                                <p className="text-sm font-medium">{option.label}</p>
                                <p className="text-xs text-gray-500 line-clamp-1">{option.desc}</p>
                              </div>
                            ))}
                          </div>
                          
                          <div className="mt-4">
                            <p className="text-sm text-gray-600 mb-2">Share notes (optional)</p>
                            <div className="bg-white rounded-lg border p-3 text-sm text-gray-400">
                              Anything else we should know
                            </div>
                          </div>
                        </div>
                      </div>

                      <Separator />

                      {/* Transportation Section */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium flex items-center gap-2">
                              <Car className="w-4 h-4 text-purple-500" />
                              Transportation
                            </h3>
                            <p className="text-sm text-gray-500">Ask how guests are getting to the event</p>
                          </div>
                          <Switch
                            checked={intakeSettings.collect_transportation}
                            onCheckedChange={(v) => {
                              const newSettings = {...intakeSettings, collect_transportation: v};
                              setIntakeSettings(newSettings);
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}`, {
                                method: "PUT",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ intake_settings: newSettings })
                              }).then((res) => {
                                if (res.ok) {
                                  toast.success("Transportation setting saved");
                                  setLastSynced(new Date());
                                  mutateEvent();
                                } else {
                                  setIntakeSettings(prev => ({...prev, collect_transportation: !v}));
                                  toast.error("Failed to save setting");
                                }
                              }).catch(() => {
                                setIntakeSettings(prev => ({...prev, collect_transportation: !v}));
                                toast.error("Failed to save setting");
                              });
                            }}
                            data-testid="toggle-transportation"
                          />
                        </div>
                        
                        {intakeSettings.collect_transportation && (
                          <div className="p-4 rounded-xl border bg-purple-50 border-purple-200">
                            <p className="text-xs text-gray-500 mb-3">Guests will be asked to select one:</p>
                            <div className="grid grid-cols-2 gap-2">
                              {[
                                { id: "driving", label: "Driving", desc: "I'll drive myself" },
                                { id: "dropoff", label: "Drop Off", desc: "Rideshare, driver, etc." }
                              ].map(option => (
                                <div key={option.id} className="p-3 rounded-lg border bg-white">
                                  <p className="text-sm font-medium">{option.label}</p>
                                  <p className="text-xs text-gray-500">{option.desc}</p>
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-gray-400 mt-3">Plus an optional notes field for parking needs, pickup location, etc.</p>
                          </div>
                        )}
                      </div>

                      <Separator />

                      {/* Interests Section - EDITABLE */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium flex items-center gap-2">
                              <Heart className="w-4 h-4 text-pink-500" />
                              Interest Categories
                              {categoryTemplate?.detected_overrides?.includes("collect_interests") && (
                                <>
                                  <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Override</span>
                                  <button onClick={() => handleResetToTemplate(["collect_interests"])} className="text-[10px] text-blue-600 hover:text-blue-700 underline">Reset</button>
                                </>
                              )}
                              {categoryTemplate?.template_intake?.collect_interests !== undefined && !categoryTemplate?.detected_overrides?.includes("collect_interests") && (
                                <span className="text-[10px] bg-blue-50 text-blue-500 px-1.5 py-0.5 rounded-full">Template</span>
                              )}
                            </h3>
                            <p className="text-sm text-gray-500">Select which interests guests can choose from (editable)</p>
                          </div>
                          <Switch
                            checked={intakeSettings.collect_interests}
                            onCheckedChange={(v) => {
                              const newSettings = {...intakeSettings, collect_interests: v};
                              setIntakeSettings(newSettings);
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}`, {
                                method: "PUT",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ intake_settings: newSettings })
                              }).then((res) => {
                                if (res.ok) {
                                  toast.success("Interests setting saved");
                                  setLastSynced(new Date());
                                  mutateEvent();
                                } else {
                                  setIntakeSettings(prev => ({...prev, collect_interests: !v}));
                                  toast.error("Failed to save setting");
                                }
                              }).catch(() => {
                                setIntakeSettings(prev => ({...prev, collect_interests: !v}));
                                toast.error("Failed to save setting");
                              });
                            }}
                            data-testid="toggle-interests"
                          />
                        </div>
                        
                        {intakeSettings.collect_interests && (
                          <div className="p-4 rounded-xl border bg-pink-50 border-pink-200">
                            <p className="text-xs text-gray-500 mb-3">Add or remove interest categories for your event:</p>
                            <div className="flex flex-wrap gap-2 mb-4">
                              {(intakeSettings.interest_categories || ['AI and Machine Learning', 'Fashion Tech', 'Sustainable Fashion', 'Wearable Tech', 'Digital Fashion', 'Networking', 'Investing', 'Art and Design', 'Storytelling', 'Entrepreneurship']).map((interest, idx) => (
                                <div key={idx} className="flex items-center gap-1 px-3 py-1.5 bg-white rounded-full border text-sm group">
                                  {interest}
                                  <button 
                                    type="button"
                                    className="ml-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const currentCategories = intakeSettings.interest_categories || ['AI and Machine Learning', 'Fashion Tech', 'Sustainable Fashion', 'Wearable Tech', 'Digital Fashion', 'Networking', 'Investing', 'Art and Design', 'Storytelling', 'Entrepreneurship'];
                                      const newCategories = currentCategories.filter((_, i) => i !== idx);
                                      setIntakeSettings({...intakeSettings, interest_categories: newCategories});
                                      toast.success(`Removed "${interest}"`);
                                    }}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <Input
                                id="new-interest-input"
                                placeholder="Add custom interest..."
                                className="flex-1"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const value = e.target.value.trim();
                                    if (value) {
                                      const currentCategories = intakeSettings.interest_categories || ['AI and Machine Learning', 'Fashion Tech', 'Sustainable Fashion', 'Wearable Tech', 'Digital Fashion', 'Networking', 'Investing', 'Art and Design', 'Storytelling', 'Entrepreneurship'];
                                      setIntakeSettings({...intakeSettings, interest_categories: [...currentCategories, value]});
                                      e.target.value = '';
                                      toast.success(`Added "${value}"`);
                                    }
                                  }
                                }}
                              />
                              <Button 
                                type="button"
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  const input = document.getElementById('new-interest-input');
                                  const value = input?.value?.trim();
                                  if (value) {
                                    const currentCategories = intakeSettings.interest_categories || ['AI and Machine Learning', 'Fashion Tech', 'Sustainable Fashion', 'Wearable Tech', 'Digital Fashion', 'Networking', 'Investing', 'Art and Design', 'Storytelling', 'Entrepreneurship'];
                                    setIntakeSettings({...intakeSettings, interest_categories: [...currentCategories, value]});
                                    input.value = '';
                                    toast.success(`Added "${value}"`);
                                  }
                                }}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Shareable Intake Links */}
                      <div className="border-t pt-6">
                        <div className="flex items-center gap-2 mb-4">
                          <Link2 className="w-5 h-5 text-purple-600" />
                          <h3 className="font-semibold">Shareable Intake Links</h3>
                        </div>
                        <p className="text-sm text-gray-500 mb-4">
                          Share these links with coordinators or team members to collect specific information from guests.
                        </p>
                        
                        <div className="grid gap-3">
                          {/* Dietary Link */}
                          {intakeSettings.collect_dietary && (
                            <div className="flex items-center justify-between p-3 rounded-lg border bg-orange-50 border-orange-200">
                              <div className="flex items-center gap-3">
                                <Utensils className="w-4 h-4 text-orange-600" />
                                <div>
                                  <p className="text-sm font-medium">Dietary Intake Only</p>
                                  <p className="text-xs text-gray-500">For catering coordinators</p>
                                </div>
                              </div>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  const url = `${window.location.origin}/events/${event?.slug}?view=dietary`;
                                  navigator.clipboard.writeText(url);
                                  toast.success("Link copied!");
                                }}
                              >
                                <Copy className="w-4 h-4 mr-1" />
                                Copy
                              </Button>
                            </div>
                          )}
                          
                          {/* Accessibility Link */}
                          {intakeSettings.collect_accessibility && (
                            <div className="flex items-center justify-between p-3 rounded-lg border bg-blue-50 border-blue-200">
                              <div className="flex items-center gap-3">
                                <Accessibility className="w-4 h-4 text-blue-600" />
                                <div>
                                  <p className="text-sm font-medium">Accessibility Intake Only</p>
                                  <p className="text-xs text-gray-500">For accessibility coordinators</p>
                                </div>
                              </div>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  const url = `${window.location.origin}/events/${event?.slug}?view=accessibility`;
                                  navigator.clipboard.writeText(url);
                                  toast.success("Link copied!");
                                }}
                              >
                                <Copy className="w-4 h-4 mr-1" />
                                Copy
                              </Button>
                            </div>
                          )}
                          
                          {/* Interests Link */}
                          {intakeSettings.collect_interests && (
                            <div className="flex items-center justify-between p-3 rounded-lg border bg-pink-50 border-pink-200">
                              <div className="flex items-center gap-3">
                                <Heart className="w-4 h-4 text-pink-600" />
                                <div>
                                  <p className="text-sm font-medium">Interests Intake Only</p>
                                  <p className="text-xs text-gray-500">For networking coordinators</p>
                                </div>
                              </div>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  const url = `${window.location.origin}/events/${event?.slug}?view=interests`;
                                  navigator.clipboard.writeText(url);
                                  toast.success("Link copied!");
                                }}
                              >
                                <Copy className="w-4 h-4 mr-1" />
                                Copy
                              </Button>
                            </div>
                          )}
                          
                          {/* Full Intake Link */}
                          <div className="flex items-center justify-between p-3 rounded-lg border bg-purple-50 border-purple-200">
                            <div className="flex items-center gap-3">
                              <FileText className="w-4 h-4 text-purple-600" />
                              <div>
                                <p className="text-sm font-medium">Full Intake Form</p>
                                <p className="text-xs text-gray-500">All enabled sections</p>
                              </div>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                const url = `${window.location.origin}/events/${event?.slug}`;
                                navigator.clipboard.writeText(url);
                                toast.success("Link copied!");
                              }}
                            >
                              <Copy className="w-4 h-4 mr-1" />
                              Copy
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Question Bank Visibility Controls */}
                      <div className="border-t pt-6">
                        <div className="flex items-center gap-2 mb-4">
                          <Eye className="w-5 h-5 text-indigo-600" />
                          <h3 className="font-semibold">Shareable Page Visibility</h3>
                        </div>
                        <p className="text-sm text-gray-500 mb-4">
                          Control which data points are visible on shareable results pages for coordinators.
                          Uncheck items to hide them from coordinators (they'll still be visible to you as the host).
                        </p>
                        
                        {/* Dietary Visibility */}
                        <div className="p-4 border rounded-lg mb-3 bg-orange-50/50">
                          <p className="text-sm font-medium mb-3 flex items-center gap-2">
                            <Utensils className="w-4 h-4 text-orange-600" />
                            Dietary Share Page
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { id: "dietary_restrictions", label: "Dietary Restrictions" },
                              { id: "meat_preferences", label: "Meat Preferences" },
                              { id: "allergies", label: "Allergies" },
                              { id: "dietary_notes", label: "Additional Notes" }
                            ].map(field => (
                              <label key={field.id} className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={intakeSettings.share_visibility?.dietary?.[field.id] !== false}
                                  onChange={(e) => {
                                    const newSettings = {
                                      ...intakeSettings,
                                      share_visibility: {
                                        ...intakeSettings.share_visibility,
                                        dietary: {
                                          ...(intakeSettings.share_visibility?.dietary || {}),
                                          [field.id]: e.target.checked
                                        }
                                      }
                                    };
                                    setIntakeSettings(newSettings);
                                    saveIntakeSettings(newSettings);
                                  }}
                                  className="rounded border-gray-300"
                                />
                                {field.label}
                              </label>
                            ))}
                          </div>
                        </div>
                        
                        {/* Accessibility Visibility */}
                        <div className="p-4 border rounded-lg mb-3 bg-blue-50/50">
                          <p className="text-sm font-medium mb-3 flex items-center gap-2">
                            <Accessibility className="w-4 h-4 text-blue-600" />
                            Accessibility Share Page
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { id: "accessibility_needs", label: "Accessibility Needs" },
                              { id: "invisible_disabilities", label: "Invisible Disabilities" },
                              { id: "mobility_needs", label: "Mobility Needs" },
                              { id: "accessibility_notes", label: "Additional Notes" }
                            ].map(field => (
                              <label key={field.id} className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={intakeSettings.share_visibility?.accessibility?.[field.id] !== false}
                                  onChange={(e) => {
                                    const newSettings = {
                                      ...intakeSettings,
                                      share_visibility: {
                                        ...intakeSettings.share_visibility,
                                        accessibility: {
                                          ...(intakeSettings.share_visibility?.accessibility || {}),
                                          [field.id]: e.target.checked
                                        }
                                      }
                                    };
                                    setIntakeSettings(newSettings);
                                    saveIntakeSettings(newSettings);
                                  }}
                                  className="rounded border-gray-300"
                                />
                                {field.label}
                              </label>
                            ))}
                          </div>
                        </div>
                        
                        {/* Interests Visibility */}
                        <div className="p-4 border rounded-lg bg-pink-50/50">
                          <p className="text-sm font-medium mb-3 flex items-center gap-2">
                            <Heart className="w-4 h-4 text-pink-600" />
                            Interests Share Page
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { id: "interests", label: "Interests" },
                              { id: "audience_type", label: "Audience Type" },
                              { id: "learning_preferences", label: "Learning Preferences" },
                              { id: "vibe", label: "Vibe/Mood" },
                              { id: "experience_apps", label: "Experience Apps" }
                            ].map(field => (
                              <label key={field.id} className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={intakeSettings.share_visibility?.interests?.[field.id] !== false}
                                  onChange={(e) => {
                                    const newSettings = {
                                      ...intakeSettings,
                                      share_visibility: {
                                        ...intakeSettings.share_visibility,
                                        interests: {
                                          ...(intakeSettings.share_visibility?.interests || {}),
                                          [field.id]: e.target.checked
                                        }
                                      }
                                    };
                                    setIntakeSettings(newSettings);
                                    saveIntakeSettings(newSettings);
                                  }}
                                  className="rounded border-gray-300"
                                />
                                {field.label}
                              </label>
                            ))}
                          </div>
                        </div>
                        
                        {/* Show Names Toggle */}
                        <div className="mt-4 p-4 border rounded-lg bg-gray-50">
                          <label className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium">Show Guest Names</p>
                              <p className="text-xs text-gray-500">When off, only aggregated counts will be shown (no names)</p>
                            </div>
                            <Switch
                              checked={intakeSettings.share_visibility?.show_names !== false}
                              onCheckedChange={(v) => {
                                const newSettings = {
                                  ...intakeSettings,
                                  share_visibility: {
                                    ...intakeSettings.share_visibility,
                                    show_names: v
                                  }
                                };
                                setIntakeSettings(newSettings);
                                saveIntakeSettings(newSettings);
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* EXPERIENCE - Now part of Intake tab */}
                {(settingsTab === "intake" || settingsTab === "experience") && (
                  <Card data-testid="settings-experience">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5" />
                        Experience Settings
                      </CardTitle>
                      <CardDescription>
                        Enable integrations that enhance your event experience
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Tap In Integration Toggle */}
                      <div className="p-4 rounded-xl border hover:border-purple-200 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <img 
                              src={TAP_IN_LOGO} 
                              alt="Tap In" 
                              className="w-14 h-14 rounded-xl object-cover"
                            />
                            <div>
                              <h3 className="font-medium">Tap In Music</h3>
                              <p className="text-sm text-gray-500">Let guests contribute to the event playlist</p>
                            </div>
                          </div>
                          <Switch
                            checked={intakeSettings.tapin_enabled}
                            onCheckedChange={(v) => {
                              // Sync both intake_settings and tech_stack for Tap In
                              const newIntakeSettings = {...intakeSettings, tapin_enabled: v};
                              const newTechStack = {...techStack, tapin: {...(techStack.tapin || {}), enabled: v}};
                              setIntakeSettings(newIntakeSettings);
                              setTechStack(newTechStack);
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}`, {
                                method: "PUT",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ intake_settings: newIntakeSettings, tech_stack: newTechStack })
                              }).then((res) => {
                                if (res.ok) {
                                  toast.success("Tap In setting saved");
                                  setLastSynced(new Date());
                                  mutateEvent();
                                } else {
                                  setIntakeSettings(prev => ({...prev, tapin_enabled: !v}));
                                  setTechStack(prev => ({...prev, tapin: {...(prev.tapin || {}), enabled: !v}}));
                                  toast.error("Failed to save setting");
                                }
                              }).catch(() => {
                                setIntakeSettings(prev => ({...prev, tapin_enabled: !v}));
                                setTechStack(prev => ({...prev, tapin: {...(prev.tapin || {}), enabled: !v}}));
                                toast.error("Failed to save setting");
                              });
                            }}
                            data-testid="toggle-tapin"
                          />
                        </div>
                        {intakeSettings.tapin_enabled && (
                          <div className="mt-4 pt-4 border-t">
                            <p className="text-xs text-gray-500 mb-2">Configure Tap In settings in Tech Stack &gt; Integrations</p>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setSettingsTab("techstack")}
                            >
                              Go to Settings
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* VIP Intros Toggle */}
                      <div className="p-4 rounded-xl border hover:border-purple-200 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: catIconColor || '#9c57f5' }}>
                              <Users className="w-7 h-7 text-white" />
                            </div>
                            <div>
                              <h3 className="font-medium">VIP Intros</h3>
                              <p className="text-sm text-gray-500">Enable personalized introductions for VIP guests</p>
                            </div>
                          </div>
                          <Switch
                            checked={intakeSettings.vip_intros_enabled}
                            onCheckedChange={(v) => {
                              const newSettings = {...intakeSettings, vip_intros_enabled: v};
                              setIntakeSettings(newSettings);
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}`, {
                                method: "PUT",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ intake_settings: newSettings })
                              }).then((res) => {
                                if (res.ok) {
                                  toast.success("VIP Intros setting saved");
                                  setLastSynced(new Date());
                                  mutateEvent();
                                } else {
                                  setIntakeSettings(prev => ({...prev, vip_intros_enabled: !v}));
                                  toast.error("Failed to save setting");
                                }
                              }).catch(() => {
                                setIntakeSettings(prev => ({...prev, vip_intros_enabled: !v}));
                                toast.error("Failed to save setting");
                              });
                            }}
                            data-testid="toggle-vip-intros"
                          />
                        </div>
                      </div>

                      {/* Builder Profile - For Instructor/Workshop Pilots */}
                      {(event?.pilot_category === 'instructor' || event?.pilot_category === 'instructors' || event?.pilot_category === 'workshop') && (
                        <div className="p-4 rounded-xl border hover:border-indigo-200 transition-colors bg-indigo-50/30">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-4">
                              <div className="w-14 h-14 rounded-xl bg-indigo-100 flex items-center justify-center">
                                <Briefcase className="w-7 h-7 text-indigo-600" />
                              </div>
                              <div>
                                <h3 className="font-medium">Builder Profile</h3>
                                <p className="text-sm text-gray-500">Collect attendee's role, stage, and project info</p>
                              </div>
                            </div>
                            <Switch
                              checked={intakeSettings.collect_builder_profile === true}
                              onCheckedChange={(v) => {
                                const newSettings = {...intakeSettings, collect_builder_profile: v};
                                setIntakeSettings(newSettings);
                                const token = localStorage.getItem("session_token");
                                fetch(`${API}/events/${eventId}`, {
                                  method: "PUT",
                                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                  body: JSON.stringify({ intake_settings: newSettings })
                                }).then((res) => {
                                  if (res.ok) {
                                    toast.success("Builder Profile setting saved");
                                    setLastSynced(new Date());
                                    mutateEvent();
                                  } else {
                                    setIntakeSettings(prev => ({...prev, collect_builder_profile: !v}));
                                    toast.error("Failed to save setting");
                                  }
                                }).catch(() => {
                                  setIntakeSettings(prev => ({...prev, collect_builder_profile: !v}));
                                  toast.error("Failed to save setting");
                                });
                              }}
                              data-testid="toggle-builder-profile"
                            />
                          </div>
                          {intakeSettings.collect_builder_profile === true && (
                            <div className="text-xs text-gray-500 bg-white/50 rounded-lg p-3 space-y-1">
                              <p className="font-medium text-gray-700 mb-2">Collects from attendees:</p>
                              <p>• Role: Founder/CEO, Engineer, Designer, Product, etc.</p>
                              <p>• Journey Stage: Exploring, Have idea, Building MVP, Launched, Scaling</p>
                              <p>• Current Project: Name and optional link</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Your Vibe Tonight - Editable Options */}
                      <div className="p-4 rounded-xl border hover:border-purple-200 transition-colors">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center">
                              <Sparkles className="w-7 h-7 text-purple-600" />
                            </div>
                            <div>
                              <h3 className="font-medium">Your Vibe Tonight</h3>
                              <p className="text-sm text-gray-500">Let guests share their social energy level</p>
                            </div>
                          </div>
                          <Switch
                            checked={intakeSettings.collect_vibe !== false}
                            onCheckedChange={(v) => {
                              const newSettings = {...intakeSettings, collect_vibe: v};
                              setIntakeSettings(newSettings);
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}`, {
                                method: "PUT",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ intake_settings: newSettings })
                              }).then((res) => {
                                if (res.ok) {
                                  toast.success("Vibe setting saved");
                                  setLastSynced(new Date());
                                  mutateEvent();
                                } else {
                                  setIntakeSettings(prev => ({...prev, collect_vibe: !v}));
                                  toast.error("Failed to save setting");
                                }
                              }).catch(() => {
                                setIntakeSettings(prev => ({...prev, collect_vibe: !v}));
                                toast.error("Failed to save setting");
                              });
                            }}
                            data-testid="toggle-vibe"
                          />
                        </div>
                        
                        {/* Vibe Options Preview */}
                        {intakeSettings.collect_vibe !== false && (
                          <div className="mt-4 pt-4 border-t">
                            <p className="text-xs text-gray-500 mb-3">Vibe options shown to guests:</p>
                            <div className="grid grid-cols-2 gap-2">
                              {/* Very social */}
                              <div className="flex items-center gap-3 p-3 bg-pink-50 rounded-xl border border-pink-100">
                                <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                                  <Users className="w-5 h-5 text-pink-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Very social</p>
                                  <p className="text-xs text-gray-500">Ready to meet everyone</p>
                                </div>
                              </div>
                              
                              {/* Selectively social */}
                              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl border border-purple-100">
                                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                                  <Heart className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Selectively social</p>
                                  <p className="text-xs text-gray-500">Quality conversations</p>
                                </div>
                              </div>
                              
                              {/* Observing */}
                              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                  <Eye className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Observing</p>
                                  <p className="text-xs text-gray-500">Taking it all in</p>
                                </div>
                              </div>
                              
                              {/* Just vibing */}
                              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                                  <Sparkles className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Just vibing</p>
                                  <p className="text-xs text-gray-500">Here for the experience</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                    </CardContent>
                  </Card>
                )}

                {/* QUESTION BANK - For Instructor Pilots Only */}
                {(settingsTab === "intake" || settingsTab === "question-bank") && event?.event_type === 'instructor_pilot' || event?.pilot_category === 'instructor' && (
                  <Card data-testid="settings-question-bank">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MessageSquare className="w-5 h-5" />
                        Question Bank
                      </CardTitle>
                      <CardDescription>
                        Allow attendees to submit questions for the instructor before the session
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Enable Question Bank */}
                      <div className="p-4 rounded-xl border hover:border-indigo-200 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-3 bg-indigo-100 rounded-xl">
                              <MessageSquare className="w-6 h-6 text-indigo-600" />
                            </div>
                            <div>
                              <h3 className="font-medium">Enable Question Submission</h3>
                              <p className="text-sm text-gray-500">Attendees can submit questions during RSVP</p>
                            </div>
                          </div>
                          <Switch
                            checked={intakeSettings.question_bank_enabled !== false}
                            onCheckedChange={(v) => {
                              const newSettings = {...intakeSettings, question_bank_enabled: v};
                              setIntakeSettings(newSettings);
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}`, {
                                method: "PUT",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ intake_settings: newSettings })
                              }).then((res) => {
                                if (res.ok) {
                                  toast.success("Question bank setting saved");
                                  setLastSynced(new Date());
                                  mutateEvent();
                                }
                              });
                            }}
                            data-testid="toggle-question-bank"
                          />
                        </div>
                      </div>

                      {/* Question Bank Settings */}
                      {intakeSettings.question_bank_enabled !== false && (
                        <div className="space-y-4">
                          {/* Question Label */}
                          <div className="space-y-2">
                            <Label>Question Prompt</Label>
                            <Input
                              value={intakeSettings.question_bank_label || "Questions for the instructor?"}
                              onChange={(e) => setIntakeSettings({...intakeSettings, question_bank_label: e.target.value})}
                              placeholder="What questions do you have for the instructor?"
                            />
                            <p className="text-xs text-gray-500">This prompt is shown to attendees on the RSVP form</p>
                          </div>

                          {/* Required Toggle */}
                          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <span className="font-medium text-sm">Require a question</span>
                              <p className="text-xs text-gray-500">Attendees must submit at least one question</p>
                            </div>
                            <Switch
                              checked={intakeSettings.question_bank_required === true}
                              onCheckedChange={(v) => setIntakeSettings({...intakeSettings, question_bank_required: v})}
                            />
                          </div>

                          {/* View Submitted Questions Link */}
                          <div className="pt-4 border-t">
                            <Button 
                              variant="outline" 
                              onClick={openQuestionBankModal}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Submitted Questions ({questionBankData.total_questions || 0})
                            </Button>
                            <p className="text-xs text-gray-500 mt-2">
                              See all questions submitted by attendees
                            </p>
                          </div>

                          {/* Save Button */}
                          <div className="flex justify-end pt-4">
                            <Button onClick={() => saveIntakeSettings(intakeSettings)} disabled={saving}>
                              <Save className="w-4 h-4 mr-1" />
                              {saving ? "Saving..." : "Save Question Bank Settings"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* CONFIRMATION PAGE - Editable Template */}
                {settingsTab === "confirmation" && (
                  <Card data-testid="settings-confirmation">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5" />
                        Confirmation Page
                      </CardTitle>
                      <CardDescription>
                        Edit the confirmation page attendees see after RSVP. Toggle sections inline and edit CTAs directly.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">

                      {/* Iter 9.40 Slice I.j - Pre / During / Post host-set text blocks
                          Drives the modern provider confirmation page (Slice I.h).
                          Each block is shown in a color-coded section: amber/purple/emerald. */}
                      <div className="rounded-2xl border border-stone-200 bg-stone-50 p-5 space-y-4" data-testid="confirmation-prepost-editor">
                        <div>
                          <h4 className="text-sm font-semibold text-stone-900 mb-1">What clients see on their confirmation page</h4>
                          <p className="text-xs text-stone-500">Three optional message blocks. Leave any blank to hide it.</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <Label className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold mb-1.5 block">Before the appointment</Label>
                            <Textarea
                              rows={4}
                              placeholder="e.g. Arrive 10 min early. Come with a clean face - no skincare please."
                              value={eventSettings?.confirmation_settings?.pre_text || ""}
                              onChange={(e) => setEventSettings(prev => ({
                                ...(prev || {}),
                                confirmation_settings: { ...(prev?.confirmation_settings || {}), pre_text: e.target.value }
                              }))}
                              className="text-xs"
                              data-testid="confirmation-pre-text"
                            />
                          </div>
                          <div>
                            <Label className="text-[11px] uppercase tracking-wide text-purple-700 font-semibold mb-1.5 block">During</Label>
                            <Textarea
                              rows={4}
                              placeholder="e.g. Sit back, relax. Studio is BYOB Saturdays."
                              value={eventSettings?.confirmation_settings?.during_text || ""}
                              onChange={(e) => setEventSettings(prev => ({
                                ...(prev || {}),
                                confirmation_settings: { ...(prev?.confirmation_settings || {}), during_text: e.target.value }
                              }))}
                              className="text-xs"
                              data-testid="confirmation-during-text"
                            />
                          </div>
                          <div>
                            <Label className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold mb-1.5 block">After</Label>
                            <Textarea
                              rows={4}
                              placeholder="e.g. Drop a final-look photo and we'll save it to your folder forever."
                              value={eventSettings?.confirmation_settings?.post_text || ""}
                              onChange={(e) => setEventSettings(prev => ({
                                ...(prev || {}),
                                confirmation_settings: { ...(prev?.confirmation_settings || {}), post_text: e.target.value }
                              }))}
                              className="text-xs"
                              data-testid="confirmation-post-text"
                            />
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <Button
                            size="sm"
                            onClick={saveEventSettings}
                            data-testid="confirmation-prepost-save"
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            <Save className="w-3.5 h-3.5 mr-1" />
                            Save text blocks
                          </Button>
                        </div>
                      </div>
                      
                      {/* Confirmation Preview - WHITE BACKGROUND */}
                      <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-sm">
                        
                        {/* Success Message */}
                        <div className="text-center mb-6">
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 flex items-center justify-center mx-auto mb-4 shadow-sm border border-green-200">
                            <Check className="w-7 h-7 text-green-600" />
                          </div>
                          <h2 className="text-xl font-semibold text-gray-900 mb-1">You're confirmed</h2>
                          <p className="text-sm text-gray-500">
                            See you {event?.date ? new Date(event.date).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) : "soon"}
                          </p>
                        </div>
                        
                        {/* Event Pass Preview */}
                        <div className="bg-white rounded-2xl p-5 mb-4 border border-gray-200">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Event Pass</p>
                              <p className="text-gray-900 font-semibold">{event?.name || "Event Name"}</p>
                            </div>
                            <span className="text-[10px] bg-green-100 text-green-700 px-2.5 py-1 rounded-full font-medium">Confirmed</span>
                          </div>
                          
                          {/* Event Info Grid */}
                          <div className="bg-gray-50 rounded-xl p-4 mb-4">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">Guest</p>
                                <p className="text-gray-900 font-medium">{user?.name || "Guest Name"}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">Date</p>
                                <p className="text-gray-900 font-medium">{event?.date ? new Date(event.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "TBA"}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">Time</p>
                                <p className="text-gray-900 font-medium">{formatTime12Hour(event?.start_time || event?.time) || "TBA"}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">Venue</p>
                                <p className="text-gray-900 font-medium text-xs">{event?.venue_name || event?.location || "TBA"}</p>
                              </div>
                            </div>
                          </div>
                          
                          {/* QR Code Placeholder */}
                          <div className="flex justify-center mb-3">
                            <div className="w-24 h-24 bg-gray-100 rounded-xl flex items-center justify-center border border-gray-200">
                              <span className="text-xs text-gray-400">QR Code</span>
                            </div>
                          </div>
                          <p className="text-gray-400 font-mono text-[10px] text-center">pass_token_here</p>
                        </div>
                        
                        {/* Action Buttons - BLACK */}
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <Button variant="outline" className="text-xs py-2.5 rounded-xl border-gray-200">
                            <Download className="w-3.5 h-3.5 mr-1.5" />
                            Save Pass
                          </Button>
                          <Button variant="outline" className="text-xs py-2.5 rounded-xl border-gray-200">
                            <ArrowUp className="w-3.5 h-3.5 mr-1.5" />
                            Share Link
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mb-6">
                          <Button className="bg-black hover:bg-gray-900 text-white py-3 rounded-xl text-xs">
                            <Wallet className="w-4 h-4 mr-1.5" />
                            Add to Wallet
                          </Button>
                          <Button variant="outline" className="border-gray-200 py-3 rounded-xl text-xs">
                            <Calendar className="w-4 h-4 mr-1.5" />
                            Add to Calendar
                          </Button>
                        </div>
                        
                        {/* Tap In Section - With inline toggle */}
                        <div className={`rounded-xl p-4 mb-4 border relative ${intakeSettings.tapin_enabled ? 'bg-gradient-to-r from-violet-50 to-purple-50 border-violet-100' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="absolute top-2 right-2">
                            <Switch
                              checked={intakeSettings.tapin_enabled}
                              onCheckedChange={(v) => {
                                // Sync both intake_settings and tech_stack for Tap In
                                const newIntakeSettings = {...intakeSettings, tapin_enabled: v};
                                const newTechStack = {...techStack, tapin: {...(techStack.tapin || {}), enabled: v}};
                                setIntakeSettings(newIntakeSettings);
                                setTechStack(newTechStack);
                                const token = localStorage.getItem("session_token");
                                fetch(`${API}/events/${eventId}`, {
                                  method: "PUT",
                                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                  body: JSON.stringify({ intake_settings: newIntakeSettings, tech_stack: newTechStack })
                                }).then((res) => {
                                  if (res.ok) {
                                    toast.success("Tap In setting saved");
                                    setLastSynced(new Date());
                                    mutateEvent();
                                  } else {
                                    setIntakeSettings(prev => ({...prev, tapin_enabled: !v}));
                                    setTechStack(prev => ({...prev, tapin: {...(prev.tapin || {}), enabled: !v}}));
                                    toast.error("Failed to save setting");
                                  }
                                }).catch(() => {
                                  setIntakeSettings(prev => ({...prev, tapin_enabled: !v}));
                                  setTechStack(prev => ({...prev, tapin: {...(prev.tapin || {}), enabled: !v}}));
                                  toast.error("Failed to save setting");
                                });
                              }}
                              data-testid="toggle-tapin-confirm"
                            />
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg overflow-hidden">
                              <img src={TAP_IN_LOGO} alt="Tap In" className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1">
                              <p className={`font-medium text-sm ${intakeSettings.tapin_enabled ? 'text-gray-900' : 'text-gray-400'}`}>Add Your Song</p>
                              <p className="text-xs text-gray-500">Let guests share tracks with the DJ</p>
                            </div>
                            {intakeSettings.tapin_enabled && (
                              <Button size="sm" className="bg-violet-600 text-white text-xs h-7 px-3 rounded-lg">
                                <Music className="w-3 h-3 mr-1" />
                                Tap In
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Hosted By Section - With inline toggle & editable CTA */}
                        <div className={`mb-4 p-4 rounded-xl border ${intakeSettings.show_hosts !== false ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide">Hosted By</h3>
                            <Switch
                              checked={intakeSettings.show_hosts !== false}
                              onCheckedChange={(v) => setIntakeSettings({...intakeSettings, show_hosts: v})}
                              data-testid="toggle-hosts-confirm"
                            />
                          </div>
                          {intakeSettings.show_hosts !== false && (
                            <div className="space-y-2">
                              {partners?.filter(p => p.is_host || p.type === 'host').length > 0 ? (
                                partners.filter(p => p.is_host || p.type === 'host').map((partner, idx) => (
                                  <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 group">
                                    {partner.logo ? (
                                      <img src={getImageUrl(partner.logo)} alt={partner.name} className="w-10 h-10 rounded-lg object-contain bg-white" />
                                    ) : (
                                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: catIconColor || '#9c57f5' }}>
                                        <span className="text-white font-bold text-sm">{partner.name?.charAt(0)}</span>
                                      </div>
                                    )}
                                    <div className="flex-1">
                                      <p className="font-medium text-gray-900 text-sm">{partner.name}</p>
                                      <Input
                                        value={partner.cta_link || ''}
                                        onChange={(e) => {
                                          const updatedPartners = partners.map((p, i) => 
                                            (p.is_host || p.type === 'host') && partners.filter(pp => pp.is_host || pp.type === 'host').indexOf(p) === idx 
                                              ? {...p, cta_link: e.target.value} : p
                                          );
                                          // Update partners state - tracked link
                                        }}
                                        className="text-xs text-gray-400 bg-transparent border-0 p-0 h-5 focus:ring-0"
                                        placeholder="+ Add CTA link (tracked)"
                                      />
                                    </div>
                                    <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-purple-500" />
                                  </div>
                                ))
                              ) : (
                                <p className="text-xs text-gray-400 text-center py-2">Add hosts in Team settings</p>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Event Partners Section - With inline toggle & editable CTAs */}
                        <div className={`mb-4 p-4 rounded-xl border ${intakeSettings.show_partners !== false ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide">Event Partners</h3>
                            <Switch
                              checked={intakeSettings.show_partners !== false}
                              onCheckedChange={(v) => setIntakeSettings({...intakeSettings, show_partners: v})}
                              data-testid="toggle-partners-confirm"
                            />
                          </div>
                          {intakeSettings.show_partners !== false && (
                            <div className="space-y-2">
                              {partners?.filter(p => !p.is_host && p.type !== 'host' && p.type !== 'sponsor').length > 0 ? (
                                partners.filter(p => !p.is_host && p.type !== 'host' && p.type !== 'sponsor').map((partner, idx) => (
                                  <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 group">
                                    {partner.logo ? (
                                      <img src={getImageUrl(partner.logo)} alt={partner.name} className="w-10 h-10 rounded-lg object-contain bg-white" />
                                    ) : (
                                      <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center">
                                        <span className="text-gray-600 font-bold text-sm">{partner.name?.charAt(0)}</span>
                                      </div>
                                    )}
                                    <div className="flex-1">
                                      <p className="font-medium text-gray-900 text-sm">{partner.name}</p>
                                      <Input
                                        value={partner.cta_link || ''}
                                        className="text-xs text-gray-400 bg-transparent border-0 p-0 h-5 focus:ring-0"
                                        placeholder="+ Add CTA link (tracked)"
                                      />
                                    </div>
                                    <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-purple-500" />
                                  </div>
                                ))
                              ) : (
                                <p className="text-xs text-gray-400 text-center py-2">Add partners in Team settings</p>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Phygital Profile CTA - ALWAYS VISIBLE (this is Phygital!) */}
                        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 mb-4 border border-purple-100">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                              <User className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 text-sm">Your Phygital Profile</p>
                              <p className="text-xs text-gray-500">Manage preferences for future events</p>
                            </div>
                            <Button size="sm" className="bg-black hover:bg-gray-900 text-white text-xs">
                              View →
                            </Button>
                          </div>
                          <p className="text-[10px] text-purple-600 mt-2 text-center">Prompts sign-in if not logged in</p>
                        </div>
                        
                        {/* Event Website Link - With inline toggle */}
                        <div className={`p-4 rounded-xl border ${intakeSettings.show_event_link !== false ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-gray-500">External Event Website</span>
                            <Switch
                              checked={intakeSettings.show_event_link !== false}
                              onCheckedChange={(v) => setIntakeSettings({...intakeSettings, show_event_link: v})}
                              data-testid="toggle-event-link-confirm"
                            />
                          </div>
                          {intakeSettings.show_event_link !== false && (
                            <Input
                              value={eventSettings.external_website_url || ''}
                              onChange={(e) => setEventSettings({...eventSettings, external_website_url: e.target.value})}
                              className="text-sm text-gray-700 border-dashed"
                              placeholder="https://yourevent.com"
                            />
                          )}
                        </div>
                      </div>
                      
                      {/* GATED CONTENT SETTINGS */}
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6 space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-amber-100 rounded-lg">
                            <Lock className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold">Gated Content & Self Check-In</h4>
                            <p className="text-sm text-gray-600">Require attendees to check-in to unlock resources</p>
                          </div>
                        </div>
                        
                        {/* Enable Self Check-in Toggle */}
                        <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div>
                            <p className="font-medium text-sm">Enable Self Check-In</p>
                            <p className="text-xs text-gray-500">Attendees can check-in from confirmation page</p>
                          </div>
                          <Switch
                            checked={intakeSettings.allow_self_checkin !== false}
                            onCheckedChange={(v) => setIntakeSettings({...intakeSettings, allow_self_checkin: v})}
                            data-testid="toggle-self-checkin"
                          />
                        </div>
                        
                        {/* Require Check-in for Content Toggle */}
                        <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div>
                            <p className="font-medium text-sm">Require Check-In to Unlock Content</p>
                            <p className="text-xs text-gray-500">Resources below are hidden until checked in</p>
                          </div>
                          <Switch
                            checked={intakeSettings.require_checkin_for_content === true}
                            onCheckedChange={(v) => setIntakeSettings({...intakeSettings, require_checkin_for_content: v})}
                            data-testid="toggle-require-checkin"
                          />
                        </div>
                        
                        {/* Gated Resources/Links */}
                        {intakeSettings.require_checkin_for_content && (
                          <div className="space-y-3">
                            <Label className="text-sm font-medium">Resources (Unlocked After Check-In)</Label>
                            <p className="text-xs text-gray-500">Add links to materials, lessons, recordings, etc.</p>
                            
                            {/* Resource Links */}
                            {(intakeSettings.gated_resources || []).map((resource, idx) => (
                              <div key={idx} className="flex gap-2">
                                <Input
                                  value={resource.title || ''}
                                  onChange={(e) => {
                                    const updated = [...(intakeSettings.gated_resources || [])];
                                    updated[idx] = {...updated[idx], title: e.target.value};
                                    setIntakeSettings({...intakeSettings, gated_resources: updated});
                                  }}
                                  placeholder="Resource title"
                                  className="flex-1"
                                />
                                <Input
                                  value={resource.url || ''}
                                  onChange={(e) => {
                                    const updated = [...(intakeSettings.gated_resources || [])];
                                    updated[idx] = {...updated[idx], url: e.target.value};
                                    setIntakeSettings({...intakeSettings, gated_resources: updated});
                                  }}
                                  placeholder="https://..."
                                  className="flex-1"
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const updated = (intakeSettings.gated_resources || []).filter((_, i) => i !== idx);
                                    setIntakeSettings({...intakeSettings, gated_resources: updated});
                                  }}
                                >
                                  <X className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            ))}
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const updated = [...(intakeSettings.gated_resources || []), { title: '', url: '' }];
                                setIntakeSettings({...intakeSettings, gated_resources: updated});
                              }}
                              className="w-full"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add Resource Link
                            </Button>
                            
                            {/* Gated Content Text */}
                            <div>
                              <Label className="text-sm font-medium">Gated Content Message</Label>
                              <Textarea
                                value={intakeSettings.gated_content_text || ''}
                                onChange={(e) => setIntakeSettings({...intakeSettings, gated_content_text: e.target.value})}
                                placeholder="Additional information, instructions, or notes that appear after check-in..."
                                rows={3}
                                className="mt-1"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Instructor Resources Curation - Only for instructor events */}
                      {event?.pilot_category === 'instructor' && (
                        <div className="space-y-3 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200" data-testid="instructor-resources-curation">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-emerald-100 rounded-lg">
                              <FileText className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold">Session Resources</h4>
                              <p className="text-sm text-gray-600">Curate what shows on confirmation page after check-in</p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="ml-auto"
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem("session_token");
                                  const res = await fetch(`${API}/events/${eventId}/resources/manage`, {
                                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                                  });
                                  if (res.ok) {
                                    const rdata = await res.json();
                                    setSubmittedResources(rdata.submitted_resources || []);
                                    setApprovedResourceIds(new Set((rdata.approved_resources || []).map(r => r.resource_id)));
                                  }
                                } catch (err) {
                                  console.error("Failed to load resources:", err);
                                }
                              }}
                              data-testid="refresh-resources-btn"
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Refresh
                            </Button>
                          </div>
                          
                          {submittedResources.length > 0 ? (
                            <div className="space-y-2">
                              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                                {submittedResources.length} resource(s) submitted
                              </p>
                              {submittedResources.map((resource) => (
                                <div
                                  key={resource.resource_id}
                                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                                    approvedResourceIds.has(resource.resource_id)
                                      ? 'bg-white border-emerald-300 ring-1 ring-emerald-200'
                                      : 'bg-gray-50 border-gray-200'
                                  }`}
                                  data-testid={`resource-item-${resource.resource_id}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={approvedResourceIds.has(resource.resource_id)}
                                    onChange={(e) => {
                                      setApprovedResourceIds(prev => {
                                        const next = new Set(prev);
                                        if (e.target.checked) next.add(resource.resource_id);
                                        else next.delete(resource.resource_id);
                                        return next;
                                      });
                                    }}
                                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{resource.title}</p>
                                    <p className="text-xs text-gray-500 truncate">{resource.url}</p>
                                    {resource.description && (
                                      <p className="text-xs text-gray-400 truncate">{resource.description}</p>
                                    )}
                                  </div>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    resource.submitted_by === 'instructor'
                                      ? 'bg-purple-100 text-purple-600'
                                      : 'bg-blue-100 text-blue-600'
                                  }`}>
                                    {resource.submitted_by === 'instructor' ? 'Instructor' : 'Host'}
                                  </span>
                                  <button
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem("session_token");
                                        const res = await fetch(`${API}/events/${eventId}/resources/${resource.resource_id}`, {
                                          method: "DELETE",
                                          headers: token ? { Authorization: `Bearer ${token}` } : {}
                                        });
                                        if (res.ok) {
                                          setSubmittedResources(prev => prev.filter(r => r.resource_id !== resource.resource_id));
                                          setApprovedResourceIds(prev => {
                                            const next = new Set(prev);
                                            next.delete(resource.resource_id);
                                            return next;
                                          });
                                          toast.success("Resource deleted");
                                        }
                                      } catch (err) {
                                        toast.error("Failed to delete resource");
                                      }
                                    }}
                                    className="p-1.5 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                                    data-testid={`delete-resource-${resource.resource_id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">No resources submitted yet. Add your own or wait for instructor to submit.</p>
                          )}

                          <div className="flex gap-2">
                            <Input
                              placeholder="Resource title"
                              value={newResourceTitle}
                              onChange={(e) => setNewResourceTitle(e.target.value)}
                              className="flex-1"
                              data-testid="new-resource-title"
                            />
                            <Input
                              placeholder="https://..."
                              value={newResourceUrl}
                              onChange={(e) => setNewResourceUrl(e.target.value)}
                              className="flex-1"
                              data-testid="new-resource-url"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!newResourceTitle || !newResourceUrl}
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem("session_token");
                                  const res = await fetch(`${API}/events/${eventId}/resources/add`, {
                                    method: "POST",
                                    headers: {
                                      "Content-Type": "application/json",
                                      ...(token ? { Authorization: `Bearer ${token}` } : {})
                                    },
                                    body: JSON.stringify({
                                      title: newResourceTitle,
                                      url: newResourceUrl,
                                      submitted_by: "host"
                                    })
                                  });
                                  if (res.ok) {
                                    const rdata = await res.json();
                                    setSubmittedResources(prev => [...prev, rdata.resource]);
                                    setApprovedResourceIds(prev => new Set([...prev, rdata.resource.resource_id]));
                                    setNewResourceTitle("");
                                    setNewResourceUrl("");
                                    toast.success("Resource added!");
                                  }
                                } catch (err) {
                                  toast.error("Failed to add resource");
                                }
                              }}
                              data-testid="add-resource-btn"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>

                          <Button
                            className="w-full"
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem("session_token");
                                const res = await fetch(`${API}/events/${eventId}/resources/curate`, {
                                  method: "POST",
                                  headers: {
                                    "Content-Type": "application/json",
                                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                                  },
                                  body: JSON.stringify({
                                    approved_resource_ids: [...approvedResourceIds]
                                  })
                                });
                                if (res.ok) {
                                  const rdata = await res.json();
                                  toast.success(`${rdata.approved_count} resource(s) approved for attendees`);
                                }
                              } catch (err) {
                                toast.error("Failed to save resource curation");
                              }
                            }}
                            data-testid="save-resources-curation-btn"
                          >
                            <Save className="w-4 h-4 mr-2" />
                            Save Resource Curation ({approvedResourceIds.size} approved)
                          </Button>
                        </div>
                      )}
                      
                    </CardContent>
                  </Card>
                )}

                {/* Instructor Confirmation Message - Editable by Host */}
                {settingsTab === "confirmation" && isInstructorEvent && (
                  <Card data-testid="settings-instructor-confirmation">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <GraduationCap className="w-5 h-5" />
                        Instructor Confirmation Message
                      </CardTitle>
                      <CardDescription>
                        Customize the message instructors see after submitting their intake
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <textarea
                        value={eventSettings.instructor_confirmation_message || ''}
                        onChange={(e) => setEventSettings(prev => ({ ...prev, instructor_confirmation_message: e.target.value }))}
                        placeholder="Thanks for submitting! We're setting up the event and will share updates along the way."
                        className="w-full text-sm border border-gray-200 rounded-lg p-3 h-24 resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        data-testid="instructor-confirmation-message"
                      />
                      <p className="text-xs text-gray-400 mt-2">This message appears on the instructor's confirmation page after they complete the intake flow.</p>
                    </CardContent>
                  </Card>
                )}


                {/* WALLET PASS - Now part of Confirmation tab */}
                {(settingsTab === "confirmation" || settingsTab === "wallet-pass") && (
                  <Card data-testid="settings-wallet-pass">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5" />
                        Apple Wallet Pass
                      </CardTitle>
                      <CardDescription>
                        Customize how your event pass appears in Apple Wallet
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Enable/Disable Toggle */}
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <Label className="text-base font-medium">Enable Apple Wallet</Label>
                          <p className="text-sm text-gray-500 mt-1">Show "Add to Apple Wallet" button on confirmation pages</p>
                        </div>
                        <Switch
                          checked={event?.show_apple_wallet || false}
                          onCheckedChange={async (checked) => {
                            try {
                              const token = localStorage.getItem("session_token");
                              const res = await fetch(`${API}/events/${eventId}`, {
                                method: "PUT",
                                headers: {
                                  "Content-Type": "application/json",
                                  Authorization: `Bearer ${token}`
                                },
                                body: JSON.stringify({ show_apple_wallet: checked })
                              });
                              if (res.ok) {
                                mutateEvent(); // Refresh event data
                                toast.success(checked ? "Apple Wallet enabled" : "Apple Wallet disabled");
                              } else {
                                const errData = await res.json().catch(() => ({}));
                                console.error("Failed to update:", errData);
                                toast.error(errData.detail || "Failed to update setting");
                              }
                            } catch (err) {
                              console.error("Error:", err);
                              toast.error("Failed to update setting");
                            }
                          }}
                          data-testid="toggle-apple-wallet"
                        />
                      </div>

                      {/* Pass Preview */}
                      <div className="bg-gray-100 rounded-xl p-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-4">Pass Preview</h4>
                        
                        {/* Simulated Wallet Pass Card - Modern Design */}
                        <div className="mx-auto max-w-xs rounded-2xl overflow-hidden shadow-xl bg-white">
                          {/* Category-Themed Banner Strip with Event Cover Overlay */}
                          <div className="h-28 relative">
                            {/* Category-Themed Background */}
                            <div 
                              className="absolute inset-0"
                              style={{
                                backgroundImage: event?.background_theme_url || event?.attendee_card_background 
                                  ? `url(${event?.background_theme_url || event?.attendee_card_background})`
                                  : undefined,
                                background: !(event?.background_theme_url || event?.attendee_card_background) 
                                  ? `linear-gradient(135deg, ${catIconColor}25 0%, ${catIconColor}50 50%, ${catIconColor}25 100%)`
                                  : undefined,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                              }}
                            />
                            
                            {/* Event Cover Image - Square Overlapping */}
                            {event?.cover_image && (
                              <div className="absolute -bottom-8 left-4 w-20 h-20 rounded-xl overflow-hidden shadow-lg border-4 border-white z-10">
                                <img 
                                  src={getImageUrl(event.cover_image)} 
                                  alt="Event" 
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            )}
                            
                            {/* Guest Type Label - Modern Opacity Style */}
                            <div className="absolute top-3 right-3">
                              <span className="px-3 py-1 bg-black/20 backdrop-blur-sm rounded-full text-white text-xs font-semibold tracking-wider">
                                VIP
                              </span>
                            </div>
                            
                            {/* Phygital branding */}
                            <div className="absolute bottom-2 right-3">
                              <span className="text-white/60 text-xs font-medium tracking-wider">phygital</span>
                            </div>
                          </div>
                          
                          {/* Pass Content - White Background */}
                          <div className="p-4 pt-10 space-y-3 bg-white">
                            {/* Guest Name */}
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Guest</p>
                              <p className="text-xl font-semibold text-gray-900">Sample Guest</p>
                            </div>
                            
                            {/* Event Name - Full Display */}
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Event</p>
                              <p className="text-base font-medium text-gray-800">{event?.name || 'Event Name'}</p>
                            </div>
                            
                            {/* Date & Location */}
                            <div className="flex justify-between pt-2">
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Date</p>
                                <p className="text-sm text-gray-800">{event?.date ? new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD'}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Location</p>
                                <p className="text-sm text-gray-800 truncate max-w-[140px]">{event?.location || 'TBD'}</p>
                              </div>
                            </div>
                            
                            {/* NFC Icon Only */}
                            <div className="flex justify-center pt-3">
                              <div className="p-3 bg-gray-100 rounded-full">
                                <svg className="w-6 h-6 text-gray-500" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>
                                  <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>
                                  <circle cx="12" cy="12" r="2"/>
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Background Theme Info */}
                      <div className="space-y-3">
                        <Label>Background Theme</Label>
                        <p className="text-sm text-gray-600">
                          The pass uses your <strong>Attendee Card Background</strong> as the banner theme. 
                          This creates a cohesive look across your event experience.
                        </p>
                        <div className="flex items-center gap-3">
                          {(event?.background_theme_url || event?.attendee_card_background) ? (
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-24 h-12 rounded-lg border-2 border-gray-200"
                                style={{
                                  backgroundImage: `url(${event?.background_theme_url || event?.attendee_card_background})`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center'
                                }}
                              />
                              <span className="text-sm text-green-600 font-medium">✓ Theme Applied</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-24 h-12 rounded-lg border-2 border-gray-200" 
                                style={{ background: `linear-gradient(135deg, ${catIconColor}25 0%, ${catIconColor}50 50%, ${catIconColor}25 100%)` }}
                              />
                              <span className="text-sm text-gray-500">Default Category Theme (Set in People Info settings)</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Info Box */}
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h4 className="font-medium text-blue-900 mb-2">How it works</h4>
                        <ul className="text-sm text-blue-800 space-y-1">
                          <li>• Guests see "Add to Apple Wallet" button on their confirmation page</li>
                          <li>• Pass shows guest name, type (VIP, Attendee, etc.), and full event name</li>
                          <li>• <strong>NFC tap</strong> enables instant check-in (no QR code needed)</li>
                          <li>• White background ensures clean, readable text</li>
                          <li>• Event cover image appears as a square thumbnail</li>
                          <li>• Back of pass has links to confirmation page and preference card</li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* TECH STACK - Now part of Team tab */}
                {/* Links & Social Settings Tab */}
                {settingsTab === "links" && (
                  <Card data-testid="settings-links">
                    <CardHeader>
                      <CardTitle>Links & Social Media</CardTitle>
                      <CardDescription>Add CTA links and social media profiles for your event</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* CTA Links */}
                      <div>
                        <h3 className="font-medium text-sm mb-3">CTA Links</h3>
                        <p className="text-xs text-gray-500 mb-3">Add clickable links shown on the confirmation page</p>
                        <div className="space-y-3">
                          {(eventSettings.cta_links || []).map((cta, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-3 bg-gray-50 rounded-xl border">
                              <div className="flex-1 space-y-2">
                                <Input
                                  value={cta.text || ''}
                                  onChange={(e) => {
                                    const updated = [...(eventSettings.cta_links || [])];
                                    updated[idx] = {...updated[idx], text: e.target.value};
                                    setEventSettings(prev => ({...prev, cta_links: updated}));
                                  }}
                                  placeholder="Link text"
                                  className="text-sm h-8"
                                  data-testid={`cta-text-${idx}`}
                                />
                                <Input
                                  value={cta.url || ''}
                                  onChange={(e) => {
                                    const updated = [...(eventSettings.cta_links || [])];
                                    updated[idx] = {...updated[idx], url: e.target.value};
                                    setEventSettings(prev => ({...prev, cta_links: updated}));
                                  }}
                                  placeholder="https://..."
                                  className="text-sm h-8"
                                  data-testid={`cta-url-${idx}`}
                                />
                                <Input
                                  value={cta.description || ''}
                                  onChange={(e) => {
                                    const updated = [...(eventSettings.cta_links || [])];
                                    updated[idx] = {...updated[idx], description: e.target.value};
                                    setEventSettings(prev => ({...prev, cta_links: updated}));
                                  }}
                                  placeholder="Description (optional)"
                                  className="text-sm h-8"
                                  data-testid={`cta-desc-${idx}`}
                                />
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => {
                                const updated = (eventSettings.cta_links || []).filter((_, i) => i !== idx);
                                setEventSettings(prev => ({...prev, cta_links: updated}));
                              }} className="text-red-500 hover:text-red-700 mt-1">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                          <Button variant="outline" size="sm" onClick={() => {
                            const updated = [...(eventSettings.cta_links || []), { text: '', url: '', description: '' }];
                            setEventSettings(prev => ({...prev, cta_links: updated}));
                          }} className="gap-2" data-testid="add-cta-link-btn">
                            <Plus className="w-4 h-4" /> Add CTA Link
                          </Button>
                        </div>
                      </div>
                      
                      {/* Social Media Links */}
                      <div>
                        <h3 className="font-medium text-sm mb-3">Host Social Media</h3>
                        <p className="text-xs text-gray-500 mb-3">Shown on event pages under "Hosted By"</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Instagram</Label>
                            <Input
                              value={eventSettings.host_social_links?.instagram || ''}
                              onChange={(e) => setEventSettings(prev => ({...prev, host_social_links: {...(prev.host_social_links || {}), instagram: e.target.value}}))}
                              placeholder="@handle or URL"
                              className="mt-1 text-sm h-9"
                              data-testid="host-social-instagram"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">LinkedIn</Label>
                            <Input
                              value={eventSettings.host_social_links?.linkedin || ''}
                              onChange={(e) => setEventSettings(prev => ({...prev, host_social_links: {...(prev.host_social_links || {}), linkedin: e.target.value}}))}
                              placeholder="URL"
                              className="mt-1 text-sm h-9"
                              data-testid="host-social-linkedin"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">X / Twitter</Label>
                            <Input
                              value={eventSettings.host_social_links?.twitter || ''}
                              onChange={(e) => setEventSettings(prev => ({...prev, host_social_links: {...(prev.host_social_links || {}), twitter: e.target.value}}))}
                              placeholder="@handle or URL"
                              className="mt-1 text-sm h-9"
                              data-testid="host-social-twitter"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Discord</Label>
                            <Input
                              value={eventSettings.host_social_links?.discord || ''}
                              onChange={(e) => setEventSettings(prev => ({...prev, host_social_links: {...(prev.host_social_links || {}), discord: e.target.value}}))}
                              placeholder="Invite URL"
                              className="mt-1 text-sm h-9"
                              data-testid="host-social-discord"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs">Website</Label>
                            <Input
                              value={eventSettings.host_social_links?.website || ''}
                              onChange={(e) => setEventSettings(prev => ({...prev, host_social_links: {...(prev.host_social_links || {}), website: e.target.value}}))}
                              placeholder="https://..."
                              className="mt-1 text-sm h-9"
                              data-testid="host-social-website"
                            />
                          </div>
                        </div>
                      </div>
                      
                    </CardContent>
                  </Card>
                )}
                
                {(settingsTab === "team" || settingsTab === "techstack") && (
                  <Card data-testid="settings-techstack">
                    <CardHeader>
                      <CardTitle>{dashboardType === DASHBOARD_TYPES.PROVIDER ? "Tools that power your business" : "Tech Stack"}</CardTitle>
                      <CardDescription>{dashboardType === DASHBOARD_TYPES.PROVIDER ? "Manage integrations for your business" : "Manage tools and integrations for your event"} - changes sync to Overview</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* TOOLS Section */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="font-medium">Tools</h3>
                            <p className="text-sm text-gray-500">Event-specific tools you're using</p>
                          </div>
                          <div className="flex gap-2">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm">
                                  <Plus className="w-4 h-4 mr-1" />
                                  Add Tool
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {[
                                  { name: 'Acuity Scheduling', logo: 'https://customer-assets.emergentagent.com/job_b6326545-361d-4d9b-a96f-1a74e4c12cbc/artifacts/caj39uiq_382266660_333002445954278_4611861879541242764_n.jpg', desc: 'Appointment booking & scheduling', link: 'https://acuityscheduling.com' },
                                  { name: 'Notion', logo: 'https://customer-assets.emergentagent.com/job_syncnow/artifacts/7cjcjd0o_notioncover.png', desc: 'Event planning & documentation', link: 'https://notion.so' },
                                  { name: 'Canva', logo: 'https://customer-assets.emergentagent.com/job_syncnow/artifacts/pg1wo99l_canvacover.png', desc: 'Design & visual assets', link: 'https://canva.com' },
                                  { name: 'Partiful', logo: 'https://customer-assets.emergentagent.com/job_syncnow/artifacts/c82wgs2i_partifulcover.png', desc: 'Event invitations', link: 'https://partiful.com' },
                                  { name: 'Posh VIP', logo: 'https://customer-assets.emergentagent.com/job_syncnow/artifacts/y1g7oxeg_poshvipcover.png', desc: 'VIP guest management', link: 'https://posh.vip' },
                                  { name: 'Tap In', logo: TAP_IN_LOGO, desc: 'Music & playlist', link: 'https://tapin.fm' }
                                ].map((tool) => (
                                  <DropdownMenuItem 
                                    key={tool.name}
                                    onClick={() => {
                                      const currentTools = techStack.tools || [];
                                      if (!currentTools.find(t => t.name === tool.name)) {
                                        const newTools = [...currentTools, { name: tool.name, logo: tool.logo, description: tool.desc, link: tool.link }];
                                        setTechStack(prev => ({ ...prev, tools: newTools }));
                                        const token = localStorage.getItem('session_token');
                                        fetch(`${API}/events/${eventId}`, {
                                          method: 'PUT',
                                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                          body: JSON.stringify({ tech_stack: { ...techStack, tools: newTools } })
                                        }).then(() => {
                                          mutateEvent();
                                          triggerGlobalSync('all', eventId);
                                          toast.success(`${tool.name} added`);
                                        });
                                      } else {
                                        toast.info(`${tool.name} already added`);
                                      }
                                    }}
                                  >
                                    <img src={tool.logo} alt={tool.name} className="w-5 h-5 rounded mr-2" />
                                    {tool.name}
                                  </DropdownMenuItem>
                                ))}
                                <DropdownMenuItem
                                  onClick={() => {
                                    // Show custom tool modal (NO SYSTEM PROMPTS)
                                    setNewToolData({ name: '', link: '', description: '' });
                                    setShowAddToolModal(true);
                                  }}
                                >
                                  <Plus className="w-4 h-4 mr-2" />
                                  Custom Tool...
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          {/* Tools from techStack local state */}
                          {(techStack.tools || []).map((tool, idx) => (
                            <div key={idx} className="p-4 border rounded-lg group" data-testid={`tool-item-${idx}`}>
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3 flex-1">
                                  {tool.logo ? (
                                    <img src={tool.logo} alt={tool.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                                  ) : (
                                    <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                                      <span className="text-gray-500 font-bold text-sm">{tool.name?.charAt(0)}</span>
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium">{tool.name}</p>
                                      {tool.link && (
                                        <a href={tool.link} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline text-xs">
                                          <ExternalLink className="w-3 h-3 inline" />
                                        </a>
                                      )}
                                    </div>
                                    <p className="text-sm text-gray-500">{tool.description}</p>
                                    {tool.link && (
                                      <p className="text-xs text-gray-400 truncate">{tool.link}</p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                  {/* Edit button */}
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => {
                                      setEditingTool({ ...tool, idx });
                                      setNewToolData({ name: tool.name, link: tool.link || '', description: tool.description || '' });
                                      setShowEditToolModal(true);
                                    }}
                                    data-testid={`edit-tool-${idx}`}
                                  >
                                    <Edit3 className="w-4 h-4 text-gray-500" />
                                  </Button>
                                  {/* Delete button */}
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => {
                                      const currentTools = techStack.tools || [];
                                      const newTools = currentTools.filter((_, i) => i !== idx);
                                      setTechStack(prev => ({ ...prev, tools: newTools }));
                                      const token = localStorage.getItem('session_token');
                                      fetch(`${API}/events/${eventId}`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                        body: JSON.stringify({ tech_stack: { ...techStack, tools: newTools } })
                                      }).then(() => {
                                        mutateEvent();
                                        triggerGlobalSync('all', eventId);
                                        toast.success(`${tool.name} removed`);
                                      });
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          {(techStack.tools || []).length === 0 && (
                            <div className="text-center py-8 text-gray-400 border-2 border-dashed rounded-lg">
                              <Zap className="w-8 h-8 mx-auto mb-2 opacity-50" />
                              <p>No tools added yet</p>
                              <p className="text-sm">Click "Add Tool" to get started</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <Separator />

                      {/* INTEGRATIONS Section */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="font-medium">Integrations</h3>
                            <p className="text-sm text-gray-500">Connected services</p>
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          {/* INSTRUCTOR ADD-ON Self-Serve */}
                          {['makeup','hair','lashes','nails','culinary','wellness','dispensary'].includes(
                            (event?.pilot_category || event?.category || '').toLowerCase()
                          ) && (
                          <div className="p-4 border rounded-lg bg-gradient-to-br from-indigo-50 to-white" data-testid="instructor-addon-integration-card">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                                  <GraduationCap className="w-5 h-5 text-indigo-600" />
                                </div>
                                <div>
                                  <p className="font-medium">{t("host.instructor_addon.title", "Instructor Add-On Dashboard")}</p>
                                  <p className="text-sm text-gray-500">Add a class-specific dashboard with instructor intake + preference insights.</p>
                                </div>
                              </div>
                              {event?.linked_instructor_dashboard_id ? (
                                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700" data-testid="instructor-addon-enabled-badge">Enabled</span>
                              ) : null}
                            </div>
                            {event?.linked_instructor_dashboard_id ? (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => window.location.assign(`/host/v2/${event.linked_instructor_dashboard_id}`)}
                                  data-testid="instructor-addon-open-btn"
                                >
                                  {t("host.instructor_addon.open_cta", "Open Instructor Dashboard")}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={async () => {
                                    if (!window.confirm("Disable the Instructor Add-On? Your data is preserved but the dashboard is archived.")) return;
                                    const token = localStorage.getItem("session_token");
                                    try {
                                      const res = await fetch(`${API}/host/events/${eventId}/disable-instructor-addon`, {
                                        method: "POST",
                                        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                      });
                                      if (!res.ok) throw new Error(await res.text());
                                      toast.success("Instructor Add-On disabled");
                                      mutateEvent();
                                    } catch (err) {
                                      toast.error("Could not disable: " + err.message);
                                    }
                                  }}
                                  data-testid="instructor-addon-disable-btn"
                                >
                                  Disable
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                onClick={async () => {
                                  const token = localStorage.getItem("session_token");
                                  try {
                                    const res = await fetch(`${API}/host/events/${eventId}/enable-instructor-addon`, {
                                      method: "POST",
                                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                      body: JSON.stringify({}),
                                    });
                                    const data = await res.json();
                                    if (!res.ok) throw new Error(data?.detail || "Could not enable");
                                    toast.success("Instructor Add-On enabled");
                                    mutateEvent();
                                    setTimeout(() => window.location.assign(`/host/v2/${data.event_id}`), 700);
                                  } catch (err) {
                                    toast.error(String(err.message || err));
                                  }
                                }}
                                data-testid="instructor-addon-enable-btn"
                              >
                                {t("host.instructor_addon.enable_cta", "Enable Instructor Add-On")}
                              </Button>
                            )}
                          </div>
                          )}

                          {/* Acuity Scheduling Integration */}
                          <div className="p-4 border rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <img 
                                  src="https://customer-assets.emergentagent.com/job_b6326545-361d-4d9b-a96f-1a74e4c12cbc/artifacts/caj39uiq_382266660_333002445954278_4611861879541242764_n.jpg" 
                                  alt="Acuity Scheduling" 
                                  className="w-10 h-10 rounded-lg object-cover"
                                />
                                <div>
                                  <p className="font-medium">Acuity Scheduling</p>
                                  <p className="text-sm text-gray-500">Appointment booking & client management</p>
                                </div>
                              </div>
                              <Switch
                                checked={!!(techStack.acuity?.enabled)}
                                onCheckedChange={async (val) => {
                                  const ts = { ...(techStack || {}), acuity: { ...(techStack?.acuity || {}), enabled: val } };
                                  setTechStack(ts);
                                  const token = localStorage.getItem('session_token');
                                  await fetch(`${API}/events/${eventId}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                    body: JSON.stringify({ tech_stack: ts })
                                  });
                                  mutateEvent();
                                  toast.success(`Acuity ${val ? 'enabled' : 'disabled'}`);
                                }}
                                data-testid="acuity-toggle"
                              />
                            </div>
                            
                            {techStack.acuity?.enabled && (
                              <div className="pt-3 border-t space-y-3">
                                {/* OAuth connect button */}
                                {techStack.acuity?.status !== "connected" ? (
                                  <Button
                                    size="sm"
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem("session_token");
                                        const res = await fetch(`${API}/acuity/auth-url?event_id=${eventId}`, {
                                          headers: { Authorization: `Bearer ${token}` },
                                        });
                                        const data = await res.json();
                                        if (data?.auth_url) {
                                          window.open(data.auth_url, "_blank", "width=600,height=700");
                                          toast.info("Connect your Acuity account in the popup window.");
                                        } else {
                                          toast.error(data?.detail || "Could not start Acuity connection");
                                        }
                                      } catch (e) {
                                        toast.error("Connect failed: " + e.message);
                                      }
                                    }}
                                    data-testid="acuity-oauth-btn"
                                    className="bg-purple-600 hover:bg-purple-700"
                                  >
                                    <ExternalLink className="w-3 h-3 mr-1.5" />
                                    Connect Acuity Account
                                  </Button>
                                ) : (
                                  <div className="flex items-center gap-2 text-sm text-green-600">
                                    <CheckCircle2 className="w-4 h-4" />
                                    Connected to Acuity
                                  </div>
                                )}

                                {/* Scheduling URL - host's public booking page */}
                                <div>
                                  <Label className="text-xs">Your Acuity scheduling URL</Label>
                                  <Input
                                    value={techStack.acuity?.scheduling_url || ""}
                                    onChange={(e) => {
                                      const ts = { ...(techStack || {}), acuity: { ...(techStack?.acuity || {}), scheduling_url: e.target.value } };
                                      setTechStack(ts);
                                    }}
                                    onBlur={async () => {
                                      const token = localStorage.getItem("session_token");
                                      await fetch(`${API}/events/${eventId}`, {
                                        method: "PUT",
                                        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                        body: JSON.stringify({ tech_stack: techStack }),
                                      });
                                      toast.success("Scheduling URL saved");
                                    }}
                                    placeholder="https://yourname.acuityscheduling.com"
                                    className="mt-1"
                                    data-testid="acuity-scheduling-url"
                                  />
                                  <p className="text-[10px] text-gray-400 mt-1">
                                    The Acuity tile in your dashboard will link directly to this URL.
                                  </p>
                                </div>

                                <Button variant="outline" size="sm" onClick={() => setActiveTab("services")} data-testid="acuity-configure-btn">
                                  Configure sync settings →
                                </Button>
                              </div>
                            )}
                          </div>

                          {/* Tap In Integration */}
                          <div className="p-4 border rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <img 
                                  src={TAP_IN_LOGO} 
                                  alt="Tap In" 
                                  className="w-10 h-10 rounded-lg object-cover"
                                />
                                <div>
                                  <p className="font-medium">Tap In Music</p>
                                  <p className="text-sm text-gray-500">Guest music preferences & playlist</p>
                                </div>
                              </div>
                              <Switch
                                checked={!!intakeSettings.tapin_enabled}
                                onCheckedChange={async (val) => {
                                  const updated = { ...intakeSettings, tapin_enabled: val };
                                  setIntakeSettings(updated);
                                  const ts = { ...(techStack || {}), tapin: { ...(techStack?.tapin || {}), enabled: val } };
                                  setTechStack(ts);
                                  const token = localStorage.getItem('session_token');
                                  await fetch(`${API}/events/${eventId}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                    body: JSON.stringify({ intake_settings: updated, tech_stack: ts })
                                  });
                                  toast.success(`Tap In ${val ? 'enabled' : 'disabled'}`);
                                }}
                                data-testid="tapin-toggle"
                              />
                            </div>
                            
                            {intakeSettings.tapin_enabled && (
                              <div className="pt-3 border-t space-y-3">
                                <div>
                                  <Label className="text-xs">Playlist Link</Label>
                                  <Input
                                    value={intakeSettings.tapin_playlist_url || ''}
                                    onChange={(e) => setIntakeSettings({...intakeSettings, tapin_playlist_url: e.target.value})}
                                    placeholder="https://tapin.fm/playlist/..."
                                    className="mt-1"
                                  />
                                  <p className="text-xs text-gray-400 mt-1">Shared with RSVPs via quick share links</p>
                                </div>
                                <div>
                                  <Label className="text-xs">DJ / Music Host Insights Link</Label>
                                  <Input
                                    value={intakeSettings.tapin_dj_url || ''}
                                    onChange={(e) => setIntakeSettings({...intakeSettings, tapin_dj_url: e.target.value})}
                                    placeholder="https://tapin.fm/insights/..."
                                    className="mt-1"
                                  />
                                  <p className="text-xs text-gray-400 mt-1">Share with your DJ to see guest music picks</p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Beehiiv - Auto-connected with cover image */}
                          <div className="p-4 border rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-lg overflow-hidden bg-yellow-50">
                                  <img 
                                    src="/assets/images/beehiiv-logo.jpeg" 
                                    alt="Beehiiv" 
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div>
                                  <p className="font-medium">Beehiiv</p>
                                  <p className="text-sm text-gray-500">Email automation (Internal)</p>
                                </div>
                              </div>
                              <Badge className="bg-green-100 text-green-700">Auto-connected</Badge>
                            </div>
                            <p className="text-xs text-gray-500">Automatically syncs RSVPs, status changes, and guest preferences for targeted email campaigns.</p>
                          </div>
                        </div>
                      </div>

                    </CardContent>
                  </Card>
                )}


                {/* ============ RESOURCES HUB ============ */}
                {settingsTab === "resources" && (
                  <Card data-testid="settings-resources">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Link2 className="w-5 h-5 text-purple-500" />
                        {dashboardType === DASHBOARD_TYPES.PROVIDER ? "Service Links" : "Event Resources"}
                      </CardTitle>
                      <CardDescription>
                        {dashboardType === DASHBOARD_TYPES.PROVIDER
                          ? "Manage links for your clients at each stage of the service journey."
                          : "Manage resources for your event. Control when each category becomes available to attendees."}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      
                      {/* Availability Toggles */}
                      <div className="grid grid-cols-3 gap-3">
                        {(dashboardType === DASHBOARD_TYPES.PROVIDER ? [
                          { key: "pre_event", label: "Pre-Appointment", desc: "Shared after booking", color: "blue" },
                          { key: "check_in", label: "During Visit", desc: "Available at appointment", color: "green" },
                          { key: "post_survey", label: "Aftercare", desc: "Sent after service", color: "purple" }
                        ] : [
                          { key: "pre_event", label: "Pre-Event", desc: "Available after RSVP", color: "blue" },
                          { key: "check_in", label: "Check-In", desc: "Toggle when event starts", color: "green" },
                          { key: "post_survey", label: "Post-Survey", desc: "Unlocked after survey", color: "purple" }
                        ]).map(cat => (
                          <div 
                            key={cat.key}
                            className={`p-3 rounded-xl border transition-all ${
                              resourceAvailability[cat.key] 
                                ? `bg-${cat.color}-50 border-${cat.color}-200` 
                                : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium">{cat.label}</span>
                              <Switch
                                checked={resourceAvailability[cat.key]}
                                onCheckedChange={async (v) => {
                                  const updated = { ...resourceAvailability, [cat.key]: v };
                                  setResourceAvailability(updated);
                                  try {
                                    await fetch(`${API}/events/${eventId}/resource-availability`, {
                                      method: "PUT",
                                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${sessionToken}` },
                                      body: JSON.stringify({ [cat.key]: v })
                                    });
                                    toast.success(`${cat.label} ${dashboardType === DASHBOARD_TYPES.PROVIDER ? 'links' : 'resources'} ${v ? 'enabled' : 'disabled'}`);
                                  } catch (err) {
                                    toast.error("Failed to update availability");
                                  }
                                }}
                                data-testid={`toggle-resource-${cat.key}`}
                              />
                            </div>
                            <p className="text-[10px] text-gray-500">{cat.desc}</p>
                            <p className="text-xs font-medium mt-1">
                              {eventResources.filter(r => r.category === cat.key).length} {dashboardType === DASHBOARD_TYPES.PROVIDER ? "links" : "resources"}
                            </p>
                          </div>
                        ))}
                      </div>

                      {/* Add Resource Button */}
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm">
                          {dashboardType === DASHBOARD_TYPES.PROVIDER ? "All Links" : "All Resources"} ({eventResources.length})
                        </h4>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => { setShowAddResource(!showAddResource); setEditingResourceId(null); setNewResource({ title: "", description: "", link: "", image_url: "", category: "pre_event" }); }}
                          data-testid="add-resource-btn"
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" />
                          {dashboardType === DASHBOARD_TYPES.PROVIDER ? "Add Link" : "Add Resource"}
                        </Button>
                      </div>

                      {/* Add/Edit Resource Form */}
                      {showAddResource && (
                        <div className="border rounded-xl p-4 bg-gray-50 space-y-3" data-testid="resource-form">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Title *</Label>
                              <Input
                                value={newResource.title}
                                onChange={(e) => setNewResource(prev => ({ ...prev, title: e.target.value }))}
                                placeholder={dashboardType === DASHBOARD_TYPES.PROVIDER ? "e.g. Skin prep guide" : "Session Recording"}
                                className="mt-1 text-sm"
                                data-testid="resource-title"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Category</Label>
                              <select
                                value={newResource.category}
                                onChange={(e) => setNewResource(prev => ({ ...prev, category: e.target.value }))}
                                className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white"
                                data-testid="resource-category"
                              >
                                <option value="pre_event">{dashboardType === DASHBOARD_TYPES.PROVIDER ? "Pre-Appointment" : "Pre-Event"}</option>
                                <option value="check_in">{dashboardType === DASHBOARD_TYPES.PROVIDER ? "During Visit" : "Check-In"}</option>
                                <option value="post_survey">{dashboardType === DASHBOARD_TYPES.PROVIDER ? "Aftercare" : "Post-Survey"}</option>
                              </select>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">Description</Label>
                            <Input
                              value={newResource.description}
                              onChange={(e) => setNewResource(prev => ({ ...prev, description: e.target.value }))}
                              placeholder={dashboardType === DASHBOARD_TYPES.PROVIDER ? "Notes for your client about this link" : "Watch the full session replay"}
                              className="mt-1 text-sm"
                              data-testid="resource-description"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Link (optional)</Label>
                            <Input
                              value={newResource.link}
                              onChange={(e) => setNewResource(prev => ({ ...prev, link: e.target.value }))}
                              placeholder="https://..."
                              className="mt-1 text-sm"
                              data-testid="resource-link"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Image (optional)</Label>
                            <div className="mt-1 flex items-center gap-2">
                              {newResource.image_url ? (
                                <div className="relative w-20 h-14 rounded-lg overflow-hidden border">
                                  <img src={newResource.image_url.startsWith('/') ? `${API.replace('/api','')}${newResource.image_url}` : newResource.image_url} alt="" className="w-full h-full object-cover" />
                                  <button 
                                    onClick={() => setNewResource(prev => ({ ...prev, image_url: "" }))}
                                    className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : null}
                              <label className="flex-1 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-center text-xs text-gray-500 cursor-pointer hover:bg-gray-100 transition-colors">
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    const formData = new FormData();
                                    formData.append("file", file);
                                    formData.append("context", eventId);
                                    formData.append("type", "resource");
                                    try {
                                      const res = await fetch(`${API}/upload/image`, {
                                        method: "POST",
                                        headers: { Authorization: `Bearer ${sessionToken}` },
                                        body: formData
                                      });
                                      if (res.ok) {
                                        const data = await res.json();
                                        setNewResource(prev => ({ ...prev, image_url: data.url }));
                                      }
                                    } catch (err) {
                                      toast.error("Upload failed");
                                    }
                                  }}
                                  data-testid="resource-image-upload"
                                />
                                Upload image
                              </label>
                            </div>
                          </div>
                          <div className="flex gap-2 pt-2">
                            <Button
                              size="sm"
                              className="bg-black text-white hover:bg-gray-800"
                              disabled={!newResource.title}
                              onClick={async () => {
                                try {
                                  if (editingResourceId) {
                                    // Update existing
                                    await fetch(`${API}/events/${eventId}/resources/${editingResourceId}`, {
                                      method: "PUT",
                                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${sessionToken}` },
                                      body: JSON.stringify(newResource)
                                    });
                                    setEventResources(prev => prev.map(r => r.resource_id === editingResourceId ? { ...r, ...newResource } : r));
                                    toast.success("Resource updated");
                                  } else {
                                    // Add new
                                    const res = await fetch(`${API}/events/${eventId}/resources`, {
                                      method: "POST",
                                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${sessionToken}` },
                                      body: JSON.stringify(newResource)
                                    });
                                    const data = await res.json();
                                    if (data.resource) {
                                      setEventResources(prev => [...prev, data.resource]);
                                    }
                                    toast.success("Resource added");
                                  }
                                  setShowAddResource(false);
                                  setEditingResourceId(null);
                                  setNewResource({ title: "", description: "", link: "", image_url: "", category: "pre_event" });
                                } catch (err) {
                                  toast.error("Failed to save resource");
                                }
                              }}
                              data-testid="save-resource-btn"
                            >
                              {editingResourceId ? "Update" : "Add"} {dashboardType === DASHBOARD_TYPES.PROVIDER ? "Link" : "Resource"}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setShowAddResource(false); setEditingResourceId(null); }}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Resource List - Grouped by Category */}
                      {["pre_event", "check_in", "post_survey"].map(cat => {
                        const catResources = eventResources.filter(r => r.category === cat);
                        if (catResources.length === 0) return null;
                        const catLabel = dashboardType === DASHBOARD_TYPES.PROVIDER
                          ? (cat === "pre_event" ? "Pre-Appointment" : cat === "check_in" ? "During Visit" : "Aftercare")
                          : (cat === "pre_event" ? "Pre-Event" : cat === "check_in" ? "Check-In" : "Post-Survey");
                        const catColor = cat === "pre_event" ? "blue" : cat === "check_in" ? "green" : "purple";
                        
                        return (
                          <div key={cat}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full bg-${catColor}-100 text-${catColor}-700`}>
                                {catLabel}
                              </span>
                              <span className="text-xs text-gray-400">{catResources.length} {dashboardType === DASHBOARD_TYPES.PROVIDER ? "link" : "resource"}{catResources.length !== 1 ? 's' : ''}</span>
                              {!resourceAvailability[cat] && (
                                <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Hidden</span>
                              )}
                            </div>
                            <div className="space-y-2">
                              {catResources.map(resource => (
                                <div 
                                  key={resource.resource_id} 
                                  className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
                                    resourceAvailability[cat] ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'
                                  }`}
                                  data-testid={`resource-card-${resource.resource_id}`}
                                >
                                  {resource.image_url && (
                                    <img 
                                      src={resource.image_url.startsWith('/') ? `${API.replace('/api','')}${resource.image_url}` : resource.image_url} 
                                      alt="" 
                                      className="w-16 h-12 rounded-lg object-cover border border-gray-100 flex-shrink-0" 
                                    />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <h5 className="text-sm font-medium text-gray-900 truncate">{resource.title}</h5>
                                    {resource.description && (
                                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{resource.description}</p>
                                    )}
                                    {resource.link && (
                                      <a href={resource.link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-500 hover:underline flex items-center gap-1 mt-1">
                                        <Link2 className="w-3 h-3" />
                                        {resource.link.replace(/^https?:\/\//, '').slice(0, 40)}
                                      </a>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <button
                                      onClick={() => {
                                        setNewResource({
                                          title: resource.title,
                                          description: resource.description || "",
                                          link: resource.link || "",
                                          image_url: resource.image_url || "",
                                          category: resource.category
                                        });
                                        setEditingResourceId(resource.resource_id);
                                        setShowAddResource(true);
                                      }}
                                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                                      data-testid={`edit-resource-${resource.resource_id}`}
                                    >
                                      <Edit3 className="w-3.5 h-3.5 text-gray-400" />
                                    </button>
                                    <button
                                      onClick={async () => {
                                        try {
                                          await fetch(`${API}/events/${eventId}/resources/${resource.resource_id}`, {
                                            method: "DELETE",
                                            headers: { Authorization: `Bearer ${sessionToken}` }
                                          });
                                          setEventResources(prev => prev.filter(r => r.resource_id !== resource.resource_id));
                                          toast.success("Resource deleted");
                                        } catch (err) {
                                          toast.error("Failed to delete");
                                        }
                                      }}
                                      className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                                      data-testid={`delete-resource-${resource.resource_id}`}
                                    >
                                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}

                      {eventResources.length === 0 && !showAddResource && (
                        <div className="text-center py-8 text-gray-400">
                          <Link2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
                          <p className="text-sm">{dashboardType === DASHBOARD_TYPES.PROVIDER ? "No links added yet" : "No resources added yet"}</p>
                          <p className="text-xs mt-1">{dashboardType === DASHBOARD_TYPES.PROVIDER ? "Add pre-appointment notes, aftercare guides, product links, and more" : "Add recordings, slides, links, and more"}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}


                {/* TEAM */}
                {settingsTab === "team" && (
                  <GatedFeature featureKey="team_management">
                  <Card data-testid="settings-team">
                    <CardHeader>
                      <CardTitle>Team & Partners</CardTitle>
                      <CardDescription>Manage your host profile, team members, and organizations. All changes sync across the platform.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                    
                      {/* === YOUR HOST PROFILE === */}
                      <div className="p-5 bg-gray-900 rounded-2xl text-white">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="font-bold text-lg flex items-center gap-2">
                              <Crown className="w-5 h-5 text-yellow-400" />
                              Your Host Profile
                            </h3>
                            <p className="text-sm text-gray-400">{dashboardType === DASHBOARD_TYPES.PROVIDER ? "This is how you appear to your clients" : "This is how you appear as the event host"}</p>
                          </div>
                          <Badge className="bg-white/10 text-white border-0">Syncs to /me</Badge>
                        </div>
                        
                        <div className="flex items-center gap-4 p-4 bg-white/5 rounded-xl mb-4">
                          <div className="relative">
                            <div className="w-16 h-16 rounded-xl bg-white/10 overflow-hidden flex items-center justify-center">
                              {(user?.profile_photo_url || user?.profile_image) ? (
                                <img src={getImageUrl(user.profile_photo_url || user.profile_image)} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <User className="w-8 h-8 text-white/60" />
                              )}
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-gray-900 flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          </div>
                          <div className="flex-1">
                            <p className="font-bold text-lg">
                              {user?.first_name && user?.last_name 
                                ? `${user.first_name} ${user.last_name}` 
                                : user?.name || "Your Name"}
                            </p>
                            <p className="text-sm text-gray-400">{user?.email}</p>
                            <p className="text-xs text-gray-500">{user?.title || "Add your title in /me"}</p>
                          </div>
                          <a href="/me" className="text-xs text-gray-400 hover:text-white underline">Edit in Profile →</a>
                        </div>
                        
                        {/* Your Organization - Event-Linked */}
                        <div className="p-4 bg-white/5 rounded-xl">
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-medium flex items-center gap-2">
                              <Building2 className="w-4 h-4" />
                              {dashboardType === DASHBOARD_TYPES.PROVIDER ? "Business" : "Event Organization"}
                            </p>
                            {displayOrg ? (
                              <Badge className="bg-green-500/20 text-green-400 border-0">Linked</Badge>
                            ) : (
                              <Badge className="bg-yellow-500/20 text-yellow-400 border-0">Not Set</Badge>
                            )}
                          </div>
                          {displayOrg ? (
                            <div className="relative">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-white/10 overflow-hidden flex items-center justify-center">
                                  {displayOrg.logo ? (
                                    <img src={getImageUrl(displayOrg.logo)} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <span className="text-white font-bold text-lg">{displayOrg.name?.charAt(0)}</span>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium">{displayOrg.name}</p>
                                  <p className="text-xs text-gray-400">Shows as host org on event</p>
                                </div>
                                {userOrganizations.length > 1 && (
                                  <button 
                                    onClick={() => setShowOrgPicker(!showOrgPicker)}
                                    className="text-xs text-gray-400 hover:text-white px-2 py-1 bg-white/10 rounded-lg transition-colors"
                                    data-testid="change-event-org-btn"
                                  >
                                    Change
                                  </button>
                                )}
                              </div>
                              {showOrgPicker && userOrganizations.length > 1 && (
                                <div className="mt-3 space-y-1 p-2 bg-white/5 rounded-lg border border-white/10 max-h-48 overflow-y-auto">
                                  {userOrganizations.filter(o => o.org_id !== displayOrg.org_id).map(org => (
                                    <button
                                      key={org.org_id}
                                      onClick={() => changeEventOrg(org)}
                                      className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 text-left transition-colors"
                                      data-testid={`pick-org-${org.org_id}`}
                                    >
                                      <div className="w-7 h-7 rounded bg-white/10 flex items-center justify-center overflow-hidden">
                                        {(org.logo || org.logo_url) ? (
                                          <img src={getImageUrl(org.logo || org.logo_url)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                          <span className="text-white text-xs font-bold">{org.name?.charAt(0)}</span>
                                        )}
                                      </div>
                                      <span className="text-sm text-gray-300">{org.name}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-3">
                              <p className="text-sm text-gray-400 mb-2">Link an organization to this event</p>
                              {userOrganizations.length > 0 ? (
                                <div className="space-y-1">
                                  {userOrganizations.slice(0, 5).map(org => (
                                    <button
                                      key={org.org_id}
                                      onClick={() => changeEventOrg(org)}
                                      className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 text-left transition-colors"
                                    >
                                      <div className="w-7 h-7 rounded bg-white/10 flex items-center justify-center overflow-hidden">
                                        {(org.logo || org.logo_url) ? (
                                          <img src={getImageUrl(org.logo || org.logo_url)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                          <span className="text-white text-xs font-bold">{org.name?.charAt(0)}</span>
                                        )}
                                      </div>
                                      <span className="text-sm text-gray-300">{org.name}</span>
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <a href="/me/settings" className="inline-block px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors">
                                  Set Up in Profile →
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Real-time sync indicator */}
                      <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                              <RefreshCw className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <h4 className="font-medium text-sm text-gray-900">Real-time Sync Active</h4>
                              <p className="text-xs text-gray-600">
                                {/* Include host (1) + non-host team members with user_id */}
                                {(() => {
                                  const nonHostTeam = team.filter(m => m.user_id !== event?.host_user_id && m.email?.toLowerCase() !== event?.host_email?.toLowerCase());
                                  return `${1 + nonHostTeam.filter(m => m.user_id).length}/${1 + nonHostTeam.length} people linked`;
                                })()} • 
                                {/* Include host's org (1 if exists) + partner orgs */}
                                {(displayOrg ? 1 : 0) + partners.filter(p => p.status === 'linked').length}/{(displayOrg ? 1 : 0) + partners.length} orgs linked
                              </p>
                            </div>
                          </div>
                          <Badge className="bg-green-100 text-green-700">
                            <Check className="w-3 h-3 mr-1" />
                            Live
                          </Badge>
                        </div>
                      </div>
                      
                      {/* === TEAM MEMBERS === */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium flex items-center gap-2">
                              <Users className="w-4 h-4 text-purple-500" />
                              Team Members
                            </h3>
                            <p className="text-xs text-gray-500">Add co-hosts, team members with dashboard access</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={syncTeamData}
                              disabled={syncingTeam}
                              data-testid="sync-team-btn"
                            >
                              <RefreshCw className={`w-4 h-4 mr-1 ${syncingTeam ? 'animate-spin' : ''}`} />
                              {syncingTeam ? 'Syncing...' : 'Sync Team'}
                            </Button>
                            <Button size="sm" onClick={() => setShowAddTeamModal(true)} data-testid="add-team-btn">
                              <UserPlus className="w-4 h-4 mr-1" />
                              Add Member
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {/* Primary Host (Owner) - Always show first */}
                          {event?.host_user_id && (
                            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="relative">
                                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center overflow-hidden">
                                      {event?.host_user_id === user?.user_id ? (
                                        user?.picture ? (
                                          <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                                        ) : (
                                          <Crown className="w-5 h-5 text-purple-500" />
                                        )
                                      ) : (
                                        <Crown className="w-5 h-5 text-purple-500" />
                                      )}
                                    </div>
                                    <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                                      <Check className="w-2.5 h-2.5 text-white" />
                                    </div>
                                  </div>
                                  <div className="flex-1">
                                    {event?.host_user_id === user?.user_id ? (
                                      <>
                                        <p className="font-medium">{user?.name || "You"} <span className="text-purple-600 text-xs">(You)</span></p>
                                        <p className="text-xs text-gray-500">{user?.email}</p>
                                        {displayOrg?.name && (
                                          <div className="flex items-center gap-1 mt-0.5">
                                            <Building2 className="w-3 h-3 text-purple-500" />
                                            <span className="text-xs text-purple-600">{displayOrg.name}</span>
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        <p className="font-medium">{event?.host_name || event?.owner_name || "Event Owner"}</p>
                                        <p className="text-xs text-gray-500">{event?.host_email || "Primary host"}</p>
                                        {/* Show owner's organization - prefer org_name over host_organization since host_organization might be co-host's org */}
                                        {(event?.owner_org_name || event?.org_name || event?.host_orgs?.find(h => h.contact === 'Host')?.name) && (
                                          <div className="flex items-center gap-1 mt-0.5">
                                            <Building2 className="w-3 h-3 text-purple-500" />
                                            <span className="text-xs text-purple-600">
                                              {event.owner_org_name || event.org_name || event.host_orgs?.find(h => h.contact === 'Host')?.name}
                                            </span>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                                <Badge className="bg-purple-100 text-purple-700">Owner</Badge>
                              </div>
                            </div>
                          )}
                          
                          {/* Team members - filter out the primary host to avoid duplication */}
                          {(() => {
                            const filteredTeam = team.filter(m => {
                              // Don't show the event host again (already shown as Owner above)
                              if (event?.host_user_id && m.user_id === event.host_user_id) return false;
                              if (event?.host_email && m.email?.toLowerCase() === event.host_email?.toLowerCase()) return false;
                              return true;
                            });
                            return filteredTeam.length === 0 ? (
                            <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-xl">
                              <p className="text-sm">No additional team members yet</p>
                            </div>
                          ) : (
                            filteredTeam.map((member, idx) => (
                              <div key={member.member_id || idx} className="p-3 bg-gray-50 rounded-lg border hover:border-purple-200 transition-colors">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="relative">
                                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center overflow-hidden">
                                        {member.profile_image ? (
                                          <img src={getImageUrl(member.profile_image)} alt={member.name} className="w-full h-full object-cover" />
                                        ) : (
                                          <User className="w-5 h-5 text-blue-500" />
                                        )}
                                      </div>
                                      {member.user_id && (
                                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                                          <Check className="w-2.5 h-2.5 text-white" />
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <p className="font-medium">{member.name || "Unnamed"}</p>
                                      <p className="text-xs text-gray-500">{member.email}</p>
                                      {member.org_name && (
                                        <div className="flex items-center gap-1 mt-0.5">
                                          <Building2 className="w-3 h-3 text-purple-500" />
                                          <span className="text-xs text-purple-600">{member.org_name}</span>
                                        </div>
                                      )}
                                      {!member.user_id && (
                                        <Badge variant="outline" className="mt-1 text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
                                          Placeholder - awaiting sign in
                                        </Badge>
                                      )}
                                      {/* Show Invite Link only for pending members (no user_id), OR if they have an existing magic_link_token */}
                                      {(!member.user_id || member.magic_link_token) && (
                                      <div className="mt-2 flex items-center gap-2">
                                        {member.magic_link_token ? (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-7 text-xs"
                                            onClick={() => {
                                              const inviteUrl = `${window.location.origin}/join/team/${member.magic_link_token}`;
                                              navigator.clipboard.writeText(inviteUrl);
                                              toast.success("Invite link copied!");
                                            }}
                                            data-testid={`copy-team-invite-${idx}`}
                                          >
                                            <Copy className="w-3 h-3 mr-1" />
                                            Copy Invite Link
                                          </Button>
                                        ) : (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-7 text-xs"
                                            onClick={async () => {
                                              const token = localStorage.getItem("session_token");
                                              try {
                                                const res = await fetch(`${API}/events/${eventId}/magic-links/team`, {
                                                  method: "POST",
                                                  headers: {
                                                    "Content-Type": "application/json",
                                                    Authorization: `Bearer ${token}`
                                                  },
                                                  body: JSON.stringify({
                                                    email: member.email,
                                                    name: member.name,
                                                    first_name: member.first_name,
                                                    last_name: member.last_name,
                                                    role: member.role || "viewer",
                                                    org_name: member.org_name || null,
                                                    member_id: member.member_id
                                                  })
                                                });
                                                if (res.ok) {
                                                  const data = await res.json();
                                                  const inviteUrl = data.token 
                                                    ? `${window.location.origin}/join/team/${data.token}`
                                                    : data.invite_url;
                                                  
                                                  if (inviteUrl) {
                                                    navigator.clipboard.writeText(inviteUrl);
                                                    setInviteLinkData({ name: member.name, link: inviteUrl, type: "team" });
                                                    setShowInviteLinkModal(true);
                                                    mutateEvent();
                                                  }
                                                } else {
                                                  toast.error("Failed to generate invite link");
                                                }
                                              } catch (err) {
                                                toast.error("Error generating invite link");
                                              }
                                            }}
                                            data-testid={`generate-team-invite-${idx}`}
                                          >
                                            <Link2 className="w-3 h-3 mr-1" />
                                            Generate Invite Link
                                          </Button>
                                        )}
                                      </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {/* Type/Label */}
                                    <Select 
                                      value={member.label || "team"} 
                                      onValueChange={async (v) => {
                                        const token = localStorage.getItem("session_token");
                                        await fetch(`${API}/events/${eventId}/team/${member.member_id}`, {
                                          method: "PATCH",
                                          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                          body: JSON.stringify({ label: v })
                                        });
                                        mutateEvent();
                                        triggerGlobalSync('all', eventId);
                                        toast.success("Type updated");
                                      }}
                                    >
                                      <SelectTrigger className="w-28 h-8">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="host">Co-Host</SelectItem>
                                        <SelectItem value="partner">Partner</SelectItem>
                                        <SelectItem value="sponsor">Sponsor</SelectItem>
                                        <SelectItem value="team">Team</SelectItem>
                                        <SelectItem value="vendor">Vendor</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    
                                    {/* Role */}
                                    <Select 
                                      value={member.role || "viewer"} 
                                      onValueChange={async (v) => {
                                        const token = localStorage.getItem("session_token");
                                        await fetch(`${API}/events/${eventId}/team/${member.member_id}`, {
                                          method: "PATCH",
                                          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                          body: JSON.stringify({ role: v })
                                        });
                                        mutateEvent();
                                        triggerGlobalSync('all', eventId);
                                        toast.success("Role updated");
                                      }}
                                    >
                                      <SelectTrigger className="w-24 h-8">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="admin">Admin</SelectItem>
                                        <SelectItem value="editor">Editor</SelectItem>
                                        <SelectItem value="viewer">Viewer</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    
                                    {/* Delete */}
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => {
                                        setDeleteTarget({
                                          type: 'team_member',
                                          name: member.name,
                                          onConfirm: async () => {
                                            try {
                                              const token = localStorage.getItem("session_token");
                                              const deleteId = member.member_id || member.user_id;
                                              const res = await fetch(`${API}/events/${eventId}/team/member/${deleteId}`, {
                                                method: "DELETE",
                                                headers: { Authorization: `Bearer ${token}` }
                                              });
                                              if (!res.ok) {
                                                const err = await res.json();
                                                throw new Error(err.detail || "Failed to delete");
                                              }
                                              mutateEvent();
                                              setShowDeleteConfirmModal(false);
                                              toast.success(`${member.name} removed`);
                                            } catch (err) {
                                              toast.error(err.message || "Failed to remove team member");
                                            }
                                          }
                                        });
                                        setShowDeleteConfirmModal(true);
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4 text-red-400" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))
                          );
                          })()}
                        </div>
                      </div>

                      <Separator />

                      {/* Partners */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-blue-500" />
                              Partner Organizations
                            </h3>
                            <p className="text-sm text-gray-500">Organizations with dashboard access</p>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => setShowAddPartnerModal(true)} data-testid="add-partner-btn">
                            <Plus className="w-4 h-4 mr-1" />
                            Add Partner
                          </Button>
                        </div>
                        {partners.length > 0 ? (
                          <div className="space-y-3">
                            {partners.map((partner, idx) => (
                              <div key={partner.org_id || idx} className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center overflow-hidden">
                                      {partner.logo ? (
                                        <img src={getImageUrl(partner.logo)} alt={partner.name} className="w-full h-full object-cover" />
                                      ) : (
                                        <Building2 className="w-5 h-5 text-blue-600" />
                                      )}
                                    </div>
                                    <div>
                                      <p className="font-medium">{partner.name}</p>
                                      {partner.website && (
                                        <a href={ensureAbsoluteUrl(partner.website)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                                          {partner.website}
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge className={`${partner.status === "linked" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                                      {partner.status === "linked" ? "✓ Linked" : "Partner"}
                                    </Badge>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => confirmRemovePartner(partner)}
                                      data-testid={`remove-partner-${idx}`}
                                    >
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </div>
                                </div>
                                {/* Linked Team Members */}
                                {partner.linked_members?.length > 0 && (
                                  <div className="mt-2 pt-2 border-t border-blue-100">
                                    <p className="text-xs text-blue-600 mb-2">Team Members</p>
                                    <div className="flex flex-wrap gap-2">
                                      {partner.linked_members.map((member, mIdx) => (
                                        <div key={mIdx} className="flex items-center gap-1.5 px-2 py-1 bg-white rounded-full text-xs">
                                          <div className="w-5 h-5 rounded-full overflow-hidden bg-gray-100">
                                            {member.profile_image ? (
                                              <img src={getImageUrl(member.profile_image)} alt={member.name} className="w-full h-full object-cover" />
                                            ) : (
                                              <div className="w-full h-full flex items-center justify-center text-[8px] font-bold text-gray-500">
                                                {(member.name || "?").charAt(0)}
                                              </div>
                                            )}
                                          </div>
                                          <span>{member.name}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {/* Contact Info */}
                                {partner.contact_email && (
                                  <div className="mt-2 pt-2 border-t border-blue-100 text-xs text-gray-500">
                                    Contact: {partner.contact_name || partner.contact_email}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-400 text-sm">No partners added</p>
                        )}
                      </div>

                      <Separator />

                      {/* Sponsors */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium">Sponsors</h3>
                            <p className="text-sm text-gray-500">Shown on event page</p>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => setShowAddSponsorModal(true)} data-testid="add-sponsor-btn">
                            <Plus className="w-4 h-4 mr-1" />
                            Add Sponsor
                          </Button>
                        </div>
                        {sponsors.length > 0 ? (
                          <div className="space-y-3">
                            {sponsors.map((sponsor, idx) => (
                              <div key={sponsor.org_id || idx} className="p-4 bg-green-50 rounded-lg border border-green-100">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center overflow-hidden">
                                      {sponsor.logo ? (
                                        <img src={getImageUrl(sponsor.logo)} alt={sponsor.name} className="w-full h-full object-cover" />
                                      ) : (
                                        <Crown className="w-5 h-5 text-green-600" />
                                      )}
                                    </div>
                                    <div>
                                      <p className="font-medium">{sponsor.name}</p>
                                      {sponsor.website && (
                                        <a href={ensureAbsoluteUrl(sponsor.website)} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:underline">
                                          {sponsor.website}
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge className={`${sponsor.status === "linked" ? "bg-green-600 text-white" : "bg-green-100 text-green-700"}`}>
                                      {sponsor.tier || "Sponsor"}
                                    </Badge>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => confirmRemoveSponsor(sponsor)}
                                      data-testid={`remove-sponsor-${idx}`}
                                    >
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </div>
                                </div>
                                {/* Linked Team Members */}
                                {sponsor.linked_members?.length > 0 && (
                                  <div className="mt-3 pt-2 border-t border-green-100">
                                    <p className="text-xs text-green-600 mb-2">Team Members</p>
                                    <div className="flex flex-wrap gap-2">
                                      {sponsor.linked_members.map((member, mIdx) => (
                                        <div key={mIdx} className="flex items-center gap-1.5 px-2 py-1 bg-white rounded-full text-xs">
                                          <div className="w-5 h-5 rounded-full overflow-hidden bg-gray-100">
                                            {member.profile_image ? (
                                              <img src={getImageUrl(member.profile_image)} alt={member.name} className="w-full h-full object-cover" />
                                            ) : (
                                              <div className="w-full h-full flex items-center justify-center text-[8px] font-bold text-gray-500">
                                                {(member.name || "?").charAt(0)}
                                              </div>
                                            )}
                                          </div>
                                          <span>{member.name}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {/* Contact Info */}
                                {sponsor.contact_email && (
                                  <div className="mt-2 pt-2 border-t border-green-100 text-xs text-gray-500">
                                    Contact: {sponsor.contact_name || sponsor.contact_email}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-400 text-sm">No sponsors added</p>
                        )}
                      </div>

                    </CardContent>
                  </Card>
                  </GatedFeature>
                )}

                {/* PRE-INVITE MANAGEMENT - New Section */}
                {/* PRE-INVITE - Now part of Welcome tab. Event-type dashboards ONLY (Issue 3 fix). */}
                {dashboardType === DASHBOARD_TYPES.EVENT && (settingsTab === "welcome" || settingsTab === "pre-invite") && (
                  <div className="space-y-6">
                    <Card data-testid="settings-pre-invite">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Star className="w-5 h-5 text-amber-500" />
                          Pre-Invite Management
                        </CardTitle>
                        <CardDescription>
                          Manage pre-invite list members before opening RSVP to the public
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Editable Inner Circle Title */}
                        <div className="p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl border border-purple-200">
                          <Label className="text-sm font-medium mb-2 block">Inner Circle List Name</Label>
                          <p className="text-xs text-gray-500 mb-3">Customize how your exclusive list appears to attendees</p>
                          <div className="flex gap-2">
                            <Input
                              defaultValue={event?.inner_circle_title || "Inner Circle"}
                              placeholder="e.g., VIP List, Early Access, Front Row"
                              className="flex-1"
                              data-testid="inner-circle-title-input"
                              id="inner-circle-title-input"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem("session_token");
                                  const inputValue = document.getElementById('inner-circle-title-input')?.value || "Inner Circle";
                                  await fetch(`${API}/events/${eventId}/settings`, {
                                    method: 'PATCH',
                                    headers: { 
                                      Authorization: `Bearer ${token}`,
                                      'Content-Type': 'application/json'
                                    },
                                    body: JSON.stringify({ inner_circle_title: inputValue })
                                  });
                                  toast.success("Title saved!");
                                  mutateEvent(); // Refresh event data
                                } catch (err) {
                                  toast.error("Failed to save title");
                                }
                              }}
                            >
                              Save
                            </Button>
                          </div>
                        </div>

                        {/* Pre-Invite Status */}
                        <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-amber-100 rounded-lg">
                                <Star className="w-5 h-5 text-amber-600" />
                              </div>
                              <div>
                                <h4 className="font-semibold">Pre-Invite Mode</h4>
                                <p className="text-sm text-gray-600">
                                  {event?.status === 'pre_invite' || event?.pre_invite_mode?.enabled 
                                    ? 'Event is in pre-invite mode - only invited members can RSVP'
                                    : 'Event is open for public RSVP'}
                                </p>
                              </div>
                            </div>
                            <Badge className={event?.status === 'pre_invite' || event?.pre_invite_mode?.enabled ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                              {event?.status === 'pre_invite' || event?.pre_invite_mode?.enabled ? 'Pre-Invite Active' : 'Public RSVP'}
                            </Badge>
                          </div>
                        </div>

                        {/* Inner Circle List Info */}
                        {event?.pre_invite_mode?.inner_circle_list_id && (
                          <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                            <div className="flex items-center gap-3 mb-3">
                              <Crown className="w-5 h-5 text-purple-600" />
                              <div>
                                <h4 className="font-semibold">{event?.inner_circle_title || "Inner Circle"} List</h4>
                                <p className="text-sm text-gray-600">Members in this list can RSVP during pre-invite</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-center">
                                <p className="text-2xl font-bold text-purple-700">{innerCircleMembers?.length || 0}</p>
                                <p className="text-xs text-gray-500">Members</p>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => navigate(`/admin/inner-circle`)}
                              >
                                Manage {event?.inner_circle_title || "Inner Circle"}
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* RSVP Migration Section */}
                        <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                          <div className="flex items-center gap-3 mb-3">
                            <RefreshCw className="w-5 h-5 text-blue-600" />
                            <div>
                              <h4 className="font-semibold">RSVP Migration</h4>
                              <p className="text-sm text-gray-600">Move existing RSVPs to pre-invite list or vice versa</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-blue-300 hover:bg-blue-100"
                              onClick={async () => {
                                if (!window.confirm('This will migrate all current RSVPs to the pre-invite list. Continue?')) return;
                                try {
                                  const token = localStorage.getItem("session_token");
                                  const res = await fetch(`${API}/events/${eventId}/migrate-rsvps-to-preinvite`, {
                                    method: 'POST',
                                    headers: { 
                                      Authorization: `Bearer ${token}`,
                                      'Content-Type': 'application/json'
                                    }
                                  });
                                  if (res.ok) {
                                    const data = await res.json();
                                    toast.success(`Migrated ${data.migrated || 0} RSVPs to pre-invite list`);
                                    fetchInnerCircleMembers();
                                  } else {
                                    const err = await res.json();
                                    toast.error(err.detail || 'Migration failed');
                                  }
                                } catch (err) {
                                  toast.error('Failed to migrate RSVPs');
                                }
                              }}
                              data-testid="migrate-to-preinvite-btn"
                            >
                              <ArrowRight className="w-4 h-4 mr-1" />
                              RSVPs → Pre-Invite
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-green-300 hover:bg-green-100"
                              onClick={async () => {
                                if (!window.confirm('This will convert all pre-invite members to confirmed RSVPs. Continue?')) return;
                                try {
                                  const token = localStorage.getItem("session_token");
                                  const res = await fetch(`${API}/events/${eventId}/convert-preinvite-to-rsvps`, {
                                    method: 'POST',
                                    headers: { 
                                      Authorization: `Bearer ${token}`,
                                      'Content-Type': 'application/json'
                                    }
                                  });
                                  if (res.ok) {
                                    const data = await res.json();
                                    toast.success(`Converted ${data.converted || 0} pre-invite members to RSVPs`);
                                    window.location.reload();
                                  } else {
                                    const err = await res.json();
                                    toast.error(err.detail || 'Conversion failed');
                                  }
                                } catch (err) {
                                  toast.error('Failed to convert pre-invite to RSVPs');
                                }
                              }}
                              data-testid="convert-preinvite-btn"
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Pre-Invite → RSVPs
                            </Button>
                          </div>
                        </div>

                        {/* Pre-Invite Member List - Now in People Tab */}
                        <div className="border rounded-xl overflow-hidden bg-purple-50/30">
                          <div className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <Users className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <h4 className="font-medium text-sm">{event?.inner_circle_title || "Pre-Invite"} Members</h4>
                                <p className="text-xs text-gray-500">{innerCircleMembers?.length || 0} members in your list</p>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setActiveTab("people");
                                setPeopleSubTab("pre_invite");
                              }}
                            >
                              View in People Tab
                              <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* ABOUT (Host portal section) - business info synced from Welcome (Iter 9.40 Slice I.d) */}
                {settingsTab === "about" && (
                  <div className="space-y-6" data-testid="settings-about-content">
                    <div>
                      <h3 className="text-lg font-semibold">About</h3>
                      <p className="text-sm text-gray-500">Business info synced from your Welcome page - logo, business name, website, description, social links and location. Edits here flow back to Welcome automatically.</p>
                    </div>
                    <Card>
                      <CardContent className="pt-6 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-stone-600">Business name</Label>
                            <Input
                              value={eventSettings.host_business_name || event?.host_name || ""}
                              onChange={(e) => setEventSettings(prev => ({ ...prev, host_business_name: e.target.value }))}
                              placeholder="e.g. Privé Beauté"
                              className="mt-1"
                              data-testid="about-business-name-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-stone-600">Website</Label>
                            <Input
                              value={eventSettings.website || ""}
                              onChange={(e) => setEventSettings(prev => ({ ...prev, website: e.target.value }))}
                              placeholder="https://"
                              className="mt-1"
                              data-testid="about-website-input"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-stone-600">Description</Label>
                          <Textarea
                            rows={3}
                            value={eventSettings.description || ""}
                            onChange={(e) => setEventSettings(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Tell clients about your business in a few sentences."
                            className="mt-1"
                            data-testid="about-description-input"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-stone-600">Instagram</Label>
                            <Input
                              value={eventSettings.instagram_handle || ""}
                              onChange={(e) => setEventSettings(prev => ({ ...prev, instagram_handle: e.target.value }))}
                              placeholder="@handle"
                              className="mt-1"
                              data-testid="about-instagram-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-stone-600">TikTok</Label>
                            <Input
                              value={eventSettings.tiktok_handle || ""}
                              onChange={(e) => setEventSettings(prev => ({ ...prev, tiktok_handle: e.target.value }))}
                              placeholder="@handle"
                              className="mt-1"
                              data-testid="about-tiktok-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-stone-600">City</Label>
                            <Input
                              value={eventSettings.city || event?.city || ""}
                              onChange={(e) => setEventSettings(prev => ({ ...prev, city: e.target.value }))}
                              placeholder="e.g. Hollywood, CA"
                              className="mt-1"
                              data-testid="about-city-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-stone-600">Booking phone</Label>
                            <Input
                              value={eventSettings.booking_phone || ""}
                              onChange={(e) => setEventSettings(prev => ({ ...prev, booking_phone: e.target.value }))}
                              placeholder="Optional"
                              className="mt-1"
                              data-testid="about-phone-input"
                            />
                          </div>
                        </div>
                        <div className="pt-2 flex justify-end">
                          <Button
                            size="sm"
                            onClick={saveEventSettings}
                            data-testid="about-save-btn"
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            <Save className="w-4 h-4 mr-1" /> Save About
                          </Button>
                        </div>
                        <p className="text-[11px] text-stone-400 pt-2">Tip: changes here also reflect on the Welcome page hero block (logo, business name, social handles).</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* UPDATES (Host portal section) - release notes / feature drops */}
                {settingsTab === "updates" && (
                  <div className="space-y-6" data-testid="settings-updates-content">
                    <div>
                      <h3 className="text-lg font-semibold">Updates</h3>
                      <p className="text-sm text-stone-500">Latest platform changes that affect your dashboard. Newest at the top.</p>
                    </div>
                    <Card>
                      <CardContent className="pt-6 space-y-4 text-sm">
                        <div className="border-l-2 border-purple-300 pl-3">
                          <p className="font-medium">Confirmed kanban stage</p>
                          <p className="text-stone-500 text-xs">Clients who pick a service and pay (or have an Acuity appointment) now land in a new "Confirmed" column on your Dashboard. <span className="text-stone-400">- May 6</span></p>
                        </div>
                        <div className="border-l-2 border-purple-300 pl-3">
                          <p className="font-medium">Settings reorganized</p>
                          <p className="text-stone-500 text-xs">Settings now grouped into Booking · Operations · Host portal. Financials moved out of Services into its own pane. <span className="text-stone-400">- May 6</span></p>
                        </div>
                        <div className="border-l-2 border-stone-200 pl-3">
                          <p className="font-medium">Preference share pages refreshed</p>
                          <p className="text-stone-500 text-xs">Clients sharing their preference cards now show proper labels, swatches and photos instead of raw values. <span className="text-stone-400">- May 6</span></p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* PARTNER PORTAL - Now inside Settings */}
                {(settingsTab === "host-portal" || settingsTab === "subscription") && (
                  <div className="space-y-6" data-testid="host-portal-section">
                    {/* Host Portal Header */}
                    <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-0 shadow-sm">
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-md" style={{ backgroundColor: catIconColor || '#9c57f5' }}>
                            <PhygitalIcon className="w-8 h-8 text-white" variant="light" />
                          </div>
                          <div className="flex-1">
                            <h2 className="text-xl font-bold text-gray-900">{event?.name || "Event"} Host Portal</h2>
                            <p className="text-gray-600 text-sm">Your portal for updates, resources, and collaboration</p>
                          </div>
                          <Badge className="bg-green-100 text-green-700 border-green-200">
                            Live Pilot
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Updates Section */}
                    <Card data-testid="host-portal-updates">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Bell className="w-5 h-5 text-purple-500" />
                          Updates
                        </CardTitle>
                        <CardDescription>News and announcements from Phygital</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <HostPortalUpdates />
                      </CardContent>
                    </Card>

                    {/* Partner Notes */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <MessageSquare className="w-5 h-5" />
                          Host Notes
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          placeholder="Add notes for your team..."
                          className="min-h-[120px]"
                          value={partnerNotes}
                          onChange={(e) => setPartnerNotes(e.target.value)}
                        />
                        <div className="flex justify-end mt-3">
                          <Button 
                            size="sm"
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem("session_token");
                                await fetch(`${API}/events/${eventId}/settings`, {
                                  method: "PATCH",
                                  headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${token}`
                                  },
                                  body: JSON.stringify({ partner_notes: partnerNotes })
                                });
                                toast.success("Notes saved");
                              } catch (err) {
                                toast.error("Failed to save notes");
                              }
                            }}
                          >
                            <Save className="w-4 h-4 mr-1" />
                            Save Notes
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Subscription Management */}
                    <Card data-testid="host-portal-subscription">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <CreditCard className="w-5 h-5 text-green-500" />
                          Subscription
                        </CardTitle>
                        <CardDescription>Manage your plan and billing</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <SubscriptionManager eventId={eventId} getAuthHeaders={getAuthHeaders} />
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            </div>
            
            {/* Sticky Save All Bar */}
            <div className={`sticky bottom-0 z-30 transition-all duration-300 ${hasUnsavedChanges ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'}`}>
              <div className="bg-white border-t border-gray-200 shadow-lg px-6 py-3 flex items-center justify-between rounded-t-xl">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                  <span className="text-sm text-gray-600">You have unsaved changes</span>
                </div>
                <div className="flex items-center gap-3">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setHasUnsavedChanges(false);
                      mutateEvent(); // Reset to server state
                    }}
                    data-testid="discard-changes-btn"
                  >
                    Discard
                  </Button>
                  <Button 
                    onClick={() => saveSettings("all")} 
                    disabled={saving}
                    className="bg-black text-white hover:bg-gray-800"
                    data-testid="save-all-btn"
                  >
                    <Save className="w-4 h-4 mr-1.5" />
                    {saving ? "Saving..." : "Save All Changes"}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

        </Tabs>
      </main>
      </UpgradeProvider>

      {/* Unsaved Changes Modal */}
      <Dialog open={showUnsavedModal} onOpenChange={setShowUnsavedModal}>
        <DialogContent className="max-w-sm" aria-describedby="unsaved-desc">
          <DialogTitle>Unsaved Changes</DialogTitle>
          <DialogDescription id="unsaved-desc">
            You have unsaved changes. Would you like to save before leaving?
          </DialogDescription>
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setHasUnsavedChanges(false);
                setShowUnsavedModal(false);
                if (pendingTabChange) {
                  setActiveTab(pendingTabChange);
                  setPendingTabChange(null);
                }
              }}
              data-testid="discard-nav-btn"
            >
              Don't Save
            </Button>
            <Button
              className="flex-1 bg-black text-white hover:bg-gray-800"
              onClick={async () => {
                await saveSettings("all");
                setShowUnsavedModal(false);
                if (pendingTabChange) {
                  setActiveTab(pendingTabChange);
                  setPendingTabChange(null);
                }
              }}
              disabled={saving}
              data-testid="save-nav-btn"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preference Card Modal - Using Universal AttendeeCard Design */}
      <Dialog open={showPreferenceCard} onOpenChange={setShowPreferenceCard}>
        <DialogContent className="max-w-[360px] w-[95vw] max-h-[85vh] p-0 overflow-hidden overflow-y-auto border-0" aria-describedby="preference-card-desc">
          <DialogTitle className="sr-only">Attendee Details</DialogTitle>
          <DialogDescription id="preference-card-desc" className="sr-only">
            View and manage attendee preferences and information
          </DialogDescription>
          {selectedPerson && (
            <>
              {/* Universal AttendeeCard Design - Category Color Gradient */}
              <div className="relative overflow-hidden">
                {/* Background - Use card_background, cover_image, or fallback to category gradient */}
                {(event?.card_background || eventSettings.card_background) ? (
                  <>
                    <div className="absolute inset-0">
                      <img 
                        src={getImageUrl(event?.card_background || eventSettings.card_background)} 
                        alt="Background" 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70" />
                    </div>
                  </>
                ) : (
                  <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${catIconColor}99, ${catIconColor}cc, ${catIconColor}ee)` }} />
                )}
                
                <div className="relative px-4 py-4 text-white">
                  {/* Header - DYNAMIC CARD LABEL from display_settings */}
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[10px] font-medium tracking-wider opacity-80" data-testid="card-label">
                      {eventSettings.display_settings?.card_label || event?.display_settings?.card_label || "ATTENDEE CARD"}
                    </span>
                    <button 
                      onClick={() => setShowPreferenceCard(false)}
                      className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  
                  {/* Event Title with Logo - Compact */}
                  <div className="flex items-center gap-2 mb-3">
                    {(event?.logo_image || eventSettings.logo_image || event?.cover_image) && (
                      <div className="w-8 h-8 rounded-lg border-2 border-white/50 overflow-hidden shadow-lg bg-black/20">
                        <img 
                          src={getImageUrl(event?.logo_image || eventSettings.logo_image || event?.cover_image)} 
                          alt={eventSettings.title || 'Event'} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <h2 className="text-sm font-bold drop-shadow-md">{eventSettings.title || "Event"}</h2>
                  </div>
                  
                  {/* Profile Section - Compact */}
                  <div className="flex flex-col items-center mb-3">
                    <div className="w-16 h-16 rounded-full border-3 border-white/30 overflow-hidden mb-2 bg-white/20 shadow-lg">
                      {(selectedPerson.profile_photo_url || selectedPerson.profile_image || selectedPerson.picture) ? (
                        <img 
                          src={getImageUrl(selectedPerson.profile_photo_url || selectedPerson.profile_image || selectedPerson.picture)} 
                          alt={selectedPerson.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-lg font-bold bg-white/10">
                          {(selectedPerson.first_name || selectedPerson.name || "G").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                        </div>
                      )}
                    </div>
                    
                    <div className="text-center">
                      <h3 className="text-base font-semibold flex items-center justify-center gap-1.5 drop-shadow-md">
                        {selectedPerson.first_name && selectedPerson.last_name 
                          ? `${selectedPerson.first_name} ${selectedPerson.last_name}` 
                          : selectedPerson.name || "Guest"}
                      </h3>
                      {/* Show all labels: Host, Speaker, VIP, etc */}
                      <div className="flex flex-wrap justify-center gap-1 mt-1">
                        {(selectedPerson.labels && selectedPerson.labels.length > 0 
                          ? selectedPerson.labels 
                          : [selectedPerson.label || "attendee"]
                        ).map((lbl, idx) => {
                          const labelInfo = TEAM_LABELS.find(l => l.value === lbl);
                          return (
                            <span 
                              key={idx} 
                              className={`text-[10px] px-2 py-0.5 rounded-full ${
                                lbl === 'host' ? 'bg-purple-500/80' :
                                lbl === 'speaker' ? 'bg-pink-500/80' :
                                lbl === 'vip' ? 'bg-amber-500/80' :
                                lbl === 'sponsor' ? 'bg-green-500/80' :
                                lbl === 'media' ? 'bg-blue-500/80' :
                                'bg-white/30'
                              }`}
                            >
                              {labelInfo?.label || lbl}
                            </span>
                          );
                        })}
                        {/* Show VIP badge if is_vip */}
                        {selectedPerson.is_vip && !selectedPerson.labels?.includes('vip') && !selectedPerson.label?.includes('vip') && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/80">VIP</span>
                        )}
                        {/* Show Speaker badge if is_speaker */}
                        {selectedPerson.is_speaker && !selectedPerson.labels?.includes('speaker') && selectedPerson.label !== 'speaker' && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-500/80">Speaker</span>
                        )}
                        {/* Show Host badge if is_host */}
                        {selectedPerson.is_host && !selectedPerson.labels?.includes('host') && selectedPerson.label !== 'host' && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/80">Host</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Interests - Compact */}
                  {(selectedPerson.interests?.length > 0 || selectedPerson.preferences?.interests?.length > 0) && (
                    <div className="mb-2">
                      <p className="text-[10px] font-medium tracking-wider opacity-70 mb-1 text-center">INTERESTS</p>
                      <div className="flex flex-wrap justify-center gap-1">
                        {[...(selectedPerson.interests || []), ...(selectedPerson.preferences?.interests || [])]
                          .filter((v, i, a) => a.indexOf(v) === i)
                          .slice(0, 4)
                          .map((interest, idx) => (
                            <Badge 
                              key={idx} 
                              className="bg-white/20 text-white border-0 hover:bg-white/30 text-[10px] px-2 py-0.5"
                            >
                              {interest}
                            </Badge>
                          ))}
                        {[...(selectedPerson.interests || []), ...(selectedPerson.preferences?.interests || [])].length > 4 && (
                          <Badge className="bg-white/10 text-white border-0 text-[10px] px-2 py-0.5">
                            +{[...(selectedPerson.interests || []), ...(selectedPerson.preferences?.interests || [])].length - 4}
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Dietary & Accessibility - Compact */}
                  {(selectedPerson.dietary_restrictions?.length > 0 || 
                    selectedPerson.preferences?.dietary_restrictions?.length > 0 ||
                    selectedPerson.meat_preferences?.length > 0 ||
                    selectedPerson.preferences?.meat_preferences?.length > 0 ||
                    selectedPerson.allergies?.length > 0 ||
                    selectedPerson.preferences?.allergies?.length > 0 ||
                    selectedPerson.accessibility_needs?.length > 0 || 
                    selectedPerson.preferences?.accessibility_needs?.length > 0 ||
                    selectedPerson.invisible_disabilities?.length > 0 ||
                    selectedPerson.preferences?.invisible_disabilities?.length > 0) && (
                    <div className="flex flex-wrap justify-center gap-1 mb-2">
                      {/* Dietary Restrictions */}
                      {[...(selectedPerson.dietary_restrictions || []), ...(selectedPerson.preferences?.dietary_restrictions || [])]
                        .filter((v, i, a) => a.indexOf(v) === i)
                        .slice(0, 2)
                        .map((d, idx) => (
                          <Badge key={`diet-${idx}`} className="bg-orange-400/80 text-white border-0 text-[10px] px-1.5 py-0.5">
                            🍽️ {d}
                          </Badge>
                        ))}
                      {/* Meat Preferences */}
                      {[...(selectedPerson.meat_preferences || []), ...(selectedPerson.preferences?.meat_preferences || [])]
                        .filter((v, i, a) => a.indexOf(v) === i)
                        .slice(0, 2)
                        .map((m, idx) => (
                          <Badge key={`meat-${idx}`} className="bg-green-500/80 text-white border-0 text-[10px] px-1.5 py-0.5">
                            🥩 {m}
                          </Badge>
                        ))}
                      {/* Allergies */}
                      {(() => {
                        const raw = [...(Array.isArray(selectedPerson.allergies) ? selectedPerson.allergies : selectedPerson.allergies ? [selectedPerson.allergies] : []),
                          ...(Array.isArray(selectedPerson.preferences?.allergies) ? selectedPerson.preferences.allergies : selectedPerson.preferences?.allergies ? [selectedPerson.preferences.allergies] : [])];
                        return raw.filter((v, i, a) => v && v !== 'None' && v !== 'none' && a.indexOf(v) === i).slice(0, 2).map((a, idx) => (
                          <Badge key={`allergy-${idx}`} className="bg-red-500/80 text-white border-0 text-[10px] px-1.5 py-0.5">
                            <AlertCircle className="w-3 h-3 mr-0.5 inline" /> {a}
                          </Badge>
                        ));
                      })()}
                      {/* Accessibility Needs */}
                      {[...(selectedPerson.accessibility_needs || []), ...(selectedPerson.preferences?.accessibility_needs || [])]
                        .filter((v, i, a) => a.indexOf(v) === i)
                        .slice(0, 2)
                        .map((a, idx) => (
                          <Badge key={`acc-${idx}`} className="bg-blue-400/80 text-white border-0 text-[10px] px-1.5 py-0.5">
                            ♿ {a.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      {/* Invisible Disabilities */}
                      {[...(selectedPerson.invisible_disabilities || []), ...(selectedPerson.preferences?.invisible_disabilities || [])]
                        .filter((v, i, a) => a.indexOf(v) === i)
                        .slice(0, 1)
                        .map((d, idx) => (
                          <Badge key={`inv-${idx}`} className="bg-purple-400/80 text-white border-0 text-[10px] px-1.5 py-0.5">
                            💜 {d.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                    </div>
                  )}
                  
                  {/* Inner Circle Badge */}
                  {selectedPerson.inner_circle && (
                    <div className="flex justify-center mb-2">
                      <Badge className="bg-yellow-400/90 text-yellow-900 border-0 text-[10px] px-2 py-0.5">
                        ⭐ Wants to join {event?.inner_circle_title || "Inner Circle"}
                      </Badge>
                    </div>
                  )}
                  
                  {/* Venue Disclaimer */}
                  <p className="text-center text-[10px] opacity-60 mb-2">
                    What the venue sees
                  </p>
                  
                  {/* Inspo Preview - Instagram-style small squares */}
                  {((selectedPerson.inspo || selectedPerson.preferences?.inspo || []).length > 0) && (
                    <div className="mb-3">
                      <p className="text-[9px] font-medium tracking-wider opacity-70 mb-1.5 text-center">
                        INSPIRATION ({(selectedPerson.inspo || selectedPerson.preferences?.inspo || []).length})
                      </p>
                      <div className="flex justify-center gap-1">
                        {(selectedPerson.inspo || selectedPerson.preferences?.inspo || []).slice(0, 4).map((inspo, idx) => {
                          const imgUrl = typeof inspo === 'string' ? inspo : (inspo.image_url || inspo.url);
                          return (
                            <div key={idx} className="w-12 h-12 rounded-lg overflow-hidden bg-white/20 shadow-sm">
                              <img 
                                src={imgUrl} 
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            </div>
                          );
                        })}
                        {(selectedPerson.inspo || selectedPerson.preferences?.inspo || []).length > 4 && (
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-black/30 flex items-center justify-center">
                            <span className="text-xs font-medium">+{(selectedPerson.inspo || selectedPerson.preferences?.inspo || []).length - 4}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Action Buttons - Compact */}
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => setShowExpandedDetails(!showExpandedDetails)}
                      className="flex-1 bg-black hover:bg-gray-900 text-white border-0 text-xs py-1.5"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      {showExpandedDetails ? "Hide Details" : "See All Details"}
                    </Button>
                    <Button 
                      onClick={() => updatePersonStatus(selectedPerson, "cant_make_it")}
                      variant="outline"
                      className="flex-1 bg-white/10 hover:bg-white/20 text-white border-white/30 text-xs py-1.5"
                    >
                      Can't Make It
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* All RSVP Details Section (Below card) - Shown when expanded */}
              {showExpandedDetails && (
              <div data-attendee-details className="bg-white px-4 py-3 space-y-3 flex-1 overflow-y-auto">
                {/* Private Inspo (provider dashboards only) */}
                {dashboardType === DASHBOARD_TYPES.PROVIDER && selectedPerson?.client_id && (
                  <div className="pb-3 border-b">
                    <PrivateInspoPanel clientId={selectedPerson.client_id} />
                  </div>
                )}
                {/* Contact Info */}
                <div className="text-xs">
                  <p className="font-semibold text-gray-800 mb-1.5 flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-gray-500" />
                    Contact
                  </p>
                  <div className="grid grid-cols-2 gap-1.5 text-gray-600">
                    {selectedPerson.email && (
                      <div>
                        <span className="text-[10px] text-gray-400">Email</span>
                        <p className="truncate">{selectedPerson.email}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-[10px] text-gray-400">Cell Phone {event?.intake_settings?.require_phone ? <span className="text-red-400">(required)</span> : <span className="text-gray-300">(optional)</span>}</span>
                      {selectedPerson.phone ? (
                        <p>{selectedPerson.phone}</p>
                      ) : (
                        <button
                          className="text-[10px] text-indigo-500 hover:text-indigo-700"
                          onClick={() => {
                            const phone = prompt("Enter client's cell phone:", "");
                            if (phone) {
                              const token = localStorage.getItem("session_token");
                              fetch(`${API}/events/${eventId}/rsvps/${selectedPerson.rsvp_id}`, {
                                method: "PATCH",
                                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                                body: JSON.stringify({ phone })
                              }).then(() => { toast.success("Phone added"); mutate(); });
                            }
                          }}
                          data-testid="add-phone-btn"
                        >+ Add phone</button>
                      )}
                    </div>
                    {selectedPerson.city && (
                      <div>
                        <span className="text-[10px] text-gray-400">City</span>
                        <p>{selectedPerson.city}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Appointment Management - Quick Edit */}
                <div className="text-xs border-t pt-2" data-testid="appointment-management">
                  <p className="font-semibold text-gray-800 mb-1.5 flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5 text-gray-500" />
                    Appointment
                  </p>
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-1.5">
                      <div>
                        <span className="text-[10px] text-gray-400">Date</span>
                        <p className="text-gray-700">
                          {selectedPerson.appointment_date 
                            ? new Date(selectedPerson.appointment_date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })
                            : "Not scheduled"}
                        </p>
                      </div>
                      <div>
                        <span className="text-[10px] text-gray-400">Time</span>
                        <p className="text-gray-700">{selectedPerson.appointment_time || "TBA"}</p>
                      </div>
                    </div>
                    {selectedPerson.service_name && (
                      <div>
                        <span className="text-[10px] text-gray-400">Service</span>
                        <p className="text-gray-700">{selectedPerson.service_name}</p>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <select 
                        className="text-[10px] px-2 py-1 rounded-md border border-gray-200 bg-white text-gray-700 flex-1"
                        value={selectedPerson.status || "confirmed"}
                        onChange={async (e) => {
                          await updatePersonStatus(selectedPerson, e.target.value);
                        }}
                        data-testid="appointment-status-select"
                      >
                        <option value="confirmed">Confirmed</option>
                        <option value="checked_in">Checked In</option>
                        <option value="completed">Completed</option>
                        <option value="no_show">No Show</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <button
                        className="text-[10px] px-2 py-1 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-600 whitespace-nowrap"
                        onClick={() => {
                          const newDate = prompt("New appointment date (YYYY-MM-DD):", selectedPerson.appointment_date ? selectedPerson.appointment_date.split('T')[0] : "");
                          const newTime = prompt("New appointment time (e.g., 2:30 PM):", selectedPerson.appointment_time || "");
                          if (newDate && newTime) {
                            const token = localStorage.getItem("session_token");
                            fetch(`${API}/events/${eventId}/rsvps/${selectedPerson.rsvp_id}`, {
                              method: "PATCH",
                              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                              body: JSON.stringify({ appointment_date: `${newDate}T${newTime.includes('AM') || newTime.includes('PM') ? '00:00:00' : newTime}`, appointment_time: newTime })
                            }).then(() => { toast.success("Appointment rescheduled"); mutate(); });
                          }
                        }}
                        data-testid="reschedule-btn"
                      >
                        Reschedule
                      </button>
                    </div>
                  </div>
                </div>

                {/* Client's Inspo Images */}
                {(selectedPerson.inspo_images?.length > 0 || selectedPerson.inspo?.length > 0) && (
                  <div className="text-xs border-t pt-2">
                    <p className="font-semibold text-gray-800 mb-1.5 flex items-center gap-1.5">
                      <Heart className="w-3.5 h-3.5 text-pink-400" />
                      Client Inspiration
                    </p>
                    <div className="grid grid-cols-4 gap-1.5">
                      {(selectedPerson.inspo_images || selectedPerson.inspo || []).slice(0, 8).map((img, i) => (
                        <div key={i} className="aspect-square rounded-lg overflow-hidden bg-gray-50 shadow-sm">
                          <img src={img} alt="" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Work Info */}
                {(selectedPerson.company || selectedPerson.title || selectedPerson.role_type) && (
                  <div className="text-xs border-t pt-2">
                    <p className="font-semibold text-gray-800 mb-1.5 flex items-center gap-1.5">
                      <Briefcase className="w-3.5 h-3.5 text-gray-500" />
                      Professional
                    </p>
                    <div className="grid grid-cols-2 gap-1.5 text-gray-600">
                      {selectedPerson.company && (
                        <div>
                          <span className="text-[10px] text-gray-400">Company</span>
                          <p className="truncate">{selectedPerson.company}</p>
                        </div>
                      )}
                      {selectedPerson.title && (
                        <div>
                          <span className="text-[10px] text-gray-400">Title</span>
                          <p className="truncate">{selectedPerson.title}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Social Media */}
                {(selectedPerson.instagram || selectedPerson.tiktok || selectedPerson.linkedin) && (
                  <div className="text-xs border-t pt-2">
                    <p className="font-semibold text-gray-800 mb-1.5 flex items-center gap-1.5">
                      <ArrowUp className="w-3.5 h-3.5 text-gray-500" />
                      Social
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {selectedPerson.instagram && (
                        <Badge variant="outline" className="text-[10px] py-0">
                          @{selectedPerson.instagram.replace('@', '')}
                        </Badge>
                      )}
                      {selectedPerson.tiktok && (
                        <Badge variant="outline" className="text-[10px] py-0">
                          @{selectedPerson.tiktok.replace('@', '')}
                        </Badge>
                      )}
                      {selectedPerson.linkedin && (
                        <Badge variant="outline" className="text-[10px] py-0">
                          LinkedIn
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Vibe & Survey Responses */}
                {(selectedPerson.survey_responses?.vibe || selectedPerson.vibe || selectedPerson.survey_responses?.tap_in_song) && (
                  <div className="text-xs border-t pt-2">
                    <p className="font-semibold text-gray-800 mb-1.5 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-gray-500" />
                      Vibe
                    </p>
                    <div className="space-y-1 text-gray-600">
                      {(selectedPerson.survey_responses?.vibe || selectedPerson.vibe) && (
                        <p className="truncate">{selectedPerson.survey_responses?.vibe || selectedPerson.vibe}</p>
                      )}
                      {selectedPerson.survey_responses?.tap_in_song && (
                        <p className="truncate text-[10px] text-gray-500">🎵 {selectedPerson.survey_responses.tap_in_song}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Notes - Combined */}
                {(selectedPerson.dietary_notes || selectedPerson.accessibility_notes || selectedPerson.anything_else_to_share || selectedPerson.preferences?.dietary_notes || selectedPerson.preferences?.accessibility_notes) && (
                  <div className="text-xs border-t pt-2">
                    <p className="font-semibold text-gray-800 mb-1.5 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5 text-gray-500" />
                      Notes
                    </p>
                    <div className="space-y-1 text-gray-600 text-[10px]">
                      {(selectedPerson.dietary_notes || selectedPerson.preferences?.dietary_notes) && (
                        <p>🍽️ {selectedPerson.dietary_notes || selectedPerson.preferences?.dietary_notes}</p>
                      )}
                      {(selectedPerson.accessibility_notes || selectedPerson.preferences?.accessibility_notes) && (
                        <p>♿ {selectedPerson.accessibility_notes || selectedPerson.preferences?.accessibility_notes}</p>
                      )}
                      {selectedPerson.anything_else_to_share && (
                        <p>💬 {selectedPerson.anything_else_to_share}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* All Intake Responses - Comprehensive View */}
                {(() => {
                  // Helper: safely convert any value to an array (handles strings, arrays, nulls)
                  const toArr = (val) => {
                    if (!val) return [];
                    if (Array.isArray(val)) return val;
                    if (typeof val === 'string') return val.trim() ? [val] : [];
                    return [];
                  };
                  // Merge + dedupe helper
                  const merge = (...arrs) => [...new Set(arrs.flatMap(a => toArr(a)))];
                  return null;
                })()}
                <div className="text-xs border-t pt-2">
                  <p className="font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
                    <ListChecks className="w-3.5 h-3.5 text-gray-500" />
                    All Intake Responses
                  </p>
                  <div className="space-y-2">
                    {/* Interests */}
                    {(() => {
                      const toArr = (v) => !v ? [] : Array.isArray(v) ? v : typeof v === 'string' && v.trim() ? [v] : [];
                      const items = [...new Set([...toArr(selectedPerson.interests), ...toArr(selectedPerson.preferences?.interests)])];
                      if (!items.length) return null;
                      return (
                      <div className="p-2 bg-indigo-50 rounded-lg">
                        <p className="text-[10px] text-indigo-600 font-medium mb-1">Interests</p>
                        <div className="flex flex-wrap gap-1">
                          {items.map((interest, idx) => (
                              <Badge key={idx} className="bg-indigo-100 text-indigo-700 text-[10px]">{interest}</Badge>
                            ))}
                        </div>
                      </div>
                      );
                    })()}
                    
                    {/* Vibe */}
                    {(selectedPerson.vibe || selectedPerson.survey_responses?.vibe || selectedPerson.preferences?.vibe) && (
                      <div className="p-2 bg-pink-50 rounded-lg">
                        <p className="text-[10px] text-pink-600 font-medium mb-1">Your Vibe Tonight</p>
                        {(() => {
                          const vibeValue = selectedPerson.vibe || selectedPerson.survey_responses?.vibe || selectedPerson.preferences?.vibe;
                          // Complete vibe mapping - matching VIBE_OPTIONS from EventPageElegant.jsx
                          const vibeMap = {
                            "social": "Very social - Ready to meet everyone",
                            "very_social": "Very social - Ready to meet everyone",
                            "selective": "Selectively social - Quality conversations",
                            "selectively_social": "Selectively social - Quality conversations",
                            "observing": "Observing - Taking it all in",
                            "vibing": "Just vibing - Here for the experience",
                            "just_vibing": "Just vibing - Here for the experience",
                            "chill": "Very chill - Happy to meet organically",
                            "very_chill": "Very chill - Happy to meet organically",
                            "organic": "Very chill - Happy to meet organically",
                            "chatty": "Very chatty - Love to mingle",
                            "quiet": "Quiet observer - Soaking it in",
                            "networking": "Networking mode - Here to connect"
                          };
                          return (
                            <p className="text-[11px] text-pink-700 italic">
                              "{vibeMap[vibeValue?.toLowerCase()] || vibeValue}"
                            </p>
                          );
                        })()}
                      </div>
                    )}
                    
                    {/* Social Media */}
                    {(selectedPerson.instagram || selectedPerson.tiktok || selectedPerson.linkedin) && (
                      <div className="p-2 bg-cyan-50 rounded-lg">
                        <p className="text-[10px] text-cyan-600 font-medium mb-1">Social</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedPerson.instagram && (
                            <Badge className="bg-cyan-100 text-cyan-700 text-[10px]">IG: @{selectedPerson.instagram.replace('@', '')}</Badge>
                          )}
                          {selectedPerson.tiktok && (
                            <Badge className="bg-cyan-100 text-cyan-700 text-[10px]">TikTok: @{selectedPerson.tiktok.replace('@', '')}</Badge>
                          )}
                          {selectedPerson.linkedin && (
                            <Badge className="bg-cyan-100 text-cyan-700 text-[10px]">LinkedIn: {selectedPerson.linkedin}</Badge>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Dietary Restrictions */}
                    {(() => {
                      const toArr = (v) => !v ? [] : Array.isArray(v) ? v : typeof v === 'string' && v.trim() ? [v] : [];
                      const items = [...new Set([...toArr(selectedPerson.dietary_restrictions), ...toArr(selectedPerson.preferences?.dietary_restrictions)])];
                      if (!items.length) return null;
                      return (
                      <div className="p-2 bg-orange-50 rounded-lg">
                        <p className="text-[10px] text-orange-600 font-medium mb-1">Dietary Restrictions</p>
                        <div className="flex flex-wrap gap-1">
                          {items.map((d, idx) => (
                              <Badge key={idx} className="bg-orange-100 text-orange-700 text-[10px]">{d}</Badge>
                            ))}
                        </div>
                      </div>
                      );
                    })()}
                    
                    {/* Meat/Protein Preferences */}
                    {(() => {
                      const toArr = (v) => !v ? [] : Array.isArray(v) ? v : typeof v === 'string' && v.trim() ? [v] : [];
                      const items = [...new Set([...toArr(selectedPerson.meat_preferences), ...toArr(selectedPerson.preferences?.meat_preferences)])];
                      if (!items.length) return null;
                      return (
                      <div className="p-2 bg-green-50 rounded-lg">
                        <p className="text-[10px] text-green-600 font-medium mb-1">Protein Preferences</p>
                        <div className="flex flex-wrap gap-1">
                          {items.map((m, idx) => (
                              <Badge key={idx} className="bg-green-100 text-green-700 text-[10px]">{m}</Badge>
                            ))}
                        </div>
                      </div>
                      );
                    })()}
                    
                    {/* Allergies */}
                    {(() => {
                      const toArr = (v) => !v ? [] : Array.isArray(v) ? v : typeof v === 'string' && v.trim() ? [v] : [];
                      const items = [...new Set([...toArr(selectedPerson.allergies), ...toArr(selectedPerson.preferences?.allergies)])];
                      if (!items.length) return null;
                      return (
                      <div className="p-2 bg-red-50 rounded-lg">
                        <p className="text-[10px] text-red-600 font-medium mb-1">Allergies</p>
                        <div className="flex flex-wrap gap-1">
                          {items.map((a, idx) => (
                              <Badge key={idx} className="bg-red-100 text-red-700 text-[10px]">{a}</Badge>
                            ))}
                        </div>
                      </div>
                      );
                    })()}
                    
                    {/* Accessibility Needs */}
                    {(() => {
                      const toArr = (v) => !v ? [] : Array.isArray(v) ? v : typeof v === 'string' && v.trim() ? [v] : [];
                      const items = [...new Set([...toArr(selectedPerson.accessibility_needs), ...toArr(selectedPerson.preferences?.accessibility_needs)])];
                      if (!items.length) return null;
                      return (
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <p className="text-[10px] text-blue-600 font-medium mb-1">Accessibility Needs</p>
                        <div className="flex flex-wrap gap-1">
                          {items.map((a, idx) => (
                              <Badge key={idx} className="bg-blue-100 text-blue-700 text-[10px]">{a.replace(/_/g, ' ')}</Badge>
                            ))}
                        </div>
                      </div>
                      );
                    })()}
                    
                    {/* Invisible Disabilities */}
                    {(() => {
                      const toArr = (v) => !v ? [] : Array.isArray(v) ? v : typeof v === 'string' && v.trim() ? [v] : [];
                      const items = [...new Set([...toArr(selectedPerson.invisible_disabilities), ...toArr(selectedPerson.preferences?.invisible_disabilities)])];
                      if (!items.length) return null;
                      return (
                      <div className="p-2 bg-purple-50 rounded-lg">
                        <p className="text-[10px] text-purple-600 font-medium mb-1">Invisible Disabilities</p>
                        <div className="flex flex-wrap gap-1">
                          {items.map((d, idx) => (
                              <Badge key={idx} className="bg-purple-100 text-purple-700 text-[10px]">{d.replace(/_/g, ' ')}</Badge>
                            ))}
                        </div>
                      </div>
                      );
                    })()}
                    
                    {/* Custom Answers */}
                    {selectedPerson.custom_answers && Object.keys(selectedPerson.custom_answers).length > 0 && (
                      <div className="p-2 bg-gray-50 rounded-lg">
                        <p className="text-[10px] text-gray-600 font-medium mb-1">Custom Responses</p>
                        <div className="space-y-1">
                          {Object.entries(selectedPerson.custom_answers).map(([question, answer], idx) => (
                            <div key={idx} className="text-[10px]">
                              <span className="font-medium text-gray-700">{question}:</span>{' '}
                              <span className="text-gray-600">{String(answer)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Category-specific preferences (flat key-value pairs from preferences object) */}
                    {(() => {
                      const prefs = selectedPerson.preferences || {};
                      // Keys already handled by specific sections above
                      const handledKeys = new Set(['dietary_restrictions', 'meat_preferences', 'allergies', 'accessibility_needs', 'invisible_disabilities', 'interests', 'interest_preferences', 'vibe', 'inspo', 'dietary', 'accessibility']);
                      const entries = Object.entries(prefs).filter(([k, v]) => !handledKeys.has(k) && v != null && v !== '' && v !== true);
                      if (!entries.length) return null;
                      return (
                        <div className="p-2 bg-violet-50 rounded-lg">
                          <p className="text-[10px] text-violet-600 font-medium mb-1">Preferences</p>
                          <div className="space-y-1">
                            {entries.map(([k, v], idx) => (
                              <div key={idx} className="text-[10px]">
                                <span className="font-medium text-gray-600 capitalize">{k.replace(/_/g, ' ')}:</span>{' '}
                                <span className="text-gray-800">{typeof v === 'object' ? (Array.isArray(v) ? v.join(', ') : Object.entries(v).map(([sk, sv]) => `${sk.replace(/_/g, ' ')}: ${sv}`).join(', ')) : String(v)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    
                    {/* Inspo Images - Grid of square thumbnails like Instagram */}
                    {((selectedPerson.inspo || selectedPerson.preferences?.inspo || []).length > 0) && (
                      <div className="p-2 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg">
                        <p className="text-[10px] text-purple-600 font-medium mb-2">Inspiration Images</p>
                        <div className={`grid gap-2 ${
                          (selectedPerson.inspo || selectedPerson.preferences?.inspo || []).length === 1 ? 'grid-cols-1 max-w-[80px]' :
                          (selectedPerson.inspo || selectedPerson.preferences?.inspo || []).length === 2 ? 'grid-cols-2 max-w-[160px]' :
                          (selectedPerson.inspo || selectedPerson.preferences?.inspo || []).length === 3 ? 'grid-cols-3 max-w-[240px]' :
                          'grid-cols-4 max-w-[300px]'
                        }`}>
                          {(selectedPerson.inspo || selectedPerson.preferences?.inspo || []).slice(0, 8).map((inspo, idx) => {
                            const imgUrl = typeof inspo === 'string' ? inspo : (inspo.image_url || inspo.url);
                            const source = typeof inspo === 'object' ? inspo.source : null;
                            return (
                              <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-white shadow-sm relative group">
                                <img 
                                  src={imgUrl} 
                                  alt={`Inspo ${idx + 1}`} 
                                  className="w-full h-full object-cover"
                                />
                                {source && (
                                  <div className={`absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[8px] font-medium ${
                                    source === 'host_curated' ? 'bg-purple-500 text-white' : 'bg-gray-700/80 text-white'
                                  }`}>
                                    {source === 'host_curated' ? 'Host' : 'User'}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {(selectedPerson.inspo || selectedPerson.preferences?.inspo || []).length > 8 && (
                          <p className="text-[10px] text-purple-500 mt-2 text-center">
                            +{(selectedPerson.inspo || selectedPerson.preferences?.inspo || []).length - 8} more images
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Inner Circle Status */}
                    {selectedPerson.inner_circle && (
                      <div className="p-2 bg-yellow-50 rounded-lg">
                        <p className="text-[10px] text-yellow-600 font-medium mb-1">Inner Circle</p>
                        <Badge className="bg-yellow-100 text-yellow-700 text-[10px]">Wants to be added to {event?.inner_circle_title || "Inner Circle"}</Badge>
                      </div>
                    )}

                    {/* Host Private Inspo for this Client */}
                    <div className="p-2 bg-gradient-to-br from-indigo-50 to-violet-50 rounded-lg" data-testid="host-inspo-section">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] text-indigo-600 font-medium">Your Private Inspo for {selectedPerson.name?.split(' ')[0] || 'Client'}</p>
                        <button
                          onClick={() => setShowHostInspoPopup(true)}
                          className="flex items-center gap-1 text-[10px] text-indigo-600 hover:text-indigo-800 font-medium px-2 py-0.5 rounded-md hover:bg-indigo-100 transition-colors"
                          data-testid="add-host-inspo-btn"
                        >
                          <Plus className="w-3 h-3" />
                          Add Inspo
                        </button>
                      </div>
                      {hostInspoLoading ? (
                        <div className="flex items-center justify-center py-3">
                          <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
                        </div>
                      ) : hostInspoItems.length > 0 ? (
                        <div className="grid grid-cols-4 gap-1.5">
                          {hostInspoItems.slice(0, 8).map((item) => (
                            <div key={item.inspo_id} className="aspect-square rounded-lg overflow-hidden bg-white shadow-sm relative group">
                              <img
                                src={item.image_url}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                              <button
                                onClick={() => deleteHostInspo(item.inspo_id)}
                                className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                                data-testid={`delete-host-inspo-${item.inspo_id}`}
                              >
                                <X className="w-2.5 h-2.5" />
                              </button>
                              <div className="absolute bottom-0.5 left-0.5 px-1 py-0.5 rounded text-[7px] font-medium bg-indigo-500 text-white">
                                Host
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[10px] text-gray-400 text-center py-2">
                          No private inspo yet. Add photos to guide this client's look.
                        </p>
                      )}
                    </div>
                    
                    {/* Show if nothing filled out - check ALL intake fields */}
                    {!selectedPerson.dietary_restrictions?.length && 
                     !selectedPerson.preferences?.dietary_restrictions?.length &&
                     !selectedPerson.meat_preferences?.length &&
                     !selectedPerson.allergies?.length &&
                     !selectedPerson.accessibility_needs?.length &&
                     !selectedPerson.invisible_disabilities?.length &&
                     !selectedPerson.interests?.length &&
                     !selectedPerson.preferences?.interests?.length &&
                     !selectedPerson.vibe &&
                     !selectedPerson.survey_responses?.vibe &&
                     !selectedPerson.instagram &&
                     !selectedPerson.tiktok &&
                     !selectedPerson.linkedin &&
                     !selectedPerson.intake_responses?.length &&
                     (!selectedPerson.custom_answers || Object.keys(selectedPerson.custom_answers).length === 0) && (
                      <p className="text-gray-400 text-center py-2">No intake responses yet</p>
                    )}
                    
                    {/* Intake Q&A Responses */}
                    {selectedPerson.intake_responses?.length > 0 && (
                      <div className="p-2 bg-gray-50 rounded-lg" data-testid="intake-responses">
                        <p className="text-[10px] text-gray-600 font-medium mb-1">Intake Responses</p>
                        <div className="space-y-1.5">
                          {selectedPerson.intake_responses.map((qa, idx) => (
                            <div key={idx} className="bg-white p-1.5 rounded border border-gray-100">
                              <p className="text-[9px] text-gray-400 font-medium">{qa.question}</p>
                              <p className="text-[11px] text-gray-800">{qa.answer}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Done Button */}
                <div className="pt-2 border-t">
                  <Button 
                    onClick={() => { setShowPreferenceCard(false); setShowExpandedDetails(false); }}
                    className="w-full bg-gray-900 hover:bg-black text-white text-xs py-1.5"
                  >
                    Done
                  </Button>
                </div>
              </div>
              )}
              {/* Per-RSVP Private Inspo Viewer */}
              {selectedPerson?.rsvp_id && (
                <RsvpPrivateInspoViewer rsvpId={selectedPerson.rsvp_id} eventId={eventId} category={event?.pilot_category} />
              )}
            </>
          )}
          <DialogTitle className="sr-only">Preference Card</DialogTitle>
          <DialogDescription id="preference-card-desc" className="sr-only">Guest preference details</DialogDescription>
        </DialogContent>
      </Dialog>

      {/* Team Notes Dialog */}
      <Dialog open={showTeamNotes} onOpenChange={setShowTeamNotes}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Team Notes</DialogTitle>
            <DialogDescription>Notes visible to team members only</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Add internal notes about this guest..."
              value={teamNotesContent}
              onChange={(e) => setTeamNotesContent(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTeamNotes(false)}>Cancel</Button>
            <Button onClick={async () => {
              if (selectedPerson?.rsvp_id) {
                const token = localStorage.getItem("session_token");
                await fetch(`${API}/events/${eventId}/rsvps/${selectedPerson.rsvp_id}`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                  body: JSON.stringify({ host_note: teamNotesContent })
                });
                toast.success("Notes saved");
                setShowTeamNotes(false);
                mutateRsvps();
              }
            }}>Save Notes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Question Bank Modal (Phase 1C: Instructor Pilot) */}
      <Dialog open={showQuestionBankModal} onOpenChange={setShowQuestionBankModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-indigo-600" />
              Question Bank ({questionBankData.total_questions || 0} questions)
            </DialogTitle>
            <DialogDescription>
              Review questions from attendees. Filter by recipient and share curated questions with the instructor.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {questionBankLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
              </div>
            ) : questionBankData.questions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No questions yet</p>
                <p className="text-sm">Questions will appear here as attendees RSVP</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Filter by Recipient + Select All */}
                <div className="flex items-center justify-between pb-3 border-b">
                  <div className="flex gap-2">
                    {['all', 'instructor', 'emergent', 'both'].map(filter => (
                      <Button
                        key={filter}
                        variant={questionBankData.filterRecipient === filter ? "default" : "outline"}
                        size="sm"
                        className="text-xs"
                        onClick={() => setQuestionBankData(prev => ({ ...prev, filterRecipient: filter }))}
                      >
                        {filter === 'all' ? 'All' : filter === 'instructor' ? 'For Instructor' : filter === 'emergent' ? 'For Emergent' : 'Both'}
                      </Button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        const filteredQuestions = questionBankData.questions.filter(q => 
                          !questionBankData.filterRecipient || questionBankData.filterRecipient === 'all' || 
                          q.recipient === questionBankData.filterRecipient || 
                          q.recipient === 'both'
                        );
                        const allIds = filteredQuestions.map(q => q.id || q.question);
                        setQuestionBankData(prev => ({ ...prev, selectedQuestions: allIds }));
                      }}
                    >
                      Select All
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setQuestionBankData(prev => ({ ...prev, selectedQuestions: [] }))}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
                
                {/* Selection count */}
                <p className="text-sm text-gray-500">
                  {(questionBankData.selectedQuestions || []).length} selected for sharing
                </p>
                
                {/* Question List with Selection and Recipient Tags */}
                {questionBankData.questions
                  .filter(q => 
                    !questionBankData.filterRecipient || questionBankData.filterRecipient === 'all' || 
                    q.recipient === questionBankData.filterRecipient || 
                    q.recipient === 'both'
                  )
                  .map((q, idx) => {
                  const qId = q.id || q.question;
                  const isSelected = (questionBankData.selectedQuestions || []).includes(qId);
                  
                  return (
                    <div 
                      key={qId || idx} 
                      className={`p-4 rounded-xl border cursor-pointer transition-all ${
                        isSelected 
                          ? 'bg-indigo-50 border-indigo-300 ring-1 ring-indigo-200' 
                          : 'bg-gray-50 border-gray-200 hover:border-indigo-200'
                      }`}
                      onClick={() => {
                        setQuestionBankData(prev => ({
                          ...prev,
                          selectedQuestions: isSelected
                            ? (prev.selectedQuestions || []).filter(id => id !== qId)
                            : [...(prev.selectedQuestions || []), qId]
                        }));
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                          isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-900 font-medium mb-2">"{q.question}"</p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">
                                {q.attendee_name || 'Anonymous'}
                              </span>
                              {/* Recipient Tag */}
                              <Badge variant="secondary" className={`text-xs ${
                                q.recipient === 'instructor' ? 'bg-blue-100 text-blue-700' :
                                q.recipient === 'emergent' ? 'bg-purple-100 text-purple-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {q.recipient === 'instructor' ? 'For Instructor' : 
                                 q.recipient === 'emergent' ? 'For Emergent' : 'Both'}
                              </Badge>
                            </div>
                            <span className="text-xs text-gray-400">
                              {q.submitted_at ? new Date(q.submitted_at).toLocaleDateString() : ''}
                            </span>
                          </div>
                          {q.shared_with_instructor && (
                            <Badge variant="secondary" className="mt-2 text-xs bg-green-100 text-green-700">
                              <Check className="w-3 h-3 mr-1" />
                              Shared with instructor
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })} 
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {/* Share Selected with Instructor */}
            <Button 
              variant="default"
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={async () => {
                const selected = questionBankData.selectedQuestions || [];
                if (selected.length === 0) {
                  toast.error("Select at least one question to share");
                  return;
                }
                try {
                  const token = localStorage.getItem("session_token");
                  const res = await fetch(`${API}/events/${eventId}/question-bank/share`, {
                    method: 'POST',
                    headers: { 
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}` 
                    },
                    credentials: 'include',
                    body: JSON.stringify({ question_ids: selected })
                  });
                  if (res.ok) {
                    toast.success(`${selected.length} question(s) shared with instructor!`);
                    fetchQuestionBank(); // Refresh to show shared status
                  } else {
                    toast.error("Failed to share questions");
                  }
                } catch (err) {
                  toast.error("Error sharing questions");
                }
              }}
              disabled={(questionBankData.selectedQuestions || []).length === 0}
            >
              <Send className="w-4 h-4 mr-1" />
              Share ({(questionBankData.selectedQuestions || []).length}) with Instructor
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                // Copy selected questions to clipboard
                const selected = questionBankData.selectedQuestions || [];
                const questions = questionBankData.questions.filter(q => selected.includes(q.id || q.question));
                const text = questions.map(q => `• ${q.question}`).join('\n');
                navigator.clipboard.writeText(text || questionBankData.questions.map(q => `• ${q.question}`).join('\n'));
                toast.success("Questions copied to clipboard!");
              }}
              disabled={questionBankData.questions.length === 0}
            >
              <Copy className="w-4 h-4 mr-1" />
              Copy
            </Button>
            <Button 
              variant="outline"
              onClick={fetchQuestionBank}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
            <Button variant="ghost" onClick={() => setShowQuestionBankModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create VIP List Modal */}
      <Dialog open={showCreateListModal} onOpenChange={setShowCreateListModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create VIP List</DialogTitle>
            <DialogDescription>Create a VIP list with a list host who can invite guests</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Info Box */}
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
              <p className="text-sm text-purple-800">
                <strong>How it works:</strong> The List Host receives a link to RSVP. Once they RSVP, they can invite guests up to the VIP limit. Each guest can bring +N based on settings below.
              </p>
            </div>
            
            <div>
              <Label>List Name *</Label>
              <Input
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder="e.g., Fashion Week VIPs, Press List"
                data-testid="new-list-name-input"
              />
            </div>
            
            {/* List Host Selection */}
            <div className="space-y-3">
              <Label>Assign List Host *</Label>
              
              {/* Option to select from existing RSVPs */}
              {rsvps && rsvps.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500">Select from existing RSVPs:</p>
                  <select
                    className="w-full p-2 border rounded-lg text-sm"
                    value={selectedListHostId || ""}
                    onChange={(e) => {
                      const selectedRsvp = rsvps.find(r => r.rsvp_id === e.target.value);
                      if (selectedRsvp) {
                        setSelectedListHostId(e.target.value);
                        setNewListHostName(`${selectedRsvp.first_name || ''} ${selectedRsvp.last_name || ''}`.trim() || selectedRsvp.name);
                        setNewListHostEmail(selectedRsvp.email || "");
                      } else {
                        setSelectedListHostId("");
                        setNewListHostName("");
                        setNewListHostEmail("");
                      }
                    }}
                    data-testid="select-list-host-dropdown"
                  >
                    <option value="">-- Select an existing attendee --</option>
                    {rsvps.map(r => (
                      <option key={r.rsvp_id} value={r.rsvp_id}>
                        {`${r.first_name || ''} ${r.last_name || ''}`.trim() || r.name} {r.email ? `(${r.email})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Or enter manually */}
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <div className="h-px flex-1 bg-gray-200"></div>
                <span>or enter manually</span>
                <div className="h-px flex-1 bg-gray-200"></div>
              </div>
              
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label className="text-xs">Host Name</Label>
                  <Input
                    value={newListHostName || ""}
                    onChange={(e) => {
                      setNewListHostName(e.target.value);
                      setSelectedListHostId(""); // Clear dropdown selection
                    }}
                    placeholder="Host name"
                    data-testid="new-list-host-name-input"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">You'll copy the invite link to share manually</p>
              
              {/* VIP Invite Limits */}
              <div className="pt-3 border-t mt-3 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-medium">VIP Guest Limit</Label>
                    <p className="text-xs text-gray-500 mb-2">Max guests the host can invite</p>
                    <Input
                      type="number"
                      min="1"
                      max="500"
                      value={newListInviteLimit}
                      onChange={(e) => setNewListInviteLimit(parseInt(e.target.value) || 10)}
                      placeholder="10"
                      className="w-full"
                      data-testid="new-list-invite-limit-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">+N Per Guest</Label>
                    <p className="text-xs text-gray-500 mb-2">Plus-ones per VIP guest</p>
                    <Input
                      type="number"
                      min="0"
                      max="10"
                      value={newListPlusLimit}
                      onChange={(e) => setNewListPlusLimit(parseInt(e.target.value) || 0)}
                      placeholder="2"
                      className="w-full"
                      data-testid="new-list-plus-limit-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateListModal(false);
              setSelectedListHostId("");
              setNewListHostEmail("");
              setNewListInviteLimit(10);
              setNewListPlusLimit(2);
            }}>Cancel</Button>
            <Button 
              onClick={async () => {
                if (!newListName.trim() || !newListHostName?.trim()) {
                  toast.error("Please fill in all fields");
                  return;
                }
                const token = localStorage.getItem("session_token");
                try {
                  const res = await fetch(`${API}/events/${eventId}/vip-lists`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ 
                      name: newListName.trim(), 
                      list_host_name: newListHostName.trim(),
                      list_host_rsvp_id: selectedListHostId || null,
                      max_guests: newListInviteLimit || 10,
                      max_plus_per_guest: newListPlusLimit ?? 2
                    })
                  });
                  if (res.ok) {
                    const data = await res.json();
                    toast.success(`"${newListName}" list created!`);
                    setShowCreateListModal(false);
                    setNewListName("");
                    setNewListHostName("");
                    setNewListHostEmail("");
                    setSelectedListHostId("");
                    setNewListInviteLimit(10);
                    setNewListPlusLimit(2);
                    fetchLists();
                    // Copy the list host invite link to clipboard
                    if (data.list_host_url) {
                      const fullUrl = window.location.origin + data.list_host_url;
                      navigator.clipboard.writeText(fullUrl);
                      toast.success("List host invite link copied to clipboard! Share it manually.", { duration: 5000 });
                    }
                  } else {
                    const err = await res.json();
                    toast.error(err.detail || "Failed to create list");
                  }
                } catch (err) {
                  toast.error("Failed to create list");
                }
              }} 
              data-testid="create-list-submit-btn"
              disabled={!newListName.trim() || !newListHostName?.trim()}
            >
              Create List
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VIP List Host Shareable View Modal */}
      <Dialog open={showVipHostViewModal} onOpenChange={setShowVipHostViewModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-500" />
              VIP List Host View
            </DialogTitle>
            <DialogDescription>
              Share this link with your list host so they can see their assigned guests.
            </DialogDescription>
          </DialogHeader>
          {selectedVipListForView && (
            <div className="space-y-4 py-4">
              <div className="bg-purple-50 p-4 rounded-lg border">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold">{selectedVipListForView.name}</h3>
                  <Badge className={selectedVipListForView.is_claimed ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
                    {selectedVipListForView.is_claimed ? "Synced" : "Pending"}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600">Host: {selectedVipListForView.list_host_name || "Not assigned"}</p>
                <p className="text-sm text-gray-600">{(selectedVipListForView.members || []).length} guests assigned</p>
              </div>
              
              {/* List of members (names only) */}
              {(selectedVipListForView.members || []).length > 0 && (
                <div className="border rounded-lg p-3">
                  <Label className="text-xs text-gray-500 mb-2 block">Assigned Guests</Label>
                  <div className="space-y-1 max-h-[150px] overflow-y-auto">
                    {selectedVipListForView.members.map((member, idx) => (
                      <div key={idx} className="text-sm flex items-center gap-2 py-1">
                        <User className="w-3 h-3 text-gray-400" />
                        {member.name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || "Unnamed Guest"}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Shareable Link */}
              <div className="bg-gray-50 p-4 rounded-lg border">
                <Label className="text-xs text-gray-500 mb-2 block">Share this link with the list host</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={`${window.location.origin}${selectedVipListForView.list_host_url || ''}`}
                    readOnly
                    className="flex-1 font-mono text-sm bg-white"
                    data-testid="vip-host-link-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}${selectedVipListForView.list_host_url || ''}`);
                      toast.success("Link copied!");
                    }}
                    data-testid="copy-vip-host-link-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="text-xs text-gray-500 text-center">
                The list host will see only the names of guests attached to their list.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowVipHostViewModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Team Member Modal */}
      <Dialog open={showAddTeamModal} onOpenChange={setShowAddTeamModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Team Member or Co-Host</DialogTitle>
            <DialogDescription>
              Select from existing RSVPs or create a new placeholder.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Option to select from existing RSVPs */}
            {rsvps && rsvps.length > 0 && (
              <div className="space-y-2">
                <Label>Select from Existing RSVPs</Label>
                <Select 
                  onValueChange={async (rsvpId) => {
                    const selectedRsvp = rsvps.find(r => r.rsvp_id === rsvpId);
                    if (selectedRsvp) {
                      const nameParts = (selectedRsvp.name || '').split(' ');
                      let orgName = selectedRsvp.org_name || selectedRsvp.organization || selectedRsvp.company || '';
                      
                      // If user has a linked account, fetch their profile to get org info
                      if (selectedRsvp.user_id) {
                        try {
                          const token = localStorage.getItem("session_token");
                          // Fetch user's organization from their profile
                          const userRes = await fetch(`${API}/users/${selectedRsvp.user_id}/public-profile`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          if (userRes.ok) {
                            const userData = await userRes.json();
                            // Get org from user profile
                            if (userData.organization?.name) {
                              orgName = userData.organization.name;
                            } else if (userData.org_name) {
                              orgName = userData.org_name;
                            } else if (userData.company) {
                              orgName = userData.company;
                            }
                          }
                        } catch (err) {
                          console.log('Could not fetch user org:', err);
                        }
                      }
                      
                      setNewTeamMember({
                        ...newTeamMember,
                        email: selectedRsvp.email || '',
                        name: selectedRsvp.name || '',
                        first_name: nameParts[0] || '',
                        last_name: nameParts.slice(1).join(' ') || '',
                        org_name: orgName,
                        from_rsvp: rsvpId,
                        user_id: selectedRsvp.user_id
                      });
                    }
                  }}
                >
                  <SelectTrigger data-testid="select-from-rsvp">
                    <SelectValue placeholder="Choose from guest list..." />
                  </SelectTrigger>
                  <SelectContent>
                    {rsvps
                      .filter(r => r.email && !team.some(t => t.email === r.email))
                      .map(r => (
                        <SelectItem key={r.rsvp_id} value={r.rsvp_id}>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-xs">
                              {r.name?.charAt(0) || '?'}
                            </div>
                            <div>
                              <span className="font-medium">{r.name || 'Guest'}</span>
                              <span className="text-xs text-gray-500 ml-2">{r.email}</span>
                            </div>
                          </div>
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">Select a guest to promote to team member/co-host</p>
              </div>
            )}
            
            {rsvps && rsvps.length > 0 && (
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Or enter manually</span>
                </div>
              </div>
            )}
            
            {/* Role Selection First */}
            <div>
              <Label>Role *</Label>
              <Select 
                value={newTeamMember.role} 
                onValueChange={(v) => setNewTeamMember({...newTeamMember, role: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">
                    <div className="flex items-center gap-2">
                      <Crown className="w-4 h-4 text-yellow-500" />
                      <div>
                        <p className="font-medium">Co-Host</p>
                        <p className="text-xs text-gray-500">Full dashboard access, can edit everything</p>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="editor">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-blue-500" />
                      <div>
                        <p className="font-medium">Team Admin</p>
                        <p className="text-xs text-gray-500">Can manage people, limited settings</p>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="viewer">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="font-medium">Team Member</p>
                        <p className="text-xs text-gray-500">View only, check-in access</p>
                      </div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Info notice based on role */}
            {newTeamMember.role === "owner" && (
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800">
                  <strong>Co-Host:</strong> They'll have full access to this event. Their profile and organization will appear on the event page.
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First Name *</Label>
                <Input
                  value={newTeamMember.first_name || ""}
                  onChange={(e) => setNewTeamMember({...newTeamMember, first_name: e.target.value, name: `${e.target.value} ${newTeamMember.last_name || ''}`.trim()})}
                  placeholder="First name"
                  data-testid="team-first-name-input"
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input
                  value={newTeamMember.last_name || ""}
                  onChange={(e) => setNewTeamMember({...newTeamMember, last_name: e.target.value, name: `${newTeamMember.first_name || ''} ${e.target.value}`.trim()})}
                  placeholder="Last name"
                  data-testid="team-last-name-input"
                />
              </div>
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={newTeamMember.email}
                onChange={(e) => setNewTeamMember({...newTeamMember, email: e.target.value})}
                placeholder="team@example.com"
                data-testid="team-email-input"
              />
              <p className="text-xs text-gray-500 mt-1">When they sign up with this email, their profile syncs automatically</p>
            </div>
            
            {/* Organization Placeholder (especially for Co-Hosts) */}
            <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-purple-500" />
                <Label className="font-medium">Organization Placeholder</Label>
              </div>
              <Input
                value={newTeamMember.org_name || ""}
                onChange={(e) => setNewTeamMember({...newTeamMember, org_name: e.target.value})}
                placeholder="Their organization name"
                data-testid="team-org-input"
              />
              <p className="text-xs text-gray-500">
                This will update when they link their Phygital organization
              </p>
            </div>
            
            {/* Show selected RSVP info */}
            {newTeamMember.from_rsvp && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  <CheckCircle2 className="w-4 h-4 inline mr-1" />
                  Selected from existing RSVP. They'll be promoted to team member.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTeamModal(false)}>Cancel</Button>
            <Button 
              onClick={addTeamMember} 
              disabled={addingTeamMember || !newTeamMember.first_name || !newTeamMember.last_name || !newTeamMember.email} 
              data-testid="add-team-submit-btn"
            >
              {addingTeamMember ? "Creating..." : newTeamMember.from_rsvp ? "Promote to Team" : "Add & Create Invite Link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Team Member Modal - Edit all info with instant save */}
      <Dialog open={showEditTeamModal} onOpenChange={setShowEditTeamModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
            <DialogDescription>
              Update this person's info. Changes save instantly.
            </DialogDescription>
          </DialogHeader>
          {editingTeamMember && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={editingTeamMember.name || ""}
                  onChange={async (e) => {
                    const newVal = e.target.value;
                    setEditingTeamMember(prev => ({ ...prev, name: newVal }));
                  }}
                  onBlur={async () => {
                    const token = localStorage.getItem("session_token");
                    await fetch(`${API}/events/${eventId}/team/${editingTeamMember.member_id}`, {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ name: editingTeamMember.name })
                    });
                    mutateEvent();
                    toast.success("Saved");
                  }}
                  placeholder="Their name"
                />
              </div>
              <div>
                <Label>Email (for account linking)</Label>
                <Input
                  type="email"
                  value={editingTeamMember.email || ""}
                  onChange={(e) => setEditingTeamMember(prev => ({ ...prev, email: e.target.value }))}
                  onBlur={async () => {
                    const token = localStorage.getItem("session_token");
                    await fetch(`${API}/events/${eventId}/team/${editingTeamMember.member_id}`, {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ email: editingTeamMember.email })
                    });
                    mutateEvent();
                    toast.success("Saved");
                  }}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <Label>Organization</Label>
                <Input
                  value={editingTeamMember.org_name || ""}
                  onChange={(e) => setEditingTeamMember(prev => ({ ...prev, org_name: e.target.value }))}
                  onBlur={async () => {
                    const token = localStorage.getItem("session_token");
                    await fetch(`${API}/events/${eventId}/team/${editingTeamMember.member_id}`, {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ org_name: editingTeamMember.org_name })
                    });
                    mutateEvent();
                    toast.success("Saved");
                  }}
                  placeholder="Organization name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select 
                    value={editingTeamMember.label || "team"} 
                    onValueChange={async (v) => {
                      setEditingTeamMember(prev => ({ ...prev, label: v }));
                      const token = localStorage.getItem("session_token");
                      await fetch(`${API}/events/${eventId}/team/${editingTeamMember.member_id}`, {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ label: v })
                      });
                      mutateEvent();
                      toast.success("Saved");
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="host">Host</SelectItem>
                      <SelectItem value="partner">Partner</SelectItem>
                      <SelectItem value="sponsor">Sponsor</SelectItem>
                      <SelectItem value="team">Team</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Role</Label>
                  <Select 
                    value={editingTeamMember.role || "viewer"} 
                    onValueChange={async (v) => {
                      setEditingTeamMember(prev => ({ ...prev, role: v }));
                      const token = localStorage.getItem("session_token");
                      await fetch(`${API}/events/${eventId}/team/${editingTeamMember.member_id}`, {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ role: v })
                      });
                      mutateEvent();
                      toast.success("Saved");
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEAM_ROLES.map(role => (
                        <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Linked status */}
              <div className="p-3 rounded-lg border bg-gray-50">
                <div className="flex items-center gap-2">
                  {editingTeamMember.user_id ? (
                    <>
                      <Check className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-green-700">Phygital account linked</span>
                    </>
                  ) : (
                    <>
                      <Clock className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm text-yellow-700">Awaiting sign in to link account</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowEditTeamModal(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Partner Modal */}
      <Dialog open={showAddPartnerModal} onOpenChange={setShowAddPartnerModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Partner</DialogTitle>
            <DialogDescription>Add a partner organization to this event</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Host Name *</Label>
              <Input
                value={newPartner.name}
                onChange={(e) => setNewPartner({...newPartner, name: e.target.value})}
                placeholder="Partner Organization"
                data-testid="partner-name-input"
              />
            </div>
            <div>
              <Label>Contact Name</Label>
              <Input
                value={newPartner.contact_name || ""}
                onChange={(e) => setNewPartner({...newPartner, contact_name: e.target.value})}
                placeholder="John Smith"
              />
            </div>
            <div>
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={newPartner.contact_email || ""}
                onChange={(e) => setNewPartner({...newPartner, contact_email: e.target.value})}
                placeholder="contact@partner.com"
              />
              <p className="text-xs text-gray-500 mt-1">They'll be invited to link their Phygital profile</p>
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input
                type="tel"
                value={newPartner.contact_phone || ""}
                onChange={(e) => setNewPartner({...newPartner, contact_phone: e.target.value})}
                placeholder="+1 555-123-4567"
              />
            </div>
            <div>
              <Label>Website</Label>
              <Input
                value={newPartner.website}
                onChange={(e) => setNewPartner({...newPartner, website: e.target.value})}
                placeholder="https://example.com"
                data-testid="partner-website-input"
              />
            </div>
            <div>
              <Label>Logo URL</Label>
              <Input
                value={newPartner.logo}
                onChange={(e) => setNewPartner({...newPartner, logo: e.target.value})}
                placeholder="https://example.com/logo.png"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPartnerModal(false)}>Cancel</Button>
            <Button onClick={addPartner} data-testid="add-partner-submit-btn">Add Partner</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Sponsor Modal */}
      <Dialog open={showAddSponsorModal} onOpenChange={setShowAddSponsorModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Sponsor</DialogTitle>
            <DialogDescription>Add a sponsor to this event</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Host Name *</Label>
              <Input
                value={newSponsor.name}
                onChange={(e) => setNewSponsor({...newSponsor, name: e.target.value})}
                placeholder="Sponsor Organization"
                data-testid="sponsor-name-input"
              />
            </div>
            <div>
              <Label>Contact Name</Label>
              <Input
                value={newSponsor.contact_name || ""}
                onChange={(e) => setNewSponsor({...newSponsor, contact_name: e.target.value})}
                placeholder="John Smith"
              />
            </div>
            <div>
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={newSponsor.contact_email || ""}
                onChange={(e) => setNewSponsor({...newSponsor, contact_email: e.target.value})}
                placeholder="contact@sponsor.com"
              />
              <p className="text-xs text-gray-500 mt-1">They'll be invited to link their Phygital profile</p>
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input
                type="tel"
                value={newSponsor.contact_phone || ""}
                onChange={(e) => setNewSponsor({...newSponsor, contact_phone: e.target.value})}
                placeholder="+1 555-123-4567"
              />
            </div>
            <div>
              <Label>Website</Label>
              <Input
                value={newSponsor.website}
                onChange={(e) => setNewSponsor({...newSponsor, website: e.target.value})}
                placeholder="https://example.com"
                data-testid="sponsor-website-input"
              />
            </div>
            <div>
              <Label>Logo URL</Label>
              <Input
                value={newSponsor.logo}
                onChange={(e) => setNewSponsor({...newSponsor, logo: e.target.value})}
                placeholder="https://example.com/logo.png"
              />
            </div>
            <div>
              <Label>Sponsor Tier</Label>
              <Select 
                value={newSponsor.tier} 
                onValueChange={(v) => setNewSponsor({...newSponsor, tier: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="platinum">Platinum</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSponsorModal(false)}>Cancel</Button>
            <Button onClick={addSponsor} data-testid="add-sponsor-submit-btn">Add Sponsor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Link Modal - Shows copyable magic link after team/VIP creation */}
      <Dialog open={showInviteLinkModal} onOpenChange={setShowInviteLinkModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              Invite Created!
            </DialogTitle>
            <DialogDescription>
              Share this link with {inviteLinkData.name} to invite them to your {inviteLinkData.type === "team" ? "team" : "VIP list"}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 p-4 rounded-lg border">
              <Label className="text-xs text-gray-500 mb-2 block">Magic Invite Link (Copy & Paste)</Label>
              <div className="flex items-center gap-2">
                <Input
                  value={inviteLinkData.link}
                  readOnly
                  className="flex-1 font-mono text-sm bg-white"
                  data-testid="invite-link-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(inviteLinkData.link);
                    toast.success("Link copied!");
                  }}
                  data-testid="copy-invite-link-btn"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>How it works:</strong> When {inviteLinkData.name} clicks this link, they'll be able to create or link their Phygital account. Their profile will automatically sync with your event.
              </p>
            </div>
            
            <div className="text-xs text-gray-500 text-center">
              No automatic emails will be sent. Copy this link and share it manually.
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowInviteLinkModal(false)} data-testid="close-invite-modal-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Custom Question Modal */}
      <Dialog open={showAddQuestionModal} onOpenChange={setShowAddQuestionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingQuestionIdx !== null ? "Edit Question" : "Add Custom Question"}</DialogTitle>
            <DialogDescription>Add a question to your RSVP form</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Question *</Label>
              <Input
                value={newQuestion.question}
                onChange={(e) => setNewQuestion({...newQuestion, question: e.target.value})}
                placeholder="e.g., How did you hear about this event?"
                data-testid="custom-question-input"
              />
            </div>
            <div>
              <Label>Answer Type</Label>
              <Select 
                value={newQuestion.type}
                onValueChange={(v) => setNewQuestion({...newQuestion, type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text (short answer)</SelectItem>
                  <SelectItem value="textarea">Text (long answer)</SelectItem>
                  <SelectItem value="select">Dropdown (select one)</SelectItem>
                  <SelectItem value="multiselect">Multi-select (select many)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(newQuestion.type === "select" || newQuestion.type === "multiselect") && (
              <div>
                <Label>Options (one per line)</Label>
                <Textarea
                  value={(newQuestion.options || []).join("\n")}
                  onChange={(e) => setNewQuestion({...newQuestion, options: e.target.value.split("\n").filter(o => o.trim())})}
                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                  rows={4}
                />
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch
                checked={newQuestion.required}
                onCheckedChange={(v) => setNewQuestion({...newQuestion, required: v})}
              />
              <Label>Required question</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddQuestionModal(false)}>Cancel</Button>
            <Button 
              onClick={() => {
                if (!newQuestion.question.trim()) {
                  toast.error("Question is required");
                  return;
                }
                const updated = [...(intakeSettings.custom_questions || [])];
                if (editingQuestionIdx !== null) {
                  updated[editingQuestionIdx] = newQuestion;
                } else {
                  updated.push(newQuestion);
                }
                setIntakeSettings({...intakeSettings, custom_questions: updated});
                setShowAddQuestionModal(false);
                setNewQuestion({ question: "", type: "text", required: false, options: [] });
                setEditingQuestionIdx(null);
                toast.success(editingQuestionIdx !== null ? "Question updated!" : "Question added!");
              }}
              data-testid="save-question-btn"
            >
              {editingQuestionIdx !== null ? "Save Changes" : "Add Question"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Person Modal - Shows user-submitted data (read-only) + host notes (editable) */}
      <Dialog open={showEditPersonModal} onOpenChange={setShowEditPersonModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Attendee Details
            </DialogTitle>
            <DialogDescription>
              View submitted information (read-only). Add private notes below.
            </DialogDescription>
          </DialogHeader>
          {editPersonData && (
            <ScrollArea className="flex-1 px-1 -mx-1">
              <div className="space-y-5 py-4 pr-4">
                
                {/* Header with profile photo and name */}
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border">
                  <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center overflow-hidden border-2 border-purple-200">
                    {editPersonData.profile_image ? (
                      <img src={getImageUrl(editPersonData.profile_image)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-2xl font-bold text-purple-600">
                        {(editPersonData.first_name || editPersonData.name || "G")[0]?.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg">
                        {editPersonData.first_name} {editPersonData.last_name}
                      </h3>
                      {getLabelBadge(editPersonData.label)}
                      {editPersonData.tags?.includes('vip') && (
                        <Badge className="bg-yellow-100 text-yellow-700">VIP</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{editPersonData.email || "No email"}</p>
                    {editPersonData.company && (
                      <p className="text-sm text-gray-500">{editPersonData.title ? `${editPersonData.title} at ` : ''}{editPersonData.company}</p>
                    )}
                  </div>
                </div>
                
                {/* Section: User-Submitted Info (READ-ONLY) */}
                <div className="space-y-4 border rounded-lg p-4 bg-gray-50/50">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm flex items-center gap-2 text-gray-700">
                      <User className="w-4 h-4" /> Submitted Information
                    </h3>
                    <Badge variant="outline" className="text-xs">Read-only</Badge>
                  </div>
                  
                  {/* Basic Info - Read-only display */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-xs text-gray-500 block">First Name</span>
                      <p className="font-medium">{editPersonData.first_name || "-"}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Last Name</span>
                      <p className="font-medium">{editPersonData.last_name || "-"}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Email</span>
                      <p className="font-medium">{editPersonData.email || "-"}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Phone</span>
                      <p className="font-medium">{editPersonData.phone || "-"}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">City</span>
                      <p className="font-medium">{editPersonData.city || "-"}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Company</span>
                      <p className="font-medium">{editPersonData.company || "-"}</p>
                    </div>
                  </div>
                  
                  {/* Social handles */}
                  {(editPersonData.instagram || editPersonData.linkedin || editPersonData.tiktok) && (
                    <div className="flex flex-wrap gap-2 pt-2 border-t">
                      {editPersonData.instagram && <Badge variant="outline" className="text-xs">IG: @{editPersonData.instagram.replace('@', '')}</Badge>}
                      {editPersonData.linkedin && <Badge variant="outline" className="text-xs">LinkedIn: {editPersonData.linkedin}</Badge>}
                      {editPersonData.tiktok && <Badge variant="outline" className="text-xs">TikTok: @{editPersonData.tiktok.replace('@', '')}</Badge>}
                    </div>
                  )}
                </div>
                
                {/* Section: Preferences (READ-ONLY) */}
                <div className="space-y-4 border rounded-lg p-4 bg-gray-50/50">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm flex items-center gap-2 text-gray-700">
                      <Sparkles className="w-4 h-4" /> Submitted Preferences
                    </h3>
                    <Badge variant="outline" className="text-xs">Read-only</Badge>
                  </div>
                  
                  {/* Dietary */}
                  <div>
                    <span className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                      <Utensils className="w-3 h-3" /> Dietary Restrictions
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {(editPersonData.dietary_restrictions || []).length > 0 ? (
                        editPersonData.dietary_restrictions.map((d, idx) => (
                          <Badge key={idx} className="bg-orange-100 text-orange-700 text-xs">🍽️ {d}</Badge>
                        ))
                      ) : (
                        <span className="text-sm text-gray-400 italic">None submitted</span>
                      )}
                    </div>
                    {editPersonData.dietary_notes && (
                      <p className="mt-1 text-sm text-gray-600 bg-white p-2 rounded border break-words">{editPersonData.dietary_notes}</p>
                    )}
                  </div>
                  
                  {/* Accessibility */}
                  <div>
                    <span className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                      <Accessibility className="w-3 h-3" /> Accessibility Needs
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {(editPersonData.accessibility_needs || []).length > 0 ? (
                        editPersonData.accessibility_needs.map((a, idx) => (
                          <Badge key={idx} className="bg-blue-100 text-blue-700 text-xs">♿ {a}</Badge>
                        ))
                      ) : (
                        <span className="text-sm text-gray-400 italic">None submitted</span>
                      )}
                    </div>
                    {(editPersonData.invisible_disabilities || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {editPersonData.invisible_disabilities.map((d, idx) => (
                          <Badge key={idx} className="bg-purple-100 text-purple-700 text-xs">💜 {d.replace(/_/g, ' ')}</Badge>
                        ))}
                      </div>
                    )}
                    {editPersonData.accessibility_notes && (
                      <p className="mt-1 text-sm text-gray-600 bg-white p-2 rounded border break-words">{editPersonData.accessibility_notes}</p>
                    )}
                    {editPersonData.anything_else_to_share && (
                      <p className="mt-1 text-sm text-gray-600 bg-white p-2 rounded border break-words"><span className="font-medium">Additional info:</span> {editPersonData.anything_else_to_share}</p>
                    )}
                  </div>
                  
                  {/* Interests */}
                  {(editPersonData.interests || []).length > 0 && (
                    <div>
                      <span className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                        <Heart className="w-3 h-3" /> Interests
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {editPersonData.interests.map((interest, idx) => (
                          <Badge key={idx} className="bg-purple-100 text-purple-700 text-xs">{interest}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Vibe */}
                  {editPersonData.vibe && (
                    <div>
                      <span className="text-xs text-gray-500 block mb-1">Vibe Check</span>
                      <p className="text-sm bg-white p-2 rounded border italic">"{editPersonData.vibe}"</p>
                    </div>
                  )}
                  
                  {/* Custom Answers */}
                  {editPersonData.custom_answers && Object.keys(editPersonData.custom_answers).length > 0 && (
                    <div className="pt-2 border-t space-y-2">
                      <span className="text-xs text-gray-500 block">Custom Responses</span>
                      {Object.entries(editPersonData.custom_answers).map(([question, answer], idx) => (
                        <div key={idx} className="bg-white p-2 rounded border">
                          <span className="text-xs text-gray-400">{question}</span>
                          <p className="text-sm">{Array.isArray(answer) ? answer.join(", ") : answer}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Section: Status & Label (Host can change) */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Label</Label>
                    <Select 
                      value={editPersonData.label} 
                      onValueChange={(v) => setEditPersonData({...editPersonData, label: v})}
                    >
                      <SelectTrigger data-testid="edit-person-label">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TEAM_LABELS.map(label => (
                          <SelectItem key={label.value} value={label.value}>
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${label.color.split(" ")[0]}`} />
                              {label.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select 
                      value={editPersonData.status} 
                      onValueChange={(v) => setEditPersonData({...editPersonData, status: v})}
                    >
                      <SelectTrigger data-testid="edit-person-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RSVP_STATUSES.map(status => (
                          <SelectItem key={status.value} value={status.value}>
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${status.color.split(" ")[0]}`} />
                              {status.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Section: Host Notes (EDITABLE) */}
                <div className="space-y-2 border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm flex items-center gap-2 text-purple-800">
                      <Shield className="w-4 h-4" /> Host Notes
                    </h3>
                    <Badge className="bg-purple-100 text-purple-700 text-xs">Private - Only visible to hosts</Badge>
                  </div>
                  <Textarea
                    value={editPersonData.host_note}
                    onChange={(e) => setEditPersonData({...editPersonData, host_note: e.target.value})}
                    placeholder="Add private notes about this attendee (allergies, special requests, VIP status, etc.)..."
                    className="min-h-[100px] bg-white"
                    data-testid="edit-person-host-note"
                  />
                </div>
              </div>
            </ScrollArea>
          )}
          <DialogFooter className="border-t pt-4 mt-2">
            <Button variant="outline" onClick={() => { setShowEditPersonModal(false); setEditPersonData(null); }}>
              Cancel
            </Button>
            <Button onClick={saveEditedPerson} disabled={updatingPerson} data-testid="save-person-btn">
              {updatingPerson ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Person Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Guest</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{personToDelete?.name || "this guest"}</strong> from the event? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowDeleteConfirm(false); setPersonToDelete(null); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deletePerson}
              className="bg-red-600 hover:bg-red-700"
              disabled={updatingPerson}
              data-testid="confirm-delete-person"
            >
              {updatingPerson ? "Removing..." : "Remove Guest"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Event Confirmation Dialog */}
      <AlertDialog open={showDeleteEventConfirm} onOpenChange={setShowDeleteEventConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete <strong>{event?.name || "this event"}</strong>? 
              This will remove all RSVPs, guest data, and settings. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteEventConfirm(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteEvent}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-event"
            >
              Delete Event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ============ CUSTOM MODALS (NO SYSTEM PROMPTS) ============ */}
      
      {/* Add Person to List Modal */}
      <AddListPersonModal
        open={showAddPersonModal}
        onOpenChange={setShowAddPersonModal}
        personData={newPersonData}
        setPersonData={setNewPersonData}
        listName={(event?.lists || []).find(l => l.list_id === currentListId)?.name}
        hostName={user?.name}
        hostOrg={user?.org_name || partners?.[0]?.name}
        onSubmit={() => {
          if (!newPersonData.name.trim()) return;
          const newMember = { 
            id: `member_${Date.now()}`,
            name: newPersonData.name, 
            email: newPersonData.email || '', 
            org_name: newPersonData.org_name || '',
            status: 'placeholder',
            type: 'person'
          };
          const updatedLists = (event?.lists || []).map(l => {
            if (l.list_id === currentListId) {
              return { ...l, members: [...(l.members || []), newMember] };
            }
            return l;
          });
          const token = localStorage.getItem("session_token");
          fetch(`${API}/events/${eventId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ lists: updatedLists })
          }).then(() => {
            mutateEvent();
            const listName = (event?.lists || []).find(l => l.list_id === currentListId)?.name;
            toast.success(`${newPersonData.name} added to ${listName}`);
            setShowAddPersonModal(false);
            setNewPersonData({ name: '', email: '', org_name: '' });
          });
        }}
      />

      {/* Add Organization to List Modal */}
      <AddListOrgModal
        open={showAddOrgModal}
        onOpenChange={setShowAddOrgModal}
        orgData={newOrgData}
        setOrgData={setNewOrgData}
        listName={(event?.lists || []).find(l => l.list_id === currentListId)?.name}
        hostName={user?.name}
        hostOrg={user?.org_name || partners?.[0]?.name}
        onSubmit={() => {
          if (!newOrgData.name.trim()) return;
          const newMember = { 
            id: `org_${Date.now()}`,
            name: newOrgData.name, 
            contact_name: newOrgData.contact_name || '',
            contact_email: newOrgData.contact_email || '',
            status: 'placeholder',
            type: 'organization'
          };
          const updatedLists = (event?.lists || []).map(l => {
            if (l.list_id === currentListId) {
              return { ...l, members: [...(l.members || []), newMember] };
            }
            return l;
          });
          const token = localStorage.getItem("session_token");
          fetch(`${API}/events/${eventId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ lists: updatedLists })
          }).then(() => {
            mutateEvent();
            const listName = (event?.lists || []).find(l => l.list_id === currentListId)?.name;
            toast.success(`${newOrgData.name} added to ${listName}`);
            setShowAddOrgModal(false);
            setNewOrgData({ name: '', contact_name: '', contact_email: '' });
          });
        }}
      />

      {/* Add Member to VIP List Modal */}
      <Dialog open={showAddMemberToListModal} onOpenChange={setShowAddMemberToListModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Member to VIP List</DialogTitle>
            <DialogDescription>Select an existing RSVP or add a placeholder that can be claimed later</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Select from existing RSVPs */}
            {rsvps && rsvps.length > 0 && (
              <div>
                <Label>Select from existing RSVPs</Label>
                <select
                  className="w-full p-2 border rounded-lg text-sm mt-1"
                  data-testid="select-rsvp-for-list"
                  onChange={async (e) => {
                    const rsvpId = e.target.value;
                    if (!rsvpId || !addMemberToListId) return;
                    
                    const selectedRsvp = rsvps.find(r => r.rsvp_id === rsvpId);
                    if (!selectedRsvp) return;
                    
                    const token = localStorage.getItem("session_token");
                    try {
                      const res = await fetch(`${API}/events/${eventId}/vip-lists/${addMemberToListId}/members`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                        body: JSON.stringify({
                          rsvp_id: rsvpId,
                          name: `${selectedRsvp.first_name || ''} ${selectedRsvp.last_name || ''}`.trim() || selectedRsvp.name,
                          email: selectedRsvp.email,
                          profile_photo_url: selectedRsvp.profile_photo_url
                        })
                      });
                      
                      if (res.ok) {
                        toast.success(`Added to VIP list!`);
                        setShowAddMemberToListModal(false);
                        fetchLists();
                        mutateRsvps();
                      } else {
                        const err = await res.json();
                        toast.error(err.detail || "Failed to add member");
                      }
                    } catch (err) {
                      toast.error("Failed to add member");
                    }
                  }}
                >
                  <option value="">-- Select an attendee --</option>
                  {rsvps.map(r => (
                    <option key={r.rsvp_id} value={r.rsvp_id}>
                      {`${r.first_name || ''} ${r.last_name || ''}`.trim() || r.name} {r.email ? `(${r.email})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Or add placeholder</span>
              </div>
            </div>
            
            {/* Add Placeholder Member - Name Only */}
            <div className="space-y-3">
              <div>
                <Label>Guest Name (Placeholder)</Label>
                <Input
                  placeholder="Enter name - they can claim via link later"
                  id="placeholder-member-name"
                  data-testid="placeholder-member-name"
                />
              </div>
              <Button
                className="w-full"
                onClick={async () => {
                  const nameInput = document.getElementById("placeholder-member-name");
                  const name = nameInput?.value?.trim();
                  if (!name || !addMemberToListId) {
                    toast.error("Please enter a name");
                    return;
                  }
                  
                  const token = localStorage.getItem("session_token");
                  try {
                    const res = await fetch(`${API}/events/${eventId}/vip-lists/${addMemberToListId}/members`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                      body: JSON.stringify({ name })
                    });
                    
                    if (res.ok) {
                      const data = await res.json();
                      toast.success(`Added ${name} as placeholder!`);
                      
                      // Show claim link if available
                      if (data.member?.claim_url) {
                        const claimUrl = `${window.location.origin}${data.member.claim_url}`;
                        navigator.clipboard.writeText(claimUrl);
                        toast.success("Claim link copied! Share it with them to RSVP.", { duration: 5000 });
                      }
                      
                      setShowAddMemberToListModal(false);
                      fetchLists();
                    } else {
                      const err = await res.json();
                      toast.error(err.detail || "Failed to add member");
                    }
                  } catch (err) {
                    toast.error("Failed to add member");
                  }
                }}
                data-testid="add-placeholder-member-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Placeholder
              </Button>
              <p className="text-xs text-gray-500 text-center">
                A claimable link will be generated that you can share
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMemberToListModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage VIP List Modal - Add placeholders & send invites */}
      <Dialog open={showManageListModal} onOpenChange={setShowManageListModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Manage {managingList?.name || "VIP List"}
              {wsConnected && (
                <span className="flex items-center gap-1 text-xs text-green-600 font-normal ml-auto">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Live
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Add placeholder guests and send them personal invite links.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            {/* Stats Bar - Only count primary VIPs against limit */}
            {managingList && (() => {
              const allMembers = (managingList.members || []).filter(m => m.role !== 'list_host');
              const primaryVIPs = allMembers.filter(m => !m.vip_invited_by && !m.invited_by_member_id);
              const plusOnes = allMembers.filter(m => m.vip_invited_by || m.invited_by_member_id);
              const maxGuests = managingList.max_guests || 10;
              const spotsLeft = maxGuests - primaryVIPs.length;
              const percentFilled = (primaryVIPs.length / maxGuests) * 100;
              
              return (
                <>
                  <div className="bg-purple-50 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-purple-900">
                        {managingList.name}
                      </p>
                      <p className="text-xs text-purple-700">
                        {primaryVIPs.length} / {maxGuests} VIP spots filled
                        {plusOnes.length > 0 && <span className="text-purple-500"> (+{plusOnes.length} guests)</span>}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-purple-700">
                        {spotsLeft}
                      </p>
                      <p className="text-xs text-purple-600">VIP spots left</p>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-600 transition-all"
                      style={{ width: `${percentFilled}%` }}
                    />
                  </div>
                </>
              );
            })()}

            {/* Quick Share - Primary Action */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <Button
                className="w-full bg-purple-600 hover:bg-purple-700"
                onClick={async () => {
                  const inviteUrl = `${window.location.origin}${managingList?.guest_invite_url || `/e/${eventSlug}?vip=gi_${managingList?.guest_invite_code}`}`;
                  if (navigator.share) {
                    try {
                      await navigator.share({
                        title: `VIP Invite: ${managingList?.name || 'VIP List'}`,
                        text: `You're invited as a VIP guest!`,
                        url: inviteUrl
                      });
                    } catch (err) {
                      if (err.name !== 'AbortError') {
                        navigator.clipboard.writeText(inviteUrl);
                        toast.success("Invite link copied!");
                      }
                    }
                  } else {
                    navigator.clipboard.writeText(inviteUrl);
                    toast.success("Invite link copied!");
                  }
                }}
                data-testid="quick-share-btn"
              >
                <ArrowUp className="w-4 h-4 mr-2" />
                Share Invite Link
              </Button>
              <p className="text-xs text-purple-700 mt-2 text-center">
                Anyone who joins counts toward your invites
              </p>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">or add by name</span>
              </div>
            </div>

            {/* Add Placeholder - Simplified (name only) */}
            <div className="flex gap-2">
              <Input
                placeholder="Guest name"
                value={newPlaceholder.name}
                onChange={(e) => setNewPlaceholder({...newPlaceholder, name: e.target.value})}
                className="flex-1"
                data-testid="placeholder-name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newPlaceholder.name.trim()) {
                    e.preventDefault();
                    document.querySelector('[data-testid="add-placeholder-btn"]')?.click();
                  }
                }}
              />
              <Button
                onClick={async () => {
                  if (!newPlaceholder.name.trim()) {
                    toast.error("Name is required");
                    return;
                  }
                  setAddingPlaceholder(true);
                  try {
                    const token = localStorage.getItem("session_token");
                    const res = await fetch(`${API}/events/${eventId}/vip-lists/${managingList.list_id}/members`, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                      },
                      body: JSON.stringify({
                        name: newPlaceholder.name,
                        is_placeholder: true
                      })
                    });
                    if (res.ok) {
                      const data = await res.json();
                      toast.success(`${newPlaceholder.name} added!`);
                      setNewPlaceholder({ name: "" });
                      fetchLists();
                      if (data.member) {
                        setManagingList(prev => ({
                          ...prev,
                          members: [...(prev.members || []), data.member]
                        }));
                      }
                    } else {
                      const err = await res.json().catch(() => ({}));
                      toast.error(err.detail || "Failed to add");
                    }
                  } catch (err) {
                    toast.error("Failed to add");
                  } finally {
                    setAddingPlaceholder(false);
                  }
                }}
                disabled={addingPlaceholder || !newPlaceholder.name.trim()}
                data-testid="add-placeholder-btn"
              >
                {addingPlaceholder ? "..." : "+ Add"}
              </Button>
            </div>

            {/* Current Members List */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Your Guests ({managingList?.members?.length || 0})</h4>
              {(!managingList?.members || managingList.members.length === 0) ? (
                <p className="text-sm text-gray-500 py-4 text-center">
                  No guests yet. Share your link or add names above.
                </p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {managingList.members.map((member, mIdx) => (
                    <div 
                      key={member.member_id || mIdx}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-purple-700">
                            {(member.name || "G")[0].toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-sm">{member.name || "Guest"}</p>
                          <p className="text-xs text-gray-500">
                            {member.rsvp_id ? (
                              <span className="text-green-600">✓ Confirmed</span>
                            ) : (
                              <span className="text-amber-600">Pending</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {member.rsvp_id ? (
                          <Badge className="bg-green-100 text-green-800 text-xs">Confirmed</Badge>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              const inviteUrl = `${window.location.origin}${managingList?.guest_invite_url || `/e/${eventSlug}?vip=gi_${managingList?.guest_invite_code}`}`;
                              if (navigator.share) {
                                try {
                                  await navigator.share({
                                    title: `VIP Invite for ${member.name}`,
                                    text: `You're invited as a VIP guest!`,
                                    url: inviteUrl
                                  });
                                } catch (err) {
                                  if (err.name !== 'AbortError') {
                                    navigator.clipboard.writeText(inviteUrl);
                                    toast.success("Link copied!");
                                  }
                                }
                              } else {
                                navigator.clipboard.writeText(inviteUrl);
                                toast.success("Link copied!");
                              }
                            }}
                            data-testid={`share-invite-${mIdx}`}
                          >
                            <ArrowUp className="w-3 h-3 mr-1" />
                            Share
                          </Button>
                        )}
                        {/* Remove from list */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            const token = localStorage.getItem("session_token");
                            try {
                              const memberId = member.member_id || member.id;
                              await fetch(`${API}/events/${eventId}/vip-lists/${managingList.list_id}/members/${memberId}`, {
                                method: "DELETE",
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success(`${member.name} removed`);
                              fetchLists();
                              setManagingList(prev => ({
                                ...prev,
                                members: prev.members.filter(m => (m.member_id || m.id) !== memberId)
                              }));
                            } catch (err) {
                              toast.error("Failed to remove");
                            }
                          }}
                          className="opacity-0 group-hover:opacity-100"
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManageListModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Custom Tool Modal */}
      <ToolModal
        open={showAddToolModal}
        onOpenChange={setShowAddToolModal}
        toolData={newToolData}
        setToolData={setNewToolData}
        isEditing={false}
        onSubmit={() => {
          if (!newToolData.name.trim()) return;
          const currentTools = techStack.tools || [];
          const newTools = [...currentTools, { 
            name: newToolData.name, 
            link: newToolData.link || '', 
            description: newToolData.description || `${newToolData.name} for event`,
            logo: ''
          }];
          setTechStack(prev => ({ ...prev, tools: newTools }));
          const token = localStorage.getItem('session_token');
          fetch(`${API}/events/${eventId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ tech_stack: { ...techStack, tools: newTools } })
          }).then(() => {
            mutateEvent();
            triggerGlobalSync('all', eventId);
            toast.success(`${newToolData.name} added`);
            setShowAddToolModal(false);
            setNewToolData({ name: '', link: '', description: '' });
          });
        }}
      />

      {/* Edit Tool Modal */}
      <ToolModal
        open={showEditToolModal}
        onOpenChange={setShowEditToolModal}
        toolData={newToolData}
        setToolData={setNewToolData}
        isEditing={true}
        onSubmit={() => {
          if (!newToolData.name.trim() || !editingTool) return;
          const currentTools = techStack.tools || [];
          const newTools = currentTools.map((tool, i) => {
            if (i === editingTool.idx) {
              return { 
                ...tool,
                name: newToolData.name, 
                link: newToolData.link || '', 
                description: newToolData.description || ''
              };
            }
            return tool;
          });
          setTechStack(prev => ({ ...prev, tools: newTools }));
          const token = localStorage.getItem('session_token');
          fetch(`${API}/events/${eventId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ tech_stack: { ...techStack, tools: newTools } })
          }).then(() => {
            mutateEvent();
            triggerGlobalSync('all', eventId);
            toast.success(`${newToolData.name} updated`);
            setShowEditToolModal(false);
            setEditingTool(null);
            setNewToolData({ name: '', link: '', description: '' });
          });
        }}
      />

      {/* Internal Notes Modal */}
      <Dialog open={showInternalNotesModal} onOpenChange={setShowInternalNotesModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Internal Notes
            </DialogTitle>
            <DialogDescription>
              Staff notes for {selectedPersonForNotes?.name || 'Guest'} - only visible to hosts
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Existing Notes */}
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {selectedPersonForNotes?.internal_notes?.length > 0 ? (
                selectedPersonForNotes.internal_notes.map((note, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <p className="text-sm text-gray-700">{note.note}</p>
                    <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                      <span>{note.added_by || 'Staff'}</span>
                      <span>{note.added_at ? new Date(note.added_at).toLocaleString() : ''}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 text-gray-400 text-sm">
                  No notes yet
                </div>
              )}
            </div>
            
            {/* Add New Note */}
            <div className="space-y-2 pt-2 border-t">
              <Label>Add Note</Label>
              <Textarea
                placeholder="Type your note here..."
                value={newInternalNote}
                onChange={(e) => setNewInternalNote(e.target.value)}
                rows={3}
                data-testid="internal-note-input"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowInternalNotesModal(false)}>
              Close
            </Button>
            <Button 
              onClick={addInternalNote}
              disabled={!newInternalNote.trim() || addingNote}
              data-testid="add-note-btn"
            >
              {addingNote ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={showDeleteConfirmModal}
        onOpenChange={setShowDeleteConfirmModal}
        title={`Delete "${deleteTarget.name}"?`}
        description="This action cannot be undone."
        onConfirm={deleteTarget.onConfirm}
      />

      {/* Host Inspo Media Library Popup */}
      <MediaLibraryPopup
        open={showHostInspoPopup}
        onClose={() => setShowHostInspoPopup(false)}
        onSelect={(url) => saveHostInspo(url)}
      />

      {/* Invite Client Modal */}
      <InviteClientModal
        open={showInviteClientModal}
        onClose={() => setShowInviteClientModal(false)}
        eventId={eventId}
        pilotCategory={event?.pilot_category}
        brandColor={catIconColor}
        existingClients={rsvps || []}
        hostLooks={[]}
        onSuccess={() => mutateRsvps()}
      />

      {/* Find / Import Client Modal (Iter 9.47.v - self-import flow) */}
      <FindOrImportClientModal
        open={showFindImportModal}
        onClose={() => setShowFindImportModal(false)}
        eventId={eventId}
        onAdded={() => { mutateRsvps && mutateRsvps(); }}
      />

      {/* Edit Client Profile Modal (Iter 9.47.v) - opens from the kanban tile,
          locks to read-only once the client has linked_user_id (signed in). */}
      <EditClientProfileModal
        open={!!editingClient}
        onClose={() => setEditingClient(null)}
        client={editingClient}
        onSaved={() => { mutateRsvps && mutateRsvps(); refetchImportedClients && refetchImportedClients(); }}
      />

      <AcuityClientSyncModal
        open={showAcuityClientSync}
        onClose={() => setShowAcuityClientSync(false)}
        eventId={eventId}
        onImported={() => mutateRsvps && mutateRsvps()}
      />

      <AcuityServiceMatchModal
        open={showAcuityServiceMatch}
        onClose={() => setShowAcuityServiceMatch(false)}
        eventId={eventId}
      />

      {/* Footer */}
      <footer className="py-6 border-t border-gray-100 bg-white mt-8">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-2 text-sm text-gray-600">
          <PhygitalIcon className="w-5 h-5" />
          <span>Powered by Phygital</span>
        </div>
      </footer>

      {/* Iter 9.37 - Upgrade modal mount */}
      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        reason={tierInfo?.is_capped ? `You've reached the Free Pilot ${tierInfo.client_cap}-client cap. Upgrade to Solo for unlimited clients.` : tierInfo?.trial_status === "expired" ? "Your 60-day free trial has ended." : null}
      />
    </div>
  );
}
